const en: Record<string, string> = {
  // App
  "app.title": "Cognota Next Gen",

  // Scope picker and workspace MCP access (NG-2585)
  "scope_picker.category.identity": "Identity",
  "scope_picker.category.workspace": "Workspace metadata",
  "scope_picker.category.work_items": "Work items",
  "scope_picker.category.projects": "Projects",
  "scope_picker.category.requests": "Intake requests",
  "scope_picker.category.programs": "Programs",
  "scope_picker.category.goals": "Goals",
  "scope_picker.category.events": "Events",
  "scope_picker.category.calendar": "Calendar",
  "scope_picker.category.users": "Members",
  "scope_picker.category.teams": "Teams",
  "scope_picker.category.qualifications": "Qualifications",
  "scope_picker.category.other": "Other",
  "scope_picker.loading": "Loading scope catalog…",
  "scope_picker.empty": "No scopes available.",
  "scope_picker.mcp_warning_title": "AI can make mistakes.",
  "scope_picker.mcp_warning_body":
    "These scopes let an AI client create, modify, or delete data in this workspace on a member’s behalf. Review carefully before saving.",
  "scope_picker.col_scope": "Scope",
  "scope_picker.col_allowed": "Allowed",
  "scope_picker.col_default": "Default",
  "scope_picker.always_granted": "Always granted",
  "scope_picker.destructive_aria": "Destructive scope",
  "scope_picker.destructive_tooltip":
    "This scope allows creating, modifying, or deleting data in the workspace.",
  "scope_picker.modifies_data": "modifies data",
  "scope_picker.default_aria": "{name} default",
  "workspace_mcp.loading": "Loading MCP access…",
  "workspace_mcp.title": "AI client access (MCP)",
  "workspace_mcp.description":
    "Let workspace members connect Claude, ChatGPT, and other MCP-capable AI tools to this workspace. Scopes you allow here are the ceiling — members pick the same set or narrower when they connect.",
  "workspace_mcp.status_on": "On",
  "workspace_mcp.status_off": "Off",
  "workspace_mcp.configure_scopes": "Configure scopes",
  "workspace_mcp.disabling": "Disabling…",
  "workspace_mcp.disable": "Disable",
  "workspace_mcp.enable": "Enable MCP access",
  "workspace_mcp.clients_show": "Show connected clients ({countText})",
  "workspace_mcp.clients_hide": "Hide connected clients ({countText})",
  "workspace_mcp.clients_help":
    "Each entry is one AI-client install authorised by a member of this workspace. Revoke takes them off the workspace until they re-consent.",
  "workspace_mcp.load_error": "Failed to load MCP access.",
  "workspace_mcp.save_error": "Failed to save MCP access.",
  "workspace_mcp.disable_error": "Failed to disable MCP access.",
  "workspace_mcp.revoke_error": "Failed to revoke client.",
  "workspace_mcp.clients.empty":
    "No AI clients are connected yet. Members can connect by pasting the MCP URL into Claude, ChatGPT, or another MCP-capable client.",
  "workspace_mcp.clients.self_declared": "(self-declared)",
  "workspace_mcp.clients.expired_title":
    "No active refresh token. The user must reconnect to revive this grant.",
  "workspace_mcp.clients.expired": "Expired",
  "workspace_mcp.clients.connected_by": "Connected by {name}",
  "workspace_mcp.clients.last_used": "last used",
  "workspace_mcp.clients.scopes_label": "Scopes:",
  "workspace_mcp.clients.revoking": "Revoking…",
  "workspace_mcp.clients.revoke": "Revoke",
  "workspace_mcp.ceiling.title": "What AI clients can do in this workspace",
  "workspace_mcp.ceiling.description":
    "Members will be shown these as options when they connect their AI client; they can pick the same set or narrower.",
  "workspace_mcp.ceiling.confirm_aria": "Confirm destructive scopes",
  "workspace_mcp.ceiling.confirm_text":
    "I understand these scopes let AI clients create, modify, or delete data in this workspace on a member’s behalf.",
  "workspace_mcp.ceiling.saving": "Saving…",

  // Error boundary
  "error_boundary.title": "Something went wrong",
  "error_boundary.description": "Please reload the page. If this keeps happening, contact support.",

  // Auth
  "auth.sign_in": "Sign in",
  "auth.sign_in_subcopy": "Enter your work email to continue.",
  "auth.use_different_email": "Use a different email",
  "auth.sso_unavailable_title": "Single sign-on is unavailable",
  "auth.sso_unavailable_message":
    "Your workspace's identity provider isn't reachable right now. Contact your workspace admin or support for help signing in.",
  "auth.sign_up": "Create account",
  "auth.sign_out": "Sign out",
  "auth.session_expired_title": "Session expired",
  "auth.session_expired_message": "Your session has ended. Please sign in again to continue.",
  "auth.email": "Email",
  "auth.work_email": "Work email",
  "auth.work_email_required": "Work email is required.",
  "auth.password": "Password",
  "auth.confirm_password": "Confirm password",
  "auth.continue": "Continue",
  "auth.checking": "Checking...",
  "auth.looking_up_account": "Looking up your account…",
  "auth.signing_in": "Signing in...",
  "auth.creating_account": "Creating account...",
  "auth.forgot_password": "Forgot password?",
  "auth.forgot_password_title": "Forgot password",
  "auth.reset_password": "Reset password",
  "auth.set_password": "Set password",
  "auth.set_password_description": "Set a password for your account.",
  "auth.send_reset_link": "Send reset link",
  "auth.sending": "Sending...",
  "auth.resetting": "Resetting...",
  "auth.setting_password": "Setting password...",
  "auth.current_password": "Current password",
  "auth.new_password": "New password",
  "auth.changing_password": "Changing password...",
  "auth.change_expired_password_title": "Update your password",
  "auth.change_expired_password_description":
    "Your workspace requires a new password before you can continue.",
  "auth.change_expired_password_submit": "Update password",
  "auth.check_email": "Check your email",
  "auth.check_email_description":
    "If an account exists for {email}, we've sent a password reset link.",
  "auth.back_to_sign_in": "Back to sign in",
  "auth.new_here": "New here?",
  "auth.already_have_account": "Already have an account?",
  "auth.or_continue_with": "Or continue with",
  "auth.magic_code_sent": "We sent a code to {email}. Enter it below.",
  "auth.code": "Code",
  "auth.verify": "Verify",
  "auth.verifying": "Verifying...",
  "auth.resend_code": "Resend code",
  "auth.resend_code_in": "Resend code in {seconds}s",
  "auth.use_magic_code": "Use a magic code instead",
  "auth.password_min_length": "Password must be at least 8 characters.",
  "auth.password_entirely_numeric": "Password can't be only numbers.",
  "auth.passwords_mismatch": "Passwords do not match.",
  "auth.invalid_email": "Enter a valid email address.",
  "auth.enter_code": "Please enter the code.",
  "auth.generic_error": "Something went wrong. Please try again.",
  "auth.sign_in_success": "Signed in successfully",
  "auth.sign_out_success": "Signed out successfully",
  "auth.invalid_credentials": "Invalid email or password",
  "auth.connection_failed": "Connection failed",
  "auth.password_show": "Show",
  "auth.password_hide": "Hide",
  "auth.failed_to_send_code": "Failed to send code. Please try again.",
  // Sign-in and sign-up error banners, by backend error code (NG-2581)
  "auth.error.instance_not_configured.title": "Instance not configured",
  "auth.error.instance_not_configured.message": "Please contact your administrator.",
  "auth.error.invalid_email.title": "Invalid email",
  "auth.error.invalid_email.message": "Please enter a valid email address.",
  "auth.error.email_required.title": "Email required",
  "auth.error.email_required.message": "Please enter your email address.",
  "auth.error.signup_disabled.title": "Sign-up disabled",
  "auth.error.signup_disabled.message": "New account creation is currently disabled.",
  "auth.error.magic_link_disabled.title": "Magic link disabled",
  "auth.error.magic_link_disabled.message": "Magic link login is not available.",
  "auth.error.password_login_disabled.title": "Password login disabled",
  "auth.error.password_login_disabled.message": "Password login is not available.",
  "auth.error.account_deactivated.title": "Account deactivated",
  "auth.error.account_deactivated.message":
    "Your account has been deactivated. Contact your administrator.",
  "auth.error.password_not_accepted.title": "Password not accepted",
  "auth.error.password_not_accepted.message":
    "That password doesn't meet the requirements. Choose a different one.",
  "auth.error.smtp_not_configured.title": "Email not configured",
  "auth.error.smtp_not_configured.message":
    "Email sending is not configured. Contact your administrator.",
  "auth.error.account_exists.title": "Account exists",
  "auth.error.account_exists.message": "An account with this email already exists. Please sign in.",
  "auth.error.sign_up_failed.title": "Sign-up failed",
  "auth.error.sign_up_failed.message": "Unable to create your account. Please try again.",
  "auth.error.missing_fields.title": "Missing fields",
  "auth.error.missing_fields.message": "Email and password are required.",
  "auth.error.code_required.title": "Code required",
  "auth.error.code_required.message": "Please enter the code sent to your email.",
  "auth.error.account_not_found.title": "Account not found",
  "auth.error.account_not_found.message": "No account exists with this email. Please sign up.",
  "auth.error.sign_in_failed.title": "Authentication failed",
  "auth.error.sign_in_failed.message": "Invalid email or password.",
  "auth.error.invalid_code.title": "Invalid code",
  "auth.error.invalid_code.message": "The code you entered is incorrect.",
  "auth.error.code_expired.title": "Code expired",
  "auth.error.code_expired.message": "Your code has expired. Please request a new one.",
  "auth.error.attempts_exhausted.title": "Too many attempts",
  "auth.error.attempts_exhausted.message":
    "You've exceeded the maximum attempts. Request a new code.",
  "auth.error.oauth_not_configured.title": "OAuth not configured",
  "auth.error.oauth_not_configured.message": "OAuth authentication is not available.",
  "auth.error.google_not_configured.title": "Google not configured",
  "auth.error.google_not_configured.message": "Google authentication is not available.",
  "auth.error.google_error.title": "Google error",
  "auth.error.google_error.message": "An error occurred with Google authentication.",
  "auth.error.invalid_reset_link.title": "Invalid reset link",
  "auth.error.invalid_reset_link.message": "This password reset link is invalid.",
  "auth.error.reset_link_expired.title": "Link expired",
  "auth.error.reset_link_expired.message":
    "This password reset link has expired. Request a new one.",
  "auth.error.incorrect_password.title": "Incorrect password",
  "auth.error.incorrect_password.message": "The current password you entered is incorrect.",
  "auth.error.password_required.title": "Password required",
  "auth.error.password_required.message": "Please enter a password.",
  "auth.error.invalid_new_password.title": "Invalid password",
  "auth.error.invalid_new_password.message": "The new password does not meet requirements.",
  "auth.error.password_already_set.title": "Password already set",
  "auth.error.password_already_set.message": "Your password has already been set.",
  "auth.error.rate_limited.title": "Too many requests",
  "auth.error.rate_limited.message": "Please wait a moment and try again.",
  "auth.error.account_locked.title": "Account temporarily locked",
  "auth.error.account_locked.message":
    "Too many failed sign-in attempts. Please wait a few minutes and try again.",
  "auth.error.unknown.title": "Something went wrong",
  "auth.error.unknown.message": "An unexpected error occurred. Please try again.",

  // Search
  "search.placeholder": "Search...",

  // Sidebar
  "sidebar.search": "Search",
  "sidebar.my_work": "My Work",
  "sidebar.my_teams": "My Teams",
  "sidebar.my_space": "My Space",
  "sidebar.workspace_views": "Views",
  "sidebar.dashboards": "Dashboards",
  "sidebar.reports": "Reports",
  "sidebar.insights": "Insights",
  "sidebar.work": "Work",
  "sidebar.strategy": "Strategy",
  "sidebar.goals": "Goals",
  "sidebar.programs": "Programs",
  "sidebar.requests": "Requests",
  "sidebar.submissions": "Submissions",

  // Dashboards
  "dashboards.title": "Dashboards",
  "dashboards.preview_empty": "No widgets yet",
  "dashboards.updated_on": "Updated {date}",
  "dashboards.preview_more": "+{count} more",
  "reporting_agent.title": "Reporting Teammate",
  "reporting_agent.empty_subtitle":
    "Describe the report you'd like to build from your workspace data.",
  "reporting_agent.composer_placeholder": "Ask for a report…",
  "reporting_agent.send": "Send",
  "reporting_agent.answer_placeholder": "Type your answer…",
  "reporting_agent.send_answer": "Send answer",
  "reporting_agent.working": "Working on it…",
  "reporting_agent.new_chat": "New chat",
  "reporting_agent.history": "History",
  "reporting_agent.history_loading": "Loading…",
  "reporting_agent.history_empty": "No past conversations",
  "reporting_agent.history_untitled": "Untitled conversation",
  "reporting_agent.approve": "Approve",
  "reporting_agent.deny": "Deny",
  "reporting_agent.approved": "Approved",
  "reporting_agent.denied": "Denied",
  "reporting_agent.view_dashboard": "View report",
  "reporting_agent.dashboard_title": "Report",
  "reporting_agent.dashboard_load_error": "Couldn't load this report. Try reopening it.",
  "reporting_agent.connection_error":
    "The connection to your Reporting Teammate was interrupted. Start a new chat to reconnect.",
  "reporting_agent.suggestion.overdue_work": "Overdue work and who owns it",
  "reporting_agent.suggestion.team_utilization": "Team utilization over the last 8 weeks",
  "reporting_agent.suggestion.request_volume": "Request volume and acceptance rate",
  "dashboards.empty": "No dashboards yet",
  "dashboards.failed_to_load": "Failed to load dashboards.",
  "dashboards.browse_denied":
    "You don't have permission to browse dashboards. Contact your workspace admin to request access.",
  "dashboards.retry": "Retry",
  "dashboards.search_placeholder": "Search dashboards",
  "dashboards.search_empty": "No dashboards match “{query}”.",
  "dashboards.shared_label": "Shared",
  "dashboards.sort_label": "Sort",
  "dashboards.sort_last_viewed": "Last viewed",
  "dashboards.sort_alphabetical": "Alphabetical",
  "dashboards.not_found": "Dashboard not found",
  "dashboards.scope_label": "Scope:",
  "dashboards.scope_all_projects": "All projects in this workspace",
  "dashboards.scope_placeholder": "Select projects",
  "dashboards.scope_remove_project": "Remove {name}",
  "dashboards.scope_subset_projects": "Only selected projects",
  "dashboards.scope_edit": "Edit",
  "dashboards.scope_edit_title": "Linked projects",
  "dashboards.scope_edit_help":
    "Linked projects narrow the chart data. Unlink all to span the workspace.",
  "dashboards.scope_no_projects_in_workspace": "No projects in this workspace yet.",
  "dashboards.scope_no_search_matches": "No projects match this search.",
  "dashboards.scope_search_placeholder": "Search projects…",
  "dashboards.scope_search_label": "Search projects",
  "dashboards.scope_search_more_hint": "Showing {shown} of {total} — type to search",
  "dashboards.chart.failed_to_render": "Chart failed to render",
  "dashboards.chart.failed_to_load": "Failed to load",
  "dashboards.chart.unsupported": "Chart type not yet supported",
  "dashboards.chart.no_data": "No data",
  "dashboards.table.search_placeholder": "Filter rows…",
  "dashboards.table.no_matching": "No matching items.",
  "dashboards.chart.not_specified": "Not specified",
  "dashboards.chart.target": "Target",
  "dashboards.chart.within_sla_counts": "{within} of {total} within SLA",
  // Period label under a NUMBER_WITH_TREND headline; count = sparkline points.
  "dashboards.chart.last_n_weeks": "Last {count, plural, one {# week} other {# weeks}}",
  "dashboards.chart.compared_periods": "{current} vs {prior}",
  "dashboards.chart.cost_locked_title": "Cost data restricted",
  "dashboards.chart.cost_locked_body": "You don't have access to cost data.",
  // Degraded tile (GH-3981) — the body copy is the backend's own `warning`
  // string, so only the title is keyed here.
  "dashboards.chart.degraded_title": "This widget can't be charted",
  // Its own key rather than reusing `dashboards.widget_edit` ("Edit"): that reads
  // fine inside the widget's action menu, but a bare "Edit" on a tile doesn't say
  // what it edits.
  "dashboards.chart.degraded_edit": "Edit widget",
  // NG-1482 — the last line of an interactive chart mark's tooltip: what a click
  // on it does. The bucket copy leaves the value unnamed because the tooltip's
  // header already names it; a stacked chart's header names the bucket, not the
  // segment under the pointer, so its copy names the segment.
  "dashboards.chart.hint_filter_add": "Click to filter the dashboard by this value",
  "dashboards.chart.hint_filter_remove": "Click to remove this dashboard filter",
  "dashboards.chart.hint_series_filter_add": "Click to filter the dashboard by {series}",
  "dashboards.chart.hint_series_filter_remove": "Click to remove the dashboard filter for {series}",
  "dashboards.chart.hint_series_filter_any":
    "Click a segment to add or remove its dashboard filter",
  "dashboards.chart.hint_open_project": "Click to open this project",
  "dashboards.chart.hint_show_breakdown": "Click to show the capacity breakdown",
  "dashboards.chart.hint_hide_breakdown": "Click to hide the capacity breakdown",
  // Human-readable chart bucket labels — used by xAxisTickFormatter,
  // tooltip headers, and stacked-bar legends in chart-renderer.tsx.
  // Falls back to a title-cased version of the code when missing.
  "dashboards.label.lob.sales": "Sales",
  "dashboards.label.lob.human_resources": "Human Resources",
  "dashboards.label.lob.engineering": "Engineering",
  "dashboards.label.lob.it": "IT",
  "dashboards.label.lob.information_technology": "Information Technology",
  "dashboards.label.lob.finance": "Finance",
  "dashboards.label.lob.operations": "Operations",
  "dashboards.label.lob.marketing": "Marketing",
  "dashboards.label.lob.legal": "Legal",
  "dashboards.label.state_group.new": "New",
  "dashboards.label.state_group.backlog": "Backlog",
  "dashboards.label.state_group.unstarted": "Unstarted",
  "dashboards.label.state_group.in_review": "In Review",
  "dashboards.label.state_group.waitlisted": "Waitlisted",
  "dashboards.label.state_group.started": "In Progress",
  "dashboards.label.state_group.on_hold": "On Hold",
  "dashboards.label.goal_status.planning": "Planning",
  "dashboards.label.goal_status.active": "Active",
  "dashboards.label.goal_status.on_hold": "On Hold",
  "dashboards.label.goal_status.complete": "Complete",

  // Goals: shared vocabulary, leads and links sections (NG-2584)
  "goals.value_type.number": "Number",
  "goals.value_type.percent": "Percent",
  "goals.value_type.currency": "Currency",
  "goals.value_type.duration": "Duration",
  "goals.leads.title": "Goal leads",
  "goals.leads.add_placeholder": "Add a lead…",
  "goals.leads.empty": "No leads yet. A goal lead can edit the goal, its metrics, and its links.",
  "goals.leads.remove_aria": "Remove {name} as goal lead",
  "goals.links.project.placeholder": "Link a project…",
  "goals.links.project.search": "Search projects…",
  "goals.links.project.no_options": "No projects to link",
  "goals.links.project.trigger_aria": "Link a project",
  "goals.links.project.none_linked": "No projects are linked to this goal.",
  "goals.links.program.placeholder": "Link a program…",
  "goals.links.program.search": "Search programs…",
  "goals.links.program.no_options": "No programs to link",
  "goals.links.program.trigger_aria": "Link a program",
  "goals.links.program.none_linked": "No programs are linked to this goal.",
  "goals.links.request.placeholder": "Link a request…",
  "goals.links.request.search": "Search requests…",
  "goals.links.request.no_options": "No requests to link",
  "goals.links.request.trigger_aria": "Link a request",
  "goals.links.request.none_linked": "No requests are linked to this goal.",
  "goals.links.inactive_hint":
    "Only active goals can take new links — reactivate the goal to link more work. Existing links can still be removed.",
  "goals.links.unlink_aria": "Unlink {name}",
  "goals.metrics.title": "Metrics",
  "goals.metrics.add": "Add metric",
  "goals.metrics.empty_read_only": "This goal has no metrics yet.",
  "goals.metrics.empty_editable":
    "Add a metric to make this goal measurable — a baseline and the target to reach.",
  "goals.metrics.name_label": "Metric name *",
  "goals.metrics.name_placeholder": "e.g. Course completion rate",
  "goals.metrics.view_data_aria": "View data for {name}",
  "goals.metrics.remove_aria": "Remove metric",
  "goals.metrics.type_label": "Type",
  "goals.metrics.baseline": "Baseline",
  "goals.metrics.target": "Target",
  "goals.metrics.no_values": "No values recorded yet — use the chart button to add the first one.",
  "goals.metrics.latest_summary":
    "Latest {value} · {date} · {count, plural, one {{countText} value} other {{countText} values}}",
  "goals.metrics.progress_to_target": "{percent}% to target",
  "goals.data.load_error": "Failed to load metric data",
  "goals.data.save_error": "Failed to save datapoint",
  "goals.data.delete_error": "Failed to delete datapoint",
  "goals.data.col_date": "Date",
  "goals.data.col_value": "Value",
  "goals.data.col_note": "Note",
  "goals.data.col_recorded_by": "Recorded by",
  "goals.data.actions_aria": "Datapoint actions",
  "goals.data.edit_aria": "Edit value for {date}",
  "goals.data.delete_aria": "Delete value for {date}",
  "goals.data.empty": "No data recorded yet.",
  "goals.data.note_placeholder": "Optional context for this point",

  "dashboards.label.state_group.accepted": "Accepted",
  "dashboards.label.state_group.delivered": "Delivered",
  "dashboards.label.state_group.completed": "Completed",
  "dashboards.label.state_group.declined": "Declined",
  "dashboards.label.state_group.cancelled": "Cancelled",
  "dashboards.label.disposition.accepted": "Accepted",
  "dashboards.label.disposition.declined": "Declined",
  "dashboards.label.disposition.covered": "Covered",
  "dashboards.label.disposition.withdrawn": "Withdrawn",
  "dashboards.label.disposition.waitlisted": "Waitlisted",
  "dashboards.label.disposition.pending": "Pending",
  "dashboards.label.request_type.coaching": "Coaching",
  "dashboards.label.request_type.compliance": "Compliance",
  "dashboards.label.request_type.content_refresh": "Content Refresh",
  "dashboards.label.request_type.elearning": "eLearning",
  "dashboards.label.request_type.instructor_led_training": "Instructor-led Training",
  "dashboards.label.request_type.onboarding": "Onboarding",
  "dashboards.label.request_type.other": "Other",
  "dashboards.label.request_type.workshop_event": "Workshop / Event",
  "dashboards.breakdown_pie.empty": "No breakdown data",
  "dashboards.breakdown_pie.project": "Project",

  // Timeline, gantt, time off, dashboards PoP and program toast copy (NG-2585)
  "dashboards.pop.increased": "Increased vs prior period",
  "dashboards.pop.decreased": "Decreased vs prior period",
  "dashboards.pop.unchanged": "Unchanged vs prior period",
  "dashboards.pop.vs_prior": "vs {value} prior",
  "schedule.gantt.drag_dependency": "Drag to create dependency",
  "time_off.band_title": "Time off · {label}",
  "time_off.default_title": "Time off",
  "timeline.conflict_time_off": "Conflicts with time off",
  "timeline.team_members": "{countText} members",
  "timeline.project_subtitle": "Project",
  "workspace_settings.templates.workitem.day_value": "Day {day}",
  "programs.update_error": "Failed to update program",

  "dashboards.breakdown_pie.event": "Event",
  "dashboards.breakdown_pie.admin": "Admin",
  "dashboards.breakdown_pie.open_capacity_detail": "Open capacity detail",
  "dashboards.coming_soon": "Coming soon",
  "dashboards.a11y.table_label": "Label",
  "dashboards.a11y.table_series": "Series",
  "dashboards.a11y.table_value": "Value",
  // A chart row's accessible name: what it is, what it measures, and what
  // activating it does — e.g. "Eric Matson, 275%, open contributing work".
  "dashboards.a11y.row_action": "{label}, {value}, {action}",
  "dashboards.a11y.action_filter": "filter the dashboard by this value",
  "dashboards.a11y.action_open_project": "open this project",
  // NG-1482 — the same actions the chart tooltips name, for the keyboard rows.
  "dashboards.a11y.action_remove_filter": "remove this dashboard filter",
  "dashboards.a11y.action_show_breakdown": "show the capacity breakdown",
  "dashboards.a11y.action_hide_breakdown": "hide the capacity breakdown",
  "dashboards.a11y.action_open_capacity": "open capacity detail",
  "dashboards.a11y.kpi_drill": "{value}, {action}",
  "dashboards.a11y.action_open_over_capacity": "open the people who are over capacity",
  "dashboards.a11y.action_open_overdue": "open the overdue tasks behind this number",
  "dashboards.widget_untitled": "Untitled widget",
  "dashboards.widget_actions": "Widget actions",
  "dashboards.widget_edit": "Edit",
  "dashboards.widget_delete": "Delete",
  "dashboards.widget_delete_error": "Failed to delete widget",
  "dashboards.widget_layout_save_error": "Couldn't save the layout change",
  "dashboards.widget_drag_handle": "Drag to move widget",
  "dashboards.configure_widget": "Configure widget",
  "dashboards.widget_name": "Name",
  "dashboards.chart_type": "Chart type",
  "dashboards.chart_type.bar": "Bar",
  "dashboards.chart_type.line": "Line",
  "dashboards.chart_type.area": "Area",
  "dashboards.chart_type.pie": "Pie",
  "dashboards.chart_type.donut": "Donut",
  "dashboards.chart_type.number": "Number",
  "dashboards.chart_type.comparison_bar": "Comparison bar",
  "dashboards.chart_type.table": "Table",
  // TABLE widget column headers (GH-4077). Backend-authored labels from each
  // source's column registry (apps/backend/cognota/dashboards/sources/*.py),
  // keyed by source + column key. A column with no entry here falls back to the
  // backend's own label, so a new registry entry stays readable.
  "dashboards.table_column.TASK.name": "Name",
  "dashboards.table_column.TASK.state": "Status",
  "dashboards.table_column.TASK.owner": "Owner",
  "dashboards.table_column.TASK.due_date": "Due",
  "dashboards.table_column.TASK.days_overdue": "Days overdue",
  "dashboards.table_column.TASK.project": "Project",
  "dashboards.table_column.PROJECT.name": "Name",
  "dashboards.table_column.PROJECT.state": "State",
  "dashboards.table_column.PROJECT.lead": "Lead",
  "dashboards.table_column.PROJECT.target_completion_date": "Target completion",
  "dashboards.table_column.PROJECT.days_overdue": "Days overdue",
  "dashboards.table_column.PROJECT.project_type": "Type",
  "dashboards.table_column.REQUEST.form": "Form",
  "dashboards.table_column.REQUEST.state": "Status",
  "dashboards.table_column.REQUEST.submitted_by": "Submitted by",
  "dashboards.table_column.REQUEST.submitted_at": "Submitted",
  "dashboards.table_column.REQUEST.request_type": "Type",
  "dashboards.table_column.TIME_LOG.logged_at": "Logged at",
  "dashboards.table_column.TIME_LOG.duration": "Duration (s)",
  "dashboards.table_column.USER_CAPACITY.user": "User",
  "dashboards.table_column.USER_CAPACITY.date": "Date",
  "dashboards.table_column.USER_CAPACITY.planned_task_minutes": "Planned task (min)",
  "dashboards.table_column.USER_CAPACITY.capacity_minutes": "Capacity (min)",
  "dashboards.table_column.CAPACITY_ALLOCATION.user": "User",
  "dashboards.table_column.CAPACITY_ALLOCATION.date": "Date",
  "dashboards.table_column.CAPACITY_ALLOCATION.minutes": "Minutes",
  "dashboards.table_column.CAPACITY_ALLOCATION.work_item": "Work item",
  "dashboards.editor.table_row_filter": "Row filter",
  "dashboards.editor.table_row_filter_none": "All rows",
  "dashboards.editor.table_row_filter_hint": "Lists only the rows this measure counts.",
  "dashboards.editor.table_columns": "Columns",
  "dashboards.editor.table_sort": "Sort by",
  "dashboards.editor.table_sort_none": "Backend order",
  "dashboards.editor.table_sort_direction": "Sort direction",
  "dashboards.editor.table_sort_asc": "Ascending",
  "dashboards.editor.table_sort_desc": "Descending",
  "dashboards.editor.table_limit": "Row limit",
  "dashboards.editor.table_limit_hint": "Between 1 and 500 rows.",
  "dashboards.x_axis": "X axis",
  "dashboards.custom_fields_group": "Custom fields",
  "dashboards.group_by": "Group by",
  "dashboards.date_grouping": "Date grouping",
  "dashboards.date_grouping.day": "Day",
  "dashboards.date_grouping.week": "Week",
  "dashboards.date_grouping.month": "Month",
  "dashboards.date_grouping.year": "Year",
  "dashboards.metric": "Metric",
  "dashboards.editor.baseline_metric": "Baseline (backdrop)",
  "dashboards.editor.compare_same_unit":
    "Only metrics in the baseline's unit (cost or hours) can be compared on one axis.",
  "dashboards.editor.outcome_metric": "Compare (front bar)",
  "dashboards.numerator": "Numerator",
  "dashboards.denominator": "Denominator",
  "dashboards.source": "Data source",
  "dashboards.source.task": "Work items",
  "dashboards.source.request": "Requests",
  "dashboards.source.project": "Projects",
  "dashboards.source.time_log": "Time log",
  "dashboards.source.capacity_allocation": "Capacity allocation",
  "dashboards.source.user_capacity": "User capacity",
  "dashboards.source.goal": "Goals",
  "dashboards.property.states": "State",
  "dashboards.property.state_groups": "State group",
  "dashboards.property.assignees": "Assignees",
  "dashboards.property.estimate_hours": "Estimate hours",
  "dashboards.property.priority": "Priority",
  "dashboards.property.start_date": "Start date",
  "dashboards.property.target_date": "Target date",
  "dashboards.property.created_at": "Created date",
  "dashboards.property.goal_status": "Goal status",
  "dashboards.property.submitted_at": "Submitted date",
  "dashboards.property.completed_at": "Completed date",
  "dashboards.property.logged_at": "Logged at",
  "dashboards.property.work_item_types": "Work item type",
  "dashboards.property.stages": "Stage",
  "dashboards.property.projects": "Project",
  "dashboards.property.project_type": "Project type",
  "dashboards.property.created_by": "Created by",
  "dashboards.property.epics": "Epic",
  "dashboards.property.disposition": "Disposition",
  "dashboards.property.request_type": "Request type",
  "dashboards.property.submitted_by": "Submitted by",
  "dashboards.property.decline_reason": "Declined reason",
  "dashboards.property.waitlist_reason": "Waitlisted reason",
  "dashboards.property.cancel_reason": "Cancelled reason",
  "dashboards.property.project_lead": "Project lead",
  "dashboards.property.delivery_teams": "Delivery teams",
  "dashboards.property.home_team": "Home team",
  "dashboards.property.target_completion_date": "Target completion date",
  "dashboards.property.actual_completion_date": "Actual completion date",
  "dashboards.property.teams": "Teams",
  "dashboards.property.lob": "Line of business",
  "dashboards.property.sla": "SLA",
  "dashboards.metric.work_item_count": "Work item count",
  "dashboards.metric.estimate_hours_count": "Estimate hours",
  "dashboards.metric.pending_work_item_count": "Pending work items",
  "dashboards.metric.completed_work_item_count": "Completed work items",
  "dashboards.metric.in_progress_work_item_count": "In-progress work items",
  "dashboards.metric.cancelled_work_item_count": "Cancelled work items",
  "dashboards.metric.work_item_due_today_count": "Due today",
  "dashboards.metric.work_item_due_this_week_count": "Due this week",
  "dashboards.metric.avg_estimate_hours": "Avg estimate hours",
  "dashboards.metric.overdue_count": "Overdue work items",
  "dashboards.metric.open_work_item_count": "Open work items",
  "dashboards.metric.avg_queue_time_seconds": "Avg queue time",
  "dashboards.metric.avg_time_to_decision_seconds": "Avg time to decision",
  "dashboards.metric.avg_build_time_seconds": "Avg build time",
  "dashboards.metric.sla_adherence_percent": "SLA adherence",
  "dashboards.metric.goal_alignment": "Goal alignment",
  "dashboards.metric.goal_count": "Goal count",
  "dashboards.metric.goal_progress": "Goal progress",
  "dashboards.metric.planned_hours": "Planned hours",
  "dashboards.metric.capacity_hours": "Capacity hours",
  "dashboards.metric.planned_minutes": "Planned minutes",
  "dashboards.metric.capacity_minutes": "Capacity minutes",
  "dashboards.metric.over_capacity_member_count": "Over-capacity members",
  "dashboards.metric.under_utilized_free_hours": "Under-utilized free hours",
  "dashboards.metric.team_utilization_percent": "Team utilization",
  "dashboards.metric.actual_cost": "Actual cost",
  "dashboards.metric.estimated_cost": "Estimated cost",
  "dashboards.metric.logged_cost": "Logged cost",
  "dashboards.metric.logged_hours": "Logged hours",
  "dashboards.metric.actual_hours": "Actual hours (logged)",
  "dashboards.metric.earned_value": "Earned value",
  "dashboards.metric.cost_variance": "Cost variance (earned − actual)",
  "dashboards.metric.estimate_at_completion": "Projected cost (EAC)",
  "dashboards.metric.ratio_percent": "Ratio (percent)",
  "dashboards.filters": "Filters",
  "dashboards.widget_save_error": "Failed to save widget",
  "dashboards.widget_save_success": "Widget saved",
  "dashboards.widget_download_image": "Download as image",
  "dashboards.widget_download_failed": "Could not export the widget image.",
  "dashboards.action_refresh": "Refresh",
  "dashboards.action_export": "Export",
  "dashboards.action_share": "Share",
  "dashboards.action_edit": "Edit",
  "dashboards.action_more": "More actions",
  "dashboards.cannot_edit_widgets": "Can't edit widgets",
  "dashboards.filters_change_your_view": "Filters and the date range change only your view.",
  "dashboards.role_cannot_edit_widgets": "Your role can't add or change widgets.",
  "dashboards.owned_by": "Owned by {name} ({email})",
  "dashboards.action_delete": "Delete dashboard",
  "dashboards.action_duplicate": "Duplicate",
  "dashboards.duplicate_success": "Dashboard duplicated",
  "dashboards.duplicate_error": "Failed to duplicate dashboard",
  "dashboards.create_widget": "Create widget",
  "dashboards.widget_create_error": "Failed to create widget",
  "dashboards.delete_confirm_title": "Delete dashboard",
  "dashboards.delete_confirm_description": 'Delete "{name}"? This cannot be undone.',
  "dashboards.delete_success": "Dashboard deleted",
  "dashboards.delete_error": "Failed to delete dashboard",
  "dashboards.no_widgets": "No widgets yet",
  "dashboards.create_title": "Create dashboard",
  "dashboards.edit_title": "Edit dashboard",
  "dashboards.name_label": "Name",
  "dashboards.name_placeholder": "e.g. Q2 Capacity",
  "dashboards.description_label": "Description",
  "dashboards.create_dashboard": "Create dashboard",
  "dashboards.save": "Save",
  "dashboards.create_success": "Dashboard created",
  "dashboards.update_success": "Dashboard updated",
  "dashboards.save_error": "Failed to save dashboard",
  // Sharing (NG-1115)
  "dashboards.share.action": "Share",
  "dashboards.share.title": "Share dashboard",
  "dashboards.share.description": "Give people access to “{name}”.",
  "dashboards.share.add_label": "Add people",
  "dashboards.share.add_placeholder": "Search for a person",
  "dashboards.share.add_button": "Share",
  "dashboards.share.add_success": "Dashboard shared",
  "dashboards.share.add_error": "Failed to share dashboard",
  "dashboards.share.role_label": "Access",
  "dashboards.share.new_recipient_role_label": "New recipient's role",
  "dashboards.share.role_viewer": "Viewer",
  "dashboards.share.role_owner": "Owner",
  "dashboards.share.role_error": "Failed to change access",
  "dashboards.share.people_label": "People with access",
  "dashboards.share.empty": "This dashboard hasn’t been shared with anyone yet.",
  "dashboards.share.load_error": "Failed to load who this dashboard is shared with",
  "dashboards.share.remove_label": "Remove {name}",
  "dashboards.share.remove_title": "Remove access?",
  "dashboards.share.remove_description": "{name} will no longer be able to see this dashboard.",
  "dashboards.share.remove_confirm": "Remove access",
  "dashboards.share.remove_success": "Access removed",
  "dashboards.share.remove_error": "Failed to remove access",
  "dashboards.share.last_viewed": "Viewed {date}",
  "dashboards.share.never_viewed": "Never viewed",
  "dashboards.new_dashboard": "New dashboard",
  "dashboards.create_first": "Create your first dashboard",
  "dashboards.card_default_description": "Open dashboard",

  // Reports (Insights → Reports)
  "reports.title": "Reports",
  "reports.create": "Create report",
  "reports.teammate_create": "Create with Reporting Teammate",
  "reports.teammate_source": "Reporting Teammate",
  "reports.generated_view_title": "Generated report",
  "reports.column.name": "Report Name",
  "reports.column.data_source": "Data Source",
  "reports.column.owner": "Owner",
  "reports.column.last_run": "Last Run",
  "reports.never_run": "Never run",
  "reports.owner.unknown": "—",
  "reports.legacy_pill": "Legacy",
  "reports.object_type.request": "Requests",
  "reports.object_type.program": "Programs",
  "reports.object_type.project": "Projects",
  "reports.object_type.work_item": "Work Items",
  "reports.object_type.time_log": "Time Logs",
  "reports.object_type.time_off": "Time Off",
  "reports.object_type.request.singular": "Request",
  "reports.object_type.program.singular": "Program",
  "reports.object_type.project.singular": "Project",
  "reports.object_type.work_item.singular": "Work Item",
  "reports.object_type.time_log.singular": "Time Log",
  "reports.object_type.time_off.singular": "Time Off",
  "reports.empty.title": "No reports yet",
  "reports.empty.description": "Build and export custom data views from your workspace.",
  "reports.empty.create_first": "Create your first report",
  "reports.row_actions.menu_aria": "Actions for {name}",
  "reports.row_actions.open": "Open",
  "reports.row_actions.edit": "Edit",
  "reports.row_actions.duplicate": "Duplicate",
  "reports.row_actions.delete": "Delete",
  "reports.duplicate.success": "Report duplicated",
  "reports.duplicate.error": "Couldn't duplicate the report. Please try again.",
  "reports.delete.title": "Delete report",
  "reports.delete.message": 'Delete "{name}"? This can\'t be undone.',
  "reports.delete.confirm": "Delete",
  "reports.delete.success": "Report deleted",
  "reports.delete.error": "Couldn't delete the report. Please try again.",
  "reports.builder.title_create": "Create report",
  "reports.builder.title_edit": "Edit report",
  "reports.builder.step.sources": "Report type",
  "reports.builder.step.columns": "Columns",
  "reports.builder.step.filters": "Filters",
  "reports.builder.step.name": "Name & save",
  "reports.builder.back": "Back",
  "reports.builder.next": "Next",
  "reports.builder.save": "Save",
  "reports.builder.save_and_view": "Save & view",
  "reports.builder.step_sources.heading": "What do you want to report on?",
  "reports.builder.step_sources.description":
    "Choose the type of object this report is about. You'll pick its columns and filters next.",
  "reports.builder.step_sources.hint.request": "Intake submissions from stakeholders.",
  "reports.builder.step_sources.hint.program": "Strategic bodies of learning work.",
  "reports.builder.step_sources.hint.project": "Builds and deliverables inside programs.",
  "reports.builder.step_sources.hint.work_item": "Tasks and events inside projects.",
  "reports.builder.step_sources.hint.time_log":
    "Logged time entries with project and work context.",
  "reports.builder.step_sources.hint.time_off": "Members' time-off records.",
  "reports.builder.step_columns.heading": "Choose columns",
  "reports.builder.step_columns.description":
    "Pick the columns to show, then drag to set their order.",
  "reports.builder.step_columns.search_placeholder": "Search fields…",
  "reports.builder.step_columns.select_all": "Select all",
  "reports.builder.step_columns.section.fields": "Fields",
  "reports.builder.step_columns.section.custom": "Custom fields",
  "reports.builder.step_columns.section_selected_count": "{count} selected",
  "reports.builder.step_columns.no_fields": "No fields match your search.",
  "reports.builder.step_columns.selected_heading": "Selected columns",
  "reports.builder.step_columns.clear_all": "Clear",
  "reports.builder.step_columns.remove_aria": "Remove {column}",
  "reports.builder.step_columns.reorder_aria": "Reorder {column}",
  "reports.builder.step_columns.scope.custom": "Custom",
  "reports.builder.step_columns.empty_title": "No columns yet",
  "reports.builder.step_columns.empty_description": "Check fields on the left to add them.",
  "reports.builder.catalog_error.permission_title":
    "You don't have access to report on this workspace",
  "reports.builder.catalog_error.permission_body":
    "Reporting needs read access to this workspace. Ask a workspace admin if you think this is wrong.",
  "reports.builder.catalog_error.transient_body": "Couldn't load the field list.",
  "reports.builder.catalog_error.retry": "Retry",
  "reports.builder.step_filters.ref_error": "Couldn't load options.",
  "reports.builder.step_filters.heading": "Filters",
  "reports.builder.step_filters.description":
    "Add filters to narrow which rows the report returns. Every filter must match.",
  "reports.builder.step_filters.no_columns":
    "Choose columns first — filters apply to your selected columns.",
  "reports.builder.step_filters.no_filterable_columns":
    "None of your selected columns can be filtered — calculated totals are display-only. Add another column to filter on.",
  "reports.builder.step_filters.add": "Add filter",
  "reports.builder.step_filters.field_placeholder": "Field",
  "reports.builder.step_filters.operator_placeholder": "Condition",
  "reports.builder.step_filters.value_placeholder": "Value",
  "reports.builder.step_filters.value_select_placeholder": "Select a value",
  "reports.builder.step_filters.value_multi_placeholder": "Select values",
  "reports.builder.step_filters.value_search_placeholder": "Search values…",
  "reports.builder.step_filters.value_empty": "No matching values.",
  "reports.builder.step_filters.value_placeholder_start": "Start",
  "reports.builder.step_filters.value_placeholder_end": "End",
  "reports.builder.step_filters.remove_aria": "Remove filter",
  "reports.builder.step_filters.empty": "Add a filter to narrow which rows the report returns.",
  "reports.builder.step_filters.required.heading": "Date range (required)",
  "reports.builder.step_filters.required.hint":
    "Every report is scoped to a date range. Choose a preset or a custom range.",
  "reports.builder.step_filters.required.range_aria": "Date range",
  "reports.builder.step_filters.range_incomplete":
    "Enter both a start and an end date for the custom range to continue.",
  "reports.builder.step_filters.optional.heading": "Additional filters",
  "reports.builder.step_filters.range.last_7": "Last 7 days",
  "reports.builder.step_filters.range.last_30": "Last 30 days",
  "reports.builder.step_filters.range.last_90": "Last 90 days",
  "reports.builder.step_filters.range.custom": "Custom",
  "reports.filter_range.last_n_days": "Last {days} days",
  "reports.filter_operator.equals": "is",
  "reports.filter_operator.not_equals": "is not",
  "reports.filter_operator.contains": "contains",
  "reports.filter_operator.not_contains": "does not contain",
  "reports.filter_operator.in": "is any of",
  "reports.filter_operator.not_in": "is none of",
  "reports.filter_operator.on": "on",
  "reports.filter_operator.before": "before",
  "reports.filter_operator.after": "after",
  "reports.filter_operator.between": "between",
  "reports.filter_operator.greater_than": "greater than",
  "reports.filter_operator.less_than": "less than",
  "reports.filter_operator.is_empty": "is empty",
  "reports.filter_operator.is_not_empty": "is not empty",
  "reports.builder.step_name.heading": "Name your report",
  "reports.builder.step_name.description":
    "Reports are private to you. The data shown reflects your workspace access level.",
  "reports.builder.step_name.name_label": "Report name",
  "reports.builder.step_name.name_placeholder": "e.g. Projects — Q2 export",
  "reports.builder.summary.report_type": "Report type",
  "reports.builder.summary.columns": "Columns",
  "reports.builder.summary.filters": "Filters",
  "reports.builder.summary.count": "{count}",
  "reports.builder.summary.privacy_note": "Only you can see this report.",
  "reports.privacy.records_note": "You'll only see records you already have access to.",
  "reports.builder.create.success": "Report created",
  "reports.builder.update.success": "Report saved",
  "reports.builder.save.error": "Couldn't save the report. Please try again.",
  "reports.builder.cancel_confirm.title": "Discard this report?",
  "reports.builder.cancel_confirm.message": "Your changes haven't been saved and will be lost.",
  "reports.builder.cancel_confirm.confirm": "Discard",
  "reports.builder.cancel_confirm.title_edit": "Discard changes?",
  "reports.builder.cancel_confirm.message_edit":
    "Your unsaved changes to this report will be lost.",
  "reports.builder.cancel_confirm.confirm_edit": "Discard changes",
  "reports.builder.cancel_confirm.keep_editing": "Keep editing",
  "reports.view.reporting_on": "Reporting on: {objects}",
  "reports.view.edit": "Edit",
  "reports.view.rerun": "Re-run",
  "reports.view.export": "Export CSV",
  "reports.view.filters_label": "Filters",
  "reports.view.filters_aria": "Active report filters",
  "reports.view.filters_none": "None",
  "reports.view.edit_filters": "Edit filters",
  "reports.view.stale_warning": "One or more fields are no longer available — edit the report.",
  "reports.view.stale_edit": "Edit report",
  "reports.view.legacy_banner":
    "This is a legacy multi-type report and is frozen. You can view, export and delete it, but not edit, re-run or duplicate it.",
  "reports.view.generating": "Generating report…",
  "reports.view.never_run_title": "This report hasn't been run yet",
  "reports.view.never_run_description": "Run it to generate a snapshot of the current data.",
  "reports.view.run_prompt": "Run this report",
  "reports.view.failed_title": "This report run failed",
  "reports.view.failed_description":
    "Something went wrong while generating the snapshot. Re-run to try again.",
  "reports.view.failed_retry": "Re-run report",
  "reports.view.last_run": "Last run {date} · {count} rows",
  "reports.view.not_found_title": "Report not found",
  "reports.view.not_found_description":
    "This report doesn't exist, or you don't have access to it. Reports are private to the person who created them.",
  "reports.view.not_found_back": "Back to Reports",
  "reports.export.async_queued": "Export is being prepared — we'll notify you when it's ready.",
  "reports.export.async_error": "Couldn't start the export. Please try again.",
  "dashboards.help.target_vs_actual_label": "How target vs actual is calculated",
  "dashboards.help.target_vs_actual":
    "Actual utilization is planned work ÷ available capacity for the period. Available capacity is each person's working hours scaled by their capacity %; the target is that available capacity scaled by their target utilization % (the workspace default unless overridden per person).",
  "dashboards.help.fan_out_dimension_label": "Why these bars can exceed the total",
  "dashboards.help.fan_out_dimension":
    "This axis ({dimension}) can hold more than one value per record. A record is counted in full under each of its values, so the bars can add up to more than the overall total.",
  "dashboards.capacity_scope.over_limit": "Over limit",
  "dashboards.capacity_scope.all": "All",
  "dashboards.filter.range_label": "Date",
  "dashboards.filter.back_to_filters": "Back to all filters",
  "dashboards.filter.custom_field_fallback": "Custom field",
  "dashboards.filter.add_filter": "Add filter",
  "dashboards.filter.range_all": "All time",
  "dashboards.filter.range_custom": "Custom range",
  "dashboards.filter.range_from": "From",
  "dashboards.filter.range_to": "To",
  "dashboards.filter.range_last_quarter": "Last quarter",
  "dashboards.filter.range_this_quarter": "This quarter",
  "dashboards.filter.range_7d": "Last 7 days",
  "dashboards.filter.range_30d": "Last 30 days",
  "dashboards.filter.range_quarter": "Quarter",
  "dashboards.filter.range_ytd": "Year to date",
  "dashboards.filter.projects": "Projects",
  // Filters by state-group CODE, not by an individual status — "Status" made testers look for "Done". Matches
  // the x-axis property label ("State group", dashboards.property.state_groups).
  "dashboards.filter.status": "State group",
  // The state-group chip's own option labels. Cross-source (the filter narrows
  // request statuses, work-item states or project states depending on the widget).
  "dashboards.filter.state_group.new": "New",
  "dashboards.filter.state_group.backlog": "Backlog",
  "dashboards.filter.state_group.unstarted": "Unstarted",
  "dashboards.filter.state_group.in_review": "In Review",
  "dashboards.filter.state_group.waitlisted": "Waitlisted",
  "dashboards.filter.state_group.started": "Started",
  "dashboards.filter.state_group.on_hold": "On Hold",
  "dashboards.filter.state_group.accepted": "Accepted",
  "dashboards.filter.state_group.delivered": "Delivered",
  "dashboards.filter.state_group.completed": "Completed",
  "dashboards.filter.state_group.declined": "Declined",
  "dashboards.filter.state_group.cancelled": "Cancelled",
  "dashboards.filter.assignee": "Assignee",
  "dashboards.filter.lob": "Line of Business",
  "dashboards.filter.request_type": "Request type",
  "dashboards.scope_chip_n_projects": "{count} projects",
  "dashboards.filter.clear_one": "Clear",
  "dashboards.filter.category": "Category",
  "dashboards.filter.request_category": "Request category",
  "dashboards.filter.clear_all": "Clear all",
  "dashboards.filter.teams": "Team",
  "dashboards.filter.home_teams": "Home team",
  "dashboards.filter.goals": "Goal",
  "dashboards.filter.no_options": "No options yet.",
  "dashboards.filter.cf.contains": "Contains",
  "dashboards.filter.cf.min": "Min",
  "dashboards.filter.cf.apply": "Apply (Enter)",
  "dashboards.filter.cf.enter_number": "Enter a number",
  "dashboards.filter.cf.max": "Max",
  "dashboards.filter.cf.from": "From",
  "dashboards.filter.cf.to": "To",
  "dashboards.filter.cf.any": "Any",
  "dashboards.filter.cf.yes": "Yes",
  "dashboards.filter.cf.no": "No",

  // Capacity trajectory reporting
  "capacity_trajectory.title": "8-week utilization trajectory",
  "capacity_trajectory.dashboard_card":
    "Member-level weekly utilization forecast with projected overload peaks",
  "capacity_trajectory.eyebrow": "Forecast",
  "capacity_trajectory.subtitle":
    "Small multiples · y-axis 0-150% utilization · over-capacity band · projections from current plan and booked work",
  "capacity_trajectory.anchor_label": "Anchor week",
  "capacity_trajectory.anchor_week_suffix": "anchor week",
  "capacity_trajectory.projection_note": "Following weeks are projected",
  "capacity_trajectory.loading": "Loading utilization trajectory...",
  "capacity_trajectory.load_error": "Failed to load utilization trajectory.",
  "capacity_trajectory.empty": "No member utilization returned for this 8-week window.",
  "capacity_trajectory.member_label": "Workspace capacity",
  "capacity_trajectory.chart_label": "{member} utilization trajectory",
  "capacity_trajectory.anchor_value": "anchor {percent}%",
  "capacity_trajectory.peak_value": "peak {week} {percent}%",
  "capacity_trajectory.summary_peak": "Peak {percent}%",
  "capacity_trajectory.summary_overloaded": "{count} over capacity",
  "capacity_trajectory.load_more": "Load more members",
  "capacity_trajectory.loading_more": "Loading more...",

  // Capacity reporting
  "capacity_reporting.title": "Capacity report",
  "capacity_reporting.dashboard_card": "Capacity, utilization, variance, and availability states",
  "capacity_reporting.scope_label": "Scope",
  "capacity_reporting.target_label": "Target",
  "capacity_reporting.group_label": "Group by",
  "capacity_reporting.refresh": "Refresh",
  "capacity_reporting.range_separator": "to",
  "capacity_reporting.loading": "Loading capacity report...",
  "capacity_reporting.load_error": "Failed to load capacity report.",
  "capacity_reporting.options_error": "Failed to load reporting options.",
  "capacity_reporting.spread_loading": "Loading utilization spread...",
  "capacity_reporting.spread_error": "Failed to load utilization spread.",
  "capacity_reporting.no_targets": "No targets",
  "capacity_reporting.empty": "No capacity data for this range.",
  "capacity_reporting.scope.workspace": "Workspace",
  "capacity_reporting.scope.team": "Team",
  "capacity_reporting.scope.project": "Project",
  "capacity_reporting.scope.program": "Program",
  "capacity_reporting.scope.member": "Member",
  "capacity_reporting.metric.capacity": "Capacity",
  "capacity_reporting.metric.planned": "Planned",
  "capacity_reporting.metric.actual": "Actual",
  "capacity_reporting.metric.remaining": "Remaining",
  "capacity_reporting.metric.utilization": "Utilization",
  "capacity_reporting.metric.variance": "Variance",
  "capacity_reporting.metric.planned_tasks": "Planned tasks",
  "capacity_reporting.metric.planned_events": "Planned events",
  "capacity_reporting.metric.actual_tasks": "Actual tasks",
  "capacity_reporting.metric.actual_events": "Actual events",
  "capacity_reporting.metric.other_allocation": "Other allocation",
  "capacity_reporting.metric.actual_utilization": "Actual utilization",
  "capacity_reporting.metric.accuracy": "Accuracy",
  "capacity_reporting.breakdown_title": "Actual vs planned",
  "capacity_reporting.open_capacity": "Open capacity grid",
  "capacity_reporting.state_mix_title": "Availability states",
  "capacity_reporting.state_mix_empty": "No availability states returned.",
  "capacity_reporting.table_label": "Capacity reporting by period",
  "capacity_reporting.column.period": "Period",
  "capacity_reporting.column.capacity": "Capacity",
  "capacity_reporting.column.planned": "Planned",
  "capacity_reporting.column.actual": "Actual",
  "capacity_reporting.column.remaining": "Remaining",
  "capacity_reporting.column.state": "State",
  "capacity_reporting.column.utilization": "Utilization",
  "capacity_reporting.column.accuracy": "Accuracy",
  "sidebar.favorites": "Favorites",
  "sidebar.work_items": "Tasks",
  "sidebar.projects": "Projects",
  "sidebar.intake": "Intake",
  "sidebar.events": "Events",
  "sidebar.availability": "Availability",
  "sidebar.capacity": "Capacity",
  "sidebar.collapse": "Collapse",
  "sidebar.open_navigation": "Open navigation",
  "sidebar.close_navigation": "Close navigation",
  "sidebar.navigation": "Navigation",
  "sidebar.resize": "Resize sidebar",
  "sidebar.no_favorites": "No favorites yet",
  "sidebar.no_projects": "No projects",
  "sidebar.project_nav.issues": "Work Items",
  "sidebar.project_nav.views": "Views",
  "sidebar.project_nav.capacity": "Capacity",
  "sidebar.project_nav.settings": "Settings",
  "sidebar.team_nav.projects": "Projects",
  "sidebar.team_nav.no_projects": "No projects yet",

  // User menu
  "user_menu.my_profile": "My Profile",
  "user_menu.help_center": "Help Center",
  "user_menu.sign_out": "Sign out",

  // User
  "user.welcome": "Welcome, {name}!",
  "user.fetch_info": "Fetch User Info",
  "user.email": "Email",
  "user.role": "Role",
  "user.last_login": "Last login",
  "user.fetch_success": "User info loaded",
  "user.fetch_error": "Failed to load user info",

  // Workspace
  "workspace.workspace": "Workspace",
  "workspace.create": "Create Workspace",
  "workspace.settings": "Workspace Settings",
  "workspace.invitations": "Workspace Invitations",
  "workspace.role.admin": "Admin",
  "workspace.role.member": "Member",
  "workspace.role.guest": "Guest",
  "workspace.member_count": "{count} members",
  "workspace.demo_label": "(Demo)",
  "workspace.demo_create_hint": "This workspace will be created as a demo workspace.",
  "workspace.create_title": "Create a new workspace",
  "workspace.name_label": "Workspace Name",
  "workspace.name_placeholder": "My Workspace",
  "workspace.url_label": "Workspace URL",
  "workspace.currency_label": "Organization Currency",
  "workspace.slug_checking": "Checking availability...",
  "workspace.slug_available": "Available",
  "workspace.slug_taken": "This URL is already taken",
  "workspace.create_success": "Workspace created successfully",
  "workspace.create_error": "Failed to create workspace",
  "workspace.creation_disabled": "Workspace creation is disabled. Contact your administrator.",
  "workspace_settings.tabs.general": "General",
  "workspace_settings.tabs.teams": "Teams",
  "workspace_settings.tabs.members": "Members",
  "workspace_settings.tabs.roles": "Roles",
  "workspace_settings.tabs.job_roles": "Job Roles",
  "workspace_settings.tabs.exports": "Exports",
  "workspace_settings.tabs.audit_log": "Audit Log",
  "workspace_settings.tabs.webhooks": "Webhooks",
  "workspace_settings.tabs.ai_provider": "AI provider",
  "workspace_settings.tabs.integration_library": "Integration library",
  "integration_library.title": "Integration library",
  "integration_library.description":
    "Connect Cognota to the systems your learning already runs in.",
  "integration_library.threesixty.name": "360Learning",
  "integration_library.threesixty.description":
    "Sync your 360Learning catalog, learners and course results into Cognota.",
  "integration_library.threesixty.credentials_gone_hint":
    "This company was disconnected, so nothing syncs from it. Its links and everything already synced are kept, and reconnecting resumes them.",
  // NG-1366's catalog. The two Planned connectors carry a name and a category
  // and no description: the mockup draws their tiles that way, and writing copy
  // for a connector nobody has scoped yet would be inventing a promise.
  "integration_library.categories.all": "All",
  "integration_library.categories.learning_platform": "Learning platforms",
  "integration_library.categories.communication": "Communication",
  "integration_library.availability.available": "Available",
  "integration_library.availability.installed": "Installed",
  "integration_library.availability.planned": "Planned",
  "integration_library.availability.planned_hint": "These integrations are not available yet.",
  "integration_library.slack.name": "Slack",
  "integration_library.cornerstone.name": "Cornerstone OnDemand",
  "integration_library.cornerstone.short_name": "Cornerstone",
  // NG-1489: the connector's own page. The direction badge says what the
  // dispatch table says — this connector reads and does not write — so nothing
  // here promises a write-back that no code performs.
  "integration_library.connector.direction_in": "Into Cognota",
  "integration_library.connector.built_by": "Built by Cognota",
  "integration_library.connector.add_connection": "Add connection",
  "integration_library.connector.connections_heading":
    "{count, plural, one {Connection · #} other {Connections · #}}",
  "integration_library.connector.connections_hint":
    "Each 360Learning company has its own credentials, webhooks, rate limits and connection state. Running more than one 360Learning company? Add a connection for each.",
  "integration_library.connector.cornerstone.connections_hint":
    "Each Cornerstone portal has its own credentials, rate limits and connection state. Running more than one Cornerstone portal? Add a connection for each.",
  "integration_library.connector.row_actions": "More actions for {name}",
  "integration_library.connector.last_sync_label": "Last sync",
  "integration_library.connector.last_test_label": "Last test",
  "integration_library.connector.last_test_never": "Not tested yet",
  "integration_library.connector.direction_out": "Out to Teams",
  "integration_library.connector.msteams.syncs_in":
    "Your own Microsoft account email, read once when you add the app, to match you to your Cognota user. No message content, no files, no directory or roster sync.",
  "integration_library.connector.msteams.syncs_out":
    "Notifications you already get by email and in-app, delivered as Teams messages.",
  "integration_library.connector.msteams.before_lead":
    "Connecting signs you in with Microsoft so Cognota knows which Microsoft 365 organization to connect. It asks for your name, email and sign-in only.",
  "integration_library.connector.msteams.before_step_1":
    "Connect, and sign in with a Microsoft account in the organization you want to connect.",
  "integration_library.connector.msteams.before_step_2":
    "Send your Teams administrator the app package and the steps shown here, so they can allow the app for your people.",
  "integration_library.connector.syncs_heading": "What this syncs",
  "integration_library.connector.syncs_in_label": "What comes in",
  "integration_library.connector.syncs_out_label": "Nothing goes back",
  "integration_library.connector.syncs_out_label_sends": "What goes out",
  "integration_library.connector.before_heading": "Before you connect",
  "integration_library.connector.permissions_label": "Permissions needed",
  "integration_library.connector.secret_label": "Secret storage",
  "integration_library.connector.secret_value": "Stored encrypted, never shown again",
  "integration_library.connector.who_label": "Who can connect",
  "integration_library.connector.who_value": "Members who can edit workspace settings",
  "integration_library.connector.unconfirmed_scopes_title":
    "{name}: Cognota could not confirm every permission",
  "integration_library.connector.unconfirmed_scopes_body":
    "These checks did not come back: {scopes}. That is often a temporary {vendor} outage rather than a missing permission. Test the connection again; if it persists, ask a {vendor} platform admin to confirm them.",
  "integration_library.connector.missing_scopes_title":
    "{name} cannot read everything Cognota syncs",
  "integration_library.connector.missing_scopes_body":
    "Ask a {vendor} platform admin to add {scopes}, then test the connection again. The catalog and learners still sync.",
  "integration_library.connector.threesixty.syncs_in":
    "Courses and learning paths, learners, and course results.",
  "integration_library.connector.threesixty.syncs_out":
    "Cognota does not create or change anything in 360Learning.",
  "integration_library.connector.threesixty.before_lead":
    "You need an API v2 credential for each 360Learning company you connect.",
  "integration_library.connector.threesixty.before_step_1":
    "In 360Learning, open Settings > API v2 and create a credential.",
  "integration_library.connector.threesixty.before_step_2":
    "Copy the client secret. 360Learning shows it once when the credential is created and never again.",
  "integration_library.connector.threesixty.before_step_3":
    "Paste the platform address, client ID and secret into Connect.",
  // NG-2242: Cornerstone's page. What comes in is what its pull reads
  // (employees, the catalog, transcripts); nothing is written back.
  "integration_library.connector.cornerstone.syncs_in":
    "Learning objects from your catalog, learners, and their transcripts.",
  "integration_library.connector.cornerstone.syncs_out":
    "Cognota does not create or change anything in Cornerstone.",
  "integration_library.connector.cornerstone.before_lead":
    "You need an OAuth 2.0 application from Cornerstone's Edge API Management for each Cornerstone portal you connect.",
  "integration_library.connector.cornerstone.before_step_1":
    "In Cornerstone, open Edge API Management and register an OAuth 2.0 application that can read employees, the learning catalog and transcripts.",
  "integration_library.connector.cornerstone.before_step_2":
    "Bind it to a dedicated service account with read access, not a person's login. That account's own permissions limit what Cognota can read.",
  "integration_library.connector.cornerstone.before_step_3":
    "Paste your portal address, client ID and secret into Connect.",
  // NG-1490: the two tabs. Installed carries the count because that is the
  // number that changes; Explore carries the search and the chips because it is
  // the only one with a list to browse.
  "integration_library.tabs_label": "Integration library views",
  "integration_library.installed.tab": "Installed",
  "integration_library.installed.companies": "{count, plural, one {# company} other {# companies}}",
  "integration_library.installed.last_sync": "last sync {when}",
  "integration_library.installed.enabled_at": "Enabled {when}",
  "integration_library.installed.no_install_seen": "no install seen yet",
  "integration_library.installed.empty_title": "No integrations yet",
  "integration_library.installed.empty_description":
    "Start with your first one. Explore has everything Cognota connects to, from your LMS to your calendar.",
  "integration_library.installed.empty_action": "Explore integrations",
  "integration_library.explore.tab": "Explore",
  "integration_library.explore.search_placeholder": "Search integrations",
  "integration_library.explore.search_label": "Search integrations",
  "integration_library.explore.categories_label": "Filter by category",
  "integration_library.explore.available_heading": "Available · {count}",
  "integration_library.explore.planned_heading": "Planned · {count}",
  "integration_library.explore.manage": "Manage",
  "integration_library.explore.no_results": 'No integration matches "{query}".',
  "integration_library.state.active": "Active",
  "integration_library.state.paused": "Paused",
  "integration_library.state.not_connected": "Not connected",
  "integration_library.connect.action": "Connect",
  "integration_library.connect.update_credentials": "Update credentials",
  "integration_library.connect.reconnect_action": "Reconnect",
  "integration_library.connect.title": "Connect {vendor}",
  "integration_library.connect.description":
    "Cognota checks the credential against your platform before saving it, so this takes a few seconds.",
  "integration_library.connect.name_label": "Connection name",
  "integration_library.connect.name_placeholder": "{vendor} - EMEA",
  "integration_library.connect.name_hint":
    "What this connection is called wherever someone has to choose between them, such as linking a course to a project.",
  "integration_library.connect.platform_url_label": "Platform address",
  "integration_library.connect.client_id_label": "Client ID",
  "integration_library.connect.client_secret_label": "Client secret",
  "integration_library.connect.client_secret_hint":
    "360Learning shows this once when the credential is created. It is stored encrypted and never shown again.",
  "integration_library.connect.cornerstone.platform_url_hint":
    "Your Cornerstone portal's address, such as acme.csod.com. Pilot and stage portals are acme-pilot.csod.com and acme-stg.csod.com.",
  "integration_library.connect.cornerstone.client_id_hint":
    "The client ID of the OAuth 2.0 application you registered in Cornerstone's Edge API Management.",
  "integration_library.connect.cornerstone.client_secret_hint":
    "The client secret of the same application. It is stored encrypted and never shown again.",
  "integration_library.connect.author_email_label": "Course author email (optional)",
  "integration_library.connect.author_email_hint":
    "The 360Learning user that courses Cognota creates will be authored as. Not needed to read your catalog.",
  "integration_library.connect.submit": "Connect",
  "integration_library.connect.submitting": "Checking the credential...",
  "integration_library.connect.success": "{vendor} connected.",
  "integration_library.connect.missing_scopes": "Ask a {vendor} platform admin to add: {scopes}",
  "integration_library.connect.check_platform_url": "Check the platform address and try again.",
  "integration_library.test.action": "Test connection",
  "integration_library.test.running": "Testing...",
  "integration_library.test.success": "The credential works and reaches everything Cognota needs.",
  "integration_library.test.incomplete":
    "The credential authenticated but cannot reach everything Cognota needs.",
  "integration_library.test.error": "Could not test this connection.",
  "integration_library.pause.action": "Pause",
  "integration_library.pause.resume_action": "Resume",
  "integration_library.pause.error": "Could not change this connection.",
  "integration_library.disconnect.action": "Disconnect",
  "integration_library.disconnect.confirm_title": "Disconnect {vendor}?",
  "integration_library.disconnect.confirm_description":
    "Syncing stops and the stored credential is deleted. Nothing already synced is removed. Reconnecting means entering a new client secret, because 360Learning shows one only once.",
  "integration_library.disconnect.cornerstone.confirm_description":
    "Syncing stops and the stored credential is deleted. Nothing already synced is removed. Reconnecting means entering the client secret again.",
  "integration_library.disconnect.success": "{vendor} disconnected.",
  "integration_library.disconnect.error": "Could not disconnect. Nothing was changed.",
  "integration_library.facts.never_synced": "Not synced yet",

  // Microsoft Teams notification channel (plan §5a, NG-1096, epic NG-886).
  // PLACEHOLDER COPY: product's OQ2/OQ3/OQ6 are open, so every string below is
  // written to be clear and correct rather than final — expect one revision pass.
  "workspace_settings.msteams.name": "Microsoft Teams",
  "workspace_settings.msteams.description":
    "Send Cognota notifications to your team as Teams messages, alongside email and in-app.",
  "workspace_settings.msteams.not_enabled_hint":
    "Enabling signs you in with Microsoft so Cognota knows which Microsoft 365 organization to connect. It asks for your name, email and sign-in only — it grants Cognota no access to your organization's data.",
  "workspace_settings.msteams.state.not_enabled": "Not enabled",
  "workspace_settings.msteams.state.pending_tenant_approval": "Pending admin approval",
  "workspace_settings.msteams.state.enabled": "Enabled",
  "workspace_settings.msteams.state.disabled": "Disabled",
  "workspace_settings.msteams.enable_action": "Enable",
  "workspace_settings.msteams.enable_pending": "Opening Microsoft sign-in...",
  "workspace_settings.msteams.enable_success": "Microsoft Teams notifications resumed.",
  "workspace_settings.msteams.enable_error":
    "Could not enable Microsoft Teams. Nothing was changed.",
  "workspace_settings.msteams.signin_error":
    "Could not start the Microsoft sign-in. Nothing was changed.",
  "workspace_settings.msteams.signin_not_configured":
    "Microsoft Teams sign-in is not configured for this environment yet. Nothing was changed — please contact support.",
  "workspace_settings.msteams.disable_action": "Disable",
  "workspace_settings.msteams.disable_success":
    "Microsoft Teams notifications stopped. Everyone stays connected, so you can turn this back on at any time.",
  "workspace_settings.msteams.disable_error":
    "Could not disable Microsoft Teams. Nothing was changed.",
  "workspace_settings.msteams.disconnect_action": "Disconnect",
  "workspace_settings.msteams.disconnect_confirm_title": "Disconnect Microsoft Teams?",
  "workspace_settings.msteams.disconnect_confirm_description":
    "Notifications stop and everyone in this workspace is unlinked from their Teams chat. Reconnecting means signing in with Microsoft again, and every member has to add the Cognota app in Teams again. Email and in-app notifications are unaffected.",
  "workspace_settings.msteams.disconnect_success": "Microsoft Teams disconnected.",
  "workspace_settings.msteams.disconnect_error": "Could not disconnect. Nothing was changed.",
  "workspace_settings.msteams.handoff.title": "Your Teams admin needs to allow the app",
  "workspace_settings.msteams.handoff.description":
    "Cognota is connected to your Microsoft 365 organization, but nobody can add the app until a Teams administrator uploads it. Send them the package and these steps. Each person starts getting Teams notifications once they have the app.",
  "workspace_settings.msteams.handoff.step_1":
    "In the Teams admin center, go to Teams apps > Setup policies > Global, and turn on Upload custom apps.",
  "workspace_settings.msteams.handoff.step_2":
    "In Teams, go to Apps > Manage your apps > Upload a custom app, and choose the Cognota app package.",
  // NG-1186: the one step that gets the app to everybody without each person adding it.
  "workspace_settings.msteams.handoff.step_3":
    "Recommended: in the Teams admin center, go to Teams apps > Setup policies > Global and add Cognota under Installed apps. Everyone then gets the app without adding it themselves.",
  "workspace_settings.msteams.handoff.step_4":
    "If you skip step 3, allow the app for the people who should get Cognota notifications, then ask them to add it from Teams > Apps > Built for your org > Cognota.",
  // Shown only once a consent has been pending for 14+ days (product OQ3).
  "workspace_settings.msteams.handoff.nudge":
    "Still waiting? Your Teams admin hasn't approved the app yet. Resend them the steps.",
  "workspace_settings.msteams.package_action": "Download app package",
  // NG-1420: the manifest id is the bot's app id and never changes between versions,
  // so Teams rejects a re-upload submitted as a NEW app ("The app's external ID is
  // already being used"). Shown wherever a connection already exists, because that is
  // exactly when someone downloads a package for an app their org already has.
  "workspace_settings.msteams.package_update_hint":
    "Already installed? Your Teams admin updates the existing Cognota app with this package rather than submitting a new one — every version carries the same app id, so a new submission is refused as a duplicate.",
  "workspace_settings.msteams.package_downloading": "Preparing the package...",
  "workspace_settings.msteams.package_error": "Could not download the app package.",
  "workspace_settings.msteams.package_not_configured":
    "The Microsoft Teams app package is not available in this environment yet. Please contact support.",
  "workspace_settings.msteams.linked_members":
    "{count, plural, =0 {No members linked yet} one {# member linked} other {# members linked}}",
  "workspace_settings.msteams.test_card_action": "Send me a test card",
  "workspace_settings.msteams.test_card_pending": "Sending...",
  "workspace_settings.msteams.test_card_success": "Test card delivered.",
  "workspace_settings.msteams.test_card_failed": "Microsoft Teams did not accept the test card.",
  "workspace_settings.msteams.test_card_no_link":
    "Install the Cognota app in Teams first, then try again.",
  "workspace_settings.msteams.test_card_error": "Could not send a test card.",
  "workspace_settings.msteams.facts.consented_by": "Enabled by",
  "workspace_settings.msteams.facts.consented_at": "Enabled on",
  "workspace_settings.msteams.facts.last_test": "Last test",
  "workspace_settings.msteams.facts.unknown": "Unknown",
  "workspace_settings.msteams.redirect_connected":
    "Microsoft Teams connected. Your Teams admin needs to allow the app before anyone can add it.",
  "workspace_settings.msteams.redirect_error_invalid_state":
    "That Microsoft sign-in link is no longer valid. Nothing was changed — please try again.",
  "workspace_settings.msteams.redirect_error_denied":
    "Microsoft sign-in was cancelled. Nothing was changed.",
  "workspace_settings.msteams.redirect_error_no_code":
    "Microsoft did not complete the sign-in. Nothing was changed — please try again.",
  "workspace_settings.msteams.redirect_error_forbidden":
    "You no longer have permission to change this workspace's integrations. Nothing was changed.",
  "workspace_settings.msteams.redirect_error_exchange_failed":
    "Cognota could not finish the sign-in with Microsoft. Nothing was changed — please try again.",
  "workspace_settings.msteams.redirect_error_already_enabled":
    "Microsoft Teams is already enabled for this workspace.",
  "workspace_settings.msteams.redirect_error_not_yet":
    "That Microsoft sign-in is not available yet. Nothing was changed.",
  // Only reachable from the personal link flow (NG-1134), but the callback is shared,
  // so this surface has to be able to say it rather than fall through to "unknown".
  "workspace_settings.msteams.redirect_error_no_enabled_workspace":
    "That Microsoft account is not connected to any of your Cognota workspaces. Nothing was changed.",
  "workspace_settings.msteams.redirect_error_unknown":
    "Connecting Microsoft Teams failed. Nothing was changed.",
  "workspace_settings.tabs.sso": "SSO",
  "workspace_settings.tabs.password_policy": "Password policy",
  "workspace_settings.tabs.session_timeout": "Session timeout",
  "workspace_settings.tabs.oauth_apps": "Connected apps",
  "workspace_settings.tabs.calendars": "Calendars",
  "workspace_settings.tabs.time_off": "Time Off",
  "workspace_settings.tabs.sla": "Intake SLAs",
  "workspace_settings.tabs.request_prioritization": "Priorities",
  "workspace_settings.tabs.skills": "Qualifications",
  "workspace_settings.tabs.forms": "Forms",
  "workspace_settings.tabs.intake_workflows": "Intake Workflows",
  "workspace_settings.tabs.templates": "Project Templates",
  "workspace_settings.tabs.custom_fields": "Global Fields",
  "workspace_settings.tabs.statuses": "Statuses",
  "workspace_settings.statuses.title": "Statuses",
  "workspace_settings.statuses.description":
    "Configure the statuses available for work items, projects, and requests.",
  "workspace_settings.statuses.tab.work_items": "Work items",
  "workspace_settings.statuses.tab.projects": "Projects",
  "workspace_settings.statuses.tab.requests": "Requests",
  "workspace_settings.tabs.processes": "Processes",
  // Settings shortcut menu on list pages (NG-19): surface-specific labels for
  // the cog dropdown beside the create button.
  "settings_shortcut.label": "Workspace settings",
  "settings_shortcut.request_statuses": "Request Statuses",
  "settings_shortcut.intake_workflows": "Intake Workflows",
  "settings_shortcut.intake_priorities": "Intake Priorities",
  "settings_shortcut.intake_slas": "Intake SLAs",
  "settings_shortcut.project_statuses": "Project Statuses",
  "settings_shortcut.project_templates": "Project Templates",
  "settings_shortcut.processes": "Processes",
  "settings_shortcut.work_item_statuses": "Work Item Statuses",
  "settings_shortcut.job_roles": "Job Roles",
  "settings_shortcut.skills": "Qualifications",
  "workspace_settings.groups.general": "General",
  "workspace_settings.groups.people": "People & access",
  "workspace_settings.groups.foundations": "Foundations",
  "workspace_settings.groups.operations": "Operations",
  "workspace_settings.groups.intelligence": "Intelligence",
  "workspace_settings.groups.integrations": "Integrations",
  "workspace_settings.request_prioritization.title": "Priorities",
  "workspace_settings.request_prioritization.description":
    "Automatically rank incoming requests by priority using your own criteria",
  "workspace_settings.request_prioritization.coming_soon": "Configuration is coming soon.",
  "workspace_settings.request_prioritization.publish_success":
    "Changes published — request priorities are being recalculated.",
  "workspace_settings.request_prioritization.publish_error":
    "Couldn't publish changes. Please try again.",
  "workspace_settings.request_prioritization.draft_save_error":
    "Couldn't save your changes. Please try again.",
  "workspace_settings.request_prioritization.scoring_enabled": "Scoring enabled",
  "workspace_settings.request_prioritization.disable_confirm_title": "Turn off priority scoring?",
  "workspace_settings.request_prioritization.disable_confirm_description":
    "Scoring turns off immediately. Each request keeps its current priority level (unscored requests default to Medium), and you can set priorities manually. Turning scoring back on will recompute them.",
  "workspace_settings.request_prioritization.disable_confirm": "Turn off scoring",
  "workspace_settings.request_prioritization.disable_cancel": "Keep scoring on",
  "workspace_settings.request_prioritization.scoring_toggle_error":
    "Couldn't change scoring. Please try again.",
  "workspace_settings.request_prioritization.publish": "Publish changes",
  "workspace_settings.request_prioritization.discard": "Discard changes",
  "workspace_settings.request_prioritization.discard_confirm_title": "Discard changes?",
  "workspace_settings.request_prioritization.discard_confirm_description":
    "Your unpublished edits will be reset to the last published configuration. This can't be undone.",
  "workspace_settings.request_prioritization.discard_confirm": "Discard",
  "workspace_settings.request_prioritization.discard_cancel": "Keep editing",
  "workspace_settings.request_prioritization.discard_error":
    "Couldn't discard changes. Please try again.",
  "workspace_settings.request_prioritization.load_error":
    "Failed to load the prioritization configuration.",
  "workspace_settings.request_prioritization.retry": "Retry",
  "workspace_settings.request_prioritization.tiers_heading": "Priority tiers",
  "workspace_settings.request_prioritization.tier.urgent": "Urgent",
  "workspace_settings.request_prioritization.tier.high": "High",
  "workspace_settings.request_prioritization.tier.medium": "Medium",
  "workspace_settings.request_prioritization.tier.low": "Low",
  "workspace_settings.request_prioritization.archived_suffix": "{label} (archived)",
  "workspace_settings.request_prioritization.default_disabled_tier_label":
    "Default priority when scoring is off",
  "workspace_settings.request_prioritization.default_disabled_tier_description":
    "New and unscored requests are set to this priority while scoring is disabled. Requests with a manual priority keep it.",
  "workspace_settings.request_prioritization.tier_points": "pts",
  "workspace_settings.request_prioritization.tier_at_least": "at least",
  "workspace_settings.request_prioritization.tier_name_aria": "{tier} tier name",
  "workspace_settings.request_prioritization.tier_name_edit": "Rename {tier} tier",
  "workspace_settings.request_prioritization.tiers_not_monotonic":
    "Thresholds must decrease: Urgent > High > Medium > Low.",
  "workspace_settings.request_prioritization.criteria_heading": "Scoring criteria",
  "workspace_settings.request_prioritization.criteria_count": "{count} configured",
  "workspace_settings.request_prioritization.criteria_empty":
    "No scoring criteria yet. Add one to start scoring requests.",
  "workspace_settings.request_prioritization.add_criteria": "Add criteria",
  "workspace_settings.request_prioritization.criterion.requester": "Requester",
  "workspace_settings.request_prioritization.criterion.request_category": "Request Category",
  "workspace_settings.request_prioritization.criterion.linked_program": "Programs",
  "workspace_settings.request_prioritization.criterion.linked_goal": "Goals",
  "workspace_settings.request_prioritization.criterion.custom_field": "Custom field",
  "workspace_settings.request_prioritization.remove_criterion": "Remove {name}",
  "workspace_settings.request_prioritization.remove_criterion_confirm_title": "Remove criterion?",
  "workspace_settings.request_prioritization.remove_criterion_confirm_description":
    "Removing {name} clears it from the criteria list. Its configured point weights are permanently lost once you publish; live requests are unaffected until then.",
  "workspace_settings.request_prioritization.remove_criterion_confirm": "Remove",
  "workspace_settings.request_prioritization.remove_criterion_cancel": "Keep criterion",
  "workspace_settings.request_prioritization.values_scored": "{count} values scored",
  "workspace_settings.request_prioritization.expand": "Expand",
  "workspace_settings.request_prioritization.collapse": "Collapse",
  "workspace_settings.request_prioritization.not_important": "Not important",
  "workspace_settings.request_prioritization.very_important": "Very important",
  "workspace_settings.request_prioritization.requester_search_placeholder": "Search members…",
  "workspace_settings.request_prioritization.requester_no_members": "No members found.",
  "workspace_settings.request_prioritization.requester_add": "Add requester",
  "workspace_settings.request_prioritization.requester_empty":
    "No requesters scored yet. Add a member to assign points.",
  "workspace_settings.request_prioritization.remove": "Remove",
  "workspace_settings.not_authorized.title": "Access denied",
  "workspace_settings.not_authorized.description":
    "You do not have permission to view this workspace settings section.",

  "workspace_settings.general.title": "General",
  "workspace_settings.general.description":
    "Update the workspace identity, timezone, and basic organization metadata.",
  "workspace_settings.general.name_label": "Workspace name",
  "workspace_settings.general.size_label": "Organization size",
  "workspace_settings.general.timezone_label": "Timezone",
  "workspace_settings.general.currency_label": "Organization Currency",
  "workspace_settings.general.currency_help":
    "Set a currency for Hourly Rate and Project Cost, so data can be aggregated workspace wide.",
  "workspace_settings.general.currency_confirm_title": "Change the organization currency?",
  "workspace_settings.general.currency_confirm_description":
    "Changing the currency does not change historical rates. Historical rates will need to be updated for all users and job roles so that it matches the new currency.",
  "workspace_settings.general.currency_confirm_action": "Change currency",
  "workspace_settings.general.region_help":
    "New members inherit this Country & Region so they aren't scheduled during their local holidays. Each member can change it on their own profile.",
  "country_region.country_label": "Country",
  "country_region.country_placeholder": "Select a country",
  "country_region.region_label": "Region",
  "country_region.region_placeholder": "Select a region",
  "country_region.region_none": "No region",
  "country_region.country_search_placeholder": "Search countries",
  "country_region.country_empty": "No countries match",
  "country_region.region_search_placeholder": "Search regions",
  "country_region.region_empty": "No regions match",
  "country_region.help":
    "Setting your Country & Region ensures you won't be scheduled for work during a holiday.",
  "workspace_settings.general.url_label": "Workspace URL",
  "workspace_settings.general.url_copy": "Copy",
  "workspace_settings.general.url_copied": "Workspace URL copied",
  "workspace_settings.general.url_copy_failed": "Failed to copy workspace URL",
  "workspace_settings.general.save": "Save changes",
  "workspace_settings.general.saving": "Saving...",
  "workspace_settings.general.save_success": "Workspace settings updated",
  "workspace_settings.general.save_error": "Failed to update workspace settings",
  "workspace_settings.general.logo_alt": "{name} logo",
  "workspace_settings.general.logo_label": "Workspace logo",
  "workspace_settings.general.logo_hint": "Upload a PNG, JPG, WEBP, or GIF image (max 5 MB).",
  "workspace_settings.general.logo_upload": "Upload logo",
  "workspace_settings.general.logo_change": "Change logo",
  "workspace_settings.general.logo_dialog_title": "Upload workspace logo",
  "workspace_settings.general.logo_dialog_description":
    "Pick a new image to represent this workspace.",
  "workspace_settings.general.logo_browse": "Choose image",
  "workspace_settings.general.logo_formats":
    "File formats supported: .jpeg, .jpg, .png, .webp, .gif",
  "workspace_settings.general.logo_save": "Upload and save",
  "workspace_settings.general.logo_uploading": "Uploading...",
  "workspace_settings.general.logo_remove": "Remove",
  "workspace_settings.general.logo_removing": "Removing...",
  "workspace_settings.general.logo_uploaded": "Workspace logo updated",
  "workspace_settings.general.logo_upload_failed": "Failed to upload workspace logo",
  "workspace_settings.general.logo_removed": "Workspace logo removed",
  "workspace_settings.general.logo_remove_failed": "Failed to remove workspace logo",
  "workspace_settings.general.logo_remove_unsupported":
    "This logo cannot be removed from the workspace settings page.",
  "workspace_settings.general.logo_invalid_type":
    "Unsupported image type. Use JPG, PNG, WEBP, or GIF.",
  "workspace_settings.general.logo_too_large": "Image must be under 5 MB.",
  "workspace_settings.general.reset": "Reset workspace",
  "workspace_settings.general.reset_section_description":
    "Wipe this demo workspace and rebuild it from its canonical seeded state. All projects, requests, work items, teams, and other demo data are permanently deleted and re-created.",
  "workspace_settings.general.reset_title": "Reset workspace?",
  "workspace_settings.general.reset_description":
    "This permanently wipes all demo data in this workspace and restores the canonical seeded state. This cannot be undone.",
  "workspace_settings.general.reset_slug_prompt": "Type {slug} to confirm",
  "workspace_settings.general.resetting": "Resetting...",
  "workspace_settings.general.reset_in_progress":
    "Resetting workspace… this can take a few minutes. Keep this open.",
  "workspace_settings.general.reset_success": "Workspace reset — restored {count} records",
  "workspace_settings.general.reset_error": "Failed to reset workspace",
  "workspace_settings.general.reset_timeout":
    "Workspace reset is taking longer than expected. It may still finish in the background — refresh shortly.",
  "workspace_settings.general.reset_template_label": "Reset to",
  "workspace_settings.general.reset_template_default": "Default (canonical seeded state)",
  "workspace_settings.general.reset_template_help":
    "Choose which template to rebuild this workspace from.",
  "workspace_settings.general.reset_version_label": "Template version",
  "workspace_settings.general.reset_version_active": "Active version (latest)",
  "workspace_settings.general.reset_version_option": "Version {version}",
  "workspace_settings.general.reset_version_help":
    "Optionally rebuild from a past version of this template instead of its active one.",

  "workspace_settings.general.danger_zone": "Danger zone",
  "workspace_settings.general.delete": "Delete workspace",
  "workspace_settings.general.delete_section_description":
    "Permanently delete this demo workspace and everything in it. Real customer workspaces cannot be deleted here.",
  "workspace_settings.general.delete_title": "Delete workspace?",
  "workspace_settings.general.delete_description":
    "This deletes the demo workspace and all of its projects, requests, work items, and teams. This cannot be undone.",
  "workspace_settings.general.delete_slug_prompt": "Type {slug} to confirm",
  "workspace_settings.general.delete_confirm_action": "Delete workspace forever",
  "workspace_settings.general.deleting": "Deleting...",
  "workspace_settings.general.delete_success": "Workspace deleted",
  "workspace_settings.general.delete_error": "Failed to delete workspace",

  "workspace_settings.templates.section_title": "Workspace templates",
  "workspace_settings.templates.section_description":
    "Saved snapshots of a workspace you can reset to. Capture the current workspace as a reusable template, or remove ones you no longer need.",
  "workspace_settings.templates.empty": "No templates yet.",
  "workspace_settings.templates.load_error": "Failed to load templates.",
  "workspace_settings.templates.default_badge": "Default",
  "workspace_settings.templates.applied_badge": "Applied",
  "workspace_settings.templates.scope_global": "Global",
  "workspace_settings.templates.scope_restricted": "Restricted",
  "workspace_settings.templates.sharing_global": "Shared with GTM",
  "workspace_settings.templates.sharing_private": "Private",
  "workspace_settings.templates.created_by": "Created by {name}",
  "workspace_settings.templates.created_by_system": "System template",
  "workspace_settings.templates.create_action": "Create workspace template",
  "workspace_settings.templates.change_template": "Change template",
  "workspace_settings.templates.refresh_dates": "Refresh dates",
  "workspace_settings.templates.refresh_dates_running": "Refreshing dates…",
  "workspace_settings.templates.refresh_dates_tooltip":
    "Move every date in this workspace onto today, a shift of {days} days. Nothing is deleted.",
  "workspace_settings.templates.refresh_dates_already_current":
    "These dates already read as of today, so there is nothing to move.",
  "workspace_settings.templates.refresh_dates_confirm":
    "These dates were last set {when} ({date}). Move every date onto today, a shift of {days} days, so the workspace reads as of today? Projects, tasks, events and everything you have added keep their content and their spacing, and nothing is deleted.",
  "workspace_settings.templates.refresh_dates_success": "Moved {count} records by {days} days.",
  "workspace_settings.templates.refresh_dates_error":
    "Could not refresh the dates. Please try again.",
  "workspace_settings.templates.refresh_dates_timeout":
    "Refreshing the dates is taking longer than expected. It may still finish — reload the page in a minute to check.",
  "workspace_settings.templates.column_name": "Name",
  "workspace_settings.templates.column_tags": "Tags",
  "workspace_settings.templates.column_captured_by": "Captured by",
  "workspace_settings.templates.column_last_applied": "Last applied",
  "workspace_settings.templates.last_applied_never": "Never",
  "workspace_settings.templates.filter_scope": "Scope",
  "workspace_settings.templates.filter_status": "Status",
  "workspace_settings.templates.filter_search_placeholder": "Filter options…",
  "workspace_settings.templates.filter_no_matches": "No matching options.",
  "workspace_settings.templates.delete_action": "Delete template",
  "workspace_settings.templates.delete_confirm":
    "Delete the template “{name}”? This cannot be undone.",
  "workspace_settings.templates.delete_success": "Template deleted",
  "workspace_settings.templates.delete_error": "Failed to delete template",
  "workspace_settings.templates.set_default_action": "Mark as default for new workspaces",
  "workspace_settings.templates.set_default_confirm":
    "Make “{name}” the default? Every new workspace will be seeded from it. You can change it back at any time.",
  "workspace_settings.templates.set_default_success": "Default template updated",
  "workspace_settings.templates.set_default_error": "Failed to set default template",
  "workspace_settings.templates.create_title": "Save workspace as template",
  "workspace_settings.templates.create_description":
    "Capture the current state of this workspace as a named template you can reset to later.",
  "workspace_settings.templates.name_label": "Template name",
  "workspace_settings.templates.name_placeholder": "e.g. Sales demo",
  "workspace_settings.templates.scope_label": "Visibility",
  "workspace_settings.templates.scope_global_help": "Usable by anyone who can use templates.",
  "workspace_settings.templates.scope_restricted_help": "Limited to this workspace's role.",
  "workspace_settings.templates.sharing_global_help":
    "Everyone on the Cognota GTM team who uses workspace templates can see it and reset a workspace to it.",
  "workspace_settings.templates.sharing_private_help":
    "Only you can see it and reset a workspace to it, plus Cognota instance admins. A private template can't be the default for new workspaces.",
  "workspace_settings.templates.create_submit": "Save template",
  "workspace_settings.templates.create_success": "Template “{name}” saved",
  "workspace_settings.templates.create_error": "Failed to save template",

  "workspace_settings.members.title": "Members",
  "workspace_settings.members.description":
    "Invite workspace members, review pending invitations, and manage member access.",
  "workspace_settings.members.invite_action": "Invite members",
  "workspace_settings.members.import.field_email": "Email",
  "workspace_settings.members.import.field_first_name": "First name",
  "workspace_settings.members.import.field_last_name": "Last name",
  "workspace_settings.members.import.field_country": "Country",
  "workspace_settings.members.import.field_region": "Region",
  "workspace_settings.members.import.field_team": "Team",
  "workspace_settings.members.import.field_system_role": "System role",
  // The user-import modal (NG-1307). The four `notice_*` lines describe what the
  // import does TODAY, not the PRD end state, and each is rewritten by the story
  // that changes the behaviour it describes — NG-1297 (update an existing person,
  // and keep the value behind a blank cell) rewrote two of them. NG-1299 owns the
  // last one still describing a gap: a row that creates somebody must name a
  // system role, because no workspace-wide default exists to fall back on.
  "workspace_settings.members.import.action": "Import users",
  "workspace_settings.members.import.title": "Import users",
  "workspace_settings.members.import.notice_title": "What this import will do",
  "workspace_settings.members.import.notice_create":
    "A row whose email is not already in this workspace creates that person, with the system role the row names. They can be assigned work straight away.",
  // NG-1478 moved the notice onto its own first step and the welcome-email control
  // onto the last one, which put three screens between them — so the notice can no
  // longer state an answer that is true when it is read. This says where the choice
  // is made; the two strings below say what each position DOES, and since NG-1478
  // they render beside the checkbox itself rather than here.
  "workspace_settings.members.import.notice_invite_choice":
    "You choose whether to send a welcome email on the last step, before you import.",
  // NG-1304 split the invitation clause off the create rule so it can tell the
  // truth either way: whichever of these two matches the control is the one
  // rendered, rather than one sentence that is wrong half the time.
  "workspace_settings.members.import.notice_invite_off":
    "No email is sent. Imported people sign in later through single sign-on, or by using “Forgot password”.",
  "workspace_settings.members.import.notice_invite_on":
    "Everyone this import adds to this workspace is emailed a link to set a password. People who are already members are not emailed.",
  "workspace_settings.members.import.notice_existing":
    "A row whose email already belongs to someone updates that person rather than creating a second one. A column left blank keeps the value they already have.",
  "workspace_settings.members.import.notice_role_required":
    "A row that leaves the system role blank gets this workspace's default role. A role the row names is used instead, and a role name we don't recognise stops that row. For someone already here, leaving it blank keeps the role they have.",
  "workspace_settings.members.import.notice_column_rules":
    "Country, region, team and job role must match a name that already exists here, ignoring capitalisation. A name we cannot match is reported against its row and left unset — the person is still imported. Capacity must be a number between 0 and 100, and anything else is reported the same way. Hourly rate is imported only by someone who can set rates.",
  "workspace_settings.members.import.withheld_hourly_rate":
    "Not a spelling problem — importing hourly rates needs permission to set them, so this column is left out.",
  "workspace_settings.members.import.error_email_required": "Email is required.",
  "workspace_settings.members.import.error_email_invalid":
    "“{email}” is not a valid email address.",
  "workspace_settings.members.import.send_welcome_email_label": "Send welcome email",
  "workspace_settings.members.import.importing": "Importing…",
  "workspace_settings.members.import.result_summary":
    "{created} created, {updated} updated, {skipped} skipped, {errors} could not be imported.",
  "workspace_settings.members.import.count_created": "{count} created",
  // NG-1390. "Emailed", not "sent" or "delivered": the message is handed to a
  // background worker, so the request that returns this genuinely cannot know
  // whether a mail server accepted it. The banner below says so outright.
  "workspace_settings.members.import.count_emailed": "{count} emailed",
  // Shown only when the mailing stopped part-way. The denominator matters more
  // than the tidier "{count} emailed" would: an admin retrying the file needs to
  // know the first {sent} have already been told.
  "workspace_settings.members.import.count_emailed_partial": "{sent} of {total} emailed",
  // The plainness NG-1390 asks for: the badge says "emailed", and on its own that
  // would imply a delivery this request cannot confirm.
  "workspace_settings.members.import.email_queued_note":
    "Welcome emails are sent in the background, so delivery is not confirmed here.",
  "workspace_settings.members.import.email_failed_title": "Welcome emails could not be sent",
  // Says plainly that re-running does NOT retry the mailing, because it reads as
  // the obvious next move and it does not work: the import only emails people it
  // newly adds, and by the second run everybody in this file is already a member,
  // so a re-run emails nobody at all.
  "workspace_settings.members.import.email_failed_body":
    "The people this import added are in the workspace. Some of them were not emailed, so they have not been told they were added. Running this import again will not email them — it only emails people it newly adds, and they are members now.",
  "workspace_settings.members.import.count_updated": "{count} updated",
  "workspace_settings.members.import.count_skipped": "{count} skipped",
  "workspace_settings.members.import.count_errors":
    "{count, plural, one {# error} other {# errors}}",
  "workspace_settings.members.import.skipped_heading": "Rows that were not changed",
  "workspace_settings.members.import.partial_heading": "Imported, but some values were left unset",
  "workspace_settings.members.import.row_issue": "Row {row} ({email}): {message}",
  // The same line for a row with no address to name (NG-1563). Reachable only
  // since that ticket stopped dropping blank-email rows before the server saw
  // them; without it the line reads "Row 10 (): Email is required."
  "workspace_settings.members.import.row_issue_no_email": "Row {row}: {message}",
  // Says only what the browser can know by looking at the file: this address is
  // on more than one row. Which of those rows the import then uses is the
  // server's call, and the preview reports it per row — so this deliberately
  // stops short of naming a winner rather than restating a rule it is not the
  // authority for (NG-1308).
  "workspace_settings.members.import.warn_duplicate_emails":
    "{count, plural, one {# email address appears on more than one row} other {# email addresses appear on more than one row}}. The preview will show what happens to each.",
  // The dry-run preview (NG-1308): the same per-row report the import produces,
  // read back BEFORE anything is written. Its wording is future tense throughout,
  // because the difference between "could not be imported" and "cannot be
  // imported" is the whole point of the screen — one is a report, the other is a
  // decision the admin still gets to make.
  "workspace_settings.members.import.checking": "Checking your file…",
  // NG-1478: the check now runs on arriving at Review rather than on pressing
  // Import, so its failure has a place on screen instead of only a toast — Import
  // stays blocked until it succeeds, and saying so beats a disabled button with no
  // reason. Both routes out are offered because either may be the fix: a failed
  // request is worth retrying, a mapping that confuses the endpoint is not.
  "workspace_settings.members.import.check_failed_title": "We couldn't check your file",
  "workspace_settings.members.import.check_failed_body":
    "Nothing has been imported. Try again, or go back and change how the columns are matched.",
  "workspace_settings.members.import.check_retry": "Try again",
  "workspace_settings.members.import.preview_title": "Before you import",
  "workspace_settings.members.import.preview_summary":
    "{created} will be created, {updated} will be updated, {skipped} will be skipped, {errors} cannot be imported.",
  "workspace_settings.members.import.preview_count_created": "{count} to create",
  "workspace_settings.members.import.preview_count_updated": "{count} to update",
  "workspace_settings.members.import.preview_count_skipped": "{count} to skip",
  // No plural: unlike "error"/"errors" beside it, this phrase does not inflect in
  // English. The French one does, and keeps its plural.
  "workspace_settings.members.import.preview_count_errors": "{count} cannot be imported",
  "workspace_settings.members.import.preview_errors_heading": "Rows that cannot be imported",
  "workspace_settings.members.import.preview_partial_heading":
    "Will be imported, but some values will be left unset",
  "workspace_settings.members.import.preview_skipped_heading": "Rows that will not be changed",
  // Counted, not listed per row, and deliberately without naming the default
  // role: a real file leaves this cell blank on most rows, so a line each would
  // bury the team and region names that genuinely did not match, and the role's
  // name only reaches the frontend inside a past-tense sentence this screen
  // cannot honestly reuse.
  "workspace_settings.members.import.preview_defaults_line":
    "{count, plural, one {# row leaves System role blank and will take this workspace's default role.} other {# rows leave System role blank and will take this workspace's default role.}}",
  "workspace_settings.members.import.toast_success":
    "{count, plural, one {# user imported} other {# users imported}}",
  "workspace_settings.members.import.toast_error": "Import failed. Please try again.",
  "workspace_settings.members.section_title": "Members",
  "workspace_settings.members.section_description":
    "Change workspace access, assign custom roles, or remove members.",
  "workspace_settings.members.no_members": "No members match the current filters.",
  "workspace_settings.members.no_email": "No email",
  "workspace_settings.members.tier_label": "License Tier",
  "workspace_settings.members.tier.admin": "Administrator",
  "workspace_settings.members.tier.standard": "Standard",
  "workspace_settings.members.tier.guest": "Guest",
  "workspace_settings.members.role_label": "Role",
  "workspace_settings.members.invite_teams_label": "Teams",
  "workspace_settings.members.invite_teams_placeholder": "Select teams (optional)",
  "workspace_settings.members.invite_teams_hint":
    "They join these teams as soon as they accept. Leave it empty to invite them without a team.",
  "workspace_settings.members.job_role_label": "Job role",
  "workspace_settings.members.job_role_none": "None",
  "workspace_settings.members.job_role_updated": "Member job role updated",
  "workspace_settings.members.job_role_update_error": "Failed to update job role",
  "workspace_settings.members.capacity_label": "Capacity",
  "workspace_settings.members.capacity_invalid": "Enter a value between 0 and 100",
  "workspace_settings.members.capacity_updated": "Member capacity updated",
  "workspace_settings.members.capacity_update_error": "Failed to update member capacity",
  "workspace_settings.members.custom_role_label": "Custom role",
  "workspace_settings.members.custom_role_none": "None",
  "workspace_settings.members.remove": "Remove",
  "workspace_settings.members.remove_confirm_title": "Remove member",
  "workspace_settings.members.remove_confirm_description":
    "Remove {name} from this workspace? They will lose access immediately.",
  "workspace_settings.members.remove_success": "Member removed",
  "workspace_settings.members.remove_error": "Failed to remove member",
  "workspace_settings.members.role_updated": "Member role updated",
  "workspace_settings.members.role_update_error": "Failed to update member role",
  "workspace_settings.members.role_blocked_title": "User holds incompatible roles:",
  "workspace_settings.members.role_blocked_dialog.title": "Can't change this role yet",
  "workspace_settings.members.role_blocked_dialog.description":
    "{member} can't become {role} while they still hold these roles:",
  "workspace_settings.members.custom_role_updated": "Custom role updated",
  "workspace_settings.members.custom_role_update_error": "Failed to update custom role",
  "workspace_settings.members.filters_search_placeholder": "Search members by name or email",
  "workspace_settings.members.filters_role_all": "All roles",
  "workspace_settings.members.filters_role_admin": "Admins",
  "workspace_settings.members.filters_role_member": "Members",
  "workspace_settings.members.filters_role_guest": "Guests",
  "workspace_settings.members.filters_role_custom": "Custom roles",
  "workspace_settings.members.invitations_title": "Pending invitations",
  "workspace_settings.members.invitations_description":
    "Review invites that have not been accepted yet.",
  "workspace_settings.members.invitations_empty": "No pending invitations.",
  "workspace_settings.members.invitations_role": "Role {role}",
  "workspace_settings.members.invitation_revoke": "Revoke",
  "workspace_settings.members.invitation_revoking": "Revoking...",
  "workspace_settings.members.invitation_revoke_success": "Invitation revoked",
  "workspace_settings.members.invitation_revoke_error": "Failed to revoke invitation",
  "workspace_settings.members.invite_dialog_title": "Invite members",
  "workspace_settings.members.invite_dialog_description":
    "Send one or more workspace invitations. Separate email addresses with commas or new lines.",
  "workspace_settings.members.invite_emails_label": "Email addresses",
  "workspace_settings.members.invite_emails_placeholder": "alex@example.com, taylor@example.com",
  "workspace_settings.members.invite_send": "Send invites",
  "workspace_settings.members.invite_sending": "Sending...",
  "workspace_settings.members.invite_success": "Invitations sent",
  "workspace_settings.members.invite_error": "Failed to send invitations",
  "workspace_settings.members.invite_emails_required": "Enter at least one email address",
  "workspace_settings.members.invite_existing_member_one":
    "{email} is already a member of this workspace. Nothing was sent.",
  "workspace_settings.members.invite_existing_member_many":
    "{count} of these addresses already belong to members of this workspace. Nothing was sent.",
  "workspace_settings.members.invite_existing_member_role_hint":
    "To change a member's role, use the System Role column on the Members page.",
  "workspace_settings.members.invite_existing_member_remove_one": "Remove it from the list",
  "workspace_settings.members.invite_existing_member_remove_many":
    "Remove all {count} from the list",
  "workspace_settings.members.invitation_already_member": "Already a member",
  "workspace_settings.members.invitation_already_member_hint":
    "Accepting this won't change their current role. You can revoke it.",
  "workspace_settings.members.role.admin": "Admin",
  "workspace_settings.members.role.member": "Standard User",
  "workspace_settings.members.role.guest": "Guest",
  "workspace_settings.members.skills_label": "Qualifications",
  "workspace_settings.members.skills_empty": "No qualifications",
  "workspace_settings.members.skills_search_placeholder": "Search qualifications...",
  "workspace_settings.members.assign_skills_title": "Assign qualifications to {name}",
  "workspace_settings.members.skills_updated": "Member qualifications updated",
  "workspace_settings.members.skills_update_error": "Failed to update member qualifications",
  "workspace_settings.members.skills_description":
    "Update the qualifications assigned to this member.",
  "workspace_settings.members.skills_loading": "Loading qualifications...",
  "workspace_settings.members.teams.tab": "Teams",
  "workspace_settings.members.teams.title": "Teams",
  "workspace_settings.members.teams.description":
    "The teams this member is on, and their role on each.",
  "workspace_settings.members.teams.loading": "Loading teams...",
  "workspace_settings.members.teams.load_error": "Failed to load this member's teams",
  "workspace_settings.members.teams.updated": "Member teams updated",
  "workspace_settings.members.teams.update_error": "Failed to update this member's teams",
  "workspace_settings.members.teams.empty": "This member is not on any team",
  "workspace_settings.members.teams.placeholder": "Add or remove teams",
  "workspace_settings.members.teams.column_team": "Team",
  "workspace_settings.members.teams.column_role": "Role",
  "workspace_settings.members.teams.empty_hint":
    "Teams decide which projects this member can be scheduled on. Use the picker above to add one.",
  // Says which team and what to do about it. The API's own refusal
  // ("At least one lead is required.") is right for an error but useless as
  // prevention — it names neither.
  "workspace_settings.members.teams.sole_lead_reason":
    "{name} needs at least one lead. Make someone else a lead there first.",
  "workspace_settings.members.teams.no_teams": "No teams",
  "workspace_settings.members.teams.trigger_label": "Teams",
  "workspace_settings.members.teams.role_label": "Role on {name}",
  "workspace_settings.members.edit_work_hours": "Edit work hours",
  "workspace_settings.members.work_hours_dialog_title": "Edit Work Hours for {name}",
  "workspace_settings.members.work_hours_loading": "Loading work hours...",
  "workspace_settings.members.work_hours_load_error": "Failed to load work hours",
  "workspace_settings.members.work_hours_save_error": "Failed to save work hours",
  "workspace_settings.members.work_hours_updated": "Work hours updated",
  "workspace_settings.members.profile_tab": "Profile",
  "workspace_settings.members.access.tab": "Access",
  "workspace_settings.members.access.title": "Access",
  "workspace_settings.members.access.description":
    "Control this member's System Role and see their License Tier.",
  "workspace_settings.members.access.system_role_label": "System Role",
  "workspace_settings.members.access.tier_hint": "The License Tier follows the System Role.",
  "workspace_settings.members.access.owner_indicator": "Workspace Owner",
  "workspace_settings.members.access.owner_indicator_hint":
    "The Workspace Owner can access all features, including workspace settings, regardless of their System Role.",
  "workspace_settings.members.rates.tab": "Rate",
  "workspace_settings.members.rates.title": "Hourly rate",
  "workspace_settings.members.rates.description":
    "Set this member's hourly rate. Rates are effective-dated: a change adds a new entry, and cost is calculated from the rate in effect when the time was logged.",
  "workspace_settings.members.rates.add_label": "Add a rate",
  "workspace_settings.members.rates.hourly_rate_label": "Hourly rate",
  "workspace_settings.members.rates.add_button": "Add rate",
  "workspace_settings.members.rates.add_hint":
    "The new rate takes effect today. To set a past rate, add it, then edit the date on its row below.",
  "workspace_settings.members.rates.history_label": "Rate history",
  "workspace_settings.members.rates.empty":
    "No rate set. This member's time is costed at 0 until a rate is added.",
  "workspace_settings.members.rates.per_hour": "{amount} / hr",
  "workspace_settings.members.rates.delete_aria": "Delete rate effective {date}",
  "workspace_settings.members.rates.edit_effective_aria":
    "Change effective date (currently {date})",
  "workspace_settings.members.rates.loading": "Loading rates…",
  "workspace_settings.members.rates.load_error": "Could not load rates.",
  "workspace_settings.members.rates.invalid_rate": "Enter a rate of 0 or more.",
  "workspace_settings.members.rates.saved": "Rate saved",
  "workspace_settings.members.rates.save_error": "Could not save the rate.",
  "workspace_settings.members.rates.update_error": "Could not update the rate.",
  "workspace_settings.members.rates.delete_error": "Could not delete the rate.",
  "workspace_settings.members.profile_title": "Profile",
  "workspace_settings.members.profile_description": "Update member profile information.",
  "workspace_settings.members.profile_save_success": "Member profile updated",
  "workspace_settings.members.profile_save_error": "Failed to update member profile",
  "workspace_settings.members.profile_load_error": "Failed to load member profile",
  "workspace_settings.members.column_member": "Member",
  "workspace_settings.members.column_actions": "Actions",
  "workspace_settings.members.select_all": "Select all members",
  "workspace_settings.members.select_one": "Select {name}",
  "workspace_settings.members.bulk_remove.title": "Remove {count} members?",
  "workspace_settings.members.bulk_remove.description":
    "This removes the {count} selected members from the workspace.",
  "workspace_settings.members.bulk_remove.success": "Removed {count} members",
  "workspace_settings.members.bulk_remove.owner_skipped":
    "The workspace owner cannot be removed and was skipped.",
  "workspace_settings.members.owner_badge": "Owner",
  "workspace_settings.members.owner_badge_tooltip":
    "Workspace Owner: has full access regardless of System Role and cannot be removed.",
  "workspace_settings.members.column_email": "Email",
  "workspace_settings.members.column_teams": "Teams",
  "workspace_settings.members.email_copy": "Copy email",
  "workspace_settings.members.email_copied": "Email copied to clipboard",
  "workspace_settings.members.email_copy_error": "Failed to copy email",
  "workspace_settings.members.actions_menu_aria": "Actions for {name}",
  "workspace_settings.members.tabs_label": "Members and pending invitations",
  "workspace_settings.members.members_tab": "Members",
  "workspace_settings.members.pending_invitations_tab": "Pending invitations",
  "workspace_settings.members.pending_invitations_summary":
    "{count, plural, one {# invitation pending} other {# invitations pending}}",
  "workspace_settings.members.pending_invitations_review": "Review",
  "workspace_settings.members.pending_invitations_search_placeholder":
    "Search invitations by email",
  "workspace_settings.members.pending_invitations_filter_age_label": "Age",
  "workspace_settings.members.pending_invitations_filter_age_expiring_soon": "Expiring soon",
  "workspace_settings.members.pending_invitations_filter_age_expired": "Expired",
  "workspace_settings.members.column_system_role": "System Role",
  "workspace_settings.members.column_invited": "Invited",
  "workspace_settings.members.column_expires": "Expires",
  "workspace_settings.members.expires_never": "Never",
  "workspace_settings.members.expires_expired": "Expired",
  "workspace_settings.members.expires_today": "Today",
  "workspace_settings.members.expires_in_days": "{count, plural, one {in # day} other {in # days}}",
  "workspace_settings.members.invited_days_ago":
    "{count, plural, =0 {Today} one {# day ago} other {# days ago}}",
  "workspace_settings.members.select_all_invitations": "Select all invitations",
  "workspace_settings.members.select_one_invitation": "Select invitation for {email}",
  "workspace_settings.members.invitation_actions_menu_aria": "Actions for invitation to {email}",
  "workspace_settings.members.invitation_resend": "Resend",
  "workspace_settings.members.invitation_resending": "Resending...",
  "workspace_settings.members.invitation_resend_success": "Invitation resent",
  "workspace_settings.members.invitation_resend_error": "Failed to resend invitation",
  "workspace_settings.members.invitation_copy_link": "Copy invitation link",
  "workspace_settings.members.invitation_copy_link_success": "Invitation link copied to clipboard",
  "workspace_settings.members.invitation_copy_link_error": "Failed to copy invitation link",
  "workspace_settings.members.invitation_edit": "Edit role/teams",
  "workspace_settings.members.edit_invitation_dialog_title": "Edit invitation for {email}",
  "workspace_settings.members.edit_invitation_dialog_description":
    "Change the role or teams this invitation grants when accepted.",
  "workspace_settings.members.edit_invitation_save": "Save changes",
  "workspace_settings.members.edit_invitation_saving": "Saving...",
  "workspace_settings.members.edit_invitation_success": "Invitation updated",
  "workspace_settings.members.edit_invitation_error": "Failed to update invitation",
  "workspace_settings.members.bulk_resend.title":
    "{count, plural, one {Resend # invitation?} other {Resend # invitations?}}",
  "workspace_settings.members.bulk_resend.description":
    "Each invitation's email is sent again and its expiry is extended.",
  "workspace_settings.members.bulk_resend.success":
    "{count, plural, one {Resent # invitation} other {Resent # invitations}}",
  "workspace_settings.members.bulk_resend.error": "Failed to resend some invitations",
  "workspace_settings.members.bulk_progress": "{done} of {total}",
  "workspace_settings.members.bulk_revoke.title":
    "{count, plural, one {Revoke # invitation?} other {Revoke # invitations?}}",
  "workspace_settings.members.bulk_revoke.description":
    "Nobody will be able to accept these invitations afterward.",
  "workspace_settings.members.bulk_revoke.success":
    "{count, plural, one {Revoked # invitation} other {Revoked # invitations}}",
  "workspace_settings.members.bulk_revoke.error": "Failed to revoke some invitations",
  "workspace_settings.members.pending_invitations_empty": "No pending invitations.",
  "workspace_settings.members.pending_invitations_empty_filtered":
    "No pending invitations match the current filters.",
  "workspace_settings.members.pending_invitations_load_error":
    "Failed to load pending invitations.",
  "profile_settings.work_hours.hours_value": "{value}h",
  "profile_settings.work_hours.capacity_value": "{value}%",

  "workspace_settings.roles.title": "Roles",
  "workspace_settings.roles.description":
    "Create custom workspace roles and manage their permission sets.",
  "workspace_settings.roles.create_action": "Create role",
  "workspace_settings.roles.search_placeholder": "Search roles…",
  "workspace_settings.roles.search_clear": "Clear search",
  "workspace_settings.roles.column_role": "Role",
  "workspace_settings.roles.column_description": "Description",
  "workspace_settings.roles.column_permissions": "Permissions",
  "workspace_settings.roles.column_members": "Members",
  "workspace_settings.roles.column_actions": "Actions",
  "workspace_settings.roles.permission_count":
    "{count, plural, one {# permission} other {# permissions}}",
  "workspace_settings.roles.member_count": "{count, plural, one {# member} other {# members}}",
  "workspace_settings.roles.row_actions_label": "Actions for {name}",
  "workspace_settings.roles.empty": "No roles yet.",
  "workspace_settings.roles.section_title": "Custom roles",
  "workspace_settings.roles.section_description":
    "Create role presets with explicit permission sets for workspace members.",
  "workspace_settings.roles.system_badge": "System",
  "workspace_settings.roles.default_badge": "Default",
  "workspace_settings.roles.no_description": "No description",
  "workspace_settings.roles.summary": "{permissions} permissions • {members} members",
  "workspace_settings.roles.view_permissions": "View permissions",
  "workspace_settings.roles.edit": "Edit",
  "workspace_settings.roles.name_edit_hint": "Rename",
  "workspace_settings.roles.set_default": "Set as default",
  "workspace_settings.roles.name_required": "A role needs a name.",
  "workspace_settings.roles.delete": "Delete",
  "workspace_settings.roles.delete_confirm_title": "Delete role",
  "workspace_settings.roles.delete_confirm_description":
    'Delete the custom role "{name}"? Members currently assigned this role will lose its permissions.',
  "workspace_settings.roles.delete_success": "Role deleted",
  "workspace_settings.roles.delete_error": "Failed to delete role",
  "workspace_settings.roles.column_tier": "Tier",
  "workspace_settings.roles.duplicate": "Duplicate",
  "workspace_settings.roles.duplicate_success": "System role duplicated",
  "workspace_settings.roles.duplicate_error": "Failed to duplicate system role",
  "workspace_settings.roles.editor_create_title": "Create role",
  "workspace_settings.roles.editor_description_editable":
    "Configure a custom role and choose which permissions it grants.",
  "workspace_settings.roles.editor_description_readonly":
    "System roles are read-only. Review the permission set below.",
  "workspace_settings.roles.name_label": "Name",
  "workspace_settings.roles.description_label": "Description",
  "workspace_settings.roles.can_be_assigned": "Can be assigned to members",
  "workspace_settings.roles.is_default": "Default role for new members",
  "workspace_settings.roles.close": "Close",
  "workspace_settings.roles.update": "Update role",
  "workspace_settings.roles.create": "Create role",
  "workspace_settings.roles.saving": "Saving...",
  "workspace_settings.roles.update_success": "Role updated",
  "workspace_settings.roles.create_success": "Role created",
  "workspace_settings.roles.update_error": "Failed to update role",
  "workspace_settings.roles.create_error": "Failed to create role",

  "workspace_settings.roles.create_modal.description":
    "Name the role and pick its License Tier. You'll choose its permissions on the next page.",
  "workspace_settings.roles.create_modal.name_label": "Role name",
  "workspace_settings.roles.create_modal.name_required": "A role needs a name.",
  "workspace_settings.roles.create_modal.description_label": "Description",
  "workspace_settings.roles.create_modal.tier_label": "License tier",
  "workspace_settings.roles.create_modal.tier_placeholder": "Select a tier",
  "workspace_settings.roles.create_modal.tier_required": "Choose a License Tier.",
  "workspace_settings.roles.create_modal.tier_help":
    "Fixed after creation. Sets the ceiling of what this role may hold.",

  "workspace_settings.roles.detail.description":
    "Set what this role can do, review what's locked by its tier, and see who's in it.",
  "workspace_settings.roles.detail.name_label": "Role name",
  "workspace_settings.roles.detail.description_label": "Description",
  "workspace_settings.roles.detail.default_label": "Give new members this role",
  "workspace_settings.roles.detail.default_set": "New members will get this role",
  "workspace_settings.roles.detail.default_error": "Failed to set the default role",
  "workspace_settings.roles.detail.save_error": "Failed to save the role",
  "workspace_settings.roles.detail.load_error":
    "The unpublished role changes could not be loaded. Try again.",
  "workspace_settings.roles.detail.publish_changes": "Publish changes",
  "workspace_settings.roles.detail.discard_changes": "Discard changes",
  "workspace_settings.roles.detail.publish_success": "Role changes published",
  "workspace_settings.roles.detail.publish_error": "Failed to publish role changes",
  "workspace_settings.roles.detail.discard_success": "Unpublished role changes discarded",
  "workspace_settings.roles.detail.discard_error": "Failed to discard role changes",
  "workspace_settings.roles.detail.tab_permissions": "Permissions",
  "workspace_settings.roles.detail.tab_members": "Role Members",
  "workspace_settings.roles.detail.members_search_placeholder": "Search members…",
  "workspace_settings.roles.detail.members_empty_title": "No members hold this role yet.",
  "workspace_settings.roles.detail.members_load_error": "This role's members could not be loaded.",

  "workspace_settings.roles.permissions.available_title": "Available permissions",
  "workspace_settings.roles.permissions.available_description":
    "Turn these on or off for this role.",
  "workspace_settings.roles.permissions.locked_title": "Locked by this tier",
  "workspace_settings.roles.permissions.locked_description":
    "Fixed by this role's tier. Tier cannot be changed at this time.",

  // Permission categories, role presets and team-projects cell names (NG-2585)
  "workspace_settings.roles.permission_category.workspace": "Workspace",
  "workspace_settings.roles.permission_category.project": "Projects",
  "workspace_settings.roles.permission_category.workitem": "Work Items",
  "workspace_settings.roles.permission_category.team": "Teams",
  "workspace_settings.roles.permission_category.request": "Requests",
  "workspace_settings.roles.permission_category.events": "Events",
  "workspace_settings.roles.permission_category.calendar": "Calendars",
  "workspace_settings.roles.permission_category.capacities": "Capacity",
  "workspace_settings.roles.permission_category.dashboard": "Dashboards",
  "workspace_settings.roles.permission_category.goal": "Goals",
  "workspace_settings.roles.permission_category.process": "Processes",
  "workspace_settings.roles.permission_category.financialData": "Financial data",
  "workspace_settings.roles.preset.admin": "Start from Admin",
  "workspace_settings.roles.preset.standard_user": "Start from Standard User",
  "workspace_settings.roles.preset.guest": "Start from Guest",
  "workspace_settings.roles.preset.empty": "No permissions",
  "team_projects.cell_aria.delivery_teams": "Delivery teams · {name}",
  "team_projects.cell_aria.client_teams": "Client teams · {name}",
  "team_projects.cell_aria.start_date": "Start date · {name}",
  "team_projects.cell_aria.target_completion": "Target completion · {name}",
  "team_projects.cell_aria.actual_completion": "Actual completion · {name}",
  "team_projects.cell_aria.target_launch": "Target launch · {name}",
  "team_projects.cell_aria.goals": "Goals · {name}",
  "team_projects.cell_aria.programs": "Programs · {name}",

  "workspace_settings.roles.permissions.view_locked":
    "View stays on while anything else here is on, because the rest is meaningless without it.",
  "workspace_settings.roles.permissions.dependency_locked":
    '"{name}" needs this on, so it stays on until "{name}" is turned off.',
  "workspace_settings.roles.permissions.loading": "Loading permissions…",
  "workspace_settings.roles.permissions.load_error": "Permissions could not be loaded.",

  "workspace_settings.job_roles.title": "Job Roles",
  "workspace_settings.job_roles.description":
    "Define the job roles available in your workspace. Job roles staff projects and work items in place of a person.",
  "workspace_settings.job_roles.create_action": "Add job role",
  "workspace_settings.job_roles.search_placeholder": "Search job roles…",
  "workspace_settings.job_roles.search_clear": "Clear search",
  "workspace_settings.job_roles.column_job_role": "Job Role",
  "workspace_settings.job_roles.column_members": "Members",
  "workspace_settings.job_roles.column_status": "Status",
  "workspace_settings.job_roles.column_actions": "Actions",
  "workspace_settings.job_roles.member_count": "{count, plural, one {# member} other {# members}}",
  "workspace_settings.job_roles.name_edit_hint": "Rename",
  "workspace_settings.job_roles.status_active": "Active",
  "workspace_settings.job_roles.status_inactive": "Inactive",
  "workspace_settings.job_roles.toggle_status_label": "Toggle status for {name}",
  "workspace_settings.job_roles.row_actions_label": "Actions for {name}",
  "workspace_settings.job_roles.empty": "No job roles yet.",
  "workspace_settings.job_roles.edit": "Edit",
  "workspace_settings.job_roles.delete": "Delete",
  "workspace_settings.job_roles.deactivate": "Deactivate",
  "workspace_settings.job_roles.delete_confirm_title": "Delete job role",
  "workspace_settings.job_roles.delete_confirm_description":
    'Delete the job role "{name}"? Any work assigned to it becomes unassigned.',
  "workspace_settings.job_roles.delete_success": "Job role deleted",
  "workspace_settings.job_roles.delete_error": "Failed to delete job role",
  "workspace_settings.job_roles.deactivate_confirm_title": "Deactivate job role",
  "workspace_settings.job_roles.deactivate_confirm_description":
    'Deactivate "{name}"? This clears the job role from all users and work items.',
  "workspace_settings.job_roles.status_error": "Failed to change job role status",
  "workspace_settings.job_roles.editor_create_title": "Add job role",
  "workspace_settings.job_roles.editor_edit_title": "Edit job role",
  "workspace_settings.job_roles.editor_description":
    "Name the job role, pick a color, and describe what it does.",
  "workspace_settings.job_roles.name_label": "Name",
  "workspace_settings.job_roles.name_placeholder": "e.g. Instructional Designer",
  "workspace_settings.job_roles.color_label": "Color",
  "workspace_settings.job_roles.description_label": "Description",
  "workspace_settings.job_roles.members_label": "Members",
  "workspace_settings.job_roles.members_placeholder": "Assign members…",
  "workspace_settings.job_roles.members_search_placeholder": "Search members…",
  "workspace_settings.job_roles.members_empty": "No eligible members",
  "workspace_settings.job_roles.members_hint": "Guests can't hold a job role and aren't listed.",
  "workspace_settings.job_roles.members_edit_label": "Edit members for {name}",
  "workspace_settings.job_roles.members_loading": "Loading members…",
  "workspace_settings.job_roles.members_save": "Save",
  "workspace_settings.job_roles.members_update_success": "Members updated",
  "workspace_settings.job_roles.members_update_error": "Failed to update members",
  "workspace_settings.job_roles.cancel": "Cancel",
  "workspace_settings.job_roles.update": "Update job role",
  "workspace_settings.job_roles.create": "Add job role",
  "workspace_settings.job_roles.saving": "Saving…",
  "workspace_settings.job_roles.update_success": "Job role updated",
  "workspace_settings.job_roles.create_success": "Job role created",
  "workspace_settings.job_roles.update_error": "Failed to update job role",
  "workspace_settings.job_roles.create_error": "Failed to create job role",
  "workspace_settings.job_roles.rates.section_label": "Hourly rate",
  "workspace_settings.job_roles.rates.add_label": "Add a rate",
  "workspace_settings.job_roles.rates.hourly_rate_label": "Hourly rate",
  "workspace_settings.job_roles.rates.add_button": "Add rate",
  "workspace_settings.job_roles.rates.add_hint":
    "The new rate takes effect today. To set a past rate, add it, then edit the date on its row below. This role's rate is used to estimate the cost of work staffed by the role.",
  "workspace_settings.job_roles.rates.history_label": "Rate history",
  "workspace_settings.job_roles.rates.empty":
    "No rate set. Work staffed by this role is estimated at 0 until a rate is added.",
  "workspace_settings.job_roles.rates.per_hour": "{amount} / hr",
  "workspace_settings.job_roles.rates.delete_aria": "Delete rate effective {date}",
  "workspace_settings.job_roles.rates.edit_effective_aria":
    "Change effective date (currently {date})",
  "workspace_settings.job_roles.rates.loading": "Loading rates…",
  "workspace_settings.job_roles.rates.load_error": "Could not load rates.",
  "workspace_settings.job_roles.rates.invalid_rate": "Enter a rate of 0 or more.",
  "workspace_settings.job_roles.rates.saved": "Rate saved",
  "workspace_settings.job_roles.rates.save_error": "Could not save the rate.",
  "workspace_settings.job_roles.rates.update_error": "Could not update the rate.",
  "workspace_settings.job_roles.rates.delete_error": "Could not delete the rate.",
  "workspace_settings.job_roles.rates.create_hint":
    "Optional — takes effect today. Left blank, work staffed by this role is estimated at 0 until you add a rate.",
  "workspace_settings.job_roles.rates.create_save_error":
    "The job role was created, but its rate could not be saved. Add it from the role's editor.",

  "workspace_settings.ai_provider.title": "AI provider",
  "workspace_settings.ai_provider.description":
    "Choose which AI provider runs the reporting agent for this workspace, and supply its credentials. Cognota curates the models.",
  "workspace_settings.ai_provider.mode_label": "Provider",
  "workspace_settings.ai_provider.mode_managed": "Cognota-managed",
  "workspace_settings.ai_provider.mode_managed_help":
    "Cognota's own AI account runs the agent. Nothing to configure.",
  "workspace_settings.ai_provider.mode_custom": "Your own provider",
  "workspace_settings.ai_provider.mode_custom_help":
    "Your organisation's own account; usage is billed to you.",
  "workspace_settings.ai_provider.provider_label": "Provider",
  "workspace_settings.ai_provider.runs_line":
    "Runs {agent} for conversations and dashboards, and {fast} for quick tasks.",
  "workspace_settings.ai_provider.docs_link": "{provider} documentation",
  "workspace_settings.ai_provider.credential_stored": "Saved key {hint}",
  "workspace_settings.ai_provider.credential_replace": "Replace key",
  "workspace_settings.ai_provider.per_role_field_label": "{title} for {model}",
  "workspace_settings.ai_provider.test_action": "Test connection",
  "workspace_settings.ai_provider.testing": "Testing connection…",
  "workspace_settings.ai_provider.test_help":
    "Testing sends one request to each model with this key. It takes a few seconds and is billed to the provider.",
  "workspace_settings.ai_provider.progress_help":
    "This makes real requests to the provider and can take several seconds.",
  "workspace_settings.ai_provider.test_passed": "Connection test passed",
  "workspace_settings.ai_provider.test_failed": "Connection test failed",
  "workspace_settings.ai_provider.test_error": "Couldn't run the connection test.",
  "workspace_settings.ai_provider.role_agent": "Agent model",
  "workspace_settings.ai_provider.role_fast": "Fast model",
  "workspace_settings.ai_provider.save": "Save",
  "workspace_settings.ai_provider.saving": "Saving…",
  "workspace_settings.ai_provider.save_requires_test": "Test the connection before saving.",
  "workspace_settings.ai_provider.save_success": "AI provider saved.",
  "workspace_settings.ai_provider.save_refused":
    "The connection test failed, so nothing was saved.",
  "workspace_settings.ai_provider.save_error": "Couldn't save the AI provider.",
  "workspace_settings.ai_provider.credential_changed":
    "The saved key changed while it was being tested. Please try again.",
  "workspace_settings.ai_provider.resetting": "Switching back to Cognota-managed…",
  "workspace_settings.ai_provider.resetting_help": "The saved provider and key are being removed.",
  "workspace_settings.ai_provider.reset_confirm_title": "Switch back to Cognota-managed?",
  "workspace_settings.ai_provider.reset_confirm_description":
    "The saved provider and key are removed, and the agent runs on Cognota's account again.",
  "workspace_settings.ai_provider.reset_confirm": "Switch back",
  "workspace_settings.ai_provider.reset_success": "Back to Cognota-managed.",
  "workspace_settings.ai_provider.reset_error": "Couldn't switch back to Cognota-managed.",
  "workspace_settings.ai_provider.last_verified": "Last verified {date}",
  "workspace_settings.ai_provider.last_error": "Last error: {error}",
  "workspace_settings.ai_provider.load_error": "Couldn't load the AI provider setting.",
  "workspace_settings.ai_provider.retry": "Retry",
  "workspace_settings.webhooks.title": "Webhooks",
  "workspace_settings.webhooks.description":
    "Create, edit, and inspect workspace webhook integrations.",
  "workspace_settings.webhooks.create_action": "Create webhook",
  "workspace_settings.webhooks.empty_title": "No webhooks yet",
  "workspace_settings.webhooks.empty_description":
    "Create a webhook to push workspace events into external systems.",
  "workspace_settings.webhooks.active": "Active",
  "workspace_settings.webhooks.disabled": "Disabled",
  "workspace_settings.webhooks.updated_at": "Updated {date}",
  "workspace_settings.webhooks.quick_edit": "Quick edit",
  "workspace_settings.webhooks.open_detail": "Open detail",
  "workspace_settings.webhooks.dialog_create_title": "Create webhook",
  "workspace_settings.webhooks.dialog_edit_title": "Edit webhook",
  "workspace_settings.webhooks.dialog_description":
    "Configure the callback URL and choose which workspace events should trigger this webhook.",
  "workspace_settings.webhooks.url_label": "Callback URL",
  "workspace_settings.webhooks.is_active": "Webhook is active",
  "workspace_settings.webhooks.event_scope": "Event scope",
  "workspace_settings.webhooks.event_scope_all": "All supported workspace events",
  "workspace_settings.webhooks.event_scope_individual": "Choose individual event groups",
  "workspace_settings.webhooks.event.project": "Projects",
  "workspace_settings.webhooks.event.cycle": "Cycles",
  "workspace_settings.webhooks.event.module": "Modules",
  "workspace_settings.webhooks.event.issue": "Work items",
  "workspace_settings.webhooks.event.issue_comment": "Comments",
  "workspace_settings.webhooks.saving": "Saving...",
  "workspace_settings.webhooks.update": "Update webhook",
  "workspace_settings.webhooks.create": "Create webhook",
  "workspace_settings.webhooks.update_success": "Webhook updated",
  "workspace_settings.webhooks.create_success": "Webhook created",
  "workspace_settings.webhooks.update_error": "Failed to update webhook",
  "workspace_settings.webhooks.create_error": "Failed to create webhook",
  "workspace_settings.webhooks.secret_notice_title": "Store this webhook secret now",
  "workspace_settings.webhooks.secret_notice_description":
    "This is the only time we'll show this secret. Copy it somewhere safe before closing.",
  "workspace_settings.webhooks.secret_copy": "Copy secret",
  "workspace_settings.webhooks.secret_copied": "Secret copied to clipboard",
  "workspace_settings.webhooks.secret_copy_error": "Failed to copy secret",
  "workspace_settings.webhooks.secret_done": "I saved it",
  "workspace_settings.webhooks.detail_title": "Webhook detail",
  "workspace_settings.webhooks.detail_description":
    "Inspect the webhook configuration, regenerate secrets, and review delivery activity.",
  "workspace_settings.webhooks.detail_panel_title": "Webhook detail",
  "workspace_settings.webhooks.detail_url": "URL",
  "workspace_settings.webhooks.detail_status": "Status",
  "workspace_settings.webhooks.detail_edit": "Edit webhook",
  "workspace_settings.webhooks.detail_regenerate": "Regenerate secret",
  "workspace_settings.webhooks.detail_load_logs": "Load logs",
  "workspace_settings.webhooks.detail_delete": "Delete webhook",
  "workspace_settings.webhooks.detail_regenerate_success": "Webhook secret regenerated",
  "workspace_settings.webhooks.detail_regenerate_error": "Failed to regenerate webhook secret",
  "workspace_settings.webhooks.detail_delete_confirm_title": "Delete webhook",
  "workspace_settings.webhooks.detail_delete_confirm_description":
    "Delete this webhook? The URL will stop receiving events immediately.",
  "workspace_settings.webhooks.detail_delete_success": "Webhook deleted",
  "workspace_settings.webhooks.detail_delete_error": "Failed to delete webhook",
  "workspace_settings.webhooks.detail_logs_error": "Failed to load webhook logs",
  "workspace_settings.webhooks.detail_secret_label": "Latest generated secret",
  "workspace_settings.webhooks.detail_deliveries": "Recent deliveries",
  "workspace_settings.webhooks.detail_event_unknown": "Unknown event",

  "workspace_settings.sso.title": "SSO",
  "workspace_settings.sso.description":
    "Configure SAML and OIDC single sign-on domains and identity provider settings.",
  "workspace_settings.sso.tabs.saml": "SAML",
  "workspace_settings.sso.tabs.oidc": "OIDC",
  "workspace_settings.sso.domains_title": "Allowed domains",
  "workspace_settings.sso.domains_description": "Restrict SSO access to one or more email domains.",
  "workspace_settings.sso.domains_placeholder": "example.com",
  "workspace_settings.sso.domains_add": "Add domain",
  "workspace_settings.sso.domains_add_suggested": "Add {domain}",
  "workspace_settings.sso.domains_remove": "Remove",
  "workspace_settings.sso.domains_empty": "No SSO domains configured yet.",
  "workspace_settings.sso.domains_added": "Domain added",
  "workspace_settings.sso.domains_add_error": "Failed to add domain",
  "workspace_settings.sso.domains_removed": "Domain removed",
  "workspace_settings.sso.domains_remove_error": "Failed to remove domain",
  "workspace_settings.sso.domain_sso_toggle": "SSO",
  "workspace_settings.sso.domain_sso_enabled": "SSO enabled for domain",
  "workspace_settings.sso.domain_sso_disabled":
    "SSO disabled — users on this domain will sign in with a password",
  "workspace_settings.sso.domain_sso_toggle_error": "Failed to update SSO setting",
  "workspace_settings.sso.provider_title": "Identity provider",
  "workspace_settings.sso.provider_description":
    "Configure the SAML provider metadata used for workspace sign-in.",
  "workspace_settings.sso.provider_enabled": "Provider is enabled",
  "workspace_settings.sso.provider_entity_id": "Entity ID",
  "workspace_settings.sso.provider_sso_url": "SSO URL",
  "workspace_settings.sso.provider_slo_url": "SLO URL",
  "workspace_settings.sso.provider_slo_url_hint":
    "Your identity provider's Single Logout endpoint. When set — and Cognota's SAML signing keys are configured — signing out of Cognota also signs the user out at the identity provider (SAML Single Logout) for sessions signed in after this was enabled, returning them to the Cognota sign-in screen. Takes precedence over the Logout URL below when it runs.",
  "workspace_settings.sso.provider_certificate": "Certificate",
  "workspace_settings.sso.provider_logout_url": "Logout URL",
  "workspace_settings.sso.provider_logout_url_hint":
    "Where a user is sent after signing out. Leave this blank to return them to the Cognota sign-in screen.",
  "workspace_settings.sso.provider_logout_url_hint_slo":
    "Where a user is sent after signing out — a plain redirect, with no sign-out at the identity provider. Used whenever Single Logout does not run: no SLO URL set, or the session cannot do SLO. Leave this blank to return them to the Cognota sign-in screen.",
  "workspace_settings.sso.provider_post_logout_redirect_enabled":
    "Send users to this URL after they sign out",
  "workspace_settings.sso.provider_validation_invalid_url": "Enter a valid http(s) URL.",
  "workspace_settings.sso.provider_saving": "Saving...",
  "workspace_settings.sso.provider_update": "Update provider",
  "workspace_settings.sso.provider_create": "Create provider",
  "workspace_settings.sso.provider_delete": "Delete provider",
  "workspace_settings.sso.provider_delete_confirm_title": "Delete SSO provider",
  "workspace_settings.sso.provider_delete_confirm_description":
    "Delete this SSO provider? SAML sign-in will be disabled for this workspace.",
  "workspace_settings.sso.provider_update_success": "SSO provider updated",
  "workspace_settings.sso.provider_create_success": "SSO provider created",
  "workspace_settings.sso.provider_update_error": "Failed to update SSO provider",
  "workspace_settings.sso.provider_create_error": "Failed to create SSO provider",
  "workspace_settings.sso.single_active_provider_error":
    "Another identity provider is already active for this workspace. Disable it before activating another.",
  "workspace_settings.sso.provider_delete_success": "SSO provider deleted",
  "workspace_settings.sso.provider_delete_error": "Failed to delete SSO provider",
  "workspace_settings.sso.metadata_title": "Service provider metadata",
  "workspace_settings.sso.metadata_description":
    "Use these values when configuring the identity provider in your SAML vendor.",
  "workspace_settings.sso.metadata_entity_id": "Entity ID",
  "workspace_settings.sso.metadata_acs_url": "ACS URL",
  "workspace_settings.sso.metadata_slo_url": "SLO URL",
  "workspace_settings.sso.metadata_copy": "Copy",
  "workspace_settings.sso.metadata_copied": "SP metadata copied",
  "workspace_settings.sso.metadata_copy_error": "Failed to copy SP metadata",
  "workspace_settings.sso.oidc.title": "OIDC identity provider",
  "workspace_settings.sso.oidc.description":
    "Configure the OIDC client used for workspace sign-in.",
  "workspace_settings.sso.oidc.enabled": "Provider is enabled",
  "workspace_settings.sso.oidc.client_id": "Client ID",
  "workspace_settings.sso.oidc.client_secret": "Client secret",
  "workspace_settings.sso.oidc.authorize_url": "Authorize URL",
  "workspace_settings.sso.oidc.token_url": "Token URL",
  "workspace_settings.sso.oidc.userinfo_url": "Userinfo URL",
  "workspace_settings.sso.oidc.logout_url": "Logout URL",
  "workspace_settings.sso.oidc.logout_url_hint":
    "Where a user is sent after signing out. Leave this blank to return them to the Cognota sign-in screen.",
  "workspace_settings.sso.oidc.post_logout_redirect_enabled":
    "Send users to this URL after they sign out",
  "workspace_settings.sso.oidc.saving": "Saving...",
  "workspace_settings.sso.oidc.update": "Update provider",
  "workspace_settings.sso.oidc.create": "Create provider",
  "workspace_settings.sso.oidc.delete": "Delete provider",
  "workspace_settings.sso.oidc.delete_confirm_title": "Delete OIDC provider",
  "workspace_settings.sso.oidc.delete_confirm_description":
    "Delete this OIDC provider? OIDC sign-in will be disabled for this workspace.",
  "workspace_settings.sso.oidc.update_success": "OIDC provider updated",
  "workspace_settings.sso.oidc.create_success": "OIDC provider created",
  "workspace_settings.sso.oidc.update_error": "Failed to update OIDC provider",
  "workspace_settings.sso.oidc.create_error": "Failed to create OIDC provider",
  "workspace_settings.sso.oidc.delete_success": "OIDC provider deleted",
  "workspace_settings.sso.oidc.delete_error": "Failed to delete OIDC provider",
  "workspace_settings.sso.oidc.redirect_uri_title": "Redirect URI",
  "workspace_settings.sso.oidc.redirect_uri_description":
    "Paste this redirect URI into your OIDC provider's allowed redirect URIs.",
  "workspace_settings.sso.oidc.redirect_uri_label": "Redirect URI",
  "workspace_settings.sso.oidc.redirect_uri_copy": "Copy",
  "workspace_settings.sso.oidc.redirect_uri_copied": "Redirect URI copied",
  "workspace_settings.sso.oidc.redirect_uri_copy_error": "Failed to copy redirect URI",
  "workspace_settings.sso.oidc.validation_missing_fields":
    "Fill in all required OIDC fields before saving.",
  "workspace_settings.sso.oidc.validation_invalid_url":
    "Authorize, token, userinfo, and logout URLs must be valid URLs.",
  "workspace_settings.sso.oidc.secret_show": "Show secret",
  "workspace_settings.sso.oidc.secret_hide": "Hide secret",
  "workspace_settings.sso.oidc.secret_placeholder": "••••••••",

  "workspace_settings.sso.override_title": "Password-login override",
  "workspace_settings.sso.override_description":
    "Temporarily allow password login and reset for this workspace, even while SSO is enforced. Use this to test a new identity provider or to recover if it stops working. Turn it back off once you're done.",
  "workspace_settings.sso.override_label": "Allow password login",
  "workspace_settings.sso.override_active_notice":
    "Password login is currently allowed for this workspace, overriding SSO enforcement.",
  "workspace_settings.sso.override_enable_confirm_title": "Allow password login?",
  "workspace_settings.sso.override_enable_confirm_description":
    "This suspends SSO enforcement for the whole workspace — anyone can sign in or reset their password without going through your identity provider until you turn this back off.",
  "workspace_settings.sso.override_enable_confirm": "Allow password login",
  "workspace_settings.sso.override_enabled_success": "Password-login override enabled",
  "workspace_settings.sso.override_disabled_success": "Password-login override disabled",
  "workspace_settings.sso.override_toggle_error": "Failed to update the password-login override",

  "workspace_settings.password_policy.title": "Password policy",
  "workspace_settings.password_policy.description":
    "Raise this workspace's password requirements above the platform minimum. Each setting can only be stricter, never weaker — and if the same account belongs to more than one workspace with password sign-in, the strictest value on each setting applies to their shared password.",
  "workspace_settings.password_policy.min_length_label": "Minimum length",
  "workspace_settings.password_policy.min_length_help":
    "Leave blank to use the platform minimum of {min} characters.",
  "workspace_settings.password_policy.variety_label": "Require a mix of character types",
  "workspace_settings.password_policy.variety_help":
    "At least 3 of: uppercase, lowercase, numbers, symbols.",
  "workspace_settings.password_policy.expiration_label": "Require periodic password changes",
  "workspace_settings.password_policy.expiration_days_label": "Days before a password expires",
  "workspace_settings.password_policy.reuse_label": "Block reusing recent passwords",
  "workspace_settings.password_policy.reuse_help": "Blocks reuse of the last 5 passwords.",
  "workspace_settings.password_policy.save": "Save changes",
  "workspace_settings.password_policy.saving": "Saving...",
  "workspace_settings.password_policy.save_success": "Password policy updated",
  "workspace_settings.password_policy.save_error": "Failed to update the password policy",

  "workspace_settings.session_timeout.title": "Session timeout",
  "workspace_settings.session_timeout.description":
    "Tighten how long a session can sit idle and the absolute limit on how long it can last. Both can only be made stricter, never looser — and if the same account belongs to more than one workspace, the strictest value on each setting applies to their one shared session.",
  "workspace_settings.session_timeout.idle_label": "End a session after a period of inactivity",
  "workspace_settings.session_timeout.idle_help": "Minimum {min} minutes. Off by default.",
  "workspace_settings.session_timeout.absolute_label": "Lower the absolute session limit",
  "workspace_settings.session_timeout.absolute_help":
    "Leave unchecked to use the platform default of {days} days.",
  "workspace_settings.session_timeout.unit_minutes": "Minutes",
  "workspace_settings.session_timeout.unit_hours": "Hours",
  "workspace_settings.session_timeout.unit_days": "Days",
  "workspace_settings.session_timeout.idle_unit_label": "Idle timeout unit",
  "workspace_settings.session_timeout.absolute_unit_label": "Absolute session limit unit",
  "workspace_settings.session_timeout.save": "Save changes",
  "workspace_settings.session_timeout.saving": "Saving...",
  "workspace_settings.session_timeout.save_success": "Session timeout settings updated",
  "workspace_settings.session_timeout.save_error": "Failed to update the session timeout settings",
  "workspace_settings.session_timeout.load_error":
    "Unable to load the current session timeout policy.",
  "workspace_settings.session_timeout.retry": "Retry",

  "workspace_settings.exports.title": "Exports",
  "workspace_settings.exports.description": "Trigger issue exports and monitor recent export jobs.",
  "workspace_settings.exports.run_title": "Run export",
  "workspace_settings.exports.run_description": "Export workspace issues as CSV, Excel, or JSON.",
  "workspace_settings.exports.format_label": "Format",
  "workspace_settings.exports.format_csv": "CSV",
  "workspace_settings.exports.format_xlsx": "Excel (.xlsx)",
  "workspace_settings.exports.format_json": "JSON",
  "workspace_settings.exports.projects_title": "Projects",
  "workspace_settings.exports.projects_hint":
    "Leave this empty to export every accessible project.",
  "workspace_settings.exports.submit": "Start export",
  "workspace_settings.exports.submitting": "Starting...",
  "workspace_settings.exports.submit_success": "Export started",
  "workspace_settings.exports.submit_error": "Failed to start export",
  "workspace_settings.exports.history_title": "Previous exports",
  "workspace_settings.exports.history_description": "Recent issue export jobs and download links.",
  "workspace_settings.exports.history_refresh": "Refresh",
  "workspace_settings.exports.history_empty": "No exports have been requested yet.",
  "workspace_settings.exports.history_previous": "Previous",
  "workspace_settings.exports.history_next": "Next",
  "workspace_settings.exports.row_label": "{provider} export",
  "workspace_settings.exports.row_download": "Download",

  "workspace_settings.calendars.title": "Calendars",
  "workspace_settings.calendars.description":
    "Manage holiday calendars, user OAuth calendar connections, and domain-wide scheduling integrations.",
  "workspace_settings.calendars.user_title": "User calendar connections",
  "workspace_settings.calendars.user_description":
    "Connect your personal Google or Outlook calendar to sync availability for scheduling.",
  "workspace_settings.calendars.user_connect_google": "Connect Google Calendar",
  "workspace_settings.calendars.user_connect_outlook": "Connect Outlook Calendar",
  "workspace_settings.calendars.user_connect_disabled_domain_wide":
    "Domain-wide sync is active for this workspace — your calendar is already being synced.",
  "workspace_settings.calendars.user_source_superseded": "Superseded by domain-wide sync",
  "workspace_settings.calendars.redirect_connected": "Calendar connected",
  "workspace_settings.calendars.redirect_error_denied": "Calendar connection cancelled",
  "workspace_settings.calendars.redirect_error_no_code":
    "Calendar connection failed — provider did not return an authorization code.",
  "workspace_settings.calendars.redirect_error_invalid_state":
    "Calendar connection failed — invalid session state.",
  "workspace_settings.calendars.redirect_error_exchange_failed":
    "Calendar connection failed — unable to exchange authorization code.",
  "workspace_settings.calendars.redirect_error_domain_wide_active":
    "Domain-wide sync is active for this workspace — a personal connection is not needed.",
  "workspace_settings.calendars.redirect_error_unknown": "Calendar connection failed.",
  "workspace_settings.calendars.user_connect_error_google":
    "Failed to start Google calendar connection",
  "workspace_settings.calendars.user_connect_error_outlook":
    "Failed to start Outlook calendar connection",
  "workspace_settings.calendars.user_sync": "Sync",
  "workspace_settings.calendars.user_sync_success": "Sync triggered",
  "workspace_settings.calendars.user_sync_error": "Failed to trigger sync",
  "workspace_settings.calendars.user_disconnect": "Disconnect",
  "workspace_settings.calendars.user_delete": "Delete",
  "workspace_settings.calendars.user_disconnect_success": "Calendar connection removed",
  "workspace_settings.calendars.user_disconnect_error": "Failed to remove calendar connection",
  "workspace_settings.calendars.user_never_synced": "Never synced",
  "workspace_settings.calendars.user_empty": "No user calendar connections yet.",
  "workspace_settings.calendars.user_events": "{count} events",
  "workspace_settings.calendars.sync_status_never": "Never synced",
  "workspace_settings.calendars.sync_status_pending": "Syncing",
  "workspace_settings.calendars.sync_status_success": "Healthy",
  "workspace_settings.calendars.sync_status_failed": "Failed",
  "workspace_settings.calendars.sync_status_skipped": "Skipped",
  "workspace_settings.calendars.sync_last_success": "Last success {date}",
  "workspace_settings.calendars.sync_last_started": "Last started {date}",
  "workspace_settings.calendars.sync_never": "Never synced",
  "workspace_settings.calendars.sync_last_event_count": "{count} last sync",
  "workspace_settings.calendars.holidays_title": "Public holidays",
  "workspace_settings.calendars.holidays_description":
    "Select your country or region to generate public holidays that reduce your capacity.",
  "workspace_settings.calendars.holidays_country_label": "Country",
  "workspace_settings.calendars.holidays_region_label": "Region",
  "workspace_settings.calendars.holidays_national": "National holidays only",
  "workspace_settings.calendars.holidays_empty": "No public holiday calendar generated yet.",
  "workspace_settings.calendars.holidays_current":
    "{region} holidays are active with {count} events.",
  "workspace_settings.calendars.holidays_save": "Generate holidays",
  "workspace_settings.calendars.holidays_saving": "Generating...",
  "workspace_settings.calendars.holidays_remove": "Remove",
  "workspace_settings.calendars.holidays_save_success": "Public holidays updated",
  "workspace_settings.calendars.holidays_save_error": "Failed to update public holidays",
  "workspace_settings.calendars.holidays_remove_success": "Public holidays removed",
  "workspace_settings.calendars.holidays_remove_error": "Failed to remove public holidays",
  "workspace_settings.calendars.google_title": "Google Workspace domain-wide sync",
  "workspace_settings.calendars.google_description":
    "Configure a service-account-based integration that syncs calendars for matched members.",
  "workspace_settings.calendars.google_field_domain": "Google Workspace domain",
  "workspace_settings.calendars.google_field_key": "Service account JSON",
  "workspace_settings.calendars.outlook_title": "Outlook domain-wide sync",
  "workspace_settings.calendars.outlook_description":
    "Configure Microsoft 365 domain-wide scheduling access for matched workspace members.",
  "workspace_settings.calendars.outlook_field_tenant": "Tenant ID",
  "workspace_settings.calendars.outlook_field_client_id": "Client ID",
  "workspace_settings.calendars.outlook_field_client_secret": "Client secret",
  "workspace_settings.calendars.outlook_field_domain": "Microsoft 365 domain",
  "workspace_settings.calendars.config_enabled": "Enabled",
  "workspace_settings.calendars.config_disabled": "Disabled",
  "workspace_settings.calendars.config_matched_members": "{count} matched members",
  "workspace_settings.calendars.config_failed_members": "{count} failed",
  "workspace_settings.calendars.config_last_dispatch": "Last dispatched {date}",
  "workspace_settings.calendars.config_test_placeholder": "user@example.com",
  "workspace_settings.calendars.config_test": "Test",
  "workspace_settings.calendars.config_sync": "Sync now",
  "workspace_settings.calendars.config_enable": "Enable",
  "workspace_settings.calendars.config_disable": "Disable",
  "workspace_settings.calendars.config_connect": "Connect",
  "workspace_settings.calendars.config_saving": "Saving...",
  "workspace_settings.calendars.config_remove": "Remove integration",
  "workspace_settings.calendars.config_test_failed": "Connection test failed",
  "workspace_settings.calendars.members_empty": "No matched members yet.",
  "workspace_settings.calendars.members_never_synced": "Never synced",
  "workspace_settings.calendars.members_events": "{count} events",
  "workspace_settings.calendars.members_showing": "Showing {shown} of {total}",
  "workspace_settings.calendars.members_load_more": "Load more",
  "workspace_settings.calendars.members_loading": "Loading...",

  "workspace_settings.calendars.holiday_calendars.title": "Custom & holiday calendars",
  "workspace_settings.calendars.holiday_calendars.description":
    "Org-managed holiday calendars. People are matched automatically by their team or country & region.",
  "workspace_settings.calendars.holiday_calendars.new": "New calendar",
  "workspace_settings.calendars.holiday_calendars.empty": "No holiday calendars yet.",
  "workspace_settings.calendars.holiday_calendars.column_name": "Name",
  "workspace_settings.calendars.holiday_calendars.column_applies_to": "Applies to",
  "workspace_settings.calendars.holiday_calendars.column_status": "Status",
  "workspace_settings.calendars.holiday_calendars.column_actions": "Actions",
  "workspace_settings.calendars.holiday_calendars.row_actions_label": "Actions for {name}",
  "workspace_settings.calendars.holiday_calendars.name_label": "Name",
  "workspace_settings.calendars.holiday_calendars.name_placeholder": "e.g. Company Holidays",
  "workspace_settings.calendars.holiday_calendars.name_edit_hint": "Rename calendar",
  "workspace_settings.calendars.holiday_calendars.view_only_badge": "View only",
  "workspace_settings.calendars.holiday_calendars.view_only_banner":
    "This calendar belongs to another team, so you can see it but not change it.",
  "workspace_settings.calendars.holiday_calendars.locked_to_team_help":
    "This calendar applies to the people on this team.",
  "workspace_settings.calendars.holiday_calendars.applies_to_label": "Applies to",
  "workspace_settings.calendars.holiday_calendars.applies_to_everyone": "Everyone",
  "workspace_settings.calendars.holiday_calendars.applies_to_specific": "Specific people",
  "workspace_settings.calendars.holiday_calendars.applies_to_team_unset": "No team selected",
  "workspace_settings.calendars.holiday_calendars.applies_to_region_unset": "No region selected",
  "workspace_settings.calendars.holiday_calendars.match_by_label": "Match by",
  "workspace_settings.calendars.holiday_calendars.match_by_country_region": "Country & region",
  "workspace_settings.calendars.holiday_calendars.match_by_team": "Team",
  "workspace_settings.calendars.holiday_calendars.team_label": "Team",
  "workspace_settings.calendars.holiday_calendars.team_placeholder": "Select a team",
  "workspace_settings.calendars.holiday_calendars.team_search_placeholder": "Search teams…",
  "workspace_settings.calendars.holiday_calendars.team_empty": "No teams found",
  "workspace_settings.calendars.holiday_calendars.status_draft": "Draft",
  "workspace_settings.calendars.holiday_calendars.status_active": "Active",
  "workspace_settings.calendars.holiday_calendars.status_inactive": "Inactive",
  "workspace_settings.calendars.holiday_calendars.new_title": "New holiday calendar",
  "workspace_settings.calendars.holiday_calendars.new_description":
    "Name the calendar and choose who it covers. You can add its dates next.",
  "workspace_settings.calendars.holiday_calendars.cancel": "Cancel",
  "workspace_settings.calendars.holiday_calendars.create": "Create",
  "workspace_settings.calendars.holiday_calendars.creating": "Creating…",
  "workspace_settings.calendars.holiday_calendars.create_success": "Holiday calendar created.",
  "workspace_settings.calendars.holiday_calendars.create_error": "Couldn't create the calendar.",
  "workspace_settings.calendars.holiday_calendars.update_success": "Changes saved.",
  "workspace_settings.calendars.holiday_calendars.update_error": "Couldn't save your changes.",
  "workspace_settings.calendars.holiday_calendars.edit": "Edit",
  "workspace_settings.calendars.holiday_calendars.delete": "Delete",
  "workspace_settings.calendars.holiday_calendars.delete_confirm_title": "Delete this calendar?",
  "workspace_settings.calendars.holiday_calendars.delete_confirm_description":
    "“{name}” covers {count} people. Deleting it removes its dates and any capacity impact. This can't be undone.",
  "workspace_settings.calendars.holiday_calendars.delete_success": "Holiday calendar deleted.",
  "workspace_settings.calendars.holiday_calendars.delete_error": "Couldn't delete the calendar.",
  "workspace_settings.calendars.holiday_calendars.suggestions.title": "Suggested calendars",
  "workspace_settings.calendars.holiday_calendars.suggestions.description":
    "No calendar covers these people, so they get no holidays.",
  "workspace_settings.calendars.holiday_calendars.suggestions.covers_region":
    "A calendar for {name}",
  "workspace_settings.calendars.holiday_calendars.suggestions.covers_team":
    "A calendar for the {name} team",
  "workspace_settings.calendars.holiday_calendars.suggestions.affected_one":
    "1 user isn't covered by any calendar.",
  "workspace_settings.calendars.holiday_calendars.suggestions.affected_other":
    "{count} users aren't covered by any calendar.",
  "workspace_settings.calendars.holiday_calendars.suggestions.create": "Create",
  "workspace_settings.calendars.holiday_calendars.suggestions.dismiss": "Dismiss {name}",
  "workspace_settings.calendars.holiday_calendars.suggestions.dismiss_error":
    "Couldn't dismiss the suggestion.",
  "workspace_settings.calendars.holiday_calendars.suggestions.suggested_name": "{name} holidays",
  "workspace_settings.calendars.holiday_calendars.publish": "Publish",
  "workspace_settings.calendars.holiday_calendars.publish_success": "Calendar published.",
  "workspace_settings.calendars.holiday_calendars.publish_error": "Couldn't publish the calendar.",
  "workspace_settings.calendars.holiday_calendars.deactivate": "Deactivate",
  "workspace_settings.calendars.holiday_calendars.deactivate_confirm_title":
    "Deactivate this calendar?",
  "workspace_settings.calendars.holiday_calendars.deactivate_confirm_description":
    "“{name}” will stop affecting covered people's capacity, including past dates. You can reactivate it later.",
  "workspace_settings.calendars.holiday_calendars.toggle_active_label": "Toggle active for {name}",
  "workspace_settings.calendars.holiday_calendars.back_to_calendars": "Back to calendars",
  "workspace_settings.calendars.holiday_calendars.coverage_title": "Coverage",
  "workspace_settings.calendars.holiday_calendars.coverage_description":
    "Who this calendar covers. Changes to the scope re-match people immediately.",
  // The banner over the Dates table when the calendar around those dates reaches
  // nobody's capacity (NG-586). The title carries the consequence, each reason
  // carries its own fix — a calendar can fail more than one at once, and they are
  // joined into one banner rather than stacked.
  "workspace_settings.calendars.holiday_calendars.inert_title":
    "These dates aren't reducing anyone's capacity yet",
  "workspace_settings.calendars.holiday_calendars.inert_reason_inactive":
    "This calendar is switched off — turn it Active to apply its dates.",
  "workspace_settings.calendars.holiday_calendars.inert_reason_never_published":
    "This calendar has never been published — publish it to apply its dates.",
  "workspace_settings.calendars.holiday_calendars.inert_reason_matches_nobody":
    "No one's profile has this country & region, so the calendar matches nobody \u2014 set Country & Region on member profiles, or scope this calendar differently.",
  "workspace_settings.calendars.holiday_calendars.people_title": "People on this calendar",
  "workspace_settings.calendars.holiday_calendars.people_count": "{count} people",
  "workspace_settings.calendars.holiday_calendars.people_column_name": "Name",
  "workspace_settings.calendars.holiday_calendars.people_column_team": "Team",
  "workspace_settings.calendars.holiday_calendars.people_column_country": "Country",
  "workspace_settings.calendars.holiday_calendars.people_column_region": "Region",
  "workspace_settings.calendars.holiday_calendars.people_loading": "Loading…",
  "workspace_settings.calendars.holiday_calendars.people_search": "Search people",
  "workspace_settings.calendars.holiday_calendars.people_search_clear": "Clear the people search",
  "workspace_settings.calendars.holiday_calendars.people_no_matches":
    "No one on this calendar matches that search.",
  "workspace_settings.calendars.holiday_calendars.people_truncated":
    "Showing {shown} of {total}. Search to find someone specific.",
  "workspace_settings.calendars.holiday_calendars.team_required":
    "Pick a team — a calendar matching by team covers nobody without one.",
  "workspace_settings.calendars.holiday_calendars.country_required":
    "Pick a country — a calendar matching by country covers nobody without one.",
  "workspace_settings.calendars.holiday_calendars.people_empty":
    "No one is currently covered by this calendar.",
  "workspace_settings.calendars.holiday_calendars.not_found": "This calendar could not be found.",
  "workspace_settings.calendars.holiday_calendars.discard": "Discard",
  "workspace_settings.calendars.holiday_calendars.discard_confirm_title":
    "Discard unpublished dates?",
  "workspace_settings.calendars.holiday_calendars.discard_confirm_description":
    "Every date added since this calendar was last published will be removed. Dates you already published are kept, and coverage changes are not affected.",
  "workspace_settings.calendars.holiday_calendars.discard_success": "Unpublished dates discarded.",
  "workspace_settings.calendars.holiday_calendars.discard_error": "Couldn't discard the dates.",
  "workspace_settings.calendars.holiday_calendars.dates.title": "Dates",
  "workspace_settings.calendars.holiday_calendars.dates.description":
    "The non-working days on this calendar. Covered people get the day off on each one.",
  "workspace_settings.calendars.holiday_calendars.dates.column_date": "Date",
  "workspace_settings.calendars.holiday_calendars.dates.column_name": "Holiday",
  "workspace_settings.calendars.holiday_calendars.dates.column_source": "Source",
  "workspace_settings.calendars.holiday_calendars.dates.scope_this_year": "This year",
  "workspace_settings.calendars.holiday_calendars.dates.scope_future": "Future",
  "workspace_settings.calendars.holiday_calendars.dates.scope_all": "All",
  "workspace_settings.calendars.holiday_calendars.dates.search_placeholder": "Search holidays",
  "workspace_settings.calendars.holiday_calendars.dates.search_clear": "Clear search",
  "workspace_settings.calendars.holiday_calendars.dates.add": "Add custom date",
  "workspace_settings.calendars.holiday_calendars.dates.add_title": "Add a custom date",
  "workspace_settings.calendars.holiday_calendars.dates.add_description":
    "Add a non-working day to this calendar. Give it an end date if it runs longer than a day.",
  "workspace_settings.calendars.holiday_calendars.dates.end_date_label": "End date (optional)",
  "workspace_settings.calendars.holiday_calendars.dates.end_before_start":
    "The end date can't be before the start date.",
  "workspace_settings.calendars.holiday_calendars.dates.edit_dates": "Edit dates",
  "workspace_settings.calendars.holiday_calendars.dates.edit_title": "Edit holiday dates",
  "workspace_settings.calendars.holiday_calendars.dates.edit_description":
    "Change when this holiday falls, or how many days it covers.",
  "workspace_settings.calendars.holiday_calendars.dates.edit_submit": "Save dates",
  "workspace_settings.calendars.holiday_calendars.dates.edit_success": "Holiday dates updated",
  "workspace_settings.calendars.holiday_calendars.dates.edit_error":
    "Failed to update the holiday's dates",
  "workspace_settings.calendars.holiday_calendars.dates.add_submit": "Add date",
  "workspace_settings.calendars.holiday_calendars.dates.name_label": "Holiday",
  "workspace_settings.calendars.holiday_calendars.dates.name_placeholder": "Company offsite",
  "workspace_settings.calendars.holiday_calendars.dates.date_label": "Date",
  "workspace_settings.calendars.holiday_calendars.dates.add_success": "Date added.",
  "workspace_settings.calendars.holiday_calendars.dates.add_error": "Couldn't add the date.",
  "workspace_settings.calendars.holiday_calendars.dates.load_error": "Couldn't load the dates.",
  "workspace_settings.calendars.holiday_calendars.dates.row_actions_label": "Date actions",
  "workspace_settings.calendars.holiday_calendars.dates.delete": "Delete",
  "workspace_settings.calendars.holiday_calendars.dates.delete_confirm_title": "Delete this date?",
  "workspace_settings.calendars.holiday_calendars.dates.delete_confirm_description":
    "“{name}” will be removed from this calendar and stops applying immediately.",
  "workspace_settings.calendars.holiday_calendars.dates.delete_error": "Couldn't delete the date.",
  "workspace_settings.calendars.holiday_calendars.dates.select_all": "Select all dates",
  "workspace_settings.calendars.holiday_calendars.dates.select_one": "Select {name}",
  "workspace_settings.calendars.holiday_calendars.dates.bulk_delete_confirm_title":
    "{count, plural, one {Delete # date?} other {Delete # dates?}}",
  "workspace_settings.calendars.holiday_calendars.dates.bulk_delete_confirm_description":
    "{count, plural, one {This will remove the date from this calendar. Covered people get the day back.} other {This will remove # dates from this calendar. Covered people get those days back.}}",
  "workspace_settings.calendars.holiday_calendars.dates.bulk_delete_success":
    "{count, plural, one {Deleted # date.} other {Deleted # dates.}}",
  "workspace_settings.calendars.holiday_calendars.dates.bulk_delete_error":
    "Couldn't delete the selected dates.",
  "workspace_settings.calendars.holiday_calendars.dates.empty_title": "No dates yet",
  "workspace_settings.calendars.holiday_calendars.dates.empty_description":
    "Add a custom date to start filling this calendar.",
  "workspace_settings.calendars.holiday_calendars.dates.import": "Import .ics",
  "workspace_settings.calendars.holiday_calendars.dates.import_title": "Import dates from a file",
  "workspace_settings.calendars.holiday_calendars.dates.import_description":
    "Upload an .ics calendar file. Its dates are added to this calendar as custom dates you can edit or delete.",
  "workspace_settings.calendars.holiday_calendars.dates.import_file_label": "Calendar file (.ics)",
  "workspace_settings.calendars.holiday_calendars.dates.import_drop_hint":
    "Drag an .ics file here, or choose one below",
  "workspace_settings.calendars.holiday_calendars.dates.import_wrong_type":
    "That isn't an .ics calendar file.",
  "workspace_settings.calendars.holiday_calendars.dates.import_submit": "Import dates",
  "workspace_settings.calendars.holiday_calendars.dates.import_submitting": "Importing…",
  "workspace_settings.calendars.holiday_calendars.dates.import_success":
    "{created} dates imported. Publish the calendar to apply them.",
  "workspace_settings.calendars.holiday_calendars.dates.import_success_with_skipped":
    "{created} dates imported, {skipped} already on this calendar. Publish the calendar to apply them.",
  "workspace_settings.calendars.holiday_calendars.dates.import_error": "Couldn't import that file.",
  "workspace_settings.calendars.holiday_calendars.dates.rename": "Rename",
  // The pencil beside a holiday name, and the message the cell shows in place if
  // the rename is refused. A synced date is renamable too — the feed owns the
  // day, you own the label — so this reads the same on every row.
  "workspace_settings.calendars.holiday_calendars.dates.name_edit_hint": "Rename this date",
  "workspace_settings.calendars.holiday_calendars.dates.name_required": "Give the date a name.",
  "workspace_settings.calendars.holiday_calendars.dates.rename_error": "Couldn't rename the date.",
  "workspace_settings.calendars.holiday_calendars.dates.row_actions": "Actions for {name}",
  "workspace_settings.calendars.holiday_calendars.dates.not_published": "Not published",
  "workspace_settings.calendars.holiday_calendars.dates.not_published_hint":
    "This date does not affect anyone's capacity until the calendar is published.",
  "workspace_settings.calendars.holiday_calendars.dates.excluded": "Excluded",
  "workspace_settings.calendars.holiday_calendars.dates.exclude": "Exclude",
  "workspace_settings.calendars.holiday_calendars.dates.include": "Include again",
  "workspace_settings.calendars.holiday_calendars.dates.exclude_error":
    "Couldn't exclude the date.",
  "workspace_settings.calendars.holiday_calendars.dates.include_error":
    "Couldn't include the date again.",

  "workspace_settings.calendars.holiday_calendars.feed.title": "Calendar feed",
  "workspace_settings.calendars.holiday_calendars.feed.description":
    "Subscribe to a published .ics calendar and its dates stay in step with it, refreshed daily. Synced dates take effect without publishing.",
  "workspace_settings.calendars.holiday_calendars.feed.empty":
    "No feed connected. Dates on this calendar are the ones you add or import.",
  "workspace_settings.calendars.holiday_calendars.feed.connect": "Connect a feed",
  "workspace_settings.calendars.holiday_calendars.feed.connect_title": "Connect a calendar feed",
  "workspace_settings.calendars.holiday_calendars.feed.connect_description":
    "Paste the address of a published .ics calendar. We read it now and once a day after that.",
  "workspace_settings.calendars.holiday_calendars.feed.url_label": "Feed address",
  "workspace_settings.calendars.holiday_calendars.feed.url_help":
    "An https:// or webcal:// link to an .ics file. One feed per calendar.",
  "workspace_settings.calendars.holiday_calendars.feed.connect_submit": "Connect",
  "workspace_settings.calendars.holiday_calendars.feed.connecting": "Connecting…",
  "workspace_settings.calendars.holiday_calendars.feed.connect_success":
    "Feed connected. {count} dates synced.",
  "workspace_settings.calendars.holiday_calendars.feed.connect_error":
    "Couldn't connect that feed.",
  "workspace_settings.calendars.holiday_calendars.feed.unnamed": "Calendar feed",
  "workspace_settings.calendars.holiday_calendars.feed.dates_count": "{count} dates in the feed",
  "workspace_settings.calendars.holiday_calendars.feed.sync": "Sync now",
  "workspace_settings.calendars.holiday_calendars.feed.syncing": "Syncing…",
  "workspace_settings.calendars.holiday_calendars.feed.sync_success":
    "Feed synced: {created} added, {updated} updated, {removed} removed.",
  "workspace_settings.calendars.holiday_calendars.feed.sync_error": "Couldn't sync that feed.",
  "workspace_settings.calendars.holiday_calendars.feed.disconnect": "Disconnect",
  "workspace_settings.calendars.holiday_calendars.feed.disconnect_confirm_title":
    "Disconnect this feed?",
  "workspace_settings.calendars.holiday_calendars.feed.disconnect_confirm_description":
    "The dates it brought in stay on this calendar as manual dates, and stop refreshing. You can delete them individually afterwards.",
  "workspace_settings.calendars.holiday_calendars.feed.disconnect_success":
    "Feed disconnected. {count} dates kept.",
  "workspace_settings.calendars.holiday_calendars.feed.disconnect_error":
    "Couldn't disconnect that feed.",

  "workspace_settings.time_off.title": "Time Off",
  "workspace_settings.time_off.select_all": "Select all time off",
  "workspace_settings.time_off.select_one": "Select time off for {name}",
  "workspace_settings.time_off.description":
    "Manage PTO entries that reduce member capacity in capacity views and scheduling.",
  "workspace_settings.time_off.add": "Add time off",
  "workspace_settings.time_off.entries_title": "Entries",
  "workspace_settings.time_off.entries_description":
    "Manual PTO entries apply to the selected member's available capacity.",
  "workspace_settings.time_off.filter_member": "Member",
  "workspace_settings.time_off.filter_all": "All members",
  "workspace_settings.time_off.empty_title": "No time off entries",
  "workspace_settings.time_off.empty_description":
    "Add a PTO entry to block capacity for a member.",
  "workspace_settings.time_off.add_title": "Add time off",
  "workspace_settings.time_off.edit_title": "Edit time off",
  "workspace_settings.time_off.dialog_description":
    "Full-day entries block the member's working capacity for each selected day.",
  "workspace_settings.time_off.member_label": "Member",
  "workspace_settings.time_off.reason_label": "Reason",
  "workspace_settings.time_off.reason_placeholder": "Vacation, appointment, leave",
  "workspace_settings.time_off.dates_edit_aria": "Edit dates",
  "workspace_settings.time_off.dates_save": "Save",
  "workspace_settings.time_off.dates_invalid_range": "End date must be on or after the start date.",
  "workspace_settings.time_off.start_label": "Start date",
  "workspace_settings.time_off.end_label": "End date",
  "workspace_settings.time_off.full_day_label": "Full day",
  "workspace_settings.time_off.hours_label": "Hours per day",
  "workspace_settings.time_off.full_day": "Full day",
  "workspace_settings.time_off.hours_per_day": "{hours}h/day",
  "workspace_settings.time_off.saving": "Saving...",
  "workspace_settings.time_off.create_success": "Time off added",
  "workspace_settings.time_off.create_error": "Failed to add time off",
  "workspace_settings.time_off.update_success": "Time off updated",
  "workspace_settings.time_off.update_error": "Failed to update time off",
  "workspace_settings.time_off.delete": "Delete",
  "workspace_settings.time_off.edit": "Edit",
  "workspace_settings.time_off.delete_confirm_title": "Delete time off?",
  "workspace_settings.time_off.delete_confirm_description":
    "This will remove the time off entry for {name} and restore that capacity.",
  "workspace_settings.time_off.delete_success": "Time off deleted",
  "workspace_settings.time_off.delete_error": "Failed to delete time off",
  "workspace_settings.time_off.validation_error": "Check the member, date range, and hours.",
  "workspace_settings.time_off.col_dates": "Dates",
  "workspace_settings.time_off.col_duration": "Duration",
  "workspace_settings.time_off.col_actions": "Actions",
  "workspace_settings.time_off.search_placeholder": "Search by member or reason…",

  "workspace_settings.sla.title": "Intake Service Level Agreements (SLAs)",
  "workspace_settings.sla.description":
    "Set decision-time targets for intake requests and track adherence.",
  "workspace_settings.sla.search_placeholder": "Search SLAs…",
  "workspace_settings.sla.create": "Create SLA",
  "workspace_settings.sla.col_name": "Name",
  "workspace_settings.sla.col_conditions": "Conditions",
  "workspace_settings.sla.col_rule": "Rule",
  "workspace_settings.sla.col_target": "Target",
  "workspace_settings.sla.col_adherence": "Adherence",
  "workspace_settings.sla.col_status": "Status",
  "workspace_settings.sla.col_actions": "Actions",
  "workspace_settings.sla.catch_all_pill": "All requests",
  "workspace_settings.sla.rule_label": "Time to decision",
  "workspace_settings.sla.target_days": "{count} days",
  "workspace_settings.sla.status_active": "Active",
  "workspace_settings.sla.status_inactive": "Inactive",
  "workspace_settings.sla.edit": "Edit",
  "workspace_settings.sla.delete": "Delete",
  "workspace_settings.sla.delete_confirm_title": "Delete SLA",
  "workspace_settings.sla.delete_confirm_description": 'Delete "{name}"? This cannot be undone.',
  "workspace_settings.sla.delete_success": "SLA deleted",
  "workspace_settings.sla.delete_error": "Failed to delete SLA",
  "workspace_settings.sla.empty_title": "No intake SLAs yet",
  "workspace_settings.sla.empty_description":
    "Create an SLA to set decision-time targets for intake requests and track adherence.",
  "workspace_settings.sla.details_title": "SLA details",
  "workspace_settings.sla.new_title": "New SLA",
  "workspace_settings.sla.breadcrumb_root": "Intake SLAs",
  "workspace_settings.sla.breadcrumb_details": "SLA details",
  "workspace_settings.sla.breadcrumb_new": "New SLA",
  "workspace_settings.sla.active_label": "Active",
  "workspace_settings.sla.delete_aria": "Delete SLA",
  "workspace_settings.sla.activate_error": "Failed to update SLA status",
  "workspace_settings.sla.history_aria": "View SLA history",
  "workspace_settings.sla.history_title": "Activity",
  "workspace_settings.sla.history_empty": "No activity yet.",
  "workspace_settings.sla.history_error": "Couldn’t load activity.",
  "workspace_settings.sla.name_label": "Name",
  "workspace_settings.sla.name_placeholder": "e.g. New Build SLA",
  "workspace_settings.sla.name_required": "Name is required.",
  "workspace_settings.sla.description_label": "Description",
  "workspace_settings.sla.description_placeholder": "What does this SLA measure?",
  "workspace_settings.sla.condition_heading": "Condition",
  "workspace_settings.sla.condition_all_label": "Apply to all requests",
  "workspace_settings.sla.condition_all_description":
    "This SLA is a catch-all and applies to all requests.",
  "workspace_settings.sla.condition_specific_label": "Apply to requests from specific workflows",
  "workspace_settings.sla.condition_specific_description":
    "Only requests submitted through the selected workflows will use this SLA.",
  "workspace_settings.sla.condition_group_label": "Condition",
  "workspace_settings.sla.workflows_label": "Workflows",
  "workspace_settings.sla.workflows_placeholder": "Select workflows",
  "workspace_settings.sla.workflows_search_placeholder": "Search workflows…",
  "workspace_settings.sla.workflows_empty": "No active workflows found.",
  "workspace_settings.sla.workflows_loading": "Loading workflows…",
  "workspace_settings.sla.workflows_required": "Select at least one workflow.",
  "workspace_settings.sla.workflows_hint":
    "Only active workflows can be added. A workflow already on this SLA stays on it after it is deactivated, and keeps its place until you remove it.",
  "workspace_settings.sla.workflow_deactivated": "{name} (deactivated)",
  "workspace_settings.sla.workflow_claimed":
    "Already covered by “{name}”. A workflow belongs to one SLA.",
  "workspace_settings.sla.workflow_not_selectable":
    "The workflow you came from isn’t active, so it can’t be added to an SLA. Activate it first, or choose another workflow.",
  "workspace_settings.sla.condition_team_scoped_hint":
    "This SLA applies to requests from the workflows you select below. Only workflows this team manages are available — an SLA that reaches further has to be created in workspace settings.",
  "workspace_settings.sla.expectation_heading": "Expectation",
  "workspace_settings.sla.rule_field_label": "Rule",
  "workspace_settings.sla.rule_locked_tooltip":
    "Round 1 supports a single rule: time to decision (closed date minus submitted date).",
  "workspace_settings.sla.rule_coming_soon": "More rules coming soon.",
  "workspace_settings.sla.pause_heading": "Pause the clock",
  "workspace_settings.sla.pause_section_description":
    "This is how you pause the clock so requests aren't penalized while waiting on a stakeholder or submitted on a weekend.",
  "workspace_settings.sla.pause_label": "Pause while status is",
  "workspace_settings.sla.pause_placeholder": "Select statuses",
  "workspace_settings.sla.pause_search_placeholder": "Search statuses…",
  "workspace_settings.sla.pause_empty": "No statuses found.",
  "workspace_settings.sla.weekends_label": "Weekends",
  "workspace_settings.sla.target_label": "Target",
  "workspace_settings.sla.target_unit_days": "days",
  "workspace_settings.sla.target_required": "Target must be at least 1 day.",
  "workspace_settings.sla.skip_weekends_label": "Skip weekends",
  "workspace_settings.sla.notifications_heading": "Notifications",
  "workspace_settings.sla.notifications_not_configurable": "Not yet configurable",
  "workspace_settings.sla.notifications_description":
    "This SLA automatically emails the request owner on this fixed schedule.",
  "workspace_settings.sla.notifications_3_days": "3 days before the breach date",
  "workspace_settings.sla.notifications_1_day": "1 day before the breach date",
  "workspace_settings.sla.notifications_on_breach": "On the breach date",
  "workspace_settings.sla.save": "Save",
  "workspace_settings.sla.saving": "Saving…",
  "workspace_settings.sla.create_success": "SLA created",
  "workspace_settings.sla.create_error": "Failed to create SLA",
  "workspace_settings.sla.update_success": "SLA updated",
  "workspace_settings.sla.update_error": "Failed to update SLA",
  "workspace_settings.sla.not_found": "SLA not found.",

  "workspace_settings.audit_log.title": "Audit Log",
  "workspace_settings.audit_log.description":
    "A record of changes made across this workspace — who did what, to which item, and when.",
  "workspace_settings.audit_log.column.when": "When",
  "workspace_settings.audit_log.select_all": "Select all events",
  "workspace_settings.audit_log.select_one": "Select {action}",
  "workspace_settings.audit_log.column.actor": "Actor",
  "workspace_settings.audit_log.column.action": "Action",
  "workspace_settings.audit_log.column.target": "Target",
  "workspace_settings.audit_log.column.source": "Source",
  "workspace_settings.audit_log.empty_title": "No activity yet",
  "workspace_settings.audit_log.empty_description":
    "Changes across the workspace will appear here as they happen.",
  "workspace_settings.audit_log.export": "Export CSV",
  "workspace_settings.audit_log.export_error": "Could not export the audit log. Please try again.",
  "workspace_settings.audit_log.detail.title": "Event details",
  "workspace_settings.audit_log.detail.error": "Could not load this event.",
  "workspace_settings.audit_log.detail.changes": "Changes",
  "workspace_settings.audit_log.detail.no_changes": "This event recorded no field changes.",
  "workspace_settings.audit_log.detail.no_changes_created":
    "This item was created — there are no prior values to compare.",
  "workspace_settings.audit_log.detail.no_changes_deleted": "This item was deleted.",
  "workspace_settings.audit_log.detail.no_changes_archived": "This item was archived.",
  "workspace_settings.skills.title": "Qualifications",
  "workspace_settings.skills.description":
    "Define the qualifications available in this workspace so members can tag themselves and be staffed on work that matches their strengths.",
  "workspace_settings.skills.add_action": "Add qualification",
  "workspace_settings.skills.import.action": "Import",
  "workspace_settings.skills.import.title": "Import qualifications",
  "workspace_settings.skills.import.field_name": "Qualification name",
  "workspace_settings.skills.import.field_name_hint":
    "The qualification's name. An existing qualification with the same name is reused, not duplicated.",
  "workspace_settings.skills.import.error_name_required": "Qualification name is required.",
  "workspace_settings.skills.import.field_assignees": "Assignees",
  "workspace_settings.skills.import.field_assignees_hint":
    "Emails of workspace members to assign this qualification to, separated by commas or semicolons. Optional.",
  "workspace_settings.skills.import.warn_assignee_not_member":
    "{email} is not an active member of this workspace and will be skipped.",
  "workspace_settings.skills.import.warn_duplicate_names":
    "{count, plural, one {# qualification name appears} other {# qualification names appear}} more than once in this file; duplicates reuse the same qualification.",
  "workspace_settings.skills.import.importing": "Importing…",
  "workspace_settings.skills.import.result_summary":
    "{created} created, {reused} reused, {assigned} assigned.",
  "workspace_settings.skills.import.count_created": "{count} created",
  "workspace_settings.skills.import.count_reused": "{count} reused",
  "workspace_settings.skills.import.count_errors":
    "{count, plural, one {# error} other {# errors}}",
  "workspace_settings.skills.import.count_skipped":
    "{count, plural, one {# assignee skipped} other {# assignees skipped}}",
  "workspace_settings.skills.import.error_row": "Row {row} ({name}): {message}",
  "workspace_settings.skills.import.unnamed_row": "no name",
  "workspace_settings.skills.import.skipped_heading": "Assignees skipped (not workspace members)",
  "workspace_settings.skills.import.skipped_row": "Row {row}: {email}",
  "workspace_settings.skills.import.toast_success":
    "{count, plural, one {# qualification imported} other {# qualifications imported}}",
  "workspace_settings.skills.import.toast_error": "Import failed. Please try again.",
  // Recommended content at intake (use case #18, NG-676)
  "intake.recommended_content.title": "Existing content",
  "intake.recommended_content.expand": "Expand existing content",
  "intake.recommended_content.collapse": "Collapse existing content",
  "intake.recommended_content.may_cover_one": "1 may cover this",
  "intake.recommended_content.may_cover_many": "{count} may cover this",
  "intake.recommended_content.subtitle":
    "From your {vendor} catalog. Kept content travels to the project.",
  "intake.recommended_content.subtitle_any_lms":
    "From your LMS catalog. Kept content travels to the project.",
  "intake.recommended_content.kept_courses_one": "1 kept course",
  "intake.recommended_content.kept_courses_many": "{count} kept courses",
  "intake.recommended_content.kept_paths_one": "1 kept learning path",
  "intake.recommended_content.kept_paths_many": "{count} kept learning paths",
  "intake.recommended_content.kept_both": "{courses} and {paths}",
  "intake.recommended_content.kept_note_one": "{items} will be attached to the project's Delivery",
  "intake.recommended_content.kept_note_many": "{items} will be attached to the project's Delivery",
  "intake.recommended_content.empty_title": "No matching content",
  "intake.recommended_content.empty_description":
    "Nothing in the connected catalog shares a word with this request's title.",
  "intake.recommended_content.load_failed": "Suggestions could not be loaded.",
  "intake.recommended_content.accepted": "Kept",
  "intake.recommended_content.accept": "Keep",
  "intake.recommended_content.accept_label": "Keep {name} for this request",
  "intake.recommended_content.dismiss_label": "Dismiss {name}",
  "intake.recommended_content.requester_notice_title_course":
    "{count, plural, one {An existing course may already cover this request} other {# existing courses may already cover this request}}",
  "intake.recommended_content.requester_notice_title_path":
    "{count, plural, one {An existing learning path may already cover this request} other {# existing learning paths may already cover this request}}",
  "intake.recommended_content.requester_notice_title_mixed":
    "{count, plural, one {An existing item of content may already cover this request} other {# existing items of content may already cover this request}}",
  "intake.recommended_content.requester_notice_description":
    "{names} from your {vendor} catalog. {count, plural, one {If it does the job} other {If one of them does the job}}, you may not need to wait for this request.",
  "intake.recommended_content.requester_notice_description_any_lms":
    "{names} from your LMS catalog. {count, plural, one {If it does the job} other {If one of them does the job}}, you may not need to wait for this request.",
  "intake.recommended_content.requester_notice_more": "+{count} more",
  "intake.recommended_content.requester_notice_preview": "Preview",
  "intake.recommended_content.review_all": "Review all",
  "intake.recommended_content.review_all_title": "Existing content that may cover this request",
  "intake.recommended_content.review_all_description":
    "Keep what fits so it travels to the project, dismiss what does not, or resolve the request if the catalog already answers it.",
  "intake.recommended_content.review_all_tally": "{kept} kept · {open} open",
  "intake.recommended_content.shares": "Shares",
  "intake.recommended_content.preview": "Preview",
  "intake.recommended_content.resolve_covered": "Resolve as covered",
  "intake.recommended_content.resolve_covered_existing": "Resolve as covered by existing content",
  "intake.recommended_content.resolve_covered_hint":
    "Closes this request as {status}. No project is created, and you can move it back.",
  "intake.recommended_content.resolve_covered_success": "Request resolved as {status}",
  "intake.recommended_content.resolve_covered_error":
    "Couldn't resolve the request. Nothing was changed.",

  "intake.import.action": "Import requests",
  "intake.import.title": "Import requests",
  "intake.import.pick_form_hint":
    "Choose the form to import requests into. Its questions become the importable columns.",
  "intake.import.target_form": "Target form",
  "intake.import.target_form_placeholder": "Select a form",
  "intake.import.no_active_forms_title": "No forms to import into",
  "intake.import.no_active_forms":
    "There are no active forms to import into. Publish a form first.",
  "intake.import.field_request_name": "Request name",
  "intake.import.example_request_name": "New laptop for a new hire",
  "intake.import.field_request_name_hint": "The title of each request.",
  "intake.import.field_requester": "Requester",
  "intake.import.field_requester_hint":
    "Email of the person who requested it. Defaults to you when blank.",
  "intake.import.field_owners": "Owners",
  "intake.import.field_owners_hint": "Owner emails, separated by commas or semicolons.",
  "intake.import.field_status": "Status",
  "intake.import.field_status_hint":
    "Request status by name. Defaults to the workspace default when blank.",
  "intake.import.error_required": "{field} is required.",
  "intake.import.warn_unknown_option": "{field}: “{value}” is not a known option.",
  "intake.import.warn_ambiguous_option":
    "{field}: “{value}” matches more than one option; map it to the one you mean.",
  "intake.import.conditional_hint": "Conditional — only applies to rows that meet its condition.",
  "intake.import.warn_dropped_conditional":
    "{field} won’t be imported for this row — it doesn’t apply to the answers given.",
  "intake.import.importing": "Importing…",
  "intake.import.result_summary":
    "{created, plural, one {# request created} other {# requests created}} of {total}.",
  "intake.import.count_created": "{count} created",
  "intake.import.count_errors": "{count, plural, one {# error} other {# errors}}",
  "intake.import.download_failed": "Download failed rows",
  "intake.import.failed_reason_header": "Import error",
  "intake.import.toast_success":
    "{count, plural, one {# request imported} other {# requests imported}}",
  "intake.import.toast_error": "Import failed. Please try again.",
  "intake.import.toast_forms_error": "Could not load forms. Please try again.",
  "intake.import.toast_definition_error": "Could not load that form's fields. Please try again.",
  "intake.import.loading_form": "Loading form fields…",
  "workspace_settings.skills.select_all": "Select all qualifications",
  "workspace_settings.skills.select_one": "Select {name}",
  "workspace_settings.skills.search_placeholder": "Search qualifications",
  "workspace_settings.skills.empty_title": "No qualifications yet",
  "workspace_settings.skills.empty_description":
    "Create the first workspace qualification to let members self-identify their expertise.",
  "workspace_settings.skills.list_empty_search": "No qualifications match your search.",
  "workspace_settings.skills.rename": "Rename",
  "workspace_settings.skills.rename_save": "Save",
  "workspace_settings.skills.rename_cancel": "Cancel",
  "workspace_settings.skills.delete": "Delete",
  "workspace_settings.skills.delete_confirm_title": "Delete qualification",
  "workspace_settings.skills.delete_confirm_description":
    "Delete {name}? Members currently tagged with this qualification will lose it.",
  "workspace_settings.skills.delete_success": "Qualification deleted",
  "workspace_settings.skills.delete_error": "Failed to delete qualification",
  "workspace_settings.skills.create_success": "Qualification created",
  "workspace_settings.skills.create_error": "Failed to create qualification",
  "workspace_settings.skills.update_success": "Qualification updated",
  "workspace_settings.skills.update_error": "Failed to rename qualification",
  "workspace_settings.skills.load_error": "Failed to load qualifications",
  "workspace_settings.skills.modal_title": "Add qualification",
  "workspace_settings.skills.modal_description":
    "Workspace admins manage this list. Members pick from it on their profile.",
  "workspace_settings.skills.modal_name_label": "Qualification name",
  "workspace_settings.skills.name_edit_hint": "Click to edit",
  "workspace_settings.skills.modal_name_placeholder": "e.g. Instructional design",
  "workspace_settings.skills.modal_submit": "Add qualification",
  "workspace_settings.skills.modal_submitting": "Saving...",
  "workspace_settings.skills.archive": "Deactivate",
  "workspace_settings.skills.unarchive": "Activate",
  "workspace_settings.skills.archive_success": "Qualification deactivated",
  "workspace_settings.skills.archive_error": "Failed to deactivate qualification",
  "workspace_settings.skills.unarchive_success": "Qualification activated",
  "workspace_settings.skills.unarchive_error": "Failed to activate qualification",
  "workspace_settings.skills.deactivate_success": "Qualification deactivated",
  "workspace_settings.skills.activate_success": "Qualification activated",
  "workspace_settings.skills.active_toggle_aria": "Toggle active state for {name}",
  "workspace_settings.skills.actions_menu_aria": "Actions for {name}",
  "workspace_settings.skills.column.name": "Qualification",
  "workspace_settings.skills.column.users": "Members",
  "workspace_settings.skills.column.status": "Status",
  "workspace_settings.skills.column.actions": "Actions",
  "workspace_settings.skills.status.active": "Active",
  "workspace_settings.skills.status.deactivated": "Deactivated",
  "workspace_settings.skills.filter.active": "Active",
  "workspace_settings.skills.filter.deactivated": "Deactivated",
  "workspace_settings.skills.users_empty": "No members",
  "workspace_settings.skills.users_count": "{count, plural, one {# member} other {# members}}",
  "workspace_settings.skills.users_search_placeholder": "Search members",
  "workspace_settings.skills.users_loading": "Loading members…",
  "workspace_settings.skills.users_list_empty": "No members found",
  "workspace_settings.skills.assign_users_title": "Assign members to {name}",
  "workspace_settings.skills.assign_users_success": "Qualification members updated",
  "workspace_settings.skills.assign_users_error": "Failed to update qualification members",
  "workspace_settings.skills.range_summary": "Showing {from}–{to} of {total}",
  "workspace_settings.skills.pagination.prev": "Previous page",
  "workspace_settings.skills.pagination.next": "Next page",

  // Profile
  "profile.skills.title": "My qualifications",
  "profile.skills.description":
    "Pick the qualifications you bring to this workspace. Admins can see your selection on the team availability page.",
  "profile.skills.no_workspace": "Select a workspace to manage your qualifications.",
  "profile.skills.empty": "No qualifications have been defined in this workspace yet.",
  "profile.skills.placeholder": "Select qualifications...",
  "profile.skills.search_placeholder": "Search qualifications...",
  "profile.skills.empty_search": "No qualifications found",
  "profile.skills.save_success": "Qualifications updated",
  "profile.skills.save_error": "Failed to save qualifications",
  "profile.skills.load_error": "Failed to load your qualifications",
  "profile.skills.permission_denied":
    "You do not have permission to update your qualifications in this workspace.",

  // Profile settings — shared
  "profile_settings.tabs.general": "Profile",
  "profile_settings.tabs.preferences": "Preferences",
  "profile_settings.tabs.notifications": "Notifications",
  "profile_settings.tabs.security": "Security",
  "profile_settings.tabs.work_hours": "Work hours",
  "profile_settings.tabs.skills": "Qualifications",
  "profile_settings.tabs.connected_apps": "Connected apps",

  // Login footer, connected apps and instance OAuth apps (NG-2585)
  "login_footer.contact_support": "Contact support",
  "login_footer.privacy": "Privacy",
  "login_footer.terms": "Terms",
  "profile_settings.connected_apps.loading": "Loading…",
  "profile_settings.connected_apps.empty":
    "You haven't connected any apps yet. When you sign in to a 3rd-party app using your Cognota account, it will appear here.",
  "profile_settings.connected_apps.intro":
    "These apps can act on your behalf in the workspaces below. Revoke any you no longer trust.",
  "profile_settings.connected_apps.expired_title":
    "This connection can't refresh anymore. Reconnect from the app to keep using it.",
  "profile_settings.connected_apps.expired": "Expired",
  "profile_settings.connected_apps.granted": "Granted",
  "profile_settings.connected_apps.last_used": "last used",
  "profile_settings.connected_apps.revoke": "Revoke access",
  "profile_settings.connected_apps.load_error": "Failed to load your apps.",
  "profile_settings.connected_apps.revoke_error": "Failed to revoke access.",
  "instance_admin.oauth_apps.loading": "Loading…",
  "instance_admin.oauth_apps.title": "Connected apps",
  "instance_admin.oauth_apps.forbidden":
    "You don't have permission to manage instance-wide connected apps.",
  "instance_admin.oauth_apps.page_title": "Connected apps (instance)",
  "instance_admin.oauth_apps.page_description":
    "Cognota-verified apps available to every workspace on this instance (the Cognota MCP server, partner integrations, etc.).",
  "instance_admin.oauth_apps.new_app": "New app",
  "instance_admin.oauth_apps.register_title": "Register instance OAuth app",
  "instance_admin.oauth_apps.delete_confirm":
    "Delete {name}? Existing tokens will fail introspection.",
  "instance_admin.oauth_apps.load_error": "Failed to load apps.",

  "profile_settings.title": "Profile settings",

  // Profile settings — general
  "profile_settings.general.title": "General",
  "profile_settings.general.description": "Update your name, timezone, and profile images.",
  "profile_settings.general.first_name_label": "First name",
  "profile_settings.general.last_name_label": "Last name",
  "profile_settings.general.display_name_label": "Display name",
  "profile_settings.general.display_name_hint":
    "This is the name that will appear to others across the app.",
  "profile_settings.general.mobile_label": "Mobile number",
  "profile_settings.general.mobile_placeholder": "+1 555 555 5555",
  "profile_settings.general.email_label": "Email",
  "profile_settings.general.email_change": "Change email",
  "profile_settings.general.save": "Save changes",
  "profile_settings.general.saving": "Saving...",
  "profile_settings.general.save_success": "Profile updated",
  "profile_settings.general.save_error": "Could not update profile",
  "profile_settings.general.avatar_label": "Profile picture",
  "profile_settings.general.avatar_hint": "A photo helps teammates recognize you.",
  "profile_settings.general.avatar_alt": "Avatar for {name}",
  "profile_settings.general.avatar_upload": "Upload",
  "profile_settings.general.avatar_change": "Change",
  "profile_settings.general.avatar_remove": "Remove",
  "profile_settings.general.avatar_uploading": "Uploading...",
  "profile_settings.general.avatar_removing": "Removing...",
  "profile_settings.general.avatar_upload_success": "Avatar updated",
  "profile_settings.general.avatar_upload_error": "Could not upload avatar",
  "profile_settings.general.avatar_remove_success": "Avatar removed",
  "profile_settings.general.avatar_remove_error": "Could not remove avatar",
  "profile_settings.general.avatar_invalid_type": "Only JPEG, PNG, WEBP, or GIF images are allowed",
  "profile_settings.general.avatar_too_large": "Image must be 5 MB or smaller",
  "profile_settings.general.danger_zone": "Danger zone",
  "profile_settings.general.danger_zone_description":
    "Permanently deactivate your account. This cannot be undone.",
  "profile_settings.general.deactivate": "Deactivate account",

  // Email change modal
  "profile_settings.email_change.title": "Change email",
  "profile_settings.email_change.step1_title": "Enter your new email address",
  "profile_settings.email_change.step1_description":
    "We'll send a verification code to confirm it's really you.",
  "profile_settings.email_change.step2_title": "Enter the verification code",
  "profile_settings.email_change.step2_description":
    "We sent a 6-digit code to {email}. Paste it below to confirm the change.",
  "profile_settings.email_change.new_email_label": "New email",
  "profile_settings.email_change.code_label": "Verification code",
  "profile_settings.email_change.send_code": "Send code",
  "profile_settings.email_change.sending": "Sending...",
  "profile_settings.email_change.verify": "Change email",
  "profile_settings.email_change.verifying": "Verifying...",
  "profile_settings.email_change.code_sent": "Code sent — check your inbox",
  "profile_settings.email_change.success": "Email updated",
  "profile_settings.email_change.send_error": "Could not send code",
  "profile_settings.email_change.verify_error": "Invalid or expired code",
  "profile_settings.email_change.invalid_email": "Please enter a valid email address",

  // Deactivate modal
  "profile_settings.deactivate.title": "Deactivate your account",
  "profile_settings.deactivate.description":
    "This removes your access to this workspace. You can sign up again with the same email, but your profile data cannot be restored.",
  "profile_settings.deactivate.confirm_label": "Type your username to confirm",
  "profile_settings.deactivate.confirm_placeholder": "{username}",
  "profile_settings.deactivate.confirm": "Deactivate account",
  "profile_settings.deactivate.confirming": "Deactivating...",
  "profile_settings.deactivate.error": "Could not deactivate account",

  // Profile settings — preferences
  "profile_settings.preferences.title": "Preferences",
  "profile_settings.preferences.description": "Choose your theme and language.",
  "profile_settings.preferences.description_job_role": "Choose your theme, language, and job role.",
  "profile_settings.preferences.description_start_of_week":
    "Choose your theme, language, and start of week.",
  "profile_settings.preferences.description_start_of_week_job_role":
    "Choose your theme, language, start of week, and job role.",
  "profile_settings.preferences.theme_label": "Theme",
  "profile_settings.preferences.theme_hint": "System follows your device setting.",
  "profile_settings.preferences.theme_light": "Light",
  "profile_settings.preferences.theme_dark": "Dark",
  "profile_settings.preferences.theme_system": "System",
  "profile_settings.preferences.language_label": "Language",
  "profile_settings.preferences.start_of_week_label": "Start of week",
  "profile_settings.preferences.start_of_week_sunday": "Sunday",
  "profile_settings.preferences.start_of_week_monday": "Monday",
  "profile_settings.preferences.job_role_label": "Job role",
  "profile_settings.preferences.job_role_none": "None",
  "profile_settings.preferences.save_success": "Preferences updated",
  "profile_settings.preferences.save_error": "Could not save preferences",

  // Profile settings — notifications
  "profile_settings.notifications.title": "Notifications",
  "profile_settings.notifications.description":
    "Choose which events trigger an in-app notification.",
  "profile_settings.notifications.property_change": "Property changes",
  "profile_settings.notifications.property_change_hint":
    "When someone updates a property on an issue you're watching.",
  "profile_settings.notifications.status_change": "Status changes",
  "profile_settings.notifications.status_change_hint":
    "When an issue you're watching moves to a new status.",
  "profile_settings.notifications.comment": "Comments",
  "profile_settings.notifications.comment_hint":
    "When someone adds a comment on an issue you're watching.",
  "profile_settings.notifications.mention": "Mentions",
  "profile_settings.notifications.mention_hint":
    "When someone @-mentions you anywhere in the workspace.",
  "profile_settings.notifications.field_change": "Field changes",
  "profile_settings.notifications.field_change_hint":
    "When fields on a request, project, or work item you're involved in change.",
  "profile_settings.notifications.deletion": "Deletions",
  "profile_settings.notifications.deletion_hint":
    "When a request, project, or work item you're involved in is deleted.",
  "profile_settings.notifications.assignment": "Assignments",
  "profile_settings.notifications.assignment_hint":
    "When a new request or work item is created and assigned to you.",
  "profile_settings.notifications.collaboration": "Collaboration",
  "profile_settings.notifications.collaboration_hint":
    "Team assignments and approvals that need your attention.",
  "profile_settings.notifications.account": "Account & team",
  "profile_settings.notifications.account_hint":
    "When your role changes, or you're added to a workspace or team.",
  "profile_settings.notifications.sla": "SLA alerts",
  "profile_settings.notifications.sla_hint":
    "When an SLA on a request you own is approaching its deadline or has been breached.",
  "profile_settings.notifications.time_off_conflict": "Time off conflicts",
  "profile_settings.notifications.time_off_conflict_hint":
    "When new time off puts work already scheduled in a project you lead into conflict.",
  "profile_settings.notifications.shared_dashboard": "Shared dashboards",
  "profile_settings.notifications.shared_dashboard_hint":
    "When someone shares a dashboard with you, or changes your access to one.",
  // Teams-only (NG-1641): delivered as a Teams card and nothing else, so its row has
  // no bell switch and is absent for anybody without a Teams link.
  "profile_settings.notifications.weekly_summary": "Monday summary",
  "profile_settings.notifications.weekly_summary_hint":
    "A Monday-morning Teams message listing your overdue and upcoming work.",
  "profile_settings.notifications.section_updates": "Updates",
  "profile_settings.notifications.section_collaboration": "Collaboration",
  "profile_settings.notifications.section_proactive": "Proactive",
  "profile_settings.notifications.section_account": "Account",
  "profile_settings.notifications.section_other": "Other",
  // Named for the channel rather than "Other", which means "a code this form has no
  // copy for" — the opposite of what a Teams-only category is.
  "profile_settings.notifications.section_teams_only": "Microsoft Teams only",
  "profile_settings.notifications.load_error": "Could not load notification preferences",
  "profile_settings.notifications.save_error": "Could not save notification preferences",

  // The two switch columns (NG-1187). Shown only to somebody whose own Microsoft
  // account is linked — for everybody else there is one switch per row and naming
  // the column it is in would be answering a question nobody asked.
  "profile_settings.notifications.column_inapp": "In-app & email",
  "profile_settings.notifications.column_teams": "Teams",
  // The Teams switch's accessible name. It must not merely contain the category
  // name, or every `getByRole("switch", { name: /comments/i })` matches two
  // elements — a screen-reader ambiguity as much as a test one (NG-847).
  "profile_settings.notifications.teams_switch_aria": "{label} in Teams",

  // "Send my Monday summary now" (NG-1641) — a demo affordance Product asked for,
  // on the Teams-only row alone. The label is the button's accessible name too, so
  // it has to be a whole sentence rather than "Send now": it sits a few pixels from
  // a switch called "Monday summary in Teams", and two controls one word apart is a
  // screen-reader ambiguity even where no locator trips over it.
  "profile_settings.notifications.send_summary_now": "Send my Monday summary now",
  // The counts are the point: "Sent" on its own leaves the reader unable to tell a
  // card worth opening from an empty one, and they are the only way to check the
  // card matches the app without leaving the page.
  "profile_settings.notifications.send_summary_sent":
    "Sent to Teams — {overdue} overdue, {upcoming} due this week",
  // A 200, not a failure: nothing was due, so there was nothing to send. Said as
  // information rather than an error, because the person did nothing wrong and
  // there is nothing here to fix.
  "profile_settings.notifications.send_summary_nothing_due": "Nothing due — no summary to send",
  // The fallback for a reason we cannot name — a network failure, a 500, or a code
  // this build has never heard of. Never used where the server named one.
  "profile_settings.notifications.send_summary_error":
    "Could not send your Monday summary. Try again.",
  // One sentence per refusal code, each naming the thing to go and do. They differ
  // because the recourse differs: an admin pause is nobody else's to fix, a missing
  // install is the person's own, and an already-sent card is not a failure at all.
  "profile_settings.notifications.send_summary_refused.no_workspace":
    "Open a workspace first — a summary is about one workspace's work.",
  "profile_settings.notifications.send_summary_refused.not_member":
    "You are no longer a member of this workspace.",
  "profile_settings.notifications.send_summary_refused.no_connection":
    "Microsoft Teams is not set up in this workspace.",
  "profile_settings.notifications.send_summary_refused.paused":
    "An admin has paused Teams notifications in this workspace.",
  "profile_settings.notifications.send_summary_refused.no_link":
    "Connect your Microsoft account first.",
  "profile_settings.notifications.send_summary_refused.not_installed":
    "Add the Cognota app in Microsoft Teams first.",
  // `not_receiving` is the digest module saying it cannot explain its own refusal —
  // `receiving_links_q` grew a term the reason-walk was not updated for. Nothing the
  // reader can do fixes that, so the copy points at the one route that can: support.
  "profile_settings.notifications.send_summary_refused.not_receiving":
    "Something is off with your Teams link — please contact support.",
  // The COGNOTA account, not the Teams link: the code is set when `is_active` is
  // false. Re-connecting Microsoft changes nothing, so the old copy sent the reader to
  // do work that could not help.
  "profile_settings.notifications.send_summary_refused.deactivated":
    "Your Cognota account is deactivated. Ask an admin to reactivate it.",
  "profile_settings.notifications.send_summary_refused.switch_off":
    "Turn the Monday summary switch on first.",
  // A raced key, not "this week": the endpoint walks to the next free day, so the key
  // it lost is very often not this week's at all. It is also worth retrying, because
  // the next press picks a different day.
  "profile_settings.notifications.send_summary_refused.already_sent":
    "This one was already sent — try again.",
  "profile_settings.notifications.send_summary_refused.too_many_manual_sends":
    "No free day is left to send a summary for. Try again in a few days.",

  // Microsoft Teams, as a delivery channel for YOUR OWN notifications (plan §5a
  // FR5-FR7, NG-1134, epic NG-886). Sits with the notification preferences rather
  // than under Connected apps because it decides WHERE your notifications go, not
  // what a third party may do on your behalf.
  // PLACEHOLDER COPY: product's OQ2 (nudging members who have not linked) and OQ6
  // (whether guests see this at all) are open, so every string below is written to
  // be clear and correct rather than final -- expect one revision pass.
  "profile_settings.msteams.title": "Microsoft Teams",
  "profile_settings.msteams.description":
    "Get the notifications you have turned on below as Teams messages too, as well as by email and in Cognota.",
  "profile_settings.msteams.state_receiving": "Receiving",
  // Distinct from `state_receiving` and from `state_not_connected`: the Microsoft
  // account is linked and the Teams app is not installed, so nothing arrives yet.
  // Calling that "Not connected" contradicted the row's own reason line.
  "profile_settings.msteams.state_connected": "Connected",
  "profile_settings.msteams.state_not_connected": "Not connected",
  // A fourth state, and not one of theirs (NG-1622): the payload lists only workspaces
  // that HAVE a Teams connection, so no row for the current one means its admin never
  // turned the channel on. "Not connected" would blame the person for that and send
  // them to a Connect button that changes nothing here.
  "profile_settings.msteams.state_not_set_up": "Not set up",
  "profile_settings.msteams.workspace_not_set_up":
    "Teams notifications are not set up in this workspace, so nothing is sent from here.",
  "profile_settings.msteams.workspace_paused":
    "Your workspace has paused Teams notifications, so nothing is sent from here for now.",
  "profile_settings.msteams.reason_none":
    "Connect your Microsoft account to get these notifications in Teams.",
  "profile_settings.msteams.reason_not_installed":
    "Your Microsoft account is connected. Add the Cognota app in Teams to start getting messages.",
  "profile_settings.msteams.reason_unmatched":
    "Someone has added the Cognota app in Teams, but we cannot tell it is you. Connect your Microsoft account to claim it.",
  "profile_settings.msteams.reason_unlinked":
    "You turned Teams notifications off. Connect again whenever you want them back.",
  "profile_settings.msteams.reason_uninstalled":
    "The Cognota app was removed from your Teams. Add it again to start getting messages.",
  "profile_settings.msteams.reason_blocked":
    "Microsoft Teams is refusing messages to your chat. Removing the Cognota app and adding it again usually fixes this.",
  // Counts the OTHER workspaces the person holds a matched link on -- "connected" here
  // has to mean what the Connected badge means, or the block says two things with one
  // word. Never rendered at zero, so there is no `=0` branch to keep honest.
  "profile_settings.msteams.other_workspaces":
    "{count, plural, one {Also connected in # other workspace} other {Also connected in # other workspaces}}",
  "profile_settings.msteams.connect_action": "Connect Microsoft account",
  // Said beside Connect because the block now shows ONE workspace, and one row above
  // one button reads as that row's button. The sign-in is the person's, not the
  // workspace's (FR5-FR7); Turn off names the same scope in its own confirm.
  "profile_settings.msteams.connect_scope_hint": "One sign-in covers every workspace you're in.",
  "profile_settings.msteams.connect_pending": "Opening Microsoft sign-in...",
  "profile_settings.msteams.connect_error":
    "Could not start the Microsoft sign-in. Nothing was changed.",
  "profile_settings.msteams.connect_not_configured":
    "Microsoft Teams sign-in is not configured for this environment yet. Nothing was changed \u2014 please contact support.",
  // Steps rather than a deep link (NG-1186): `teams.microsoft.com/l/app/<id>` resolves
  // only for a sideloaded app, and a customer's catalog id is not something we can read.
  // "Built for your org" is what Teams calls the tab holding an org-uploaded app.
  "profile_settings.msteams.install_steps":
    "To add it: in Teams, open Apps > Built for your org > Cognota, then choose Add.",
  "profile_settings.msteams.disconnect_action": "Turn off Teams notifications",
  "profile_settings.msteams.disconnect_pending": "Turning off...",
  "profile_settings.msteams.disconnect_confirm_title": "Turn off Teams notifications?",
  "profile_settings.msteams.disconnect_confirm_description":
    "Cognota stops sending you Teams messages in every workspace, and anything queued for you is dropped. Your email and in-app notifications are unaffected, and you can connect again at any time.",
  "profile_settings.msteams.disconnect_success":
    "Teams notifications are off. Email and in-app notifications are unaffected.",
  "profile_settings.msteams.disconnect_error":
    "Could not turn off Teams notifications. Nothing was changed.",
  "profile_settings.msteams.redirect_connected":
    "Microsoft account connected. Add the Cognota app in Teams if you have not already.",
  "profile_settings.msteams.redirect_error_invalid_state":
    "That Microsoft sign-in link is no longer valid. Nothing was changed \u2014 please try again.",
  "profile_settings.msteams.redirect_error_denied":
    "Microsoft sign-in was cancelled. Nothing was changed.",
  "profile_settings.msteams.redirect_error_no_code":
    "Microsoft did not complete the sign-in. Nothing was changed \u2014 please try again.",
  "profile_settings.msteams.redirect_error_forbidden":
    "You no longer have access to that workspace. Nothing was changed.",
  "profile_settings.msteams.redirect_error_exchange_failed":
    "Cognota could not finish the sign-in with Microsoft. Nothing was changed \u2014 please try again.",
  "profile_settings.msteams.redirect_error_already_enabled":
    "That Microsoft account is already connected.",
  "profile_settings.msteams.redirect_error_not_yet":
    "That Microsoft sign-in is not available yet. Nothing was changed.",
  "profile_settings.msteams.redirect_error_no_enabled_workspace":
    "That Microsoft account is not connected to any of your Cognota workspaces. Sign in with the Microsoft account your workspace uses, and nothing else needs to change.",
  "profile_settings.msteams.redirect_error_unknown":
    "Connecting your Microsoft account failed. Nothing was changed.",

  // The unlinked-member nudge (OQ2, NG-1134). Neither a workspace-settings nor a
  // profile-settings string: it renders in the in-app notifications bell, which is
  // where somebody who has never heard of the Teams channel is looking.
  "msteams.nudge.body_one":
    "Microsoft Teams notifications are on for {workspace}. Connect your Microsoft account to get them there.",
  "msteams.nudge.body_many":
    "Microsoft Teams notifications are on for {workspace} and {count, plural, one {# other workspace} other {# other workspaces}}. Connect your Microsoft account to get them there.",
  "msteams.nudge.action": "Connect",
  "msteams.nudge.pending": "Opening Microsoft sign-in...",
  "msteams.nudge.dismiss": "Dismiss the Microsoft Teams reminder",

  // Profile settings — security
  "profile_settings.security.title": "Security",
  "profile_settings.security.description": "Change your account password.",
  "profile_settings.security.current_password_label": "Current password",
  "profile_settings.security.new_password_label": "New password",
  "profile_settings.security.confirm_password_label": "Confirm new password",
  "profile_settings.security.save": "Update password",
  "profile_settings.security.saving": "Updating...",
  "profile_settings.security.save_success": "Password updated",
  "profile_settings.security.save_error": "Could not update password",
  "profile_settings.security.autoset_hint":
    "Your account uses magic links — set a password below to also sign in with one.",
  "profile_settings.security.current_required": "Enter your current password",
  "profile_settings.security.weak_password":
    "Pick a stronger password (mix of upper, lower, digits, and symbols).",
  "profile_settings.security.strength_label": "Password strength",
  "profile_settings.security.strength_0": "Too short",
  "profile_settings.security.strength_1": "Weak",
  "profile_settings.security.strength_2": "Fair",
  "profile_settings.security.strength_3": "Good",
  "profile_settings.security.strength_4": "Strong",

  // Profile settings — Work hours
  "profile_settings.work_hours.title": "Work Hours",
  "profile_settings.work_hours.description":
    "Configure your working hours and calendar to help the scheduler plan your availability.",
  "profile_settings.work_hours.capacity_section_label": "Capacity",
  "profile_settings.work_hours.capacity_label": "Overall capacity",
  "profile_settings.work_hours.daily_hours_label": "Daily Hours",
  "profile_settings.work_hours.work_schedule_label": "Work Schedule",
  "profile_settings.work_hours.day_monday": "Monday",
  "profile_settings.work_hours.day_tuesday": "Tuesday",
  "profile_settings.work_hours.day_wednesday": "Wednesday",
  "profile_settings.work_hours.day_thursday": "Thursday",
  "profile_settings.work_hours.day_friday": "Friday",
  "profile_settings.work_hours.day_saturday": "Saturday",
  "profile_settings.work_hours.day_sunday": "Sunday",
  "profile_settings.work_hours.start_time_label": "Start time",
  "profile_settings.work_hours.end_time_label": "End time",
  "profile_settings.work_hours.timezone_label": "Timezone",
  "profile_settings.work_hours.invalid_hours": "Enter a value between 0 and 24",
  "profile_settings.work_hours.invalid_capacity": "Enter a value between 0 and 100",
  "profile_settings.work_hours.end_before_start": "End time must be after start time",
  "profile_settings.work_hours.load_error": "Could not load work hours",
  "profile_settings.work_hours.save_error": "Could not save work hours",

  // Workspace settings — Templates
  "workspace_settings.templates.list_title": "Project templates",
  "workspace_settings.templates.list_description":
    "Create reusable project templates to kickstart new projects with a common structure.",
  "workspace_settings.templates.new": "Create Template",
  "workspace_settings.templates.column.name": "Title",
  "workspace_settings.templates.column.privacy": "Privacy",
  "workspace_settings.templates.column.created": "Created",
  "workspace_settings.templates.column.updated": "Last updated",
  "workspace_settings.templates.search_placeholder": "Search templates…",
  "workspace_settings.templates.name_edit_hint": "Click to edit",
  "workspace_settings.templates.update_name_error": "Could not update template name",
  "workspace_settings.templates.update_privacy_error": "Could not update template privacy",
  "workspace_settings.templates.edit_aria": "Edit template",
  "workspace_settings.templates.delete_aria": "Delete template",
  "workspace_settings.templates.actions_menu_aria": "Actions for {name}",
  "workspace_settings.templates.select_all": "Select all templates",
  "workspace_settings.templates.select_one": "Select {name}",
  "workspace_settings.templates.bulk_delete.title": "Delete {count} templates?",
  "workspace_settings.templates.bulk_delete.description":
    "This permanently deletes the {count} selected templates. This can't be undone.",
  "workspace_settings.templates.bulk_delete.success": "Deleted {count} templates",
  "workspace_settings.templates.empty_state": "No templates yet. Create one to get started.",
  "workspace_settings.templates.list_error": "Could not load templates",
  "workspace_settings.templates.menu.edit": "Edit",
  "workspace_settings.templates.menu.delete": "Delete",
  "workspace_settings.templates.delete.title": "Delete template",
  "workspace_settings.templates.delete.message":
    "Are you sure you want to delete {name}? This cannot be undone.",
  "workspace_settings.templates.delete.confirm": "Delete",
  "workspace_settings.templates.delete.success": "Template deleted",
  "workspace_settings.templates.delete.error": "Could not delete template",
  "workspace_settings.templates.view_only_badge": "View only",
  "workspace_settings.templates.view_only_banner.title": "This template is view only",
  "workspace_settings.templates.view_only_banner.description":
    "It's shared with your team rather than owned by it, so only the teams it's scoped to can change it.",
  "workspace_settings.templates.duplicate.action": "Duplicate",
  "workspace_settings.templates.duplicate.success": "Created {name}",
  "workspace_settings.templates.duplicate.error": "Could not duplicate template",
  "workspace_settings.templates.overview.template_details_heading": "Template Details",
  "workspace_settings.templates.overview.project_overview_heading": "Project Overview",
  "workspace_settings.templates.system_fields.heading": "System fields",
  "workspace_settings.templates.system_fields.description":
    "Included on every project automatically — there's no need to create custom fields for these.",
  "workspace_settings.templates.system_fields.status": "Status",
  "workspace_settings.templates.system_fields.start_date": "Start Date",
  "workspace_settings.templates.system_fields.end_date": "End Date",
  "workspace_settings.templates.system_fields.launch_date": "Launch Date",
  "workspace_settings.templates.system_fields.completed_date": "Completed Date",
  "workspace_settings.templates.system_fields.client_teams": "Client Teams",
  "workspace_settings.templates.system_fields.delivery_teams": "Delivery Teams",
  "workspace_settings.templates.system_fields.client_teams_input_label": "Client Teams label",
  "workspace_settings.templates.system_fields.delivery_teams_input_label": "Delivery Teams label",
  "workspace_settings.templates.system_fields.type_status": "Status",
  "workspace_settings.templates.system_fields.type_date": "Date",
  "workspace_settings.templates.system_fields.type_teams": "Teams",
  "workspace_settings.templates.panel.sub_workitems_heading": "Sub-tasks",
  "workspace_settings.templates.panel.no_sub_workitems": "No sub-tasks yet.",
  "workspace_settings.templates.panel.dependencies_heading": "Dependencies",
  "workspace_settings.templates.panel.no_dependencies": "No dependencies yet.",
  "workspace_settings.templates.panel.attachments_heading": "Attachments",
  "workspace_settings.templates.panel.no_attachments": "No attachments yet.",
  "workspace_settings.templates.panel.attachment_upload": "Upload file",
  "workspace_settings.templates.panel.attachment_delete": "Delete attachment",
  "workspace_settings.templates.panel.attachments_save_first":
    "Save the template to add attachments to this work item.",
  "workspace_settings.templates.panel.no_custom_fields": "No global fields on this work item.",
  "workspace_settings.templates.form.new_title": "New template",
  "workspace_settings.templates.form.name_label": "Template name",
  "workspace_settings.templates.form.description_label": "Template description",
  "workspace_settings.templates.form.team_access_label": "Team access",
  "workspace_settings.templates.form.team_access_placeholder": "Select teams",
  "workspace_settings.templates.form.team_access_all_teams": "All teams",
  // Product copy, kept verbatim from #2926 (including the missing "to" in
  // "will be able use") — reword only on a product call, not silently here.
  "workspace_settings.templates.form.team_access_hint":
    "Selected teams and every team beneath them in the hierarchy will be able use this template when creating projects.",
  "workspace_settings.templates.form.team_access_locked_hint":
    "This template will be available to this team and every team beneath it.",
  "workspace_settings.templates.form.project_description_label": "Project description",
  "workspace_settings.templates.form.privacy_label": "Privacy",
  "workspace_settings.templates.form.privacy_public": "Public",
  "workspace_settings.templates.form.privacy_private": "Private",
  "workspace_settings.templates.form.lead_label": "Default project lead(s)",
  "workspace_settings.templates.form.lead_remove": "Remove lead",
  "workspace_settings.templates.form.lead_placeholder": "None",
  "workspace_settings.templates.form.members_label": "Members",
  "workspace_settings.templates.form.skip_weekends_label": "Skip weekends",
  "workspace_settings.templates.form.skip_weekends_description":
    "When enabled, task due dates that fall on a Saturday or Sunday will automatically shift to the following Monday.",
  "workspace_settings.templates.form.workitems_title": "Work items",
  "workspace_settings.templates.form.add_workitem": "Add work item",
  "workspace_settings.templates.form.create_workitem": "Create work item",
  "workspace_settings.templates.form.add_sub_workitem": "Add sub-item",
  "workspace_settings.templates.form.no_workitems": "No work items yet",
  "workspace_settings.templates.form.no_workitem_matches": "No work items match your search.",
  "workspace_settings.templates.form.search_workitems_placeholder": "Search work items…",
  "workspace_settings.templates.form.workitem_col_name": "Name",
  "workspace_settings.templates.form.workitem_col_type": "Type",
  "workspace_settings.templates.form.workitem_col_priority": "Priority",
  "workspace_settings.templates.form.workitem_col_estimate": "Estimate",
  "workspace_settings.templates.form.workitem_col_start_day": "Start day",
  "workspace_settings.templates.form.workitem_col_end_date": "End date",
  "workspace_settings.templates.form.workitem_col_end_day": "End day",
  "workspace_settings.templates.form.workitem_col_stage": "Stage",
  "workspace_settings.templates.form.workitem_col_job_role": "Job Role",
  "workspace_settings.templates.form.workitem_col_category": "Category",
  "workspace_settings.templates.form.workitem_col_custom_fields": "Custom fields",
  "workspace_settings.templates.form.workitem_col_dependencies": "Dependencies",
  "workspace_settings.templates.form.select_all_workitems": "Select all work items",
  "workspace_settings.templates.form.select_workitem": "Select {name}",
  "workspace_settings.templates.form.bulk_assign_job_role": "Assign job role",
  "workspace_settings.templates.form.bulk_job_role_assigned":
    "{count, plural, one {Job role set on # work item} other {Job role set on # work items}}",
  "workspace_settings.templates.form.move_up": "Move up",
  "workspace_settings.templates.form.move_down": "Move down",
  "workspace_settings.templates.form.reorder_handle": "Drag to reorder",
  "workspace_settings.templates.form.save": "Save",
  "workspace_settings.templates.form.save_success": "Template saved",
  "workspace_settings.templates.form.save_error": "Could not save template",
  "workspace_settings.templates.form.missing_template_name": "Template name is required.",
  "workspace_settings.templates.form.process_label": "Process",
  "workspace_settings.templates.form.process_none": "No process",
  "workspace_settings.templates.form.category_label": "Default category",
  "workspace_settings.templates.custom_fields.section_title": "Custom Fields",
  "workspace_settings.templates.custom_fields.empty_state":
    "No custom fields. Add fields from your workspace's bank.",
  "workspace_settings.templates.custom_fields.empty_state_link": "Manage bank →",
  "workspace_settings.templates.custom_fields.add_field": "Add field",
  "workspace_settings.templates.custom_fields.picker.title": "Add a custom field",
  "workspace_settings.templates.custom_fields.picker.search_placeholder": "Search bank…",
  "workspace_settings.templates.custom_fields.picker.no_matches": "No matching fields.",
  "workspace_settings.templates.custom_fields.picker.empty":
    "No fields available. Define them in workspace settings",
  "workspace_settings.templates.custom_fields.row.required": "Required",
  "workspace_settings.templates.custom_fields.row.default_value": "Default value",
  "workspace_settings.templates.custom_fields.row.remove": "Remove",
  "workspace_settings.templates.custom_fields.row.archived_badge": "Archived",
  "workspace_settings.templates.global_fields.section_heading": "Custom fields",
  "workspace_settings.templates.global_fields.add_button": "Add fields",
  "workspace_settings.templates.global_fields.empty_no_fields":
    "No custom fields in this workspace yet.",
  "workspace_settings.templates.global_fields.empty_no_fields_link": "Manage bank →",
  "workspace_settings.templates.global_fields.required_label": "Required",
  "workspace_settings.templates.global_fields.options_locked_hint":
    "Options are managed in the global field library",
  "workspace_settings.templates.global_fields.options_empty": "No options defined on this field.",
  "workspace_settings.templates.global_fields.drag_handle": "Drag to reorder",
  "workspace_settings.templates.workitem.name_label": "Name",
  "workspace_settings.templates.workitem.description_label": "Description",
  "workspace_settings.templates.workitem.type_task": "Task",
  "workspace_settings.templates.workitem.type_event": "Event",
  "workspace_settings.templates.workitem.type_milestone": "Milestone",
  "workspace_settings.templates.workitem.milestone_due_day_label":
    "Due day (days from project start)",
  "workspace_settings.templates.workitem.cf_true": "Yes",
  "workspace_settings.templates.workitem.duration_days":
    "{count, plural, one {# day} other {# days}}",
  "workspace_settings.templates.workitem.priority_label": "Priority",
  "workspace_settings.templates.workitem.priority_urgent": "Urgent",
  "workspace_settings.templates.workitem.priority_high": "High",
  "workspace_settings.templates.workitem.priority_medium": "Medium",
  "workspace_settings.templates.workitem.priority_low": "Low",
  "workspace_settings.templates.workitem.priority_none": "None",
  "workspace_settings.templates.workitem.estimate_label": "Estimate (hours)",
  "workspace_settings.templates.workitem.relative_start_day_label":
    "Start day (from project start)",
  "workspace_settings.templates.workitem.due_offset_days_label": "Duration (days)",
  "workspace_settings.templates.workitem.event_start_day_label": "Start day",
  "workspace_settings.templates.workitem.event_due_day_label": "Due day",
  "workspace_settings.templates.workitem.event_start_day_time_label": "Start day / time",
  "workspace_settings.templates.workitem.event_due_day_time_label": "Due day / time",
  "workspace_settings.templates.workitem.event_fields_required":
    "Events need a start day, due day, and times (or All Day).",
  "workspace_settings.templates.workitem.stage_label": "Stage",
  "workspace_settings.templates.workitem.job_role_label": "Default job role",
  "workspace_settings.templates.workitem.job_role_none": "None",
  "workspace_settings.templates.workitem.job_role_edit_aria": "Edit job role for {name}",
  "workspace_settings.templates.workitem.stage_edit_aria": "Edit stage for {name}",
  "workspace_settings.templates.workitem.category_edit_aria": "Edit category for {name}",
  "workspace_settings.templates.workitem.job_role_search_placeholder": "Search job roles",
  "workspace_settings.templates.workitem.job_role_empty": "No job roles found",
  "workspace_settings.templates.workitem.category_label": "Category",
  "workspace_settings.templates.workitem.skills_label": "Qualifications",
  "workspace_settings.templates.workitem.skills_placeholder": "Select qualifications",
  "workspace_settings.templates.workitem.skills_select_aria": "Select qualifications",
  "workspace_settings.templates.workitem.add_title": "Create work item",
  "workspace_settings.templates.workitem.edit_title": "Edit work item",
  "workspace_settings.templates.workitem.end_day_label": "End day",
  "workspace_settings.templates.workitem.name_required": "Work item name is required",
  "workspace_settings.templates.workitem.rename_aria": "Rename {name}",
  "workspace_settings.templates.workitem.start_day_edit_aria": "Edit start day for {name}",
  "workspace_settings.templates.workitem.end_day_edit_aria": "Edit end day for {name}",
  "workspace_settings.templates.workitem.priority_edit_aria": "Edit priority for {name}",
  "workspace_settings.templates.workitem.estimate_edit_aria": "Edit estimate for {name}",
  "workspace_settings.templates.workitem.estimate_invalid": "Enter a number of hours (0 or more)",
  "workspace_settings.templates.workitem.day_offset_invalid":
    "Enter a whole number of days (0 or more)",
  "workspace_settings.templates.workitem.end_day_before_start":
    "End day can't be before the start day",
  "workspace_settings.templates.workitem.skills_edit_aria": "Edit qualifications for {name}",
  "workspace_settings.templates.workitem.skills_search_placeholder": "Search qualifications",
  "workspace_settings.templates.workitem.skills_empty": "No qualifications found",
  "workspace_settings.templates.workitem.custom_fields_button": "Custom fields",
  "workspace_settings.templates.workitem.custom_fields_button_with_count":
    "Custom fields ({count})",
  "workspace_settings.templates.workitem.custom_fields_title": "Global custom fields — {name}",
  "workspace_settings.templates.workitem.dependencies_button": "Dependencies",
  "workspace_settings.templates.workitem.dependencies_button_with_count": "Dependencies ({count})",
  "workspace_settings.templates.workitem.dependencies_title": "Dependencies — {name}",
  "workspace_settings.templates.workitem.no_dependencies": "No dependencies yet.",
  "workspace_settings.templates.workitem.no_dependency_candidates":
    "Add another work item to the template to create a dependency.",
  "workspace_settings.templates.workitem.dependency_type_label": "Type",
  "workspace_settings.templates.workitem.dependency_workitem_label": "Work item",
  "workspace_settings.templates.workitem.select_workitem": "Select a work item",
  "workspace_settings.templates.workitem.select_workitem_search": "Search work items",
  "workspace_settings.templates.workitem.select_workitem_empty": "No work items found",
  "workspace_settings.templates.workitem.add_dependency": "Add",
  "workspace_settings.templates.workitem.remove_dependency": "Remove dependency",
  "workspace_settings.templates.workitem.dependency_unknown_workitem": "Unknown work item",
  "workspace_settings.templates.workitem.dependency_blocks": "Blocks",
  "workspace_settings.templates.workitem.dependency_blocked_by": "Blocked by",
  "workspace_settings.templates.workitem.dependency_relates_to": "Relates to",

  // Workspace settings — template detail and connected apps (NG-2578)
  "workspace_settings.templates.detail.tab_overview": "Overview",
  "workspace_settings.templates.detail.tab_table": "Table",
  "workspace_settings.templates.detail.tab_workflow": "Workflow",
  "workspace_settings.templates.detail.sections_aria": "Template sections",
  "workspace_settings.templates.detail.name_aria": "Template name",
  "workspace_settings.templates.detail.delete_aria": "Delete template",
  "workspace_settings.templates.detail.delete_title": "Delete template",
  "workspace_settings.templates.detail.delete_description":
    'Delete "{name}"? This cannot be undone.',
  "workspace_settings.templates.detail.delete_fallback_name": "this template",
  "workspace_settings.templates.detail.delete_confirm": "Delete",
  "workspace_settings.templates.detail.deleted": "Template deleted",
  "workspace_settings.templates.detail.delete_error": "Failed to delete template",
  "workspace_settings.templates.detail.rename_error": "Failed to rename template",
  "workspace_settings.templates.detail.loading": "Loading…",
  "workspace_settings.templates.detail.work_items_save_error": "Failed to save work items",
  "workspace_settings.oauth_apps.title": "Connected apps",
  "workspace_settings.oauth_apps.description":
    "Register 3rd-party apps that can authenticate users in this workspace via OAuth.",
  "workspace_settings.oauth_apps.new_app": "New app",
  "workspace_settings.oauth_apps.loading": "Loading…",
  "workspace_settings.oauth_apps.load_error": "Failed to load apps.",
  "workspace_settings.oauth_apps.delete_confirm":
    "Delete {name}? Existing tokens will fail introspection.",

  // Workspace settings — standard fields
  "workspace_settings.standard_fields.empty_state": "No standard fields configured.",
  "workspace_settings.standard_fields.editor.default_badge": "Default",
  "workspace_settings.standard_fields.editor.quick_add_placeholder": "Add an option…",
  "workspace_settings.standard_fields.editor.add_value": "Add option",
  "workspace_settings.standard_fields.editor.toast_added": "Option added",
  "workspace_settings.standard_fields.editor.toast_renamed": "Option updated",
  "workspace_settings.standard_fields.editor.toast_activated": "Option reactivated",
  "workspace_settings.standard_fields.editor.toast_deactivated": "Option deactivated",
  "workspace_settings.standard_fields.editor.last_active_tooltip":
    "At least one active option must remain.",
  "workspace_settings.standard_fields.editor.duplicate_error":
    "An option with this name already exists.",
  "workspace_settings.standard_fields.editor.save_error": "Couldn't save the change. Try again.",
  "workspace_settings.standard_fields.editor.inactive_heading": "Inactive options",
  "workspace_settings.standard_fields.editor.drag_handle": "Drag to reorder",
  "workspace_settings.standard_fields.editor.deactivate": "Deactivate option",
  "workspace_settings.standard_fields.editor.reactivate": "Reactivate option",
  "workspace_settings.standard_fields.editor.delete": "Delete option",
  "workspace_settings.standard_fields.editor.toast_deleted": "Option deleted",
  "workspace_settings.standard_fields.editor.delete_error":
    "Couldn't delete the option. Try again.",
  "workspace_settings.standard_fields.editor.delete_confirm_title": "Delete option?",
  "workspace_settings.standard_fields.editor.delete_confirm_body":
    "Permanently delete “{label}”. This can't be undone. If the option is still referenced anywhere, the deletion will be blocked until those references are cleared.",
  "workspace_settings.standard_fields.editor.delete_confirm_action": "Delete",
  "workspace_settings.standard_fields.editor.delete_blocked_default":
    "This is the default option and can't be deleted. Set a different default first.",
  "workspace_settings.standard_fields.editor.delete_blocked_active":
    "Deactivate this option before deleting it.",
  "workspace_settings.standard_fields.editor.delete_blocked_usage_title": "Option still in use",
  "workspace_settings.standard_fields.editor.delete_blocked_usage_body":
    "“{label}” can't be deleted because it's still referenced by these records. Clear those references first, then try again.",
  "workspace_settings.standard_fields.editor.usage_projects": "Projects",
  "workspace_settings.standard_fields.editor.usage_tasks": "Tasks",
  "workspace_settings.standard_fields.editor.usage_events": "Events",
  "workspace_settings.standard_fields.editor.usage_milestones": "Milestones",
  "workspace_settings.standard_fields.editor.usage_intake_submissions": "Intake submissions",
  "workspace_settings.standard_fields.editor.usage_intake_forms": "Intake forms",
  "workspace_settings.standard_fields.editor.values_heading": "Answer options",
  "workspace_settings.standard_fields.editor.values_help":
    "Options can be deactivated, and deleted once inactive and unused — records that captured a deactivated option stay intact.",
  "workspace_settings.standard_fields.editor.search_placeholder": "Search options",
  "workspace_settings.standard_fields.editor.search_clear": "Clear search",
  "workspace_settings.standard_fields.editor.no_matches": "No options match your search.",
  "standard_fields.category_select.placeholder": "Select a category",
  "standard_fields.category_select.search": "Search categories…",
  "standard_fields.category_select.empty": "No categories",
  "standard_fields.category_select.inactive": "{label} (inactive)",
  "standard_fields.category_select.none": "No category",
  "standard_fields.category_select.aria": "Category",

  // Workspace settings — custom fields
  "workspace_settings.custom_fields.list_title": "Global Fields",
  "workspace_settings.custom_fields.list_description":
    "Define reusable fields that can be used across Cognota",
  "workspace_settings.custom_fields.new": "Create Custom Field",
  "workspace_settings.custom_fields.empty_state":
    "No custom fields yet. Create one to start using them on templates and intake forms.",
  "workspace_settings.custom_fields.empty_state.no_matches": "No fields match the current filters.",
  "workspace_settings.custom_fields.search_placeholder": "Search by field name",
  "workspace_settings.custom_fields.select_all": "Select all custom fields",
  "workspace_settings.custom_fields.select_one": "Select {name}",
  "workspace_settings.custom_fields.show_archived": "Show archived",
  "workspace_settings.custom_fields.filter.kind": "Field type",
  "workspace_settings.custom_fields.filter.kind_search": "Search field types…",
  "workspace_settings.custom_fields.filter.project_templates": "Project template",
  "workspace_settings.custom_fields.filter.project_templates_search": "Search templates…",
  "workspace_settings.custom_fields.filter.intake_forms": "Intake form",
  "workspace_settings.custom_fields.filter.intake_forms_search": "Search forms…",
  "workspace_settings.custom_fields.filter.empty": "No matches.",
  "workspace_settings.custom_fields.filter.clear": "Clear filters",
  "workspace_settings.custom_fields.column.name": "Field name",
  "workspace_settings.custom_fields.column.id": "ID",
  "workspace_settings.custom_fields.column.kind": "Type",
  "workspace_settings.custom_fields.column.category": "Category",
  "workspace_settings.custom_fields.column.updated": "Updated",
  "workspace_settings.custom_fields.column.config": "Config",
  "workspace_settings.custom_fields.column.actions": "Actions",
  "workspace_settings.custom_fields.count_summary": "Showing {count} of {total}",
  "workspace_settings.custom_fields.range_summary": "Showing {from}–{to} of {total}",
  "workspace_settings.custom_fields.pagination.prev": "Previous page",
  "workspace_settings.custom_fields.pagination.next": "Next page",
  "workspace_settings.custom_fields.action.edit": "Edit",
  "workspace_settings.custom_fields.action.more": "More actions",
  "workspace_settings.custom_fields.action.archive": "Archive",
  "workspace_settings.custom_fields.action.restore": "Restore",
  "workspace_settings.custom_fields.action.delete": "Delete permanently",
  "workspace_settings.custom_fields.action.duplicate": "Duplicate",
  "workspace_settings.custom_fields.toast.duplicated": "Duplicated to {name}",
  "workspace_settings.custom_fields.toast.error_duplicate": "Could not duplicate the field.",
  "workspace_settings.custom_fields.usage.heading": "Used by",
  "workspace_settings.custom_fields.usage.project_templates": "Project Templates",
  "workspace_settings.custom_fields.usage.intake_forms": "Intake Forms",
  "workspace_settings.custom_fields.usage.empty":
    "This field isn't used in any active project templates or intake forms yet.",
  "workspace_settings.custom_fields.usage.none": "None",
  "workspace_settings.custom_fields.usage.loading": "Loading usage…",
  "workspace_settings.custom_fields.usage.error": "Could not load usage.",
  "workspace_settings.custom_fields.editor.title.create": "New custom field",
  "workspace_settings.custom_fields.editor.title.edit": "Edit custom field",
  "workspace_settings.custom_fields.editor.field.name": "Name",
  "workspace_settings.custom_fields.editor.field.description": "Description",
  "workspace_settings.custom_fields.editor.field.kind": "Kind",
  "workspace_settings.custom_fields.editor.field.options": "Options",
  "workspace_settings.custom_fields.editor.kind.text": "Single Line Text",
  "workspace_settings.custom_fields.editor.kind.paragraph": "Paragraph Text",
  "workspace_settings.custom_fields.editor.kind.number": "Number",
  "workspace_settings.custom_fields.editor.kind.boolean": "True / False",
  "workspace_settings.custom_fields.editor.kind.date": "Date / Time",
  "workspace_settings.custom_fields.editor.kind.select": "Single Select",
  "workspace_settings.custom_fields.editor.kind.multiselect": "Multiselect",
  "workspace_settings.custom_fields.editor.kind.email": "Email Address",
  "workspace_settings.custom_fields.editor.kind.phone": "Phone Number",
  "workspace_settings.custom_fields.editor.kind.currency": "Currency",
  "workspace_settings.custom_fields.editor.kind.user_list": "User List",
  "workspace_settings.custom_fields.editor.kind.locked":
    "Kind cannot be changed once the field is in use.",
  "workspace_settings.custom_fields.editor.display.label": "Display as",
  "workspace_settings.custom_fields.editor.display.dropdown": "Dropdown",
  "workspace_settings.custom_fields.editor.display.radio": "Radio buttons",
  "workspace_settings.custom_fields.editor.display.checkboxes": "Checkboxes",
  "workspace_settings.custom_fields.editor.select_type.current_label": "Select type",
  "workspace_settings.custom_fields.editor.select_type.single": "Single Select",
  "workspace_settings.custom_fields.editor.select_type.multi": "Multiselect",
  "workspace_settings.custom_fields.editor.select_type.convert_action": "Convert to multiselect",
  "workspace_settings.custom_fields.editor.select_type.not_reversible_tooltip":
    "A multiselect field can't be converted back to single select.",
  "workspace_settings.custom_fields.editor.select_type.convert_error":
    "Couldn't convert to multiselect. Please try again.",
  "workspace_settings.custom_fields.editor.select_type.convert_confirm_title":
    "Convert to multiselect?",
  "workspace_settings.custom_fields.editor.select_type.convert_confirm_body":
    "Convert “{name}” to a multiselect field. This can't be undone — a multiselect field can't be converted back to single select.",
  "workspace_settings.custom_fields.editor.option.label": "Label",
  "workspace_settings.custom_fields.editor.option.drag_handle": "Drag to reorder",
  "workspace_settings.custom_fields.editor.option.add": "Add option",
  "workspace_settings.custom_fields.editor.option.remove": "Remove",
  "workspace_settings.custom_fields.editor.option.archive": "Archive option",
  "workspace_settings.custom_fields.editor.option.unarchive": "Unarchive option",
  "workspace_settings.custom_fields.editor.option.archived_heading": "Archived options",
  "workspace_settings.custom_fields.editor.option.search_placeholder": "Search options",
  "workspace_settings.custom_fields.editor.option.search_clear": "Clear search",
  "workspace_settings.custom_fields.editor.option.empty": "No answer options yet.",
  "workspace_settings.custom_fields.editor.option.no_matches": "No options match your search.",
  "workspace_settings.custom_fields.editor.option.page_indicator": "Page {page} of {total}",
  "workspace_settings.custom_fields.editor.option.page_prev": "Previous page",
  "workspace_settings.custom_fields.editor.option.page_next": "Next page",
  "workspace_settings.custom_fields.editor.option.archive_confirm.title": "Archive this option?",
  "workspace_settings.custom_fields.editor.option.archive_confirm.body":
    "{label} will stay visible on existing records but cannot be selected on new submissions. You can unarchive it any time.",
  "workspace_settings.custom_fields.editor.option.archive_confirm.confirm": "Archive",
  "workspace_settings.custom_fields.editor.option.archive_confirm.cancel": "Cancel",
  "workspace_settings.custom_fields.editor.option.archive_confirm.checking":
    "Checking conditional-logic dependencies…",
  "workspace_settings.custom_fields.editor.option.archive_confirm.precheck_error":
    "Couldn't check this option's dependencies. Try again.",
  "workspace_settings.custom_fields.editor.option.archive_confirm.blocked_intro":
    "Conditional logic on active forms depends on this option's value. Archive is blocked until those dependencies are resolved.",
  "workspace_settings.custom_fields.editor.option.archive_confirm.blocked_forms_heading":
    "Dependent questions",
  "workspace_settings.custom_fields.editor.option.archive_confirm.blocked_retry_hint":
    "Open each form in a new tab, remove the dependency, then click Retry to re-check.",
  "workspace_settings.custom_fields.editor.option.archive_confirm.retry": "Retry archive",
  "workspace_settings.custom_fields.editor.cancel": "Cancel",
  "workspace_settings.custom_fields.editor.save": "Save",
  "workspace_settings.custom_fields.archive_dialog.consequences_heading":
    "This field is currently used in:",
  "workspace_settings.custom_fields.archive_dialog.consequences_templates":
    "{count, plural, one {# project template} other {# project templates}}",
  "workspace_settings.custom_fields.archive_dialog.consequences_forms":
    "{count, plural, one {# intake form} other {# intake forms}}",
  "workspace_settings.custom_fields.archive_dialog.title": "Archive {name}?",
  "workspace_settings.custom_fields.archive_dialog.body":
    "Archived fields are hidden from new templates, intake forms, and project pickers. Existing usages will continue to render.",
  "workspace_settings.custom_fields.archive_dialog.confirm": "Archive",
  "workspace_settings.custom_fields.archive_dialog.cancel": "Cancel",
  "workspace_settings.custom_fields.archive_dialog.checking":
    "Checking conditional-logic dependencies…",
  "workspace_settings.custom_fields.archive_dialog.precheck_error":
    "Couldn't check this field's dependencies. Try again.",
  "workspace_settings.custom_fields.archive_dialog.blocked_intro":
    "Conditional logic on active forms depends on this field. Archive is blocked until those dependencies are resolved.",
  "workspace_settings.custom_fields.archive_dialog.blocked_forms_heading": "Dependent questions",
  "workspace_settings.custom_fields.archive_dialog.blocked_retry_hint":
    "Open each form in a new tab, remove the dependency, then click Retry to re-check.",
  "workspace_settings.custom_fields.archive_dialog.retry": "Retry archive",
  "workspace_settings.custom_fields.duplicate_dialog.title": "Duplicate {name}?",
  "workspace_settings.custom_fields.duplicate_dialog.body":
    "A copy will be created with the same configuration, description, and answer options. The duplicate gets a new unique identifier and won't be assigned to any forms or project templates.",
  "workspace_settings.custom_fields.duplicate_dialog.confirm": "Duplicate",
  "workspace_settings.custom_fields.duplicate_dialog.cancel": "Cancel",
  "workspace_settings.custom_fields.delete_dialog.title": "Delete {name} permanently?",
  "workspace_settings.custom_fields.delete_dialog.checking": "Checking where this field is in use…",
  "workspace_settings.custom_fields.delete_dialog.precheck_error":
    "Couldn't check this field's usage. Try again.",
  "workspace_settings.custom_fields.delete_dialog.blocked_active_data":
    "Active projects or requests have data for this field. It can't be deleted while that data is in use — archive the field instead.",
  "workspace_settings.custom_fields.delete_dialog.blocked_active_usage":
    "This field is currently used by active forms or project templates. Remove it from each one — or archive the field — before deleting.",
  "workspace_settings.custom_fields.delete_dialog.warn_permanent":
    "This is permanent and can't be undone.",
  "workspace_settings.custom_fields.delete_dialog.warn_inactive_removed":
    "The field will be permanently removed from all inactive forms and templates using it.",
  "workspace_settings.custom_fields.delete_dialog.warn_broken_logic":
    "Any conditional logic that depends on this field will be broken and can't be recovered.",
  "workspace_settings.custom_fields.delete_dialog.warn_inactive_manual":
    "If any inactive form or template is reactivated later, it will need to be updated manually.",
  "workspace_settings.custom_fields.delete_dialog.archive_recommend":
    "Archive is recommended if you might need the field again.",
  "workspace_settings.custom_fields.delete_dialog.usage_templates_heading":
    "Project templates affected",
  "workspace_settings.custom_fields.delete_dialog.usage_workitem_templates_heading":
    "Template work items affected",
  "workspace_settings.custom_fields.delete_dialog.usage_forms_heading": "Intake forms affected",
  "workspace_settings.custom_fields.delete_dialog.usage_projects_heading":
    "Projects using this field",
  "workspace_settings.custom_fields.delete_dialog.usage_work_items_heading":
    "Work items using this field",
  "workspace_settings.custom_fields.delete_dialog.archive_instead": "Archive instead",
  "workspace_settings.custom_fields.delete_dialog.confirm": "Delete permanently",
  "workspace_settings.custom_fields.delete_dialog.cancel": "Cancel",
  "workspace_settings.custom_fields.toast.deleted": "Deleted {name}",
  "workspace_settings.custom_fields.toast.error_delete": "Could not delete the field.",
  "workspace_settings.custom_fields.archived_badge": "Archived",
  "workspace_settings.custom_fields.default_badge": "Default",
  // The Category column's badges and its filter options (NG-854). Kept
  // separate from the *_badge keys above, which the field edit pages still
  // use, so a wording change on one surface cannot silently move the other.
  "workspace_settings.custom_fields.category.system": "System Field",
  "workspace_settings.custom_fields.category.default": "Default",
  "workspace_settings.custom_fields.category.custom": "Custom",
  "workspace_settings.custom_fields.export_no_bank_fields":
    "Nothing to export — the export covers workspace fields only, and System Field rows are not among them.",
  "workspace_settings.custom_fields.config_preview.options_count":
    "{count, plural, one {# option} other {# options}}",
  "workspace_settings.custom_fields.config_preview.none": "—",
  "workspace_settings.custom_fields.editor.number.min": "Min",
  "workspace_settings.custom_fields.editor.number.max": "Max",
  "workspace_settings.custom_fields.editor.number.step": "Step",
  "workspace_settings.custom_fields.editor.number.unit": "Unit",
  "workspace_settings.custom_fields.toast.created": "Custom field created.",
  "workspace_settings.custom_fields.toast.updated": "Custom field updated.",
  "workspace_settings.custom_fields.toast.archived": "{name} archived.",
  "workspace_settings.custom_fields.toast.restored": "{name} restored.",
  "workspace_settings.custom_fields.toast.error_save": "Couldn't save the field. Please try again.",
  "workspace_settings.custom_fields.toast.error_archive": "Couldn't archive the field.",
  "workspace_settings.custom_fields.toast.error_restore": "Couldn't restore the field.",
  "workspace_settings.custom_fields.edit_page.loading": "Loading…",
  "workspace_settings.custom_fields.edit_page.not_found":
    "This field doesn't exist or has been deleted.",
  "workspace_settings.custom_fields.edit_page.updated_meta": "Updated {date}",
  "workspace_settings.custom_fields.edit_page.delete_locked":
    "Archive this field before deleting it.",
  "workspace_settings.custom_fields.edit_page.tab.details": "Details",
  "workspace_settings.custom_fields.edit_page.tab.usage": "Usage",
  "workspace_settings.custom_fields.edit_page.details_heading": "Field details",
  "workspace_settings.custom_fields.edit_page.autosave_help":
    "Changes save automatically. Field type and identifier can't be changed after creation.",
  "workspace_settings.custom_fields.edit_page.options_heading": "Answer options",
  "workspace_settings.custom_fields.edit_page.options_help":
    "Options can be archived but not deleted — records that captured an archived value stay intact.",
  "workspace_settings.custom_fields.edit_page.usage_column.name": "Name",
  "workspace_settings.custom_fields.edit_page.usage_column.kind": "Type",
  "workspace_settings.custom_fields.edit_page.usage_kind.form": "Intake form",
  "workspace_settings.custom_fields.edit_page.usage_kind.template": "Project template",
  "workspace_settings.custom_fields.create_modal.title_step_1": "Create custom field",
  "workspace_settings.custom_fields.create_modal.subtitle_step_1":
    "Pick the field type that matches the data you want to collect. Field type can't be changed after creation.",
  "workspace_settings.custom_fields.create_modal.title_step_2": "New {kind} field",
  "workspace_settings.custom_fields.create_modal.subtitle_step_2":
    "Give your field a clear, descriptive name. You can reuse it across any form or project template.",
  "workspace_settings.custom_fields.create_modal.name_placeholder": "e.g. Delivery location",
  "workspace_settings.custom_fields.create_modal.description_placeholder":
    "Explain what this field is for. Only visible to admins.",
  "workspace_settings.custom_fields.create_modal.optional": "(optional)",
  "workspace_settings.custom_fields.create_modal.options_followup":
    "You'll be able to add answer options after creating the field.",
  "workspace_settings.custom_fields.create_modal.continue": "Continue",
  "workspace_settings.custom_fields.create_modal.back": "Back",
  "workspace_settings.custom_fields.create_modal.submit": "Create field",
  "workspace_settings.custom_fields.create_modal.kind_desc.text": "Short freeform text",
  "workspace_settings.custom_fields.create_modal.kind_desc.paragraph": "Longer multi-line text",
  "workspace_settings.custom_fields.create_modal.kind_desc.number":
    "A number, with optional min, max, step, or unit",
  "workspace_settings.custom_fields.create_modal.kind_desc.boolean": "A single true / false toggle",
  "workspace_settings.custom_fields.create_modal.kind_desc.date": "A date, with optional time",
  "workspace_settings.custom_fields.create_modal.kind_desc.select":
    "Pick one or multiple options from a set list",
  "workspace_settings.custom_fields.create_modal.kind_desc.multiselect":
    "Pick multiple options (checkboxes or dropdown)",
  "workspace_settings.custom_fields.create_modal.select_tile": "Select",
  "workspace_settings.custom_fields.create_modal.select_type_label": "Select type",
  "workspace_settings.custom_fields.create_modal.kind_desc.email": "An email address",
  "workspace_settings.custom_fields.create_modal.kind_desc.phone": "A phone number",
  "workspace_settings.custom_fields.create_modal.kind_desc.currency":
    "A monetary amount in a chosen currency. Custom currency fields are not included in Cognota's Cost metrics.",
  "workspace_settings.custom_fields.create_modal.kind_desc.user_list":
    "Select one or more workspace members",
  "custom_fields.user_list.placeholder": "Select members",
  "custom_fields.user_list.search_placeholder": "Search members",
  "custom_fields.user_list.empty": "No members found",
  "custom_fields.multiselect.placeholder": "Select options",
  "custom_fields.multiselect.search_placeholder": "Search options",
  "custom_fields.multiselect.empty": "No options found",
  "workspace_settings.custom_fields.editor.currency.label": "Currency code",
  "workspace_settings.custom_fields.editor.currency.placeholder": "USD",
  "workspace_settings.custom_fields.editor.currency.help":
    "ISO 4217 currency code (e.g. USD, EUR, GBP).",
  "workspace_settings.custom_fields.cost_field.actual_cost": "Actual Cost",
  "workspace_settings.custom_fields.cost_field.estimated_cost": "Estimated Cost",
  "workspace_settings.custom_fields.cost_field.actual_cost_help":
    "Cognota uses this field to calculate the Actual Cost of work by multiplying work item's actual hours by user hourly rate. Only users with Financial permission can view this field.",
  "workspace_settings.custom_fields.cost_field.estimated_cost_help":
    "Cognota uses this field to calculate the Estimated Cost of work by multiplying work item's estimated hours by user hourly rate. Only users with Financial permission can view this field.",
  "workspace_settings.custom_fields.system_field.target_launch_date": "Target Launch Date",
  "workspace_settings.custom_fields.system_field.target_launch_date_help":
    "A request's target launch date. Add it to an intake workflow from the form builder's Global Fields panel; a request collects it only on workflows that include it, and converting the request carries the date onto the new project.",
  "workspace_settings.custom_fields.editor.currency.metrics_banner":
    "Custom currency fields are not included in Cognota's Cost metrics. Use Estimated and Actual Cost to leverage Cognota's Cost metrics.",
  "workspace_settings.custom_fields.editor.currency.invalid":
    "Enter a 3-letter ISO currency code (e.g. USD).",
  "workspace_settings.custom_fields.editor.precision.label": "Decimal places",

  // Workspace settings — states
  "workspace_settings.states.title": "Work Item Statuses",
  "workspace_settings.states.description":
    "Configure the workflow statuses used by work items in this workspace.",
  "workspace_settings.states.group.backlog": "Backlog",
  "workspace_settings.states.group.unstarted": "Unstarted",
  "workspace_settings.states.group.started": "Started",
  "workspace_settings.states.group.on_hold": "On Hold",
  "workspace_settings.states.group.completed": "Completed",
  "workspace_settings.states.group.cancelled": "Cancelled",
  "workspace_settings.states.add_state": "Add status",
  "workspace_settings.states.create": "Create status",
  "workspace_settings.states.save": "Save",
  "workspace_settings.states.edit": "Edit status",
  "workspace_settings.states.delete": "Delete status",
  "workspace_settings.states.deleting": "Deleting…",
  "workspace_settings.states.mark_default": "Mark as default",
  "workspace_settings.states.default_badge": "Default",
  "workspace_settings.states.empty_group": "No statuses in this group.",
  "workspace_settings.states.drag_handle": "Drag to reorder",
  "workspace_settings.states.cannot_delete_default":
    "The default status cannot be deleted. Mark another status as default first.",
  "workspace_settings.states.create_success": "Status created.",
  "workspace_settings.states.create_error": "Failed to create status.",
  "workspace_settings.states.update_success": "Status updated.",
  "workspace_settings.states.update_error": "Failed to update status.",
  "workspace_settings.states.delete_success": "Status deleted.",
  "workspace_settings.states.delete_error": "Failed to delete status.",
  "workspace_settings.states.mark_default_success": "Default status updated.",
  "workspace_settings.states.mark_default_error": "Failed to mark status as default.",
  "workspace_settings.states.reorder_error": "Failed to reorder statuses.",
  "workspace_settings.states.delete_title": "Delete status",
  "workspace_settings.states.delete_description":
    'Delete the status "{name}"? Work items assigned to it may need to be reassigned.',
  "workspace_settings.states.form.name_label": "Name",
  "workspace_settings.states.form.name_placeholder": "e.g. In review",
  "workspace_settings.states.form.group_label": "Group",
  "workspace_settings.states.form.color_label": "Color",

  // Workspace settings — project states
  "workspace_settings.project_states.title": "Project Statuses",
  "workspace_settings.project_states.description":
    "Configure the statuses used by projects in this workspace.",
  "workspace_settings.project_states.group.backlog": "Backlog",
  "workspace_settings.project_states.group.unstarted": "Unstarted",
  "workspace_settings.project_states.group.started": "Started",
  "workspace_settings.project_states.group.completed": "Completed",
  "workspace_settings.project_states.group.cancelled": "Cancelled",
  "workspace_settings.project_states.add_state": "Add status",
  "workspace_settings.project_states.create": "Create status",
  "workspace_settings.project_states.save": "Save",
  "workspace_settings.project_states.edit": "Edit status",
  "workspace_settings.project_states.delete": "Delete status",
  "workspace_settings.project_states.deleting": "Deleting…",
  "workspace_settings.project_states.mark_default": "Mark as default",
  "workspace_settings.project_states.default_badge": "Default",
  "workspace_settings.project_states.empty_group": "No statuses in this group.",
  "workspace_settings.project_states.drag_handle": "Drag to reorder",
  "workspace_settings.project_states.cannot_delete_default":
    "The default status cannot be deleted. Mark another status as default first.",
  "workspace_settings.project_states.create_success": "Status created.",
  "workspace_settings.project_states.create_error": "Failed to create status.",
  "workspace_settings.project_states.update_success": "Status updated.",
  "workspace_settings.project_states.update_error": "Failed to update status.",
  "workspace_settings.project_states.delete_success": "Status deleted.",
  "workspace_settings.project_states.delete_error": "Failed to delete status.",
  "workspace_settings.project_states.mark_default_success": "Default status updated.",
  "workspace_settings.project_states.mark_default_error": "Failed to mark status as default.",
  "workspace_settings.project_states.reorder_error": "Failed to reorder statuses.",
  "workspace_settings.project_states.delete_title": "Delete status",
  "workspace_settings.project_states.delete_description":
    'Delete the status "{name}"? Projects assigned to it may need to be reassigned.',
  "workspace_settings.project_states.delete_usage_warning":
    "Used by {templateCount, plural, one {# template} other {# templates}} and {workflowCount, plural, one {# project workflow} other {# project workflows}}. Deleting removes it from their workflows.",
  "workspace_settings.project_states.form.name_label": "Name",
  "workspace_settings.project_states.form.name_placeholder": "e.g. In review",
  "workspace_settings.project_states.form.group_label": "Group",
  "workspace_settings.project_states.form.color_label": "Color",

  // Request states
  "workspace_settings.request_statuses.title": "Request Statuses",
  "workspace_settings.request_statuses.description":
    "Configure the statuses used by requests in this workspace.",
  "workspace_settings.request_statuses.group.new": "New",
  "workspace_settings.request_statuses.group.in_review": "In Review",
  "workspace_settings.request_statuses.group.waitlisted": "Waitlisted",
  "workspace_settings.request_statuses.group.accepted": "Accepted",
  "workspace_settings.request_statuses.group.delivered": "Delivered",
  "workspace_settings.request_statuses.group.declined": "Declined",
  "workspace_settings.request_statuses.group.cancelled": "Cancelled",
  "workspace_settings.request_statuses.group.covered": "Covered",
  "workspace_settings.request_statuses.add_state": "Add status",
  "workspace_settings.request_statuses.create": "Create status",
  "workspace_settings.request_statuses.save": "Save",
  "workspace_settings.request_statuses.edit": "Edit status",
  "workspace_settings.request_statuses.delete": "Delete status",
  "workspace_settings.request_statuses.deleting": "Deleting…",
  "workspace_settings.request_statuses.mark_default": "Mark as default",
  "workspace_settings.request_statuses.default_badge": "Default",
  "workspace_settings.request_statuses.empty_group": "No statuses in this group.",
  "workspace_settings.request_statuses.drag_handle": "Drag to reorder",
  "workspace_settings.request_statuses.cannot_delete_default":
    "The default status cannot be deleted. Mark another status as default first.",
  "workspace_settings.request_statuses.create_success": "Status created.",
  "workspace_settings.request_statuses.create_error": "Failed to create status.",
  "workspace_settings.request_statuses.update_success": "Status updated.",
  "workspace_settings.request_statuses.update_error": "Failed to update status.",
  "workspace_settings.request_statuses.delete_success": "Status deleted.",
  "workspace_settings.request_statuses.delete_error": "Failed to delete status.",
  "workspace_settings.request_statuses.mark_default_success": "Default status updated.",
  "workspace_settings.request_statuses.mark_default_error": "Failed to mark status as default.",
  "workspace_settings.request_statuses.reorder_error": "Failed to reorder statuses.",
  "workspace_settings.request_statuses.delete_title": "Delete status",
  "workspace_settings.request_statuses.delete_description":
    'Delete the status "{name}"? Requests assigned to it may need to be reassigned.',
  "workspace_settings.request_statuses.form.name_label": "Name",
  "workspace_settings.request_statuses.form.name_placeholder": "e.g. Under review",
  "workspace_settings.request_statuses.form.group_label": "Group",
  "workspace_settings.request_statuses.form.color_label": "Color",

  // Request disposition reasons
  "workspace_settings.request_reasons.section_heading": "Reasons",
  "workspace_settings.request_reasons.count": "{count} reasons",
  "workspace_settings.request_reasons.add_value": "Add reason",
  "workspace_settings.request_reasons.add_label.declined": "New decline reason",
  "workspace_settings.request_reasons.add_label.waitlisted": "New waitlist reason",
  "workspace_settings.request_reasons.add_label.cancelled": "New cancel reason",
  "workspace_settings.request_reasons.add_placeholder.declined": "e.g. Budget availability",
  "workspace_settings.request_reasons.add_placeholder.waitlisted": "e.g. Resource constraints",
  "workspace_settings.request_reasons.add_placeholder.cancelled": "e.g. Timeline conflict",
  "workspace_settings.request_reasons.inactive_heading": "Retired",
  "workspace_settings.request_reasons.drag_handle": "Reorder reason",
  "workspace_settings.request_reasons.deactivate": "Retire reason",
  "workspace_settings.request_reasons.reactivate": "Restore reason",
  "workspace_settings.request_reasons.delete": "Delete reason",
  "workspace_settings.request_reasons.last_active_tooltip.declined":
    "This is the only decline reason left. A reviewer can't decline a request without one, so add another before retiring this.",
  "workspace_settings.request_reasons.last_active_tooltip.waitlisted":
    "This is the only waitlist reason left. A reviewer can't waitlist a request without one, so add another before retiring this.",
  "workspace_settings.request_reasons.last_active_tooltip.cancelled":
    "This is the only cancel reason left. A reviewer can't cancel a request without one, so add another before retiring this.",
  "workspace_settings.request_reasons.duplicate_error.declined":
    "A decline reason with that name already exists.",
  "workspace_settings.request_reasons.duplicate_error.waitlisted":
    "A waitlist reason with that name already exists.",
  "workspace_settings.request_reasons.duplicate_error.cancelled":
    "A cancel reason with that name already exists.",
  "workspace_settings.request_reasons.save_error": "Couldn't save that change.",
  "workspace_settings.request_reasons.reorder_error": "Couldn't save the new order.",
  "workspace_settings.request_reasons.delete_error": "Couldn't delete that reason.",
  "workspace_settings.request_reasons.toast_added": "Reason added",
  "workspace_settings.request_reasons.toast_renamed": "Reason renamed",
  "workspace_settings.request_reasons.toast_activated": "Reason restored",
  "workspace_settings.request_reasons.toast_deactivated": "Reason retired",
  "workspace_settings.request_reasons.toast_deleted": "Reason deleted",
  "workspace_settings.request_reasons.delete_confirm_title": "Delete this reason?",
  "workspace_settings.request_reasons.delete_confirm_body.declined":
    'Requests already declined for "{label}" will keep the reviewer note but lose the reason. Retiring it instead keeps the history intact.',
  "workspace_settings.request_reasons.delete_confirm_body.waitlisted":
    'Requests already waitlisted for "{label}" will keep the reviewer note but lose the reason. Retiring it instead keeps the history intact.',
  "workspace_settings.request_reasons.delete_confirm_body.cancelled":
    'Requests already cancelled for "{label}" will keep the reviewer note but lose the reason. Retiring it instead keeps the history intact.',
  "workspace_settings.request_reasons.delete_confirm_action": "Delete",

  // Processes
  // Marks an option a picker is only still showing because it is the CURRENT
  // value (NG-635). In the label rather than as a badge, because these lists are
  // rendered by half a dozen different controls and a text suffix is the one
  // thing all of them carry — and the one a screen reader reads out.
  "process.inactive_option": "{name} (inactive)",
  "workspace_settings.processes.title": "Processes",
  "workspace_settings.processes.description":
    "Define delivery processes (ADDIE, SAM, or custom) and their stages. Assign a process to a project template to track work items through each stage.",
  "workspace_settings.processes.system_badge": "Default",
  "workspace_settings.processes.stage_label": "Stages",
  "workspace_settings.processes.empty": "No processes defined yet.",
  "workspace_settings.processes.column.name": "Name",
  "workspace_settings.processes.column.description": "Description",
  "workspace_settings.processes.column.stages": "Stages",
  "workspace_settings.processes.column.type": "Type",
  "workspace_settings.processes.type.system": "Default",
  "workspace_settings.processes.type.custom": "Custom",
  "workspace_settings.processes.search_placeholder": "Search processes…",
  "workspace_settings.processes.edit_action": "Edit",
  "workspace_settings.processes.delete_action": "Delete",
  "workspace_settings.processes.new": "New process",
  "workspace_settings.processes.row_actions_label": "Actions for {name}",
  "workspace_settings.processes.select_all": "Select all processes",
  "workspace_settings.processes.select_one": "Select {name}",
  // Retirement (NG-635). "Deactivate" is the answer to a delete the in-use guard
  // has to refuse, so its wording has to say plainly what SURVIVES — an admin
  // who reads it as a soft delete will not use it.
  "workspace_settings.processes.inactive_badge": "Inactive",
  "workspace_settings.processes.column.status": "Status",
  "workspace_settings.processes.status.active": "Active",
  "workspace_settings.processes.status.inactive": "Inactive",
  "workspace_settings.processes.deactivate_action": "Deactivate",
  "workspace_settings.processes.reactivate_action": "Reactivate",
  "workspace_settings.processes.deactivate_title": "Deactivate process",
  "workspace_settings.processes.deactivate_description":
    "\u201c{name}\u201d will no longer be offered when someone picks a process. Projects and templates already using it keep it and carry on working, and you can reactivate it at any time.",
  "workspace_settings.processes.deactivate_confirm": "Deactivate",
  "workspace_settings.processes.deactivate_success": "Process deactivated.",
  "workspace_settings.processes.reactivate_success": "Process reactivated.",
  "workspace_settings.processes.deactivate_error": "Failed to deactivate the process.",
  "workspace_settings.processes.reactivate_error": "Failed to reactivate the process.",
  // Offered next to "View usage" on the blocked delete: the delete cannot happen,
  // and this is the thing the admin actually wanted — stop offering it.
  "workspace_settings.processes.editor.deactivate_instead": "Deactivate instead",
  "workspace_settings.processes.editor.inactive_notice":
    "This process is inactive. It is not offered for new projects or templates, and everything already using it is unaffected.",
  // Stage retirement. Same promise as the process one, and the sentence has to
  // carry it: nothing moves.
  "workspace_settings.processes.editor.stage_inactive_chip": "Inactive",
  "workspace_settings.processes.editor.deactivate_stage": "Deactivate {name}",
  "workspace_settings.processes.editor.reactivate_stage": "Reactivate {name}",
  "workspace_settings.processes.editor.deactivate_stage_title": "Deactivate stage",
  "workspace_settings.processes.editor.deactivate_stage_description":
    "\u201c{name}\u201d will no longer be offered when someone picks a stage. Work items already in it stay where they are and carry on working, and you can reactivate it at any time.",
  "workspace_settings.processes.editor.stage_deactivated": "Stage deactivated.",
  "workspace_settings.processes.editor.stage_reactivated": "Stage reactivated.",
  // The move dialog a non-empty Remove opens (NG-635). It replaces a disabled
  // button: the stage CAN go, the work in it just has to land somewhere.
  "workspace_settings.processes.editor.move_stage_title": "Move work items",
  "workspace_settings.processes.editor.move_stage_description":
    "\u201c{name}\u201d holds {count, plural, one {# work item} other {# work items}}. Choose the stage to move {count, plural, one {it} other {them}} to, then the stage is removed.",
  "workspace_settings.processes.editor.move_stage_target_label": "Move to",
  "workspace_settings.processes.editor.move_stage_target_placeholder": "Choose a stage",
  "workspace_settings.processes.editor.move_stage_confirm": "Move and remove",
  "workspace_settings.processes.editor.move_stage_moving": "Moving…",
  "workspace_settings.processes.editor.move_stage_none":
    "This is the only active stage left, so there is nowhere to move its work items. Add another stage first.",
  "workspace_settings.processes.editor.remove_stage_last":
    "There is no active stage left to move this stage's work items to. Add or reactivate one first.",
  // Custom process editor (NG-634), behind the custom-processes flag.
  "workspace_settings.processes.editor.create_title": "New process",
  "workspace_settings.processes.editor.create_description":
    "Name the process. You'll add its stages on the next screen.",
  "workspace_settings.processes.editor.create_stages_hint":
    "Stages are configured after the process is created.",
  "workspace_settings.processes.editor.creating": "Creating…",
  "workspace_settings.processes.editor.optional": "(optional)",
  "workspace_settings.processes.editor.name_required": "A process needs a name to be saved.",
  "workspace_settings.processes.editor.page_subtitle":
    "Changes to the name and description save on their own. Stage edits apply immediately.",
  "workspace_settings.processes.editor.load_error": "Failed to load this process.",
  "workspace_settings.processes.editor.not_found":
    "That process no longer exists. It may have been deleted.",
  "workspace_settings.processes.editor.stage_removed": "Stage removed.",
  "workspace_settings.processes.editor.stage_save_error": "Failed to save the stage.",
  "workspace_settings.processes.editor.stage_delete_blocked":
    "This stage still holds {count, plural, one {# work item} other {# work items}}. Move them to another stage before removing it.",
  "workspace_settings.processes.editor.name_label": "Name",
  "workspace_settings.processes.editor.name_placeholder": "e.g. Rapid response",
  "workspace_settings.processes.editor.description_label": "Description",
  "workspace_settings.processes.editor.description_placeholder":
    "What this process is for, and when to pick it",
  "workspace_settings.processes.column.team_access": "Team access",
  "workspace_settings.processes.team_access_all_teams": "All teams",
  "workspace_settings.processes.team_access_hidden":
    "+{count, plural, one {# private team} other {# private teams}}",
  "workspace_settings.processes.form.team_access_label": "Team access",
  "workspace_settings.processes.form.team_access_placeholder": "Select teams",
  "workspace_settings.processes.form.team_access_all_teams": "All teams",
  // Deliberately NOT a copy of the template hint above: that string preserves a
  // missing "to" verbatim from #2926, and replicating a typo into new copy would
  // make it look intentional in both places.
  "workspace_settings.processes.form.team_access_hint":
    "Selected teams and every team beneath them in the hierarchy will be able to use this process on their projects.",
  "workspace_settings.processes.form.team_access_locked_hint":
    "This process will be available to this team and every team beneath it.",
  "workspace_settings.processes.editor.stages_label": "Stages",
  "workspace_settings.processes.editor.stages_hint":
    "Drag a stage by its handle to change the order projects move through.",
  "workspace_settings.processes.editor.stages_empty": "No stages yet.",
  "workspace_settings.processes.editor.add_stage": "Add stage",
  "workspace_settings.processes.editor.stage_name_label": "Stage {position} name",
  "workspace_settings.processes.editor.stage_name_placeholder": "Stage name",
  "workspace_settings.processes.editor.stage_name_duplicate":
    "Two stages of one process cannot share a name.",
  "workspace_settings.processes.editor.unnamed_stage": "this stage",
  "workspace_settings.processes.editor.drag_handle": "Drag to reorder {name}",
  "workspace_settings.processes.editor.remove_stage": "Remove {name}",
  "workspace_settings.processes.editor.create": "Create process",
  "workspace_settings.processes.editor.create_success": "Process created.",
  "workspace_settings.processes.editor.create_error": "Failed to create the process.",
  "workspace_settings.processes.editor.update_error": "Failed to save the process.",
  "workspace_settings.processes.editor.usage_error": "Failed to check what uses this process.",
  "workspace_settings.processes.editor.delete_title": "Delete process",
  "workspace_settings.processes.editor.delete_description":
    'Delete the process "{name}"? This cannot be undone.',
  "workspace_settings.processes.editor.delete_blocked_both":
    "Used by {projectCount, plural, one {# project} other {# projects}} and {templateCount, plural, one {# template} other {# templates}}. Move them to another process before deleting this one.",
  "workspace_settings.processes.editor.delete_blocked_projects":
    "Used by {projectCount, plural, one {# project} other {# projects}}. Move {projectCount, plural, one {it} other {them}} to another process before deleting this one.",
  "workspace_settings.processes.editor.delete_blocked_templates":
    "Used by {templateCount, plural, one {# template} other {# templates}}. Move {templateCount, plural, one {it} other {them}} to another process before deleting this one.",
  "workspace_settings.processes.editor.delete": "Delete process",
  "workspace_settings.processes.editor.deleting": "Deleting…",
  "workspace_settings.processes.editor.delete_success": "Process deleted.",
  "workspace_settings.processes.editor.delete_error": "Failed to delete the process.",
  // Details / Usage tabs on the editor (NG-723). The blocked-delete and
  // blocked-stage messages link here, so "View usage" is the one wording that
  // has to match what the tab is called.
  "workspace_settings.processes.editor.tab.details": "Details",
  "workspace_settings.processes.editor.tab.usage": "Usage",
  "workspace_settings.processes.editor.view_usage": "View usage",
  "workspace_settings.processes.editor.usage.loading": "Checking what uses this process…",
  "workspace_settings.processes.editor.usage.projects_heading": "Projects",
  "workspace_settings.processes.editor.usage.projects_empty": "No projects use this process yet.",
  // No total: `shown` is fenced to what the reader can see and the count is the
  // unfenced one, so "showing 50 of 312" would invite the arithmetic that
  // subtracting the two makes sense — it does not.
  "workspace_settings.processes.editor.usage.projects_truncated":
    "Showing the first {shown, plural, one {# project} other {# projects}}.",
  "workspace_settings.processes.editor.usage.templates_heading": "Project templates",
  "workspace_settings.processes.editor.usage.templates_empty":
    "No project templates use this process yet.",
  "workspace_settings.processes.editor.usage.templates_truncated":
    "Showing the first {shown, plural, one {# template} other {# templates}}.",
  "workspace_settings.processes.editor.usage.column.project": "Project",
  "workspace_settings.processes.editor.usage.column.identifier": "ID",
  "workspace_settings.processes.editor.usage.column.current_stage": "Current stage",
  "workspace_settings.processes.editor.usage.column.template": "Template",
  "workspace_settings.processes.editor.usage.no_current_stage": "Not started",
  // Sits under the stage list whenever a stage is un-removable, so the reason a
  // × is disabled has a reachable route to the numbers behind it — a tooltip
  // cannot hold a link a keyboard user can follow.
  "workspace_settings.processes.editor.stages_blocked_hint":
    "{count, plural, one {# stage holds work items and has no active stage to move them to, so it cannot be removed.} other {# stages hold work items and have no active stage to move them to, so they cannot be removed.}}",

  // Project creation — template select
  "projects.estimate_summary.title": "Estimate vs Actual",
  "projects.estimate_summary.actual_label": "Actual cost",
  "projects.estimate_summary.estimated_label": "Estimated cost",
  "projects.estimate_summary.variance_label": "Variance",
  "projects.estimate_summary.over_estimate": "Over estimate",
  "projects.estimate_summary.under_estimate": "Under estimate",
  "projects.estimate_summary.on_estimate": "On estimate",
  "projects.import.action": "Import projects",
  "projects.import.title": "Import projects",
  "projects.import.loading_definition": "Loading import fields…",
  "projects.import.toast_definition_error": "Couldn't load the import fields. Try again.",
  "projects.import.definition_error_title": "Import fields unavailable",
  "projects.import.definition_error":
    "The importable fields couldn't be loaded, so a file can't be mapped. Close this dialog and try again.",
  "projects.import.error_required": "{field} is required.",
  "projects.import.error_invalid_date":
    "{field}: ''{value}'' isn't a valid date. Use YYYY-MM-DD, e.g. 2026-01-15.",
  "projects.import.error_invalid_date_suggest":
    "{field}: ''{value}'' isn't a valid date. Use YYYY-MM-DD, e.g. 2026-01-15. Did you mean {suggestion}?",
  // The short format hint shown against a date-mapped column on the Map columns step.
  "projects.import.map_date_hint": "Format: YYYY-MM-DD",
  // Upload-step help text: the date rule up front, and the Excel round-trip caveat.
  "projects.import.date_format_note": "Dates must be in YYYY-MM-DD format (e.g. 2026-01-15).",
  "projects.import.excel_date_note":
    "Saving from Excel can change date formats. Check that your dates are YYYY-MM-DD before uploading.",
  "projects.import.checking": "Checking your file…",
  "projects.import.importing": "Importing…",
  "projects.import.check_failed_title": "Couldn't check this file",
  "projects.import.check_failed_body":
    "Something went wrong checking your file against this workspace. Try again.",
  "projects.import.check_retry": "Retry",
  "projects.import.toast_success": "{count, plural, one {# project} other {# projects}} imported.",
  "projects.import.toast_commit_error": "Something went wrong importing your file. Try again.",
  "projects.import.preview_title": "Import preview",
  "projects.import.preview_summary":
    "{valid, plural, one {# project} other {# projects}} will be created. {invalid, plural, one {# row} other {# rows}} cannot be imported.",
  "projects.import.preview_count_valid":
    "{count, plural, one {# will be created} other {# will be created}}",
  "projects.import.preview_count_invalid":
    "{count, plural, one {# cannot be imported} other {# cannot be imported}}",
  "projects.import.preview_errors_heading": "Rows that will not be imported",
  // Single-banner states for the redesigned Review step (NG-2348).
  "projects.import.preview_title_ready": "{valid} of {total} rows are ready to import",
  "projects.import.preview_title_none": "None of the {total} rows can be imported yet",
  "projects.import.preview_summary_all": "Every row is ready to import.",
  "projects.import.preview_summary_some":
    "{invalid, plural, one {# row has} other {# rows have}} problems and will be skipped. Fix them below, or import the {valid} ready rows now.",
  "projects.import.preview_summary_none": "Fix the problems below, then upload the file again.",
  "projects.import.preview_count_issues":
    "{count, plural, one {# has issues} other {# have issues}}",
  "projects.import.issues_to_fix_heading":
    "{count, plural, one {# issue to fix} other {# issues to fix}}",
  "projects.import.issue_problem_line": "{message} ({count, plural, one {# row} other {# rows}})",
  "projects.import.issue_unknown": "This row can't be imported.",
  "projects.import.fields_unset_heading": "Projects importing with some custom fields left unset",
  "projects.import.unset_field_detail": "{field}: {reason}",
  "projects.import.count_created": "{count, plural, one {# created} other {# created}}",
  "projects.import.count_errors": "{count, plural, one {# errored} other {# errored}}",
  "projects.import.result_summary":
    "{created, plural, one {# project} other {# projects}} created. {errors, plural, one {# row} other {# rows}} errored.",
  "projects.import.download_failed": "Download failed rows",
  "projects.import.failed_reason_header": "Import error",
  "projects.import.example_name": "Onboarding Revamp",
  "projects.import.example_start_date": "2026-01-15",
  "projects.import.example_description": "Refresh the new-hire onboarding curriculum.",
  "projects.import.example_due_date": "2026-03-31",
  "projects.import.example_launch_date": "2026-04-15",
  "projects.import.warn_unknown_option": "{field}: “{value}” is not a known option.",
  "projects.import.warn_ambiguous_option":
    "{field}: “{value}” matches more than one option; map it to the one you mean.",
  "projects.import.error_date_order": "{field} cannot be before the start date.",
  "projects.import.field_due_date": "Due date",
  "projects.import.field_launch_date": "Launch date",
  "projects.layout_paging.loading": "Loading projects… {loaded} of {total}",
  "projects.layout_paging.showing": "Showing {loaded} of {total} projects",
  "projects.layout_paging.capped_description":
    "Narrow the list with search or filters, or load the next batch.",
  "projects.layout_paging.failed_title": "Couldn't load all projects",
  "projects.cost.title": "Cost",
  "projects.cost.estimated_label": "Estimated",
  "projects.cost.actual_label": "Actual",
  "projects.cost.variance_over": "{amount} over estimate",
  "projects.cost.variance_under": "{amount} under estimate",
  "projects.cost.variance_on": "On estimate",
  "projects.cost.by_task": "By task",
  "projects.cost.by_person": "By person",
  "projects.cost.task_col": "Task",
  "projects.cost.person_col": "Person",
  "projects.cost.hours_col": "Hours",
  "projects.cost.no_tasks": "No tasks with cost yet.",
  "projects.cost.no_people": "No logged time yet.",
  "projects.cost.untitled": "Untitled",
  "projects.cost.load_error": "Unavailable",
  "projects.cost.hint":
    "Estimated is scope (estimated hours × rate); actual is logged time × rate. Time by people without a rate is costed at 0.",
  "projects.create.template_select.placeholder": "Use a template",
  "projects.create.template_select.none": "No template",
  "projects.create.template_select.search": "Search templates",
  "projects.create.template_select.clear": "Clear template",
  "projects.create.template_select.admin_add": "Add new template",
  "projects.create.template_select.no_results": "No templates found",
  "projects.create.template_label": "Template",
  "project_create.custom_fields.title": "Custom Fields",
  "project_create.custom_fields.required_error": "This field is required.",

  // Common
  "common.add": "Add",
  "common.cancel": "Cancel",
  "common.confirm": "Confirm",
  "common.delete": "Delete",
  "common.download": "Download",
  "common.not_authorized_title": "Access restricted",
  "common.not_authorized_description":
    "You don't have permission to view this page. Contact your workspace administrator for access.",
  "common.coming_soon": "Coming soon",
  "common.load_more": "Load more",
  "common.loading": "Loading...",
  "common.loading_more": "Loading…",
  "common.more": "More",
  "common.less": "Less",
  "comments.mention.empty": "No matching people",
  "comments.reply": "Reply",
  "comments.reply_placeholder": "Reply…",
  "comments.author_fallback": "Someone",
  "common.untitled": "Untitled",
  "common.remove": "Remove",
  "common.retry": "Retry",
  "common.save": "Save",
  "common.search": "Search",
  "common.empty_placeholder": "—",
  "common.not_found": "Page not found",
  "common.not_found_description": "The page you're looking for doesn't exist.",
  "common.go_home": "Go home",

  // Shared — Timezone select
  "shared.timezone.placeholder": "Select a time zone",
  "shared.timezone.search_placeholder": "Search by city or time zone...",
  "shared.timezone.no_matches": "No time zones found",
  "shared.timezone.more_results":
    "{count, plural, one {# more match. Keep typing to narrow.} other {# more matches. Keep typing to narrow.}}",

  "shared.currency.placeholder": "Select a currency",
  "shared.currency.search_placeholder": "Search by name or code...",
  "shared.currency.no_matches": "No currencies found",

  // Global field picker — shared by the template form and the
  // intake form builder (NG-689).
  "shared.global_field_picker.title": "Global fields",
  "shared.global_field_picker.message":
    "These fields are managed centrally by your admin and stay in sync wherever they are used. Tick a field to add it, untick one to remove it.",
  "shared.global_field_picker.search_placeholder": "Search by name or identifier…",
  "shared.global_field_picker.col_name": "Name",
  "shared.global_field_picker.col_description": "Description",
  "shared.global_field_picker.col_identifier": "Identifier",
  "shared.global_field_picker.col_type": "Type",
  "shared.global_field_picker.col_updated": "Last updated",
  "shared.global_field_picker.cancel": "Cancel",
  "shared.global_field_picker.save": "Save",
  "shared.global_field_picker.create": "Create global field",

  // Connected apps: the OAuth app detail page (NG-2581)
  "oauth_apps.detail.loading": "Loading…",
  "oauth_apps.detail.load_error": "Failed to load the app.",
  "oauth_apps.detail.save_error": "Save failed.",
  "oauth_apps.detail.delete_confirm": "Delete {name}? Existing tokens will fail introspection.",
  "oauth_apps.detail.back": "← Back to connected apps",
  "oauth_apps.detail.owner_instance": "Instance-owned (Cognota-verified)",
  "oauth_apps.detail.owner_workspace": "Workspace-owned · {workspace}",
  "oauth_apps.detail.enabled": "Enabled",
  "oauth_apps.detail.disable_note":
    "Disabling blocks new token issuance and introspection. Already-issued tokens remain valid until they expire — to immediately revoke them, the user (or you, on their behalf) must revoke each consent grant.",
  "oauth_apps.detail.section_basic": "Basic info",
  "oauth_apps.detail.field_name": "Name",
  "oauth_apps.detail.field_description": "Description",
  "oauth_apps.detail.field_homepage": "Homepage URL",
  "oauth_apps.detail.field_logo": "Logo URL",
  "oauth_apps.detail.section_credentials": "Credentials",
  "oauth_apps.detail.field_client_id": "Client ID",
  "oauth_apps.detail.field_client_type": "Client type",
  "oauth_apps.detail.rotate_secret": "Rotate client secret",
  "oauth_apps.detail.public_client_note":
    "Public clients have no secret. PKCE protects them at the token endpoint.",
  "oauth_apps.detail.section_redirect_uris": "Redirect URIs",
  "oauth_apps.detail.section_scopes": "Scopes",
  "oauth_apps.detail.scopes_note":
    "Removing a scope here does not narrow tokens already issued — those carry the scope set they were granted. The change applies to future tokens only.",
  "oauth_apps.detail.section_auto_trust": "Auto-trust",
  "oauth_apps.detail.auto_trust_label": "Skip the user consent screen on first use.",
  "oauth_apps.detail.auto_trust_note":
    "Use sparingly — only for Cognota-operated apps where the consent decision is implicit in the user adopting the integration.",
  "oauth_apps.detail.section_danger": "Danger zone",
  "oauth_apps.detail.delete_app": "Delete app",
  "oauth_apps.detail.destructive_confirm_aria": "Confirm destructive scopes",
  "oauth_apps.detail.destructive_confirm":
    "I understand these scopes can create, modify, or delete data.",
  "oauth_apps.detail.discard": "Discard",
  "oauth_apps.detail.saving": "Saving…",
  "oauth_apps.detail.save": "Save changes",

  // Onboarding
  "onboarding.title": "Onboarding",
  "onboarding.complete_profile": "Complete your profile",
  "onboarding.display_name": "Display name",
  "onboarding.get_started": "Get started",
  "onboarding.create_workspace": "Create a workspace",
  "onboarding.join_workspace": "Join with an invite",
  "onboarding.workspace_name": "Workspace name",
  "onboarding.workspace_slug": "Workspace URL",

  // Errors
  "error.instance_not_configured": "Instance not configured",
  "error.instance_not_configured_desc": "Please contact your administrator.",
  "error.bootstrap_failed": "Something went wrong loading the app. Please try again.",
  "error.instance_setup_incomplete": "Instance setup is not complete. Contact your administrator.",

  // Invitations
  "invitation.dashboard_title": "Your Workspace Invitations",
  "invitation.accept_join": "Accept & Join ({count})",
  "invitation.accept_join_single": "Accept & Join",
  "invitation.accept_success": "Invitations accepted successfully",
  "invitation.accept_error": "Failed to accept invitations",
  "invitation.back_to_home": "Back to home",
  "invitation.empty_title": "No pending invitations",
  "invitation.empty_description": "You don't have any workspace invitations at the moment.",
  "invitation.error_title": "Couldn't load your invitations",
  "invitation.error_description":
    "Something went wrong loading your workspace invitations. This isn't a sign that you have none — try again in a moment.",
  "invitation.invited_to_join": "You've been invited to join",
  "invitation.role_label": "Role",
  "invitation.role.admin": "Admin",
  "invitation.role.member": "Standard User",
  "invitation.role.guest": "Guest",
  "invitation.ignore": "Ignore",
  // Public invitation landing page
  "invitation.first_name": "First name",
  "invitation.last_name": "Last name",
  "invitation.first_name_required": "Please enter your first name.",
  "invitation.last_name_required": "Please enter your last name.",
  "invitation.create_account_join": "Create account & join",
  "invitation.password_requirements":
    "At least 8 characters. Avoid common passwords, ones that have appeared in a data breach, and anything close to your name or email.",
  "invitation.password_too_common": "This password is too common.",
  "invitation.password_too_similar": "This password is too close to your name or email.",
  "invitation.password_breached": "This password has appeared in a data breach.",
  "invitation.password_refused": "That password can't be used.",
  "invitation.password_choose_another": "Choose a different one.",
  "invitation.already_have_account": "You already have an account. Log in to join this workspace.",
  "invitation.log_in_to_join": "Log in",
  "invitation.invalid_title": "This invitation is no longer valid",
  "invitation.wrong_account_title": "You're signed in as someone else",
  "invitation.wrong_account_description":
    "This invitation is for {invited}, but you're signed in as {current}. Open the invitation link in a private window, or sign out and follow it again.",
  "invitation.wrong_account_sign_out": "Sign out",
  "invitation.invalid_description":
    "It may have expired, already been used, or been revoked by the workspace admin. Contact your workspace admin for a new invitation.",
  "invitation.retry_title": "Something went wrong loading this invitation",
  "invitation.retry_description":
    "The problem may be temporary — your invitation is most likely still fine. Try again in a moment, and contact your workspace admin if it keeps failing.",
  "invitation.joined_success": "You've joined the workspace",
  "invitation.ignored": "Invitation ignored",
  "invitation.respond_error": "Failed to respond to invitation",
  "invitation.not_found": "Invitation not found",
  "invitation.already_responded": "This invitation has already been responded to",
  "invitation.already_member": "You're already a member of this workspace",

  // Project
  "project.tabs.costs": "Costs",
  "project.tabs.resources": "Resources",
  // Delivery and Results became project tabs in NG-1557; they were settings tabs,
  // and the old project_settings.tabs.* keys went with them.
  "project.tabs.delivery": "Delivery",
  "project.tabs.results": "Results",

  // Project tabs layout and overview route (NG-2583)
  "project.tabs.overview": "Overview",
  "project.tabs.work_items": "Work items",
  "project.tabs.files": "Files",
  "project.tabs.aria": "Project sections",
  "project_overview.requests_heading": "Requests",
  "project_overview.link_request": "Link Request",
  "project_overview.close_comments": "Close comments",

  // Project › Files
  "project.files.upload": "Upload file",
  "project.files.uploading": "Uploading…",
  "project.files.loading": "Loading files…",
  "project.files.load_failed": "Failed to load project files.",
  "project.files.empty_title": "No files attached to this project yet.",
  "project.files.empty_hint":
    "Upload a file or convert a request with attachments to populate this list.",
  "project.files.link": "Link",
  "project.files.from_request": 'From request "{label}"',
  "project.files.from_work_item": 'From work item "{label}"',
  "project.files.remove_label": "Remove {name}",
  "project.files.uploaded_toast": '"{name}" uploaded.',
  "project.files.upload_failed_toast": 'Failed to upload "{name}".',
  "project.files.removed_toast": '"{name}" removed.',
  "project.files.remove_failed_toast": 'Failed to remove "{name}".',
  "project.create_title": "Create Project",
  "project.name_label": "Name",
  "project.name_placeholder": "My Project",
  "project.identifier_label": "Identifier",
  "project.identifier_checking": "Checking...",
  "project.identifier_taken": "This identifier is already in use",
  "project.identifier_available": "Identifier is available",
  "project.description_label": "Description",
  "project.description_placeholder": "What is this project about?",
  "project.description_save_error": "Failed to save project description",
  "project.privacy_label": "Privacy",
  "project.process_label": "Process",
  "project.process_none": "No process",
  "project.stage_label": "Stage",
  "project.stage_derived_hint": "Derived from task completion",
  "project.statuses_loading": "Loading statuses...",
  "project.processes_loading": "Loading processes...",
  "project.category_label": "Category",
  "project.project_type_label": "Project type",
  "project.project_type_placeholder": "Course Development, Onboarding, …",
  "project.start_date_label": "Start date",
  "project.due_date_label": "Due date",
  "project.client_teams_label": "Client teams",
  "project.client_teams_placeholder": "Select client teams…",
  "project.delivery_teams_label": "Delivery teams",
  "project.delivery_teams_placeholder": "Select delivery teams…",
  "project.delivery_teams_loading": "Loading teams…",
  "project.delivery_teams_min_one": "A project must be delivered by at least one team.",
  "project.teams_label": "Teams",
  "project.teams_placeholder": "Select teams...",
  "project.current_team_not_selected_warning": "Current team is not included in the selection",
  "project.no_teams": "No teams in this workspace yet.",
  "project.team_select_placeholder": "Select teams…",
  "project.team_search_placeholder": "Search teams…",
  "project.programs_label": "Programs",
  "project.programs_placeholder": "Select programs…",
  "project.programs_search_placeholder": "Search programs…",
  "project.no_programs": "No programs available",
  "project.programs_trigger_aria_label": "Select programs",
  "project.goals_label": "Goals",
  "project.goals_placeholder": "Select goals…",
  "project.goals_search_placeholder": "Search goals…",
  "project.no_goals": "No goals available",
  "project.goals_trigger_aria_label": "Select goals",

  // Project properties panel (NG-2583)
  "project.status_label": "Status",
  "project.panel_start_date": "Start Date",
  "project.panel_due_date": "Due Date",
  "project.panel_completion_date": "Completion Date",
  "project.panel_launch_date": "Launch Date",
  "project.processes_search_placeholder": "Search processes...",
  "project.no_processes": "No processes available",
  "project.change_process_title": "Change process?",
  "project.change_process_description":
    "Switching the process clears the stage assignment on every work item in this project. Work items keep their other fields.",
  "project.change_process_confirm": "Change process",
  "project.panel_programs_search_placeholder": "Search programs...",
  "project.program_remove_aria": "Remove from {name}",
  "project.program_added": "Project added to program",
  "project.program_add_error": "Failed to add project to program",
  "project.program_removed": "Project removed from program",
  "project.program_remove_error": "Failed to remove project from program",
  "project.viewers_label": "Viewers",
  "project.viewer_add_me": "Add me",
  "project.viewer_add_aria": "Add viewer",
  "project.viewer_remove_aria": "Remove viewer {name}",
  "project.viewer_add_error": "Failed to add viewer",
  "project.viewer_remove_error": "Failed to remove viewer",
  "project.members_search_placeholder": "Search members...",
  "project.no_members_available": "No members available",
  "project.custom_fields_search_placeholder": "Search fields...",
  "project.no_custom_fields_available": "No fields available",
  "project.custom_fields_empty": "No global fields yet.",
  "project.custom_field_remove_aria": "Remove {name}",
  "project.custom_field_remove_title": "Remove “{name}”?",
  "project.custom_field_remove_body":
    "This removes the field from this project. Saved values will be lost.",
  "project.custom_field_save_error": "Failed to save custom field",
  "project.custom_field_add_error": "Failed to add custom field",
  "project.custom_field_remove_error": "Failed to remove custom field",

  "project.privacy.public": "Public",
  "project.privacy.secret": "Secret",
  "project.lead_label": "Project Leads",
  "project.select_lead": "Select leads...",
  "project.no_lead": "No lead",
  "project.member_select.clear": "Clear",
  "project.leads_selected_count": "{count} leads selected",
  "project.lead_not_in_team_warning": "A selected lead is not a member of the current team",
  "project.create_project": "Create Project",
  "project.create_success": "Project created successfully",
  "project.create_error": "Failed to create project",
  "project.linked_requests.link_error": "Failed to link the request",

  // Project overview cards and dialogs (NG-2583)
  "project.team_required": "At least one team is required",
  "team_projects.layout_aria": "Project layout",
  "project_overview.activity_no_match": "No matching activity.",
  "project.linked_requests.dialog_title": "Link a Request",
  "project.linked_requests.search_placeholder": "Search requests…",
  "project.linked_requests.loading": "Loading requests…",
  "project.linked_requests.no_match": "No requests match your search.",
  "project.linked_requests.none_available": "No requests available to link.",
  "project.linked_requests.fallback_name": "Request",
  "project.linked_requests.origin": "Origin",
  "project.linked_requests.origin_title": "This project was created from this request",
  "project.linked_requests.unlink_aria": "Unlink request",
  "project.linked_requests.empty": "No requests linked yet.",

  "project.page_title": "Projects",
  "project.create_new": "New Project",
  "project.search_placeholder": "Search projects...",
  "team_projects.summary": "{count} projects · {in_progress} in progress",
  "team_projects.view.no_status": "No status",
  "team_projects.board.collapse_column": "Collapse {name}",
  "team_projects.board.expand_column": "Expand {name}",
  "board.viewing_prefix": "Viewing:",
  "team_projects.board.no_process": "No process",
  "team_projects.board.no_process_selected": "Choose a process to see its stages.",
  "team_projects.board.no_processes_hint": "Add a process in Settings › Processes",
  "team_projects.board.other_processes": "{count} hidden on other processes",
  "team_projects.view.column_empty": "No projects",
  "team_projects.view.calendar_empty": "No projects scheduled this month",
  "team_projects.view.calendar_more": "+{count} more",
  "team_projects.group_by.none": "No grouping",
  "team_projects.group_by.status": "Status",
  "team_projects.group_by.category": "Category",
  "team_projects.group_by.lead": "Lead",
  "team_projects.group_by.delivery_teams": "Teams",
  "team_projects.group_by.programs": "Program",
  "team_projects.group_by.none_status": "No status",
  "team_projects.group_by.none_category": "No category",
  "team_projects.group_by.none_lead": "No lead",
  "team_projects.group_by.none_team": "No team",
  "team_projects.group_by.none_program": "No program",
  "team_projects.view.timeline_empty": "No projects to show",
  "team_projects.view.no_dates": "No dates",
  "programs.view.timeline_empty": "No programs to show",
  "programs.view.no_dates": "No dates",
  "programs.view.board_column_empty": "No programs",
  "programs.toast.archive_failed": "Couldn't archive {name}",
  "programs.toast.delete_failed": "Couldn't delete {name}",
  // Programs list table: columns, cells and row actions (NG-2579)
  "programs.status.planning": "Planning",
  "programs.status.active": "Active",
  "programs.status.on_hold": "On Hold",
  "programs.status.complete": "Complete",
  "programs.columns.select_all": "Select all programs",
  "programs.columns.select_one": "Select {name}",
  "programs.columns.name": "Name",
  "programs.columns.status": "Status",
  "programs.columns.owner": "Owner",
  "programs.columns.progress": "Progress",
  "programs.columns.health": "Health",
  "programs.columns.effort": "Effort",
  "programs.columns.start_date": "Start Date",
  "programs.columns.end_date": "End Date",
  "programs.columns.created": "Created",
  "programs.columns.goals": "Goals",
  "programs.columns.home_team": "Home team",
  "programs.columns.team_access": "Team Access",
  "programs.columns.actions": "Actions",
  "programs.columns.archived_badge": "Archived",
  "programs.columns.name_aria": "Program name",
  "programs.columns.rename": "Rename program",
  "programs.columns.status_aria": "Program status",
  "programs.columns.status_search": "Search statuses",
  "programs.columns.status_empty": "No statuses",
  "programs.columns.owner_aria": "Program owner",
  "programs.columns.owner_search": "Search members",
  "programs.columns.owner_empty": "No members",
  "programs.columns.owner_unset": "No owner",
  "programs.columns.start_date_aria": "Start date",
  "programs.columns.end_date_aria": "End date",
  "programs.columns.clear_date": "Clear date",
  "programs.columns.effort_estimated": "/ {hours}h est.",
  "programs.columns.options_aria": "Program options",
  "programs.columns.edit": "Edit",
  "programs.columns.archive": "Archive",
  "programs.columns.unarchive": "Unarchive",
  "team_projects.view.day": "Day",
  "team_projects.view.week": "Week",
  "team_projects.view.month": "Month",
  "team_projects.view.prev_period": "Previous period",
  "team_projects.view.next_period": "Next period",
  "team_projects.select_all": "Select all projects",
  "team_projects.select_project": "Select {name}",
  "data_table.cell.unassigned": "Unassigned",
  "data_table.cell.no_tasks": "No tasks",
  "data_table.cell.overdue": "Past target date",
  "data_table.cell.set_date": "Set date",
  "data_table.cell.clear_date": "Clear date",
  "overdue.label": "Overdue",
  "overdue.days_late": "{count, plural, one {# day late} other {# days late}}",
  // Accessible name for the table's overdue glyph. Keeps "Past target date"
  // verbatim (the description that already shipped) and appends the magnitude,
  // which is otherwise only on the tooltip.
  "overdue.aria":
    "{count, plural, =0 {Past target date} one {Past target date, # day late} other {Past target date, # days late}}",
  "overdue.filter": "Overdue only",
  "overdue.filter_group": "Due",
  "programs.export.label": "Export programs",
  // Programs list page and its create / edit dialog (NG-2578)
  "programs.list.title": "Programs",
  "programs.list.new_program": "New Program",
  "programs.list.export": "Export",
  "programs.list.export_preparing": "Preparing your export…",
  "programs.list.export_failed": "Export failed",
  "programs.list.empty": "No programs yet. Create your first program.",
  "programs.list.search": "Search programs",
  "programs.list.layout_aria": "Programs layout",
  "programs.list.layout_table": "Table",
  "programs.list.layout_board": "Board",
  "programs.list.layout_timeline": "Timeline",
  "programs.list.display": "Display",
  "programs.list.group": "Group",
  "programs.list.group_none": "No grouping",
  "programs.list.group_status": "Status",
  "programs.list.show_archived": "Show archived",
  "programs.list.table_options": "Table options",
  "programs.list.updated": "Program updated",
  "programs.list.update_error": "Failed to update program",
  "programs.list.archive_title": "Archive Program",
  "programs.list.archive_description":
    'Archive "{name}"? Archived programs won\'t appear in lists but remain available for reporting.',
  "programs.list.archive_confirm": "Archive",
  "programs.list.archived": "Program archived",
  "programs.list.unarchived": "Program unarchived",
  "programs.list.unarchive_error": "Failed to unarchive program",
  "programs.dialog.edit_title": "Edit Program",
  "programs.dialog.new_title": "New Program",
  "programs.dialog.name_label": "Name *",
  "programs.dialog.name_placeholder": "Program name",
  "programs.dialog.description_label": "Description",
  "programs.dialog.description_placeholder": "Optional description",
  "programs.dialog.owner_label": "Owner",
  "programs.dialog.owner_placeholder": "No owner",
  "programs.dialog.owner_empty": "No members found",
  "programs.dialog.status_label": "Status",
  "programs.dialog.start_date_label": "Start Date",
  "programs.dialog.end_date_label": "End Date",
  "programs.dialog.team_access_label": "Team Access *",
  "programs.dialog.teams_placeholder": "Select teams",
  "programs.dialog.teams_empty": "No teams available",
  "programs.dialog.team_access_help":
    "Choose which teams can access this program. Members and leads of the selected teams and their parent teams can see it. Program leads and workspace administrators also have access. Programs with no Team Access are visible to everyone in the workspace. You can only pick teams you have access to.",
  "programs.dialog.create": "Create",
  "programs.dialog.created": "Program created",
  "programs.dialog.updated": "Program updated",
  "programs.dialog.create_error": "Failed to create program",
  "programs.dialog.update_error": "Failed to update program",
  "data_table.import.label": "Import",
  "data_table.export.label": "Export",
  "data_table.export.preparing": "Preparing your export…",
  "data_table.export.ready": "Your export is ready — the download will start shortly.",
  "data_table.export.failed": "Export failed. Please try again.",
  "data_table.filter.clear": "Clear",
  "data_table.filter.contains": "Contains text…",
  "data_table.filter.search_options": "Search options",
  "data_table.filter.no_options": "No options",
  "data_table.filter.loading_options": "Loading options…",
  "data_table.filter.min": "Min",
  "data_table.filter.max": "Max",
  "data_table.filter.from": "From",
  "data_table.filter.to": "To",
  "data_table.filter.column_aria": "Filter {column}",
  "data_table.date.type_a_date": "Type a date",
  "data_table.date.type_a_date_placeholder": "YYYY-MM-DD",
  "data_table.columns.label": "Columns",
  "data_table.columns.toggle_aria": "Show or hide columns",
  "data_table.columns.heading": "Visible columns",
  "data_table.columns.show_all": "Show all",
  "data_table.columns.reorder_aria": "Reorder {column} (position {position} of {total})",
  "data_table.columns.reorder_hint": "Drag to reorder, or focus a handle and use the arrow keys.",
  "data_table.columns.reordered": "{column} moved to position {position} of {total}.",
  "data_table.columns.pinned_section": "Pinned to left",
  "data_table.columns.scroll_section": "Scroll",
  "data_table.columns.pinned_hint": "Stays visible while scrolling.",
  "data_table.columns.scroll_hint": "Scrolls horizontally with the table.",
  "data_table.columns.visible_count": "{visible} of {total} visible",
  "data_table.columns.reset_default": "Reset to default",
  "data_table.toolbar.clear_selection": "Clear selection",
  "data_table.toolbar.active_filters": "Active filters",
  "data_table.state.no_results_title": "No matching results",
  "data_table.state.no_results_description": "No rows match your current filters.",
  "data_table.state.clear_filters": "Clear filters",

  // Data table, progress bar and placeholder copy (NG-2585)
  "data_table.row.collapse": "Collapse row",
  "data_table.row.expand": "Expand row",
  "data_table.group.collapse": "Collapse group",
  "data_table.group.expand": "Expand group",
  "data_table.columns.resize_aria": "Resize {column}",
  "data_table.columns.drag_to_reorder": "Drag to reorder",
  "data_table.filter_chip.clear_one": "Clear filter on {label}",
  "data_table.filter_chip.clear_all": "Clear all filters",
  "common.loading_aria": "Loading",

  "data_table.state.load_failed_title": "Couldn't load this list",
  "data_table.state.try_again": "Try again",
  "data_table.state.load_more_failed": "Couldn't load more.",
  "data_table.state.retry": "Retry",
  "data_table.footer.showing": "Showing {loaded} of {total}",
  "data_table.footer.loading_more": "Loading more…",
  "data_table.pagination.showing_range": "Showing {from}-{to} of {total}",
  "data_table.pagination.previous": "Previous",
  "data_table.pagination.next": "Next",
  "data_table.toolbar.selected_count": "{count, plural, one {# selected} other {# selected}}",
  "data_table.toolbar.selection_region": "Selected rows",
  "data_table.bulk.delete": "Delete",
  "data_table.bulk.delete_confirm_title":
    "{count, plural, one {Delete # item?} other {Delete # items?}}",
  "data_table.bulk.delete_confirm_body":
    "{count, plural, one {This will delete the selected item.} other {This will delete the # selected items.}}",
  "data_table.bulk.delete_failed":
    "Couldn't delete the selected items. You may not have permission to delete some of them.",
  "data_table.bulk.deleted": "{count, plural, one {Deleted # item.} other {Deleted # items.}}",
  "data_table.bulk.deleted_none":
    "{count, plural, one {Couldn't delete the selected item.} other {Couldn't delete any of the # selected items.}}",
  "data_table.bulk.deleted_with_skipped":
    "Deleted {deleted}; skipped {skipped} you don't have permission to delete.",
  "data_table.bulk.deleting": "Deleting…",
  "data_table.bulk.deleting_progress": "Deleting… {processed} of {total}",
  "data_table.bulk.deleted_then_cancelled":
    "{count, plural, one {Cancelled after deleting # item.} other {Cancelled after deleting # items.}}",
  "data_table.bulk.select_all_matching": "Select all {count}",
  "data_table.bulk.cancel": "Cancel",
  "data_table.bulk.cancelling": "Cancelling…",
  "data_table.bulk.cancel_delete_note": "Items already deleted won't be restored.",
  "data_table.bulk.cancel_archive_note": "Items already archived stay archived.",
  "data_table.bulk.cancel_status_note": "Items already updated keep their new status.",
  "data_table.bulk.cancel_assignees_note": "Assignee changes already applied stay applied.",
  "data_table.bulk.eta_seconds":
    "{seconds, plural, one {~# second remaining} other {~# seconds remaining}}",
  "data_table.bulk.set_status": "Set status",
  "data_table.bulk.assign_leads": "Assign leads",
  "data_table.bulk.archive": "Archive",
  "data_table.bulk.archive_confirm_title":
    "{count, plural, one {Archive # item?} other {Archive # items?}}",
  "data_table.bulk.archive_confirm_body":
    "{count, plural, one {The selected item will move out of the active list. You can restore it from the archived view.} other {The # selected items will move out of the active list. You can restore them from the archived view.}}",
  "data_table.bulk.archive_failed":
    "Couldn't archive the selected items. You may not have permission to archive some of them.",
  "data_table.bulk.archived": "{count, plural, one {Archived # item.} other {Archived # items.}}",
  "data_table.bulk.archived_with_skipped":
    "Archived {archived}; skipped {skipped} you don't have permission to archive.",
  "data_table.bulk.archived_none":
    "{count, plural, one {Couldn't archive the selected item.} other {Couldn't archive any of the # selected items.}}",
  "data_table.bulk.archiving": "Archiving…",
  "data_table.bulk.archiving_progress": "Archiving… {processed} of {total}",
  "data_table.bulk.archived_then_cancelled":
    "{count, plural, one {Cancelled after archiving # item.} other {Cancelled after archiving # items.}}",
  "data_table.bulk.status_updated":
    "{count, plural, one {Updated the status of # item.} other {Updated the status of # items.}}",
  "data_table.bulk.status_updated_none":
    "{count, plural, one {Couldn't update the selected item.} other {Couldn't update any of the # selected items.}}",
  "data_table.bulk.status_updated_with_skipped":
    "Updated {updated}; skipped {skipped} you don't have permission to edit.",
  "data_table.bulk.status_update_failed":
    "Couldn't update the status of the selected items. You may not have permission to edit some of them.",
  "data_table.bulk.setting_status": "Updating status…",
  "data_table.bulk.setting_status_progress": "Updating status… {processed} of {total}",
  "data_table.bulk.status_updated_then_cancelled":
    "{count, plural, one {Cancelled after updating # item.} other {Cancelled after updating # items.}}",
  "data_table.bulk.set_assignees": "Assignees",
  "data_table.bulk.assignees_updated":
    "{count, plural, one {Updated assignees on # item.} other {Updated assignees on # items.}}",
  "data_table.bulk.assignees_updated_none":
    "{count, plural, one {Couldn't update the selected item.} other {Couldn't update any of the # selected items.}}",
  "data_table.bulk.assignees_updated_with_skipped":
    "Updated {updated}; skipped {skipped} you don't have permission to edit.",
  "data_table.bulk.assignees_update_failed":
    "Couldn't update assignees on the selected items. You may not have permission to edit some of them.",
  "data_table.bulk.setting_assignees": "Updating assignees…",
  "data_table.bulk.setting_assignees_progress": "Updating assignees… {processed} of {total}",
  "data_table.bulk.assignees_updated_then_cancelled":
    "{count, plural, one {Cancelled after updating # item.} other {Cancelled after updating # items.}}",
  "data_table.bulk.export": "Export",
  "data_table.bulk.exportComingSoon": "CSV export is coming soon.",
  "data_table.search.clear": "Clear search",
  "data_table.search.count_results": "{count, plural, one {# result} other {# results}}",
  "data_view.previous": "Previous",
  "data_view.next": "Next",
  "data_view.today": "Today",
  "data_view.show_archived": "Show archived",
  // NG-1601 — the Timeline's axis bounds, in the Display menu. "View
  // weekends" is the PRD's and the ticket's wording; the Timeline's own
  // toolbar still says "Show weekends" for the same setting
  // (timeline.settings.weekends) on the Plan page and the Event detail tab.
  "data_view.view_weekends": "View weekends",
  "data_view.hours": "Hours",
  "data_view.hours_from": "First hour shown",
  "data_view.hours_to": "Last hour shown",
  "data_view.hide_archived": "Hide archived",
  "data_view.filter": "Filter",
  "data_view.filter_ellipsis": "Filter…",
  "data_view.search_options": "Search options",
  "data_view.sort_by": "Sort",
  "data_view.display": "Display",
  "data_view.filters_applied": "{count} filters applied",
  "data_view.clear_all": "Clear all",
  "data_view.operator.is": "is",
  "data_view.operator.before": "before",
  "data_view.operator.after": "after",
  "data_view.operator.between": "between",
  "data_view.pick_a_date": "Pick a date",
  "data_view.start_date": "Start date",
  "data_view.end_date": "End date",
  "data_view.to": "to",
  "data_view.overdue": "Overdue",
  "data_view.over_capacity": "Overcapacity",
  "data_view.overdue_yes": "yes",
  "issue.overdue_filter.apply": "Show only overdue work",
  "issue.allocation_filter.apply":
    "Show only the {count} work items whose assignee is over capacity",
  "issue.allocation_filter.clear": "Stop filtering by over capacity",
  "issue.overdue_filter.clear": "Stop filtering by overdue",
  "data_view.operator.is_any_of": "is any of",
  "data_view.remove_filter": "Remove {field} filter",
  "data_view.reset": "Reset",
  "data_view.sort_ascending": "Sort ascending",
  "data_view.sort_descending": "Sort descending",
  "data_view.group_by": "Group",
  // NG-1593: which colour a Gantt / Timeline / Calendar bar draws in.
  // "Custom" deliberately, not "By event color" — tasks and milestones
  // carry a colour too.
  "data_view.color_code": "Color code",
  "data_view.color_code.status": "By status",
  "data_view.color_code.custom": "Custom",
  "data_view.clear_filters": "Clear filters",
  "data_view.loading_filters": "Loading filters…",
  "data_view.filter_search_placeholder": "Search filters...",
  "data_view.no_matches": "No matches.",
  "team_projects.count_noun": "{count, plural, one {project} other {projects}}",
  "team_projects.no_matches": "No projects match the current filters.",
  "team_projects.column.check": "",
  "team_projects.column.name": "Name",
  "team_projects.column.identifier": "Project ID",
  "team_projects.column.status": "Status",
  "team_projects.column.stage": "Stage",
  "team_projects.column.process": "Process",
  "team_projects.column.category": "Category",
  "team_projects.column.lead": "Lead",
  "team_projects.column.progress": "Progress",
  "team_projects.column.deliveryTeams": "Delivery teams",
  "team_projects.column.homeTeam": "Home team",
  "team_projects.column.clientTeams": "Client teams",
  "team_projects.column.openTasks": "Open tasks",
  "team_projects.column.startDate": "Start date",
  "team_projects.column.targetCompletion": "Target completion",
  "team_projects.column.actualCompletion": "Actual completion",
  "team_projects.column.targetLaunch": "Target launch",
  "team_projects.column.programs": "Programs",
  "team_projects.column.goals": "Goals",
  "team_projects.column.sourceTemplate": "Template",
  "team_projects.column.actions": "",
  "team_projects.filter.search_placeholder": "Search projects, leads, identifiers…",
  "team_projects.filter.clear_all": "Clear filters",
  "team_projects.actions.menu": "Row actions for {name}",
  "team_projects.actions.archive": "Archive",
  "team_projects.actions.duplicate": "Duplicate",
  "team_projects.actions.delete": "Delete",
  "team_projects.confirm.archive_title": "Archive project?",
  "team_projects.confirm.archive_description": "Archive {name}? You can restore it later.",
  "team_projects.confirm.delete_title": "Delete project?",
  "team_projects.confirm.delete_description": "Permanently delete {name}? This cannot be undone.",
  "team_projects.toast.archived": "Project archived",
  "team_projects.toast.duplicated": "Project duplicated",
  "team_projects.toast.duplicate_failed": "Couldn't duplicate the project",
  "team_projects.toast.deleted": "Project deleted",
  "team_projects.toast.archive_failed": "Couldn't archive {name}",
  "team_projects.toast.delete_failed": "Couldn't delete {name}",
  "team_projects.confirm.bulk_delete_title": "Delete {count} projects?",
  "team_projects.confirm.bulk_delete_description":
    "{count} projects will be permanently removed. This cannot be undone.",
  "team_projects.confirm.bulk_archive_title":
    "{count, plural, one {Archive # project?} other {Archive # projects?}}",
  "team_projects.confirm.bulk_archive_description":
    "{count, plural, one {The selected project will be archived. You can restore it later.} other {The # selected projects will be archived. You can restore them later.}}",
  "team_projects.bulk_status.updated":
    "{count, plural, one {Updated # project.} other {Updated # projects.}}",
  "team_projects.bulk_status.updated_with_skipped":
    "Updated {updated}; skipped {skipped} — blocked by workflow, needing approval, missing permission, or an unexpected error.",
  "team_projects.bulk_status.failed": "Couldn't update the selected projects' status.",
  "team_projects.bulk_leads.search_placeholder": "Search members...",
  "team_projects.bulk_leads.search_empty": "No members found",
  "team_projects.bulk_leads.skipped":
    "Couldn't assign that person as lead — they may not be eligible for the project.lead role.",
  "team_projects.bulk_leads.failed": "Couldn't update project leads for the selected projects.",
  "team_projects.toast.update_failed": "Couldn't save the change",
  "team_projects.edit.name_aria": "Edit name for {name}",
  "team_projects.edit.lead_aria": "Edit lead for {name}",
  "team_projects.edit.lead_search": "Search people…",
  "team_projects.edit.no_members": "No members",
  "team_projects.edit.no_options": "No options",
  "team_projects.edit.clear_date": "Clear date",
  "team_projects.edit.name_required": "Name can’t be empty",
  "team_projects.edit.rename": "Rename",
  "team_projects.edit.home_team_locked": "Home team",
  "team_projects.edit.home_team_aria": "Edit Home team for {name}",
  "team_projects.edit.saving": "Saving…",
  "team_projects.side_panel.title": "Project details",
  "team_projects.side_panel.description": "Read-only summary of project fields.",
  "team_projects.side_panel.close": "Close",
  "team_projects.side_panel.view_project": "View project",
  "team_projects.side_panel.open_task_count": "{count} open tasks",
  "team_projects.side_panel.section.people": "People",
  "team_projects.side_panel.section.dates": "Dates",
  "team_projects.side_panel.section.alignment": "Alignment",
  "team_projects.side_panel.section.recent_activity": "Recent activity",
  "team_projects.side_panel.activity.empty": "No activity yet",
  "team_projects.side_panel.activity.system_actor": "Cognota",
  "team_projects.side_panel.activity.verb.archived": "archived this project",
  "team_projects.side_panel.activity.verb.unarchived": "restored this project",
  "team_projects.side_panel.activity.verb.duplicated": "duplicated this project",
  "team_projects.side_panel.activity.verb.created": "created this project",
  "team_projects.side_panel.activity.verb.state_changed": "updated status",
  "team_projects.side_panel.activity.verb.process_changed": "changed the process",
  "team_projects.side_panel.activity.verb.field_changed": "updated {field}",
  "team_projects.side_panel.activity.field.description": "the description",
  "team_projects.side_panel.activity.field.project_type": "project type",
  "team_projects.side_panel.activity.field.delivery_type": "delivery type",
  "team_projects.side_panel.activity.field.process": "process",
  "team_projects.side_panel.activity.field.auto_advance_stages": "auto-advance stages",
  "team_projects.side_panel.activity.field.archive_on_completion_days": "auto-archive timing",
  // The projects list has its OWN cost labels. Do not point these columns back at
  // `issue.table.{estimated,actual}_cost` — those are the work-item table's, which
  // NG-266 relabelled to "Est Cost" / "Act Cost"; sharing the keys silently
  // relabelled this list too.
  "team_projects.table.estimated_cost": "Estimated $",
  "team_projects.table.actual_cost": "Actual $",
  "project.filter_privacy": "Privacy",
  "project.sort_by": "Sort by",
  "project.privacy.all": "All",
  "project.sort.manual": "Manual order",
  "project.sort.name": "Name",
  "project.sort.updated": "Last updated",
  "project.sort.created": "Created date",
  "project.join": "Join",
  "project.state.no_state": "No status",
  "project.state.update_success": "Project status updated.",
  "project.state.update_error": "Failed to update project status.",
  "project.state.search_placeholder": "Search statuses...",
  "project.state.search_empty": "No statuses found",
  "project.workflow.transition_blocked_title": "Transition Not Allowed",
  "project.workflow.transition_blocked_description":
    'This project cannot move from "{from}" to "{to}".',
  "project.workflow.allowed_transitions": "Allowed transitions:",
  "project.workflow.requires_approval": "Requires approval",
  "project.workflow.contact_admin": "Contact a project admin to update workflow rules.",
  "project.leave": "Leave",
  "project.left": "Left project",
  "project.settings": "Settings",
  "project.copy_link": "Copy link",
  "project.status_band.due": "Due {date}",
  "project.status_band.overdue": "{count} overdue",
  "project.status_band.over_capacity": "{count} over capacity",
  "project.source_template.label": "Template:",
  "project.source_template.deleted_name": "{name} (deleted)",
  "project.status_band.tooltip.focus_label": "{fact}. Focus for details.",
  "project.status_band.tooltip.state_fact": "{state} project status",
  "project.status_band.tooltip.state":
    "This is the project's current workflow status, set on the project. It is separate from work-item statuses.",
  "project.status_band.tooltip.process_fact": "{process} process",
  "project.status_band.tooltip.process_stage_fact": "{process} process, {stage} stage",
  "project.status_band.tooltip.process": "This is the project's L&D process.",
  "project.status_band.tooltip.process_stage":
    "This is the project's L&D process. The stage is derived as the first active process stage whose work is not all completed or cancelled; deactivated stages and stages with no live work are skipped.",
  "project.status_band.tooltip.due":
    "This is the project's planned completion date, set on the project. It is also the end of the plan window used to calculate project health.",
  "common.identifier_copy": "Click to copy",
  "common.identifier_copied": "Identifier copied to clipboard",
  "common.identifier_copy_error": "Couldn't copy the identifier",
  "project.link_copied": "Link copied to clipboard",
  "project.archive": "Archive",
  "project.archived": "Project archived",
  "project.archive_error": "Failed to archive project",
  "project.update_error": "Failed to update project",
  "project.archive_confirm_title": "Archive project?",
  "project.archive_confirm_description":
    "Archive {name}? You can restore it later from the archive.",
  "project.delete": "Delete",
  "project.deleted": "Project deleted",
  "project.delete_error": "Failed to delete project",
  "project.delete_confirm_title": "Delete project?",
  "project.delete_confirm_description": "Delete {name}? This can't be undone.",
  "project.duplicate": "Duplicate",
  "project.duplicated": "Project duplicated",
  "project.duplicate_error": "Failed to duplicate project",
  "project.duplicate_confirm_title": "Duplicate project?",
  "project.duplicate_confirm_description":
    "A copy of {name} will be created with the same fields and work items. All statuses will be reset to the default.",
  "project.add_favorite": "Add to favorites",
  "project.remove_favorite": "Remove from favorites",
  "project.empty_title": "No projects yet",
  "project.empty_description": "Create your first project to get started.",
  "project.empty_filtered": "No projects match your filters",

  // Issues / Work Items
  "issue.page_title": "Work Items",
  "issue.page_description": "Track, organize, and update project work.",
  "issue.search_placeholder": "Search work items...",
  "issue.create": "Create work item",
  "issue.create_work_item": "Create Work Item",
  "issue.move_failed": "Couldn't move work item. Please try again.",
  "issue.project": "Project",
  "issue.project_placeholder": "Select a project",
  "issue.project_search_placeholder": "Search projects…",
  "issue.project_empty": "No projects found",
  "issue.project_none": "No project",
  "issue.name": "Name",
  "issue.created_at": "Created at",
  "issue.kind": "Type",
  "issue.name_placeholder": "What needs to be done?",
  "issue.description": "Description",
  "issue.description_placeholder": "Add more context for this work item...",
  "issue.description_loading": "Loading the description",
  "issue.description_load_failed":
    "Couldn't load the description. Close and reopen this panel to try again.",
  "issue.none": "None",
  "issue.priority": "Priority",
  "issue.status": "Status",
  "issue.priority.none": "No priority",
  "issue.priority.low": "Low",
  "issue.priority.medium": "Medium",
  "issue.priority.high": "High",
  "issue.priority.urgent": "Urgent",
  "issue.state.none": "No status",
  "issue.state.search_placeholder": "Search states...",
  "issue.state.search_empty": "No states found",
  "issue.display_options": "View options",
  "issue.display.show_identifier": "Show identifier",
  "issue.display.show_state": "Show state",
  "issue.display.show_assignee": "Show assignee",
  "issue.display.show_priority": "Show priority",
  "issue.display.show_target_date": "Show dates",
  "issue.display.columns": "Columns",
  "issue.display.show_archived": "Show archived",
  "issue.display.layout_options": "{layout} options",
  "issue.display.set_default_for_everyone": "Set default for everyone",
  "issue.display.no_workspace_default": "Saving a workspace default is not wired up yet",
  "issue.table.name": "Name",
  "issue.display.show_predecessors": "Show predecessors",
  "issue.layout.change": "Change layout",
  "issue.layout.list": "Table",
  "issue.layout.board": "Board",
  "issue.layout.spreadsheet": "Spreadsheet",
  "issue.layout.calendar": "Calendar",
  "issue.layout.gantt": "Gantt",
  "issue.layout.timeline": "Timeline",
  "issue.layout.truncated_notice":
    "Showing the first {count} work items. Narrow the list to see the rest.",
  "issue.filter.button": "Filters",
  "issue.filter.clear_all": "Clear all",
  "issue.filter.state": "State",
  "issue.filter.assigneeUnassigned": "Unassigned",
  "issue.filter.assignee": "Assignee",
  "issue.filter.unassigned": "Unassigned",
  "issue.filter.project": "Project",
  "issue.filter.project_status": "Project status",
  "issue.filter.all_priorities": "All priorities",
  "issue.filter.all_states": "All states",
  "issue.filter.all_assignees": "All assignees",
  "issue.filter.all_labels": "All labels",
  "issue.group_by": "Group by",
  "issue.group_by.none": "None",
  "issue.group_by.count_partial": "{count}+",
  "issue.group_by.process_stage": "Process stage",
  "issue.group_by.no_stage": "No stage",
  "issue.group_by.no_category_value": "No category",
  "issue.group_by.no_process": "Set a process on this project to group by stage",
  "issue.board.group_by": "View by",
  "issue.board.no_process_title": "Add a process",
  "issue.board.no_process_description":
    "Pick a process from the project overview page to view work by process stage.",
  // NG-320 — shown where a Stage control would be when the project runs no
  // process: the create modal's Stage field and the panel / quick-view Stage rows.
  "issue.stage.label": "Stage",
  "issue.stage.add_process": "Add a process to the project",
  "issue.stage.no_process": "No process selected",
  "issue.sort_by": "Sort by",
  "issue.sort.-created_at": "Newest created",
  "issue.sort.created_at": "Oldest created",
  "issue.sort.-updated_at": "Recently updated",
  "issue.sort.updated_at": "Least recently updated",
  "issue.sort.sequence_id": "ID ascending",
  "issue.sort.-sequence_id": "ID descending",
  "issue.sort.name": "Name A-Z",
  "issue.sort.-name": "Name Z-A",
  "issue.assignees": "Assignees",
  "issue.add_assignee": "Add assignee",
  "issue.no_assignees": "No assignees",
  "issue.no_assignees_available": "No assignees available",
  "issue.search_assignees": "Search people",
  "issue.no_assignees_match": "No matches",

  // Work item tables and pickers (NG-2582)
  "issue.sub_task_create_error": "Couldn't create sub-task",
  "issue.layout_event_aria": "Event layout",
  "issue.layout_task_aria": "Task layout",

  "issue.assignee_unnamed": "Unnamed user",
  "issue.assignee_suggested": "Suggested",
  "issue.assignee_picker.assigned": "Assigned",
  "issue.assignee_picker.show_more": "Show more",
  "issue.job_role_picker.section": "Job role",

  "smart_assignment.title": "Smart Assignment",
  "smart_assignment.subtitle": "job roles still need a person assigned",
  "smart_assignment.work_summary": "{count, plural, one {# task} other {# tasks}}",
  "smart_assignment.date_range": "{start} – {end}",
  "smart_assignment.using_project_dates": "using project dates",
  "smart_assignment.other_options": "Other options",
  "smart_assignment.over_allocated": "Over capacity",
  "smart_assignment.capacity_free": "{hours} free if assigned",
  "smart_assignment.capacity_over_by": "{hours} over if assigned",
  "smart_assignment.capacity_unknown": "Capacity unavailable (no dates or estimate)",
  "smart_assignment.no_candidates": "No one holds this job role yet.",
  "smart_assignment.assign_action": "Assign",
  "smart_assignment.assigning": "Assigning…",
  "smart_assignment.assign_success":
    "Assigned {name} to {count, plural, one {# task} other {# tasks}}",
  "smart_assignment.assign_error": "Failed to assign the job role's work",
  "smart_assignment.over_alloc_title": "{name} will be over capacity",
  "smart_assignment.over_alloc_description":
    "Assigning all {count, plural, one {# task} other {# tasks}} to {name} pushes them over capacity for the work's dates. Assign anyway?",
  "smart_assignment.assign_anyway": "Assign anyway",
  "issue.assignee_picker.placeholder": "Select assignees",
  "issue.assignee_picker.selected_count": "{count} selected",
  "issue.assignee_picker.best_match": "Best match",
  "issue.assignee_picker.over_capacity_title": "{name} will be over capacity",
  "issue.assignee_picker.over_capacity_description":
    "{hours} over if assigned, for this task's dates. Assign anyway?",
  "issue.assignee_capacity.remaining": "{hours} remaining",
  "issue.assignee_capacity.over_by": "{hours} over",
  "issue.assignee_capacity.after_task": "{hours} free if assigned",
  "issue.assignee_capacity.after_task_over_by": "{hours} over if assigned",
  "issue.assignee_capacity.hours_not_set": "Hours not set",
  "issue.assignee_capacity.skill_match": "{percent}% qualified",
  "issue.assignee_capacity.risk_title": "Assignee capacity risk",
  "issue.assignee_capacity.risk_description":
    "Selected assignees are over capacity or unavailable for this task's date range.",
  "issue.assignee_capacity.risk_item_over_by": "{name} is over by {hours}",
  "issue.assignee_capacity.risk_item_unavailable": "{name} has no available capacity",
  "issue.assignee_capacity.replacements_title": "Replacement candidates",
  "issue.assignee_capacity.replacements_description":
    "Choose a project member to replace only the selected assignee.",
  "issue.assignee_capacity.replace_for": "Replace {name}",
  "issue.assignee_capacity.replacement_skill_match": "{percent}% qualification match",
  "issue.assignee_capacity.replacement_remaining": "{hours} remaining",
  "issue.assignee_capacity.replacement_capacity_gap": "{hours} capacity gap",
  "issue.assignee_capacity.replace_button": "Replace",
  "issue.assignee_capacity.replace_button_label": "Replace {from} with {to}",
  "issue.assignee_capacity.replacements_empty": "No replacement candidates are available.",
  "issue.allocation_warning.banner":
    "{count, plural, one {# task has an allocation issue} other {# tasks have allocation issues}} — an assignee is over capacity for the task's dates.",
  "issue.allocation_warning.icon_label": "Allocation issue",
  "issue.allocation_warning.icon_tooltip": "An assignee is over capacity for this task's dates.",
  "issue.allocation_warning.label": "Overcapacity",
  "issue.blocked.count": "{count, plural, one {# blocking item} other {# blocking items}}",
  "issue.blocked.jump": "Jump to the dependencies",
  "issue.blocked.label": "{count, plural, one {Blocked by # item} other {Blocked by # items}}",
  "issue.blocked.complete_title": "Complete this item while it's still blocked?",
  "issue.blocked.complete_description":
    "{count, plural, one {It's waiting on an item that hasn't finished} other {It's waiting on items that haven't finished}}: {blockers}. You can complete it anyway.",
  "issue.blocked.complete_confirm": "Complete anyway",
  "issue.remove_assignee": "Remove {name}",
  "issue.estimate_hours": "Estimated hours",
  "issue.target_date": "Due date",
  "issue.start_date": "Start date",
  "issue.completed_at": "Completion date",
  "issue.category": "Type",
  "issue.category.task": "Task",
  "issue.category.event": "Event",
  "issue.category.milestone": "Milestone",
  "milestones.section.title": "Project milestones",
  "milestones.section.hide": "Hide",
  "milestones.section.show": "Show",
  "milestones.timeline.today": "Today",
  "milestones.list.no_status": "No status",
  "milestones.list.no_due_date": "No due date",
  "milestones.list.due_date": "Due date",
  "milestones.list.completed_date": "Completed date",
  "milestones.list.completed_on": "Completed {date}",
  "milestones.list.unassigned": "Unassigned",
  "milestones.empty.title": "No milestones yet",
  "milestones.empty.description":
    "Add a milestone to track your progress against key deliverables, checkpoints, or events.",
  "issue.archived_toggle.active": "Active",
  "issue.archived_toggle.archived": "Archived",
  "issue.sub_task": "Sub-task",
  "issue.session": "Session",
  "issue.convert_type": "Convert type",
  "issue.convert_to_task": "Convert to Task",
  "issue.convert_to_event": "Convert to Event",
  "issue.convert_to_milestone": "Convert to Milestone",
  "issue.convert_milestone_confirm_title": "Convert to milestone?",
  "issue.convert_milestone_confirm_body":
    "Milestones don't track estimated or logged hours. Converting will clear this work item's hours, and they can't be recovered.",
  "issue.convert_error": "Failed to convert work item type.",
  "issue.skills_label": "Qualifications",
  "issue.skills_search_placeholder": "Search qualifications…",
  "issue.skills_empty": "No qualifications found",
  "issue.skills_update_error": "Failed to update work item qualifications",
  "issue.type": "Type",
  "issue.category_value": "Category",
  "issue.task_category": "Task Category",
  "issue.event_category": "Event Category",
  "issue.milestone_category": "Milestone Category",
  "issue.choose_category_placeholder": "Choose category",
  "issue.create_description_placeholder": "Describe the work",
  "issue.estimate_placeholder": "Enter estimated hours",
  "issue.event_start": "Starts",
  "issue.event_end": "Ends",
  "issue.event_time": "Event time",
  "issue.event_start_date": "Start date",
  "issue.event_end_date": "End date",
  "issue.event_start_time": "Start time",
  "issue.event_end_time": "End time",
  "issue.event_all_day": "All-day event",
  "issue.event_all_day_short": "All Day",
  "issue.event_due_date": "Due Date",
  "issue.event_start_date_time": "Start Date / Time",
  "issue.event_due_date_time": "Due Date / Time",
  "issue.event_timezone": "Time Zone",
  "issue.event_dates_required": "Events require start and end datetimes.",
  "issue.event_dates_order_error": "Event end must be after start.",
  "issue.parent_bounds_hint": "Dates must fall within the parent's window: {start} – {end}.",
  "issue.parent_bounds_hint_after": "Dates must start on or after {start}.",
  "issue.parent_bounds_hint_before": "Dates must end on or before {end}.",
  "issue.parent_bounds_out_of_range": "These dates fall outside the parent's start/end window.",
  "issue.estimate_hours_negative": "Estimated hours can't be negative.",
  "issue.event_time_update_error": "Failed to update event time.",
  "issue.save_event_time": "Save time",
  "project_overview.title": "Project overview",
  "project_overview.subtitle": "Health, hours, and stage progress at a glance.",
  "project_overview.custom_fields.title": "Custom Fields",
  "project_overview.global_fields.title": "Global Fields",
  "project_overview.custom_fields.empty_value": "—",
  "project_overview.custom_fields.archived": "archived",
  "project_overview.custom_fields.required_error": "This field is required.",
  "project_overview.custom_fields.required_marker": "required",
  "project_overview.custom_fields.row.edit": "Edit {name}",
  "project_overview.custom_fields.row.save": "Save",
  "project_overview.custom_fields.row.cancel": "Cancel",
  // PRD §7 — admin manage of global custom fields on work items.
  "work_item_overview.custom_fields.title": "Global fields",
  "work_item_overview.custom_fields.archived": "archived",
  "work_item_overview.custom_fields.empty_state": "No global fields on this work item yet.",
  "work_item_overview.custom_fields.manage_button": "Manage fields",
  "work_item_overview.custom_fields.required_badge": "Required",
  "work_item_overview.custom_fields.row.required": "Required",
  "work_item_overview.custom_fields.row.required_toggle": "Toggle required for {name}",
  "work_item_overview.custom_fields.row.remove": "Remove {name}",
  "work_item_overview.custom_fields.row.edit": "Edit {name}",
  "work_item_overview.custom_fields.row.edit_value": "Edit value",
  "work_item_overview.custom_fields.row.save": "Save",
  "work_item_overview.custom_fields.row.cancel": "Cancel",
  "work_item_overview.custom_fields.empty_value": "—",
  "work_item_overview.custom_fields.picker.title": "Add a global field",
  "work_item_overview.custom_fields.picker.search_placeholder": "Search global fields…",
  "work_item_overview.custom_fields.picker.empty": "No global fields available.",
  "work_item_overview.custom_fields.picker.empty_link": "Manage fields",
  "work_item_overview.custom_fields.picker.no_matches": "No matches.",
  "work_item_overview.custom_fields.picker.already_added": "Already added",
  "work_item_overview.custom_fields.remove_confirm.title": "Remove “{name}” from this work item?",
  "work_item_overview.custom_fields.remove_confirm.body":
    "Are you sure you want to remove this field from this work item? Values captured under this field will be preserved on this work item but the field will no longer be visible.",
  "work_item_overview.custom_fields.remove_confirm.cancel": "Cancel",
  "work_item_overview.custom_fields.remove_confirm.confirm": "Remove",
  "project_overview.health": "Health",
  "project_overview.health.not_started": "Not started",
  "project_overview.health.on_track": "On track",
  "project_overview.health.at_risk": "At risk",
  "project_overview.health.off_track": "Off track",
  "project_overview.health.completed": "Completed",
  "project_overview.health_fallback_warning":
    "Health accuracy is limited because no work items have estimated hours, so this reading uses percent complete instead.",
  "project.health_tooltip.accessible_label": "{health} project health. Focus for details.",
  "project.health_tooltip.not_started":
    "Not started means the project has not reached its planned start date, so progress is not expected yet.",
  "project.health_tooltip.on_track":
    "On track means hours logged are keeping pace with how much of the project's planned time window has passed.",
  "project.health_tooltip.at_risk":
    "At risk means hours logged are slightly behind how much of the project's planned time window has passed.",
  "project.health_tooltip.off_track":
    "Off track means hours logged are significantly behind how much of the project's planned time window has passed.",
  "project.health_tooltip.completed": "Completed means the project has a recorded completion date.",
  "project.health_tooltip.recalculated": "Last calculated {when}.",
  "project.health_tooltip.recalculation_trigger":
    "This updates after changes to the project, its work items, or their logged work.",
  "project_overview.hours_summary": "Hours",
  "project_overview.hours_estimated": "Estimated",
  "project_overview.hours_actual": "Logged",
  "project_overview.hours_remaining": "Remaining",
  "project_overview.stage_progress": "Stage progress",
  "project_overview.stage_current": "Current",
  "project_overview.stage_done": "Done",
  "project_overview.no_process": "No process selected.",
  "project_overview.flags": "Flags",
  "project_overview.no_flags": "No active flags.",
  "project_overview.flags_overdue": "{count} overdue work item(s)",
  "project_overview.flags_hours_overrun": "{count} work item(s) over their estimate",
  "project_overview.tab_overview": "Overview",
  "project_overview.tab_activity": "Activity",
  "project_overview.tab_comments": "Comments",
  "project_overview.comments": "Comments",
  "project_overview.comments_empty": "No comments yet.",
  "project_overview.comments_placeholder": "Add a comment",
  "project_overview.comment_submit": "Post comment",
  // Program overview page, its edit dialog and the program settings page (NG-2579)
  "program_overview.page.add_projects": "Add Projects",
  "program_overview.page.add_projects_search": "Search projects...",
  "program_overview.page.add_projects_empty": "No projects to add",
  "program_overview.page.add_projects_selected": "{countText} selected",
  "program_overview.page.add_projects_confirm": "Add",
  "program_overview.page.projects_added": "Projects added",
  "program_overview.page.projects_add_error": "Failed to add projects",
  "program_overview.page.project_removed": "Project removed",
  "program_overview.page.project_remove_error": "Failed to remove project",
  "program_overview.page.no_projects":
    'No projects linked yet. Click "Add Projects" to link projects to this program.',
  "program_overview.page.no_projects_read_only": "No projects linked yet.",
  "program_overview.page.identifier": "ID: {identifier}",
  "program_overview.page.no_owner": "No owner",
  "program_overview.page.details": "Details",
  "program_overview.page.settings_aria": "Program settings",
  "program_overview.page.activity_aria": "Activity",
  "program_overview.page.comments_aria": "Comments",
  "program_overview.page.more_options_aria": "More options",
  "program_overview.page.edit": "Edit",
  "program_overview.page.restore": "Restore",
  "program_overview.page.archive": "Archive",
  "program_overview.page.delete": "Delete",
  "program_overview.page.comments_title": "Comments",
  "program_overview.page.close_comments": "Close comments",
  "program_overview.page.update_error": "Failed to update program",
  "program_overview.page.archive_title": "Archive Program",
  "program_overview.page.archive_description":
    'Archive "{name}"? Archived programs won\'t appear in lists but remain available for reporting.',
  "program_overview.page.archive_confirm": "Archive",
  "program_overview.page.archived": "Program archived",
  "program_overview.page.unarchived": "Program unarchived",
  "program_overview.page.unarchive_error": "Failed to unarchive program",
  "program_overview.page.delete_title": "Delete Program",
  "program_overview.page.delete_description": 'Delete "{name}"? This cannot be undone.',
  "program_overview.page.delete_confirm": "Delete",
  "program_overview.page.deleted": "Program deleted",
  "program_overview.health.title": "Health",
  "program_overview.health.on_track": "On Track",
  "program_overview.health.at_risk": "At Risk",
  "program_overview.health.off_track": "Off Track",
  "program_overview.health.not_started": "Not Started",
  "program_overview.health.completed": "Completed",
  "program_overview.edit_dialog.title": "Edit Program",
  "program_overview.edit_dialog.name_label": "Name *",
  "program_overview.edit_dialog.name_placeholder": "Program name",
  "program_overview.edit_dialog.owner_label": "Owner",
  "program_overview.edit_dialog.owner_placeholder": "No owner",
  "program_overview.edit_dialog.owner_empty": "No members found",
  "program_overview.edit_dialog.updated": "Program updated",
  "program_overview.edit_dialog.update_error": "Failed to update program",
  "program_settings.title": "Settings",
  "program_settings.denied_title": "You cannot edit this program",
  "program_settings.denied_description":
    "Editing a program is limited to its owner, the leads of the teams it is shared with, and workspace admins. You can still view the program.",
  "program_settings.back": "Back to program",
  "program_overview.kpis.title": "Delivery",
  "program_overview.kpis.last_calculated": "Last calculated: {when}",
  "program_overview.kpis.last_calculated_help":
    "Recalculated only when something in the program changes, so a quiet program reads older than it is.",
  "program_overview.kpis.pipeline_last_run": "The metrics pipeline last ran {when}.",
  "program_overview.kpis.not_computed":
    "Not calculated yet — these figures appear within a few minutes of the program’s first change.",
  "program_overview.kpis.progress": "Program progress",
  "program_overview.kpis.progress_sub": "Based on task completion across linked projects",
  "program_overview.kpis.hours_logged": "Hours logged",
  "program_overview.kpis.hours_of_estimate": "/ {estimated} est.",
  "program_overview.kpis.hours_sub": "{cost} cost of labor",
  "program_overview.kpis.hours_window": "Logged to date",
  "program_overview.kpis.projects": "Projects",
  "program_overview.kpis.overdue": "Projects overdue",
  "program_overview.kpis.overdue_of": "of {total}",
  "program_overview.kpis.overdue_sub": "{name} — {days} days overdue",
  "program_overview.kpis.overdue_none": "Nothing past its due date",
  "program_overview.kpis.content_delivery": "Content delivery",
  "program_overview.kpis.content_delivery_sub":
    "{count, plural, one {Course level, from # linked project: it covers everyone the LMS shows on that content, not only the project's audience} other {Course level, from # linked projects: it covers everyone the LMS shows on their content, not only the projects' audiences}}",
  "program_overview.kpis.content_delivery_none": "No linked project has LMS results yet",
  // Program overview: linked projects and requests tables (NG-2579)
  "program_overview.linked_projects.column_project": "Project",
  "program_overview.linked_projects.column_identifier": "Identifier",
  "program_overview.linked_projects.column_owner": "Owner",
  "program_overview.linked_projects.column_status": "Status",
  "program_overview.linked_projects.column_health": "Health",
  "program_overview.linked_projects.column_goals": "Goals",
  "program_overview.linked_projects.column_progress": "Progress",
  "program_overview.linked_projects.column_effort_share": "Effort %",
  "program_overview.linked_projects.column_due_date": "Due Date",
  "program_overview.linked_projects.goals_aria": "Goals · {name}",
  "program_overview.linked_projects.goals_search": "Search goals…",
  "program_overview.linked_projects.actions_aria": "Actions for {name}",
  "program_overview.linked_projects.remove": "Remove",
  "program_overview.linked_requests.column_request": "Request",
  "program_overview.linked_requests.column_status": "Status",
  "program_overview.linked_requests.column_requested_by": "Requested by",
  "program_overview.linked_requests.column_submitted": "Submitted",
  "program_overview.linked_requests.column_form": "Form",
  "program_overview.linked_requests.untitled": "Request",
  "program_overview.linked_requests.actions_aria": "Actions for {name}",
  "program_overview.linked_requests.actions_fallback_name": "request",
  "program_overview.linked_requests.remove": "Remove",
  "program_overview.goals_update_failed": "Could not update the project’s goals",
  "program_overview.alignment.aligned": "Aligned to goals",
  "program_overview.alignment.unaligned": "Not aligned",
  "program_overview.alignment.aligned_sub": "{count} of {total} linked to at least one goal",
  "program_overview.alignment.unaligned_sub": "{count} of {total} linked to no goal",
  "program_overview.alignment.unaligned_button": "{count} not aligned",
  "program_overview.alignment.no_projects": "No projects in this program yet",
  "program_overview.kpis.projects_by_status": "Projects by status",
  "program_overview.kpis.projects_by_health": "Projects by health",
  "program_overview.kpis.projects_by_effort": "Projects by effort",
  "program_overview.kpis.no_status": "No status",
  "program_overview.name_label": "Program name",
  "program_overview.kpis.more_slices": "+{count} more",
  "program_overview.filtered_notice": "Showing {count} of {total} projects",
  "program_overview.clear_filter": "Clear filter",
  "program_overview.linked_requests.heading": "Linked requests",
  "program_overview.linked_requests.count": "{count, plural, one {# request} other {# requests}}",
  "program_overview.linked_requests.empty":
    "No requests are linked to this program yet. Add them here, or pick this program on a request's intake form.",
  "program_overview.linked_requests.empty_read_only":
    "No requests are linked to this program yet. A request is linked when this program is picked on its intake form.",
  "program_overview.linked_requests.remove_error": "Failed to remove the request from this program",
  "program_overview.linked_requests.added":
    "{count, plural, one {Added # request} other {Added # requests}}",
  "program_overview.linked_requests.add_failed":
    "{count, plural, one {# request could not be added} other {# requests could not be added}}",
  "program_overview.add_requests.button": "Add requests",
  "program_overview.add_requests.title": "Add requests to {program}",
  "program_overview.add_requests.description":
    "Pick the requests this program answers. Each request's Program answer updates to match.",
  "program_overview.add_requests.search_label": "Search requests",
  "program_overview.add_requests.search_placeholder": "Search by name, form or ID (R-284)",
  "program_overview.add_requests.clear_search": "Clear search",
  "program_overview.add_requests.results_label": "Requests",
  "program_overview.add_requests.already_linked": "Already linked",
  "program_overview.add_requests.selected_count": "{count} selected",
  "program_overview.add_requests.more_results":
    "Showing the {shown} most recent matches. Search to narrow it down.",
  "program_overview.add_requests.submit":
    "{count, plural, =0 {Add requests} one {Add # request} other {Add # requests}}",
  "program_overview.add_requests.no_matches_title": "No requests match \u201c{query}\u201d",
  "program_overview.add_requests.no_matches_description":
    "Try a request name, a form, or an ID such as R-284.",
  "program_overview.add_requests.none_title": "No requests yet",
  "program_overview.add_requests.none_description":
    "Requests show up here once someone submits one.",
  "program_overview.add_requests.load_error_title": "Couldn't load requests",
  "program_overview.add_requests.load_error_description":
    "Something went wrong fetching the list. Your selection is safe.",
  "program_overview.add_requests.retry": "Try again",
  "program_overview.comments_empty": "No comments yet.",
  "program_overview.comments_placeholder": "Add a comment",
  "program_overview.comment_submit": "Post comment",
  // Program properties panel, description and details sheet (NG-2579)
  "program_overview.properties.owner": "Owner",
  "program_overview.properties.owner_placeholder": "No owner",
  "program_overview.properties.owner_empty": "No members found",
  "program_overview.properties.status": "Status",
  "program_overview.properties.status_aria": "Status",
  "program_overview.properties.start_date": "Start Date",
  "program_overview.properties.start_date_placeholder": "Set start date",
  "program_overview.properties.end_date": "End Date",
  "program_overview.properties.end_date_placeholder": "Set end date",
  "program_overview.properties.set_date": "Set date",
  "program_overview.properties.team_access": "Team Access",
  "program_overview.properties.team_access_about": "About Team Access",
  "program_overview.properties.team_access_tooltip":
    "Members and leads of the selected teams and their parent teams can see this program. Program leads and workspace administrators also have access. Programs with no Team Access are visible to everyone in the workspace.",
  "program_overview.properties.teams_placeholder": "Select teams",
  "program_overview.properties.teams_empty": "No teams available",
  "program_overview.properties.goals": "Goals",
  "program_overview.properties.goals_placeholder": "Select goals…",
  "program_overview.properties.goals_search": "Search goals…",
  "program_overview.properties.goals_empty": "No goals available",
  "program_overview.properties.goals_aria": "Select goals",
  "program_overview.properties.created": "Created",
  "program_overview.description.heading": "Description",
  "program_overview.description.empty": "No description",
  "program_overview.description.aria": "Program description",
  "program_overview.description.save_error": "Failed to save description",
  "program_overview.details.title": "Details",
  "program_overview.details.close": "Close details",
  "program_overview.activity.no_match": "No matching activity.",
  "issue.empty.title": "No work items yet",
  "issue.empty.description": "Create the first work item in this project to get moving.",
  "issue.empty.filtered_title": "No work items match your filters",
  "issue.empty.filtered_description": "Try adjusting the filters or create a new work item.",
  "issue.bulk.selected_count": "{count} selected",
  "issue.bulk.clear": "Clear",
  "issue.bulk.delete": "Delete selected",
  "issue.bulk.select_all_aria": "Select all rows",
  "issue.bulk.select_one": "Select {name}",
  "issue.load_more": "Load more work items",
  "issue.loading_more": "Loading...",
  "issue.rename_aria": "Rename {name}",
  "issue.rename": "Rename",
  "issue.name_required": "Name can’t be empty",
  "issue.rename_failed": "Failed to rename work item",
  "issue.quick_actions": "Open actions",
  "issue.archive": "Archive",
  "issue.restore": "Restore",
  "issue.delete": "Delete",
  "issue.name_label": "Task name",
  "issue.more_actions": "More actions",
  "issue.delete_archived_success": "Work item deleted",
  "issue.delete_archived_error": "Failed to delete work item",
  "issue.archive_title": "Archive work item",
  "issue.archive_description": 'Archive "{name}"? You can restore it later.',
  "issue.archive_success": "Work item archived",
  "issue.archive_error": "Failed to archive work item",
  "issue.restore_title": "Restore work item",
  "issue.restore_description": 'Restore "{name}" to the active list?',
  "issue.restore_success": "Work item restored",
  "issue.restore_error": "Failed to restore work item",
  "issue.update_error": "Failed to update work item",
  "issue.delete_archived_title": "Delete work item",
  "issue.delete_archived_description": 'Permanently delete "{name}"? This cannot be undone.',
  "issue.properties": "Properties",
  "issue.comments": "Comments",
  "issue.comments_empty": "No comments yet.",
  "issue.comments_placeholder": "Add a comment",
  "issue.comment_submit": "Post comment",
  "issue.add_reaction": "Add reaction",
  "issue.activity": "Activity",
  "issue.activity_empty": "No activity yet",
  "issue.attachments": "Attachments",

  // Views
  "view.page_title": "Views",
  "view.page_description": "Save focused project slices and reopen them when you need them.",
  "view.create": "Create view",
  "view.create_title": "Create view",
  "view.create_submit": "Create",
  "view.create_error": "Failed to create view",
  "view.created": "View created",
  "view.edit_title": "Edit view",
  "view.edit": "Edit",
  "view.updated": "View updated",
  "view.update_error": "Failed to update view",
  "view.delete": "Delete",
  "view.deleted": "View deleted",
  "view.delete_error": "Failed to delete view",
  "view.delete_confirm": 'Delete "{name}"?',
  "view.copy_link": "Copy link",
  "view.copy_error": "Failed to copy link",
  "view.link_copied": "Link copied to clipboard",
  "view.actions": "View actions",
  "view.open": "Open",
  "view.name_label": "Name",
  "view.name_placeholder": "Backlog triage",
  "view.description_label": "Description",
  "view.description_placeholder": "Summarize what this view is for",
  "view.save_changes": "Save changes",
  "view.search_placeholder": "Search views...",
  "view.sort.default": "Default",
  "view.sort.name": "Name",
  "view.sort.created": "Created",
  "view.sort.updated": "Updated",
  "view.filter.favorites": "Favorites",
  "view.access.public": "Public",
  "view.access.private": "Private",
  "view.no_description": "No description provided.",
  "view.fetch_error": "Failed to load views",
  "view.favorite_error": "Failed to update favorite",
  "view.empty.title": "No views yet",
  "view.empty.description": "Create a saved view for recurring triage, planning, or review work.",
  "view.empty.filtered_title": "No views match your filters",
  "view.empty.filtered_description": "Try another search or clear the current filters.",
  "view.clear_filters": "Clear filters",
  "view.disabled_title": "Views are disabled for this project",
  "view.disabled_description":
    "This project does not currently expose saved views. Re-enable the feature to manage them here.",
  "view.error_title": "Could not load views",
  "view.error_description": "Try again or come back in a moment.",
  "view.read_denied_title": "You don't have permission to view saved views",
  "view.read_denied_description": "Contact your workspace admin to request access.",
  "view.updated_on": "Updated {date}",
  "view.add_favorite_named": "Add {name} to favorites",
  "view.remove_favorite_named": "Remove {name} from favorites",
  "view.back_to_list": "Back to views",
  "view.kind.work_item": "Work items",
  "view.kind.request": "Requests",
  "view.kind.project": "Projects",
  "view.kind.field_label": "What this view holds",
  "view.kind.field_hint": "A view slices one kind of record, and this cannot be changed later.",
  "view.kind.filter_label": "Kind",
  "view.kind.all": "All kinds",
  "view.locked": "Locked",
  "view.save_as": "Save as",
  "view.save_as_hint": "Save the current filters as a separate view.",
  "view.reset": "Reset",
  "view.update_current": "Update view",
  "view.unsaved_changes": "You have unsaved changes.",
  "view.detail.not_found_title": "View not found",
  "view.detail.not_found_description": "This saved view could not be loaded.",
  "view.detail.work_items_denied_title": "You can't view work items here",
  "view.detail.work_items_denied_description":
    "Ask a workspace admin for permission to list work items.",
  "view.detail.placeholder_description": "Saved view detail will be expanded in the next phase.",
  "view.detail.placeholder_body":
    "This route is wired so create/open flows work now. The full saved-view issue experience lands in the next phase.",
  "view.workspace.page_title": "Views",
  "view.workspace.saved_views": "Saved workspace views",
  "view.workspace.saved_views_description":
    "Custom cross-project views that you can reopen and share later.",
  "view.workspace.empty_title": "No workspace views match right now",
  "view.workspace.empty_description":
    "Start from a quick view or create a custom workspace view for cross-project work.",
  "view.workspace.table_title": "Cross-project work items",
  "view.workspace.table_description":
    "Workspace views stay table-first in A3 so you can scan work across projects without switching layouts.",
  "view.workspace.assignee_count": "{count} assignees",
  "view.workspace.unknown_assignee": "Unknown assignee",
  "view.workspace.static.all-issues.title": "All Work",
  "view.workspace.static.all-issues.description":
    "Everything in the workspace, with local filters layered on top for this session.",
  "view.workspace.static.assigned.title": "Assigned to me",
  "view.workspace.static.assigned.description":
    "Work items assigned to you across every project in the workspace.",
  "view.workspace.static.created.title": "Created by me",
  "view.workspace.static.created.description":
    "Cross-project work you opened, ready for follow-up or handoff.",
  "view.workspace.static.subscribed.title": "Subscribed",
  "view.workspace.static.subscribed.description":
    "Work items you are following so you can check progress without hunting through projects.",
  "issue.attachments_empty": "No attachments yet",
  "issue.attachments_error": "Failed to load attachments",
  "issue.attachments_forbidden": "You don't have access to this item's attachments",
  "issue.worklog_unknown_user": "Unknown user",
  "issue.attachments_upload": "Upload",
  "issue.attachments_uploading": "Uploading…",
  "issue.attachments_upload_error": "Failed to upload attachment",
  "issue.attachments_delete": "Delete attachment",

  // Work item overview and sub-tasks (NG-2582)
  "issue.overview.id_subtitle": "ID: {id}",
  "issue.overview.close_comments": "Close comments",
  "issue.subtasks.click_to_edit": "Click to edit",
  "issue.subtasks.back": "Back",
  "issue.subtasks.open_page": "Open page",
  "issue.subtasks.none": "No sub-tasks",
  "issue.subtasks.time_logs": "Time Logs",
  "issue.subtasks.collapse": "Collapse",
  "issue.subtasks.expand": "Expand",
  "issue.subtasks.complete": "complete",
  "issue.subtasks.loading": "Loading sub-tasks…",
  "issue.subtasks.forbidden": "You don't have access to this item's sub-tasks.",
  "issue.subtasks.load_error": "Couldn't load sub-tasks. Please try again.",
  "issue.subtasks.col_due": "Due",
  "issue.subtasks.empty": "No sub-tasks yet.",

  "issue.relations": "Relations",
  "issue.dependencies": "Dependencies",
  "issue.relations_empty": "No related work yet",
  "issue.add_dependency": "Add Dependency",
  "issue.dependency_type_label": "Dependency type",
  "issue.dependency_relation.blocked_by": "Blocked by",
  "issue.dependency_relation.blocks": "Blocking",
  "issue.dependency_relation.relates_to": "Related to",
  "issue.task_or_event_label": "Task or Event",
  "issue.select_task_or_event": "Select task or event",
  "issue.search_tasks_and_events": "Search tasks and events",
  "issue.add_relation": "Add relation",
  "issue.remove_relation": "Remove relation",
  "issue.relation_remove_error": "Failed to remove relation",
  "issue.relation_type_label": "Relation type",
  "issue.relation_type.blocked_by": "Blocked by",
  "issue.relation_type.blocks": "Blocks",
  "issue.relation_type.relates_to": "Relates to",
  "issue.dependency_subtype_label": "Dependency type",
  "issue.dependency_subtype.finish_to_start": "Finish to start (FS)",
  "issue.dependency_subtype.start_to_start": "Start to start (SS)",
  "issue.dependency_subtype.finish_to_finish": "Finish to finish (FF)",
  "issue.dependency_subtype.start_to_finish": "Start to finish (SF)",
  "issue.relation_search_placeholder": "Search work items...",
  "issue.relation_add_error": "Failed to add relation",
  "issue.relation_fetch_error": "Failed to load relations. Please try again.",
  "issue.relation_forbidden": "You don't have access to this item's dependencies",
  "issue.relation_group.blocked_by": "Blocked by",
  "issue.relation_group.blocks": "Blocks",
  "issue.relation_group.relates_to": "Relates to",
  "issue.links": "Links",
  "issue.links_empty": "No links yet",
  "issue.add_link": "Add link",
  "issue.remove_link": "Remove link",
  "issue.link_add_error": "Failed to add link",
  "issue.link_remove_error": "Failed to remove link",
  "issue.link_invalid_url": "Enter a valid URL (e.g. https://example.com)",
  "issue.link_url_placeholder": "https://...",
  "issue.link_title_placeholder": "Title (optional)",
  "issue.sub_issues": "Sub-items",
  "issue.sub_issues_empty": "No sub-items yet",
  "issue.expand_sub_issues": "Expand sub-items",
  "issue.collapse_sub_issues": "Collapse sub-items",
  "issue.create_sub_work_item": "Create Sub-task",
  "issue.create_sub_work_item_title": "Create Sub-item",
  "issue.search_work_items": "Search work items",
  "issue.no_work_item_matches": "No matching work items",
  "issue.sub_work_item_create_error": "Failed to create sub-item",
  "issue.show_panel": "Show",
  "issue.hide_panel": "Hide",
  "issue.subscribe": "Subscribe",
  "issue.unsubscribe": "Unsubscribe",
  "issue.open_detail": "Open detail page",
  "issue.saving": "Saving...",
  "issue.not_found": "Work item not found",
  "issue.not_found_description":
    "The work item could not be resolved from this link. Try returning to the project work-items page.",
  "issue.load_error": "Failed to load work item",
  "issue.load_error_description":
    "A network error occurred while loading this work item. Please check your connection and try again.",
  "issue.back_to_project": "Back to project work items",
  "issue.create_error": "Failed to create work item. Please try again.",
  "issue.activity_error": "Failed to load activity",
  "issue.relations_error": "Failed to load relations",
  "issue.table.id": "ID",
  "issue.table.start": "Start",
  "issue.table.target": "Due",
  "issue.table.predecessors": "Predecessors",
  "issue.table.created": "Created",
  "issue.table.estimated_cost": "Est Cost",
  "issue.table.actual_cost": "Act Cost",
  "issue.table.estimated_hours": "Estimated hours",
  "issue.table.actual_hours": "Actual hours",
  "issue.table.estimate_edit_aria": "Estimated hours for {name}",
  "issue.table.estimate_invalid": "Enter hours as a number of 0 or more.",
  "issue.table.estimate_save_failed": "Failed to update estimated hours",
  "issue.add_row.placeholder": "Add a work item…",
  "issue.add_row.aria": "New work item name",
  "issue.add_row.subtask_placeholder": "Sub-task name…",
  "issue.add_row.subtask_aria": "Sub-task name",
  "issue.add_row.creating": "Adding…",
  "issue.add_row.failed": "Couldn't create the work item",
  "issue.predecessors.placeholder": "Add…",
  "issue.predecessors.aria": "Predecessors for {name}",
  "issue.predecessors.edit_aria": "Edit predecessors for {name}",
  "issue.predecessors.invalid": "Enter comma-separated IDs (e.g. 3, OTHER-5).",
  "issue.predecessors.update_error": "Couldn't update predecessors.",

  // Locale
  "locale.en": "EN",
  "locale.fr": "FR",

  // Favorites
  "favorite.add": "Add to favorites",
  "favorite.add_error": "Failed to add favorite",
  "favorite.expand_folder": "Expand {name}",
  "favorite.collapse_folder": "Collapse {name}",
  "favorite.folder_name_placeholder": "New folder name",
  "favorite.folder_create_error": "Failed to create folder",
  "favorite.rename_folder": "Rename",
  "favorite.delete_folder": "Delete folder",
  "favorite.remove": "Remove from favorites",
  "favorite.remove_error": "Failed to remove favorite",
  "favorite.move_to_root": "Move to root",
  "favorite.move_error": "Failed to move favorite",
  "favorite.item_actions": "Actions for {name}",
  "favorite.rename_error": "Failed to rename folder",
  "favorite.rename_folder_input": "Rename {name}",

  // Greeting (My Work landing page) + relative-time strings (team activity feed)
  "home.good_morning": "Good morning, {name}",
  "home.good_afternoon": "Good afternoon, {name}",
  "home.good_evening": "Good evening, {name}",
  "home.recents.just_now": "Just now",
  "home.recents.minutes_ago": "{count}m ago",
  "home.recents.hours_ago": "{count}h ago",
  "home.recents.days_ago": "{count}d ago",

  // Accessibility
  "project.more_options": "More options",
  "common.close": "Close",

  // The ten swatches, named. A hex code is not a thing a person can read aloud,
  // and it is what a screen reader announces without these (NG-1594).
  "common.color.gray": "Gray",
  "common.color.blue": "Blue",
  "common.color.green": "Green",
  "common.color.yellow": "Yellow",
  "common.color.orange": "Orange",
  "common.color.red": "Red",
  "common.color.purple": "Purple",
  "common.color.pink": "Pink",
  "common.color.sky": "Sky",
  "common.color.teal": "Teal",
  "common.color.set_to": "Set color to {color}",
  "common.color.selected": "Color: {color}",
  "common.color.choose": "Choose a color",
  "common.color.custom": "Custom",

  // Command Palette
  "command.palette_title": "Command Palette",
  "command.palette_description": "Search for a command to run...",
  "command.palette_empty": "No matching commands",
  "command.quick_actions": "Quick actions",
  "command.group_projects": "Projects",
  "command.group_issues": "Work items",
  "command.group_workspaces": "Workspaces",
  "command.nav_your_work": "Go to Your Work",
  "command.nav_browse": "Browse all items",
  "command.nav_projects": "Browse projects",
  "command.nav_notifications": "Open notifications",
  "command.create_project": "Create project",
  "command.create_work_item": "Create work item",
  "command.toggle_theme": "Toggle light/dark theme",
  "command.toggle_language": "Toggle language (EN / FR)",
  "command.sign_out": "Sign out",
  "command.searching": "Searching…",
  "command.search_error": "Search failed",

  // My Work & Browse
  "my_work.title": "Your Work",
  "my_work.tab_assigned": "Assigned to me",
  "my_work.tab_created": "Created by me",
  "my_work.tab_subscribed": "Subscribed",
  "my_work.time_off.title": "Upcoming Time Off",
  "my_work.time_off.today": "Today",
  "my_work.time_off.tomorrow": "Tomorrow",
  "my_work.time_off.in_days": "{days, plural, one {In # day} other {In # days}}",
  "my_work.time_off.day_count": "{days, plural, one {# day} other {# days}}",
  "my_work.time_off.empty_title": "No upcoming time off",
  "my_work.time_off.description":
    "Add time off that reduces your available capacity in capacity views and scheduling.",
  "my_work.time_off.empty_description":
    "Add a PTO entry to block your available capacity. Holidays from a calendar that covers you show up here on their own.",
  "my_work.time_off.holiday_badge": "Holiday",
  "my_work.cards.assigned": "Open work",
  "my_work.cards.availability": "Utilization",
  "my_work.assigned.overdue": "overdue",
  "my_work.assigned.days_late": "{days, plural, one {up to # day late} other {up to # days late}}",
  "my_work.assigned.need_review": "need your review",
  "my_work.assigned.due_this_week": "due this week",
  "my_work.assigned.due_later": "due after this week",
  "my_work.assigned.empty_title": "You're all caught up.",
  "my_work.assigned.empty_sub": "Your open tasks, events, and requests land here.",
  "my_work.cards.availability_left": "{hours}h left",
  "my_work.cards.availability_sub": "{percent}% utilized this week",
  "my_work.cards.availability_sub_next": "{percent}% utilized next week",
  "my_work.availability.empty": "No work hours set for this week.",
  "my_work.availability.empty_cta": "Set work hours",
  "my_work.availability.error": "Availability couldn't load.",
  "my_work.table.title": "Your work",
  "my_work.table.placeholder": "Your tasks, events, and requests will appear here.",
  "my_work.mentions.title": "Mentions",
  "my_work.mentions.unread_title": "Unread mentions",
  "my_work.mentions.empty_title": "No mentions yet.",
  "my_work.mentions.no_unread_title": "No unread mentions.",
  "my_work.mentions.no_unread_body": "New mentions of you show up here. View all for earlier ones.",
  "my_work.mentions.placeholder": "When someone @mentions you, it shows up here.",
  "my_work.mentions.view_all": "View all",
  "my_work.mentions.unread": "Unread",
  "my_work.time_off.view": "View time off",
  "my_work.col.name": "Name",
  "my_work.col.project": "Project",
  "my_work.col.type": "Type",
  "my_work.col.status": "Status",
  "my_work.col.priority": "Priority",
  "my_work.col.due_date": "Due date",
  "my_work.col.actual_hours": "Actual hours",
  "my_work.col.blocked_by": "Blocked by",
  "my_work.type.task": "Task",
  "my_work.type.event": "Event",
  "my_work.type.request": "Request",
  "my_work.group_by.label": "Group by",
  "my_work.group_by.none": "None",
  "my_work.group_by.type": "Type",
  "my_work.group_by.project": "Project",
  "my_work.group_by.no_project": "No project",
  "my_work.group_by.toggle": "Toggle {label}",
  "my_work.search.placeholder": "Search your work…",
  "work_items.title": "Tasks",
  "work_items.description": "All work items across projects",
  "work_items.create": "Create Task",
  "work_items.export": "Export tasks",

  // Archives
  "archives.tabs.work_items": "Work items",
  "archives.archived_on": "Archived {date}",
  "archives.issues.title": "Archived work items",
  "archives.issues.subtitle": "Review, restore, or permanently delete archived work items.",
  "archives.issues.empty_title": "No archived work items",
  "archives.issues.empty_description": "Archive a work item to see it here.",
  "archives.issues.empty_filtered_title": "No archived work items match your filters",
  "archives.issues.empty_filtered_description":
    "Try adjusting filters or clear the search to see all archived items.",
  "archives.issues.bulk.restore": "Restore selected",
  "archives.issues.bulk.delete": "Delete selected",
  "archives.issues.bulk.restore_success": "Restored {count} work items",
  "archives.issues.bulk.delete_success": "Deleted {count} work items",
  "archives.issues.bulk.restore_partial":
    "Restored {succeeded} of {total} work items; {failed} could not be restored",
  "archives.issues.bulk.archive_partial":
    "Archived {succeeded} of {total} work items; {failed} could not be archived",

  // Goals list, create dialog and Edit Goal page (NG-2578)
  "goals.list.title": "Goals",
  "goals.list.count_noun": "goals",
  "goals.list.new_goal": "New Goal",
  "goals.list.column_name": "Name",
  "goals.list.column_status": "Status",
  "goals.list.column_metrics": "Metrics",
  "goals.list.column_progress": "Progress",
  "goals.list.empty_title": "No goals yet",
  "goals.list.empty_description":
    "Goals connect your team's work to measurable outcomes. Create one to start tracking the KPIs that matter.",
  "goals.create.title": "New Goal",
  "goals.create.name_label": "Name *",
  "goals.create.name_placeholder": "e.g. Increase course completion",
  "goals.create.description_label": "Description",
  "goals.create.description_placeholder": "What does success look like?",
  "goals.create.submit": "Create",
  "goals.create.created": "Goal created",
  "goals.create.error": "Failed to create goal",
  "goals.detail.back_aria": "Back to goals",
  "goals.detail.back_link": "Goals",
  "goals.detail.name_aria": "Goal name",
  "goals.detail.status_aria": "Status",
  "goals.detail.delete_aria": "Delete goal",
  "goals.detail.description_label": "Description",
  "goals.detail.description_placeholder": "What does success look like?",
  "goals.detail.linked_projects": "Linked projects",
  "goals.detail.linked_programs": "Linked programs",
  "goals.detail.linked_requests": "Linked requests",
  "goals.detail.updated": "Goal updated",
  "goals.detail.update_error": "Failed to update goal",
  "goals.detail.delete_title": 'Delete "{name}"?',
  "goals.detail.delete_description":
    "The goal, its metrics and recorded values, and its links to projects and programs will be removed everywhere. This cannot be undone.",
  "goals.detail.delete_confirm": "Delete goal",
  "goals.detail.deleted": "Goal deleted",
  "goals.detail.delete_error": "Failed to delete goal",
  "goals.detail.add_lead_error": "Failed to add goal lead",
  "goals.detail.remove_lead_error": "Failed to remove goal lead",
  "goals.detail.link_error.project": "Failed to link project",
  "goals.detail.link_error.program": "Failed to link program",
  "goals.detail.link_error.request": "Failed to link request",
  "goals.detail.unlink_error.project": "Failed to unlink project",
  "goals.detail.unlink_error.program": "Failed to unlink program",
  "goals.detail.unlink_error.request": "Failed to unlink request",

  // Modules

  // Notifications
  "notifications.title": "Notifications",
  "notifications.tab.all": "All",
  "notifications.tab.mentions": "Mentions",
  "notifications.action.refresh": "Refresh",
  "notifications.action.mark_all_read": "Mark all as read",
  "notifications.action.mark_read": "Mark as read",
  "notifications.action.mark_unread": "Mark as unread",
  "notifications.action.more_aria": "More actions for {title}",
  "notifications.action.archive": "Archive",
  "notifications.action.unarchive": "Restore",
  "notifications.action.snooze": "Snooze",
  "notifications.action.unsnooze": "Unsnooze",
  "notifications.action.load_more": "Load more",
  "notifications.bell.open": "Open notifications",
  "notifications.bell.empty": "No notifications.",
  "notifications.bell.loading": "Loading notifications...",
  "notifications.bell.view_all": "View all notifications",
  "notifications.row.unknown_user": "Someone",
  "notifications.row.unread": "Unread",
  "notifications.group.today": "Today",
  "notifications.group.yesterday": "Yesterday",
  // NG-849: the field-change summary names the actor and the fields; it used
  // to render a bare "updated 3 fields", which said neither who nor what.
  "notifications.summary.fields_updated":
    "{actor} updated {count, plural, =1 {{first}} =2 {{first} and {second}} other {{first}, {second} and {extra} more}}",
  // Fallback label for a diff row whose field the sender could NOT name (a
  // legacy row carrying a raw id and no `field_label`) — never show the reader
  // a UUID. The backend has an English-only twin baked into the payload
  // (notifications/tasks.py::_UNLABELLED_FIELD); keep the two in step.
  "notifications.summary.untitled_field": "Untitled field",
  // The object type shown before the object name on a notification's
  // secondary line (NG-849). Keyed by `entity_name`.
  "notifications.entity.project": "Project",
  "notifications.entity.program": "Program",
  "notifications.entity.goal": "Goal",
  "notifications.entity.work_item": "Work item",
  "notifications.entity.request": "Request",
  "notifications.entity.report": "Report",
  "notifications.account.type.profile": "Profile updated",
  "notifications.account.type.time_off": "Time off scheduled",
  "notifications.account.type.role": "Role updated",
  "notifications.account.type.workspace": "Workspace access",
  "notifications.account.type.team_member": "Team membership",
  "notifications.account.type.update": "Update",
  "notifications.account.type.team_deleted": "Team deleted",
  "notifications.account.cta.teams": "Go to Teams",
  "notifications.account.cta.reassign": "Open the reassignment",
  "notifications.account.field.dates": "Dates",
  "notifications.account.field.role": "Role",
  "notifications.filter.title": "Filters",
  "notifications.filter.view.active": "Active",
  "notifications.filter.view.archived": "Archived",
  "notifications.filter.view.snoozed": "Snoozed",
  "notifications.filter.unread_only": "Unread only",
  "notifications.filter.clear": "Clear filters",
  "notifications.empty.active": "You're all caught up.",
  "notifications.empty.mentions": "No mentions right now.",
  "notifications.empty.archived": "No archived notifications.",
  "notifications.empty.snoozed": "No snoozed notifications.",
  "notifications.empty.filtered": "No notifications match your filters.",
  "notifications.detail.placeholder.title": "Select a notification",
  "notifications.detail.placeholder.description":
    "Choose a notification from the list to see its details.",
  "notifications.detail.error.title": "We couldn't load this notification.",
  "notifications.detail.error.description": "Try refreshing or picking another row.",
  "notifications.detail.inbox.title": "Intake item",
  "notifications.detail.inbox.description":
    "This notification relates to an intake request. Open it to continue.",
  "notifications.detail.inbox.cta": "Open intake item",
  "notifications.request.type.status": "Request status",
  "notifications.request.type.updated": "Request updated",
  "notifications.request.type.commented": "New comment",
  "notifications.request.type.page_ready": "Needs your input",
  "notifications.request.type.review_requested": "Review needed",
  "notifications.request.field.status": "Status",
  "notifications.request.field.comment": "Comment",
  "notifications.request.field.step": "Step",
  "notifications.request.field.message": "Message",
  "notifications.request.cta.view": "View request",
  "notifications.project.type.status": "Project status",
  "notifications.project.type.updated": "Project updated",
  "notifications.project.type.deleted": "Project deleted",
  "notifications.project.field.status": "Status",
  "notifications.project.cta.view": "View project",
  "notifications.program.type.status": "Program status",
  "notifications.program.type.updated": "Program updated",
  "notifications.program.field.status": "Status",
  "notifications.program.cta.view": "View program",
  "notifications.goal.type.status": "Goal status",
  "notifications.goal.type.updated": "Goal updated",
  "notifications.goal.type.lead": "Goal lead",
  "notifications.goal.field.status": "Status",
  "notifications.goal.cta.view": "View goal",
  "notifications.work_item.type.status": "Work item status",
  "notifications.work_item.type.updated": "Work item updated",
  "notifications.work_item.type.deleted": "Work item deleted",
  "notifications.work_item.type.due": "Due date",
  "notifications.work_item.field.status": "Status",
  "notifications.work_item.field.due": "Due date",
  "notifications.work_item.cta.view": "View work item",
  "notifications.report.type.complete": "Report ready",
  "notifications.report.type.failed": "Report failed",
  "notifications.report.type.updated": "Report updated",
  "notifications.report.type.export_ready": "Export ready",
  "notifications.report.cta.view": "View report",
  "notifications.report.export_ready.cta": "Download CSV",
  "notifications.error.load": "We couldn't load your notifications.",
  "notifications.error.retry": "Try again",
  "notifications.error.mutation": "That action didn't work. Please try again.",
  "notifications.toast.archived": "Notification archived",
  "notifications.toast.unarchived": "Notification restored",
  "notifications.toast.snoozed": "Notification snoozed",
  "notifications.toast.unsnoozed": "Snooze cleared",
  "notifications.toast.all_read": "All notifications marked as read",
  "notifications.snooze.title": "Snooze notification",
  "notifications.snooze.preset.one_hour": "1 hour",
  "notifications.snooze.preset.tomorrow": "Tomorrow",
  "notifications.snooze.preset.next_week": "Next week",
  "notifications.snooze.custom": "Custom...",
  "notifications.snooze.confirm": "Snooze",
  "notifications.snooze.cancel": "Cancel",
  "notifications.mobile.back": "Back to notifications",

  // Events
  "events.status.draft": "Draft",
  "events.status.past": "Past",
  "events.status.upcoming": "Upcoming",

  "events.schedule.title": "Schedule",
  "events.schedule.start": "Start",
  "events.schedule.due": "Due",
  "events.list.title": "Events",
  "events.list.new": "New Event",
  "events.list.create": "Create Event",
  "events.list.export": "Export events",
  "events.list.count_one": "{count} event",
  "events.list.count_other": "{count} events",
  "events.list.not_scheduled": "Not scheduled",
  "events.list.ends": "ends {date}",
  "events.list.sessions.expand": "Show sessions for {name}",
  "events.list.sessions.collapse": "Hide sessions for {name}",
  "events.view.list": "List",
  "events.view.timeline": "Timeline",
  "events.filter.all": "All",
  "events.filter.upcoming": "Upcoming",
  "events.filter.past": "Past",
  "events.list.empty.title_all": "No events yet",
  "events.list.empty.title_upcoming": "No upcoming events",
  "events.list.empty.title_past": "No past events",
  "events.list.empty.description_all": "Create your first event to get started.",
  "events.list.empty.description_filtered": "Try changing the filter to see more events.",
  "events.duration.custom": "Custom",
  "events.duration.minutes_short": "min",

  "events.create.discard": "Discard",
  "events.create.submit": "Create Event",
  "events.create.title_placeholder": "Event title",
  "events.create.description_placeholder": "Add a description...",
  "events.create.reschedule_label": "Choose a different time",
  "events.create.attendees_changed_warning":
    "Assignees changed — there may be scheduling conflicts.",
  "events.create.recheck_availability": "Re-check availability",
  "events.create.duration_adjusted":
    "The event end time has been adjusted for the new duration. Review for conflicts.",
  "events.create.created_success": "Event created",
  "events.create.create_failed": "Failed to create event",
  "events.create.create_work_item": "Create work item",
  "events.create.project": "Project",
  "events.create.project_placeholder": "Select project",
  "events.create.project_required": "Select a project first",

  "events.project.label": "Project",
  "events.project.placeholder": "Select a project",
  "events.project.none": "No project",
  "events.project.unknown": "A project you can't see",
  // Home team (NG-2155): the copy is the prototype's (Event — Create / Overview).
  "events.home_team.label": "Home team",
  "events.home_team.description":
    "Every event belongs to one Home team — through its project, or set directly when there is no project.",
  "events.home_team.group_aria": "How this event gets its Home team",
  "events.home_team.link_project": "Link to a project",
  "events.home_team.link_project_hint":
    "Uses the project's Home team and follows it if that changes.",
  "events.home_team.set_team": "Set a Home team",
  "events.home_team.set_team_hint": "For a standalone event with no project.",
  "events.home_team.inherited": "Inherited from the project",
  "events.home_team.any_level": "Any team at any level can be a Home team.",
  "events.home_team.pending_team":
    "Pick a Home team to finish. This unlinks the event from {project}. Until you pick one, it keeps its current Home team.",
  "events.home_team.pending_project":
    "Pick a project to finish. Its Home team replaces {team}. Until you pick one, nothing changes.",
  "events.home_team.keep_current": "Keep current",
  "events.home_team.denied": "You need the event.edit permission to change this event's Home team.",
  "events.home_team.standalone_denied":
    "You need the event.create permission to create an event with no project.",
  "events.home_team.unknown_team": "A team you can't see",
  "events.home_team.update_failed": "Couldn't change the event's Home team.",
  "home_team_select.placeholder": "Select a team…",
  "home_team_select.search_placeholder": "Search teams",
  "home_team_select.empty": "No teams found",
  "events.status.default_placeholder": "Workspace default",
  "events.parent.label": "Parent",
  "events.parent.none": "No parent",
  "events.timezone.label": "Time Zone",
  "events.timezone.unresolved":
    "Couldn't read the event's time zone, so the time wasn't saved. Pick a time zone on the event and try again.",
  "events.parent.clear": "Clear parent",
  "events.parent.search_placeholder": "Search work items...",
  "events.parent.no_matches": "No work items found",
  "events.sessions.title": "Sessions",
  "events.sessions.add": "Add session",
  "events.sessions.name_placeholder": "Session name",
  "events.sessions.empty": "No sessions yet.",
  "events.sessions.edit": "Edit session",
  "events.sessions.create_failed": "Failed to create session",
  "events.sessions.update_failed": "Failed to update session",
  "events.sessions.load_failed": "Failed to load sessions",
  "events.sessions.bounds_hint": "Must fall within the event: {start} – {end}.",
  "events.sessions.out_of_range":
    "Pick a start on or after the event's start and an end on or before its end.",
  "events.sessions.start_label": "Session start",
  "events.sessions.end_label": "Session end",
  "events.sessions.timezone_hint": "Times are in the event's time zone ({timezone}).",
  "events.sessions.invalid_schedule": "Enter a valid start and end time.",
  "events.detail.category_update_failed": "Failed to update category",
  "events.detail.color_update_failed": "Couldn't update the color",
  "events.detail.project_update_failed": "Failed to update project",
  "events.detail.parent_update_failed": "Failed to update parent",
  "events.detail.timezone_update_failed": "Failed to update time zone",
  "events.detail.assignee_update_failed": "Failed to update assignees",

  "events.detail.tabs_label": "Event views",
  "events.detail.tab_overview": "Overview",
  "events.detail.tab_timeline": "Timeline",
  "events.detail.name_label": "Event name",
  "events.detail.more_actions": "More actions",
  "events.detail.activity": "Activity",
  "events.detail.delete": "Delete event",
  "events.recurrence.recurring_event": "Recurring event",
  "events.recurrence.delete_series_title": "Delete recurring event",
  "events.recurrence.delete_series_description":
    '"{{name}}" repeats. Deleting it removes the whole series and all its occurrences. This can\'t be undone.',
  "events.recurrence.make_recurring": "Make recurring",
  "events.recurrence.make_recurring_title": "Make this event recurring",
  "events.recurrence.make_recurring_description":
    "Choose a repeat pattern. This event becomes the first occurrence of the series.",
  "events.recurrence.make_recurring_confirm": "Make recurring",
  "events.recurrence.make_recurring_cancel": "Cancel",
  "events.recurrence.make_recurring_success": "Event now repeats",
  "events.recurrence.make_recurring_error": "Failed to make the event recurring",

  // Recurrence editor, badge and scope dialog (NG-2584)
  "events.recurrence.frequency.daily": "Daily",
  "events.recurrence.frequency.weekly": "Weekly",
  "events.recurrence.frequency.monthly": "Monthly",
  "events.recurrence.frequency.yearly": "Yearly",
  "events.recurrence.unit.daily": "{count, plural, one {day} other {days}}",
  "events.recurrence.unit.weekly": "{count, plural, one {week} other {weeks}}",
  "events.recurrence.unit.monthly": "{count, plural, one {month} other {months}}",
  "events.recurrence.unit.yearly": "{count, plural, one {year} other {years}}",
  "events.recurrence.repeat_label": "Repeat",
  "events.recurrence.does_not_repeat": "Does not repeat",
  "events.recurrence.repeat_every": "Repeat every",
  "events.recurrence.interval_aria": "Interval",
  "events.recurrence.on_these_days": "On these days",
  "events.recurrence.days_of_week_aria": "Days of week",
  "events.recurrence.on_day": "On day",
  "events.recurrence.day_of_month_aria": "Day of month",
  "events.recurrence.month_day_skip_hint": "Skipped in months without this day.",
  "events.recurrence.ends": "Ends",
  "events.recurrence.ends_on": "On",
  "events.recurrence.end_date_aria": "End date",
  "events.recurrence.ends_default_hint": "Defaults to the project's end date.",
  "events.recurrence.ends_after": "After",
  "events.recurrence.occurrence_count_aria": "Occurrence count",
  "events.recurrence.occurrences": "occurrences",
  "events.recurrence.recurring": "Recurring",
  "events.recurrence.edited_occurrence": "Edited occurrence of a recurring event",
  "events.recurrence.scope.this": "This event",
  "events.recurrence.scope.this_and_following": "This and following events",
  "events.recurrence.scope.all": "All events",
  "events.recurrence.scope.edit_title": "Edit recurring event",
  "events.detail.id_subtitle": "ID: {id}",
  "events.recurrence.summary.every.daily":
    "{interval, plural, one {every day} other {every {intervalText} days}}",
  "events.recurrence.summary.every.weekly":
    "{interval, plural, one {every week} other {every {intervalText} weeks}}",
  "events.recurrence.summary.every.monthly":
    "{interval, plural, one {every month} other {every {intervalText} months}}",
  "events.recurrence.summary.every.yearly":
    "{interval, plural, one {every year} other {every {intervalText} years}}",
  "events.recurrence.summary.every_weekday": "Repeats every weekday",
  "events.recurrence.summary.weekly_on": "Repeats {every} on {days}",
  "events.recurrence.summary.monthly_on_day": "Repeats {every} on day {day}",
  "events.recurrence.summary.plain": "Repeats {every}",
  "events.recurrence.summary.until": "until {date}",
  "events.recurrence.summary.for_count": "for {countText} occurrences",
  "events.recurrence.weekday.SU": "Sunday",
  "events.recurrence.weekday.MO": "Monday",
  "events.recurrence.weekday.TU": "Tuesday",
  "events.recurrence.weekday.WE": "Wednesday",
  "events.recurrence.weekday.TH": "Thursday",
  "events.recurrence.weekday.FR": "Friday",
  "events.recurrence.weekday.SA": "Saturday",
  "events.recurrence.weekday_short.SU": "S",
  "events.recurrence.weekday_short.MO": "M",
  "events.recurrence.weekday_short.TU": "T",
  "events.recurrence.weekday_short.WE": "W",
  "events.recurrence.weekday_short.TH": "T",
  "events.recurrence.weekday_short.FR": "F",
  "events.recurrence.weekday_short.SA": "S",

  "events.recurrence.already_recurring": "This event already repeats",
  "events.recurrence.edit": "Edit recurrence",
  "events.recurrence.edit_title": "Edit recurrence",
  "events.recurrence.edit_description":
    "Change how this event repeats. This updates the whole series.",
  "events.recurrence.edit_confirm": "Save",
  "events.recurrence.edit_apply_anyway": "Apply anyway",
  "events.recurrence.edit_cancel": "Cancel",
  "events.recurrence.edit_success": "Recurrence updated",
  "events.recurrence.edit_error": "Failed to update the recurrence",
  "events.recurrence.edit_orphan_warning":
    "This change drops {count, plural, one {# customized or cancelled occurrence} other {# customized or cancelled occurrences}}, which will no longer apply.",
  "events.detail.delete_confirm_title": "Delete event",
  "events.detail.delete_confirm_description": 'Delete "{name}"? This can\'t be undone.',
  "events.detail.delete_success": "Event deleted",
  "events.detail.delete_failed": "Failed to delete event",
  "events.detail.rename_failed": "Failed to rename event",
  "events.detail.not_found": "Event not found.",
  "events.detail.load_failed": "Failed to load event",
  "events.detail.comment_failed": "Failed to post comment",
  "events.detail.duration_update_failed": "Failed to update duration",
  "events.detail.work_item_update_failed": "Failed to update work item",
  "events.detail.schedule_button": "Schedule Event",
  "events.detail.find_new_time": "Find a new time",
  "events.detail.description_placeholder": "Add a description...",
  "events.detail.description_empty": "Add a description...",
  "events.detail.description_save_error": "Failed to save event description",

  "events.properties.event_time": "Event Time",
  "events.timezone.your_time": "Your time: {time}",
  "events.properties.date": "Date",
  "events.properties.not_scheduled": "Not scheduled",
  "events.properties.work_item": "Work item",
  "events.properties.no_work_item": "No work item",
  "events.properties.link_work_item": "Link work item",
  "events.properties.unlink_work_item": "Unlink work item",
  "events.properties.search_work_items": "Search work items...",
  "events.properties.no_work_items": "No work items found",
  "events.properties.loading_work_items": "Loading work items...",
  "events.properties.duration": "Duration",
  "events.properties.repeats": "Repeats",
  "events.properties.attendees": "Assignees",
  "events.properties.no_members_available": "No members available",
  "events.properties.add_attendee": "Add assignee",
  "events.properties.find_a_time": "Find a Time",
  "events.properties.reschedule": "Reschedule",

  "events.find_a_time.title": "Find a Time",
  "events.find_a_time.back": "Back",
  "events.find_a_time.today": "Today",
  "events.find_a_time.previous_week": "Previous week",
  "events.find_a_time.next_week": "Next week",
  "events.find_a_time.event_label": "Event",
  "events.find_a_time.event_title_placeholder": "Event title",
  "events.find_a_time.duration_label": "Duration",
  "events.find_a_time.attendees_label": "Attendees ({count})",
  "events.find_a_time.add_attendee": "+ Add attendee",
  "events.find_a_time.selected_time": "Selected Time",
  "events.find_a_time.no_attendees": "No attendees yet — all times are open.",
  "events.find_a_time.cancel": "Cancel",
  "events.find_a_time.confirm_time": "Confirm Time",
  "events.find_a_time.reschedule_action": "Reschedule",
  "events.find_a_time.fetch_failed": "Failed to fetch availability",
  "events.find_a_time.reschedule_success": "Event rescheduled",
  "events.find_a_time.reschedule_failed": "Failed to reschedule",
  "events.find_a_time.timezone_note": "Times shown in {timezone}",

  "events.suggested_times.title": "Suggested Times",
  "events.suggested_times.empty": "No available slots found for this week.",
  "events.suggested_times.next_week": "Browse next week",

  // Calendar layout
  "calendar.previous_month": "Previous month",
  "calendar.next_month": "Next month",
  "calendar.today": "Today",
  "calendar.more": "+{count} more",
  "calendar.show_less": "Show less",
  "calendar.reschedule_failed": "Failed to reschedule issue",

  // Connected apps: the create dialog, the apps table and the redirect URI list (NG-2581)
  "oauth_apps.create.title": "Register OAuth app",
  "oauth_apps.create.name": "Name",
  "oauth_apps.create.redirect_uris": "Redirect URIs (one per line)",
  "oauth_apps.create.redirect_uris_hint":
    "Exact match required at sign-in. You can add more after creation.",
  "oauth_apps.create.client_type": "Client type",
  "oauth_apps.create.client_confidential": "Confidential (server-side, has a secret)",
  "oauth_apps.create.client_public": "Public (native or SPA, no secret)",
  "oauth_apps.create.auto_trust":
    "Auto-trust this app — skip the user consent screen on first use.",
  "oauth_apps.create.scopes": "Scopes",
  "oauth_apps.create.destructive_confirm_aria": "Confirm destructive scopes",
  "oauth_apps.create.destructive_confirm":
    "I understand these scopes can create, modify, or delete data in the workspace.",
  "oauth_apps.create.creating": "Creating…",
  "oauth_apps.create.submit": "Create",
  "oauth_apps.create.error": "Failed to create the app.",
  "oauth_apps.list.column_name": "Name",
  "oauth_apps.list.column_client_id": "Client ID",
  "oauth_apps.list.column_type": "Type",
  "oauth_apps.list.column_scopes": "Scopes",
  "oauth_apps.list.column_enabled": "Enabled",
  "oauth_apps.list.enabled_yes": "yes",
  "oauth_apps.list.enabled_no": "no",
  "oauth_apps.list.actions_aria": "Actions for {name}",
  "oauth_apps.list.delete": "Delete",
  "oauth_apps.list.empty": 'No connected apps yet. Click "New app" to register one.',
  "oauth_apps.redirect_uris.note":
    "Cognota refuses to send the authorization code to any URI not in this list. Matching is exact — query strings and trailing slashes count.",
  "oauth_apps.redirect_uris.empty": "No redirect URIs yet.",
  "oauth_apps.redirect_uris.remove": "Remove",
  "oauth_apps.redirect_uris.adding": "Adding…",
  "oauth_apps.redirect_uris.add": "Add",
  "oauth_apps.redirect_uris.error_empty": "URI may not be empty.",
  "oauth_apps.redirect_uris.error_fragment": "Redirect URIs may not contain fragments.",
  "oauth_apps.redirect_uris.error_scheme": "Redirect URI must start with http:// or https://.",
  "oauth_apps.redirect_uris.error_add": "Failed to add URI.",

  // Theme
  "theme.toggle": "Toggle theme",
  "theme.use_light": "Switch to light",
  "theme.use_dark": "Switch to dark",

  // Team availability
  "availability.title": "Team availability",
  "availability.description":
    "Team capacity and calendar blocks. Drag to scroll; hover a cell for details.",
  "availability.date_from": "From",
  "availability.date_to": "To",
  "availability.date_invalid": "Please enter a valid date.",
  "availability.date_end_before_start": "The end date must be after the start date.",
  "availability.date_range_too_long": "Please select a range of 90 days or fewer.",
  "availability.filter_placeholder": "Filter by qualifications",
  "availability.filter_clear": "Clear filters",
  "availability.filter_count": "{count} selected",
  "availability.empty_filtered": "No team members match the selected filters.",
  "availability.loading": "Loading availability...",
  "availability.load_error": "Failed to load team availability.",
  "availability.not_authorized":
    "You do not have permission to view team availability in this workspace.",
  "availability.legend.high": "≥75% available",
  "availability.legend.mid": "25-75%",
  "availability.legend.low": "<25%",
  "availability.legend.none": "Weekend / no capacity",
  "availability.tooltip.capacity": "Capacity",
  "availability.tooltip.committed": "Committed",
  "availability.tooltip.blocked": "Calendar blocked",
  "availability.tooltip.available": "Available",
  "availability.tooltip.status": "Status",
  "availability.weekday.sun": "Sun",
  "availability.weekday.mon": "Mon",
  "availability.weekday.tue": "Tue",
  "availability.weekday.wed": "Wed",
  "availability.weekday.thu": "Thu",
  "availability.weekday.fri": "Fri",
  "availability.weekday.sat": "Sat",

  // Capacity
  "capacity.title": "Capacity",
  "capacity.no_permission": "You don't have permission to view capacity.",
  "capacity.error.fetch_failed": "Couldn't load capacity data.",
  "capacity.date_from": "From",
  "capacity.date_to": "To",
  "capacity.date_week": "Week",
  "capacity.date_month": "Month",
  "capacity.range_separator": "to",
  "capacity.view.label": "Capacity view",
  "capacity.view.table": "Table view",
  "capacity.view.triage": "Triage board",
  "capacity.display.label": "Capacity display",
  "capacity.display.hours": "Hours",
  "capacity.display.percent": "Percent",
  "capacity.tweaks.open": "Configurations",
  "capacity.tweaks.title": "Capacity configurations",
  "capacity.tweaks.view.label": "Default view",
  "capacity.tweaks.view.heatmap": "Heatmap",
  "capacity.tweaks.view.timeline": "Timeline",
  "capacity.tweaks.unit.label": "Unit",
  "capacity.tweaks.granularity.label": "Granularity",
  "capacity.tweaks.group_rows.label": "Group by",
  "capacity.tweaks.density.label": "Density",
  "capacity.tweaks.density.compact": "Compact",
  "capacity.tweaks.density.standard": "Standard",
  "capacity.tweaks.over_threshold": "Over threshold",
  "capacity.tweaks.under_threshold": "Under threshold",
  "capacity.tweaks.show_weekends": "Show weekends",
  "capacity.tweaks.reset": "Reset configurations",
  "capacity.availability_state.available": "Available",
  "capacity.availability_state.fully_utilized": "Fully utilized",
  "capacity.availability_state.over_capacity": "Over capacity",
  "capacity.availability_state.no_configured_capacity": "No configured capacity",
  "capacity.availability_state.non_working_day": "Non-working day",
  "capacity.availability_state.time_off": "Time off",
  "capacity.availability_state.holiday": "Holiday",
  "capacity.availability_state.calendar_blocked": "Calendar blocked",
  "capacity.availability_state.not_in_scope": "Not in scope",
  "capacity.availability_state.short.available": "OK",
  "capacity.availability_state.short.fully_utilized": "FULL",
  "capacity.availability_state.short.over_capacity": "OVER",
  "capacity.availability_state.short.no_configured_capacity": "N/A",
  "capacity.availability_state.short.non_working_day": "OFF",
  "capacity.availability_state.short.time_off": "PTO",
  "capacity.availability_state.short.holiday": "HOL",
  "capacity.availability_state.short.calendar_blocked": "CAL",
  "capacity.availability_state.short.not_in_scope": "N/S",
  "capacity.availability_reduction.time_off": "Time off",
  "capacity.availability_reduction.holiday": "Holiday",
  "capacity.availability_reduction.external_calendar": "External calendar",
  "capacity.availability_reduction.app_event": "Calendar event",
  "capacity.availability_reduction.other": "Other reduction",
  "capacity.group.label": "Capacity grouping",
  "capacity.group.day": "Day",
  "capacity.group.week": "Week",
  "capacity.group.month": "Month",
  "capacity.row_group.label": "Capacity rows",
  "capacity.row_group.member": "Members",
  "capacity.row_group.team": "Teams",
  "capacity.row_group.project": "Projects",
  "capacity.date_invalid": "Please enter a valid date.",
  "capacity.date_end_before_start": "The end date must be after the start date.",
  "capacity.date_range_too_long": "Please select a range of 90 days or fewer.",
  "capacity.loading": "Loading capacity...",
  "capacity.load_error": "Failed to load capacity.",
  "capacity.not_authorized": "You do not have permission to view capacity in this workspace.",
  "capacity.empty": "No workspace members found.",
  "capacity.metric.capacity": "Capacity",
  "capacity.metric.available": "Available",
  "capacity.metric.planned": "Planned demand",
  "capacity.metric.estimated": "Estimated allocated",
  "capacity.metric.actual": "Actual logged",
  "capacity.metric.remaining": "Remaining",
  "capacity.metric.utilization": "Utilization",
  "capacity.metric.overallocated": "Overallocated",
  "capacity.metric.underutilized": "Underutilized",
  "capacity.metric.of_capacity": "of capacity",
  "capacity.summary.members": "members",
  "capacity.summary.teams": "teams",
  "capacity.summary.projects": "projects",
  "capacity.summary.periods": "periods",
  "capacity.summary.people": "people",
  "capacity.problem.overallocated": "Most overallocated",
  "capacity.problem.slack": "Most slack",
  "capacity.problem.no_overallocated": "No overallocated members in this range.",
  "capacity.problem.no_slack": "No underutilized members in this range.",
  "capacity.problem.of": "of",
  "capacity.problem.over": "over",
  "capacity.problem.available": "available",
  "capacity.triage.label": "Capacity triage board",
  "capacity.triage.title": "Triage board",
  "capacity.triage.overallocated": "Overallocated",
  "capacity.triage.on_target": "On target",
  "capacity.triage.underutilized": "Underutilized",
  "capacity.triage.overcommitted": "overcommitted",
  "capacity.triage.planned": "planned",
  "capacity.triage.available": "available",
  "capacity.triage.empty": "No members in this status.",
  "capacity.triage.action.review_overload": "Review overload",
  "capacity.triage.action.view": "View",
  "capacity.triage.action.review_availability": "Review availability",
  "capacity.table.daily_totals": "Daily totals",
  "capacity.legend.under": "<65% underbooked",
  "capacity.legend.healthy": "65-115% on target",
  "capacity.legend.warning": "115-125% over",
  "capacity.legend.over": ">125% severely over",
  "capacity.legend.under_label": "underbooked",
  "capacity.legend.healthy_label": "on target",
  "capacity.legend.warning_label": "over",
  "capacity.legend.over_label": "severely over",
  "capacity.group_rows.empty_teams": "No teams found.",
  "capacity.group_rows.empty_projects": "No projects found.",
  "capacity.group_rows.load_error": "Failed to load grouped capacity.",
  "capacity.table_label": "Workspace capacity",
  "capacity.column.member": "Member",
  "capacity.column.team": "Team",
  "capacity.column.project": "Project",
  "capacity.column.total": "Range total",
  "capacity.column.slack_debt": "Slack / debt",
  "capacity.timeline.slack": "slack",
  "capacity.timeline.over": "over",
  "capacity.workspace_total": "Workspace total",
  "capacity.cell.planned": "Plan",
  "capacity.cell.estimated": "Est",
  "capacity.cell.actual": "Act",
  "capacity.cell.capacity": "Capacity",
  "capacity.cell.available": "Availability",
  "capacity.actual_tbd": "TBD",
  "capacity.close": "Close",
  "capacity.calculation.title": "How the numbers are calculated",
  "capacity.calculation.summary":
    "Hours and Percent use the same inputs; Percent converts planned or actual demand into utilization against capacity.",
  "capacity.calculation.capacity_label": "Capacity",
  "capacity.calculation.capacity":
    "Configured work hours minus PTO, holidays, and calendar blocks, adjusted by capacity percent.",
  "capacity.calculation.planned_label": "Planned",
  "capacity.calculation.planned":
    "Estimated task and event demand allocated into the selected day, week, or month.",
  "capacity.calculation.actual_label": "Actual",
  "capacity.calculation.actual": "Logged task and event time when work logs are available.",
  "capacity.calculation.remaining_label": "Remaining",
  "capacity.calculation.remaining":
    "Remaining availability is capacity minus planned demand and other allocations.",
  "capacity.beeswarm.eyebrow": "Distribution",
  "capacity.beeswarm.title": "Utilization spread",
  "capacity.beeswarm.subtitle": "{range} · each dot = 1 {entity}",
  "capacity.beeswarm.view_label": "Distribution grouping",
  "capacity.beeswarm.by_member": "By member",
  "capacity.beeswarm.by_team": "By team",
  "capacity.beeswarm.by_project": "By project",
  "capacity.beeswarm.category.under": "Underutilized",
  "capacity.beeswarm.category.target": "On target",
  "capacity.beeswarm.category.over": "Over capacity",
  "capacity.beeswarm.category.severe": "Severe",
  "capacity.beeswarm.range.under": "< {percent}%",
  "capacity.beeswarm.range.between": "{start}-{end}%",
  "capacity.beeswarm.range.over": "> {percent}%",
  "capacity.beeswarm.marker.under": "Under {percent}%",
  "capacity.beeswarm.marker.capacity": "Capacity 100%",
  "capacity.beeswarm.empty": "No utilization data for this selection",
  "capacity.beeswarm.helper":
    "Dots left of the underutilized marker show available capacity. Dots right of 100% show demand above capacity.",
  "capacity.beeswarm.tooltip": "{label}: {utilization}% · {planned} of {capacity}",
  "capacity.beeswarm.tooltip_detail": "{utilization}% · {planned} of {capacity}",
  "capacity.beeswarm.entity.member": "member",
  "capacity.beeswarm.entity.team": "team",
  "capacity.beeswarm.entity.project": "project",
  "capacity.details.title": "Capacity details",
  "capacity.details.calculation_title": "How this is calculated",
  "capacity.details.calculation_capacity_label": "Capacity",
  "capacity.details.calculation_capacity":
    "Configured work hours minus PTO, holidays, and calendar blocks, adjusted by capacity percent.",
  "capacity.details.calculation_planned_label": "Planned",
  "capacity.details.calculation_planned":
    "Estimated task and event demand allocated into this period.",
  "capacity.details.calculation_actual_label": "Actual",
  "capacity.details.calculation_actual": "Logged task and event time when work logs are available.",
  "capacity.details.calculation_remaining_label": "Remaining",
  "capacity.details.calculation_remaining":
    "Remaining = capacity - planned demand - other allocations.",
  "capacity.details.planned": "Planned",
  "capacity.details.estimated": "Estimated",
  "capacity.details.actual": "Actual",
  "capacity.details.capacity": "Capacity",
  "capacity.details.available": "Available",
  "capacity.details.remaining": "Remaining",
  "capacity.details.model_summary": "Planned vs actual",
  "capacity.details.planned_task": "Planned task",
  "capacity.details.planned_event": "Planned event",
  "capacity.details.planned_utilization": "Planned utilization",
  "capacity.details.actual_task": "Actual task",
  "capacity.details.actual_event": "Actual event",
  "capacity.details.actual_utilization": "Actual utilization",
  "capacity.details.variance": "Variance",
  "capacity.details.accuracy": "Accuracy",
  "capacity.details.calendar_blocked": "Calendar blocked",
  "capacity.details.availability_status": "Availability status",
  "capacity.details.availability_reductions": "Availability reductions",
  "capacity.details.availability_reductions_description":
    "Calendar events reduce available capacity but are not counted as task allocations.",
  "capacity.details.no_calendar_events": "No calendar events returned for this period.",
  "capacity.details.full_day": "Full day",
  "capacity.details.tasks": "Events and tasks affecting this item",
  "capacity.details.allocations": "Events and tasks affecting this item",
  "capacity.details.empty": "No events or task allocations for this period.",
  "capacity.details.empty_allocations": "No events or task allocations for this period.",
  "capacity.details.task": "Task",
  "capacity.details.event": "Event",
  "capacity.details.project": "Project",
  "capacity.details.no_project": "No project",
  "capacity.details.private_project": "Private project",
  "capacity.details.private_task": "Private task",
  "capacity.details.private_event": "Private event",
  "capacity.details.items": "Items",
  "capacity.details.member": "Member",
  "capacity.details.over_capacity": "Over capacity",
  "capacity.details.over_capacity_description":
    "These items push the selected item over its limit.",
  "capacity.details.over_by": "Over by",
  "capacity.details.running_total": "Running total",
  "capacity.details.unassign_task": "Unassign",
  "capacity.details.unassign_task_label": "Unassign {task} from {member}",
  "capacity.details.reassign_task": "Assign someone else",
  "capacity.details.reassign_task_label": "Assign someone else to {task}",
  "capacity.details.reassignment_candidates": "Replacement candidates",
  "capacity.details.reassignment_candidates_description":
    "Pick a project member with room for this task.",
  "capacity.details.reassignment_loading": "Finding replacements...",
  "capacity.details.reassignment_candidates_empty": "No replacement candidates found.",
  "capacity.details.reassignment_candidates_error": "Could not load replacement candidates.",
  "capacity.details.reassign_to_label": "Assign to {member}",
  "capacity.details.reassignment_remaining": "{hours} remaining",
  "capacity.details.reassignment_capacity_gap": "{hours} capacity gap",
  "capacity.details.assignment_update_success": "Task assignment updated.",
  "capacity.details.assignment_update_error": "Could not update task assignment.",
  "capacity.details.load_more": "Load more",
  "capacity.details.showing_count": "Showing {shown} of {total}",
  "capacity.filter.all_members": "All members",
  "capacity.filter.no_members": "No members",
  "capacity.filter.members": "Members",
  "capacity.filter.all": "All",
  "capacity.filter.none": "None",
  "capacity.filter.empty_selection": "No members selected.",
  "capacity.matching.open": "Task matching",
  "capacity.matching.title": "Task matching",
  "capacity.matching.tab_tasks": "Tasks for member",
  "capacity.matching.tab_users": "Members for task",
  "capacity.matching.member": "Member",
  "capacity.matching.no_members": "No members",
  "capacity.matching.include_assigned": "Include assigned",
  "capacity.matching.member_results": "{member} task matches",
  "capacity.matching.loading": "Loading matches...",
  "capacity.matching.load_error": "Failed to load task matches.",
  "capacity.matching.empty_tasks": "No matching tasks found.",
  "capacity.matching.fits_capacity": "Fits capacity",
  "capacity.matching.capacity_gap": "{hours} gap",
  "capacity.matching.score": "Score {score}",
  "capacity.matching.skills_percent": "Qualifications {percent}%",
  "capacity.matching.remaining": "{hours} remaining",
  "capacity.matching.matched_skills": "Matched: {skills}",
  "capacity.matching.missing_skills": "Missing: {skills}",
  "capacity.matching.no_required_skills": "No required qualifications",
  "capacity.matching.assigned_count": "{count} assigned",
  "capacity.matching.open_task": "Open task",
  "capacity.matching.find_users": "Find users",
  "capacity.matching.task": "Task",
  "capacity.matching.task_placeholder": "Task name, ID, or number",
  "capacity.matching.search": "Search",
  "capacity.matching.empty_users": "No matching members found.",

  // Project capacity
  "project_capacity.title": "Project capacity",
  "project_capacity.unknown_project": "Project",
  "project_capacity.range_separator": "to",
  "project_capacity.date_from": "From",
  "project_capacity.date_to": "To",
  "project_capacity.date_invalid": "Please enter a valid date.",
  "project_capacity.date_end_before_start": "The end date must be after the start date.",
  "project_capacity.loading": "Loading project capacity...",
  "project_capacity.load_error": "Failed to load project capacity.",
  "project_capacity.not_authorized": "You do not have permission to view project capacity.",
  "project_capacity.empty": "No project members found.",
  "project_capacity.metric.members": "Members",
  "project_capacity.metric.available": "Capacity",
  "project_capacity.metric.allocated": "Planned demand",
  "project_capacity.metric.other_allocation": "Other allocation",
  "project_capacity.metric.actual": "Actual logged",
  "project_capacity.metric.remaining": "Remaining",
  "project_capacity.metric.utilization": "Utilization",
  "project_capacity.timeline.title": "Capacity by period",
  "project_capacity.timeline.label": "Project capacity by period",
  "project_capacity.timeline.available": "Capacity",
  "project_capacity.timeline.estimated": "Planned",
  "project_capacity.timeline.actual": "Actual",
  "project_capacity.table_label": "Project member capacity",
  "project_capacity.column.member": "Member",
  "project_capacity.column.total": "Total",
  "project_capacity.column.available": "Capacity",
  "project_capacity.column.allocated": "Planned",
  "project_capacity.column.other": "Other",
  "project_capacity.column.actual": "Actual",
  "project_capacity.column.calendar_blocked": "Calendar blocked",
  "project_capacity.column.remaining": "Remaining",
  "project_capacity.column.utilization": "Utilization",
  "project_capacity.total": "Project total",

  // Team capacity
  "team_capacity.title": "Team capacity",
  "team_capacity.breadcrumb": "Capacity",
  "team_capacity.unknown_team": "Team",
  "team_capacity.range_separator": "to",
  "team_capacity.date_from": "From",
  "team_capacity.date_to": "To",
  "team_capacity.date_invalid": "Please enter a valid date.",
  "team_capacity.date_end_before_start": "The end date must be after the start date.",
  "team_capacity.loading": "Loading team capacity...",
  "team_capacity.load_error": "Failed to load team capacity.",
  "team_capacity.team_load_error": "Failed to load team.",
  "team_capacity.back_to_team": "Back to team",
  "team_capacity.empty": "No team members found.",
  "team_capacity.metric.members": "Members",
  "team_capacity.metric.projects": "Projects",
  "team_capacity.metric.available": "Capacity",
  "team_capacity.metric.estimated": "Planned demand",
  "team_capacity.metric.other_allocation": "Other allocation",
  "team_capacity.metric.actual": "Actual logged",
  "team_capacity.metric.remaining": "Remaining",
  "team_capacity.metric.utilization": "Utilization",
  "team_capacity.timeline.title": "Capacity by period",
  "team_capacity.timeline.label": "Team capacity by period",
  "team_capacity.timeline.available": "Capacity",
  "team_capacity.timeline.estimated": "Planned",
  "team_capacity.timeline.actual": "Actual",
  "team_capacity.table_label": "Team member capacity",
  "team_capacity.column.member": "Member",
  "team_capacity.column.total": "Total",
  "team_capacity.column.available": "Capacity",
  "team_capacity.column.estimated": "Planned",
  "team_capacity.column.other": "Other",
  "team_capacity.column.actual": "Actual",
  "team_capacity.column.calendar_blocked": "Calendar blocked",
  "team_capacity.column.remaining": "Remaining",
  "team_capacity.column.utilization": "Utilization",
  "team_capacity.total": "Team total",

  // Worklog / time tracking
  "worklog.title": "Time logs",
  "worklog.property_label": "Time logs",
  "worklog.empty": "No time logged yet.",
  "worklog.loading": "Loading time logs...",
  "worklog.load_error": "Failed to load time logs.",
  "worklog.show": "Show",
  "worklog.hide": "Hide",
  "worklog.summary_count": "{count} · {total}",
  "worklog.log_action": "Log time",
  "worklog.edit_action": "Edit",
  "worklog.delete_action": "Delete",
  "worklog.delete_confirm_title": "Delete time entry",
  "worklog.delete_confirm_description": "This will permanently remove this time entry.",
  "worklog.delete_success": "Time entry deleted",
  "worklog.delete_error": "Failed to delete time entry",
  "worklog.save_success": "Time entry saved",
  "worklog.save_error": "Failed to save time entry",
  "worklog.modal.create_title": "Log time",
  "worklog.modal.edit_title": "Edit time entry",
  "worklog.modal.date_label": "Date",
  "worklog.modal.hours_label": "Hours worked",
  "worklog.modal.hours_part_label": "Hours",
  "worklog.modal.minutes_part_label": "Minutes",
  "worklog.modal.duration_preview": "Duration: {duration}",
  "worklog.modal.description_label": "Description",
  "worklog.modal.description_placeholder": "What did you work on?",
  "worklog.modal.submit": "Save",
  "worklog.modal.submitting": "Saving...",
  "worklog.modal.cancel": "Cancel",
  "worklog.modal.duration_required": "Choose a date and enter hours worked.",
  "worklog.modal.duration_required_hm": "Choose a date and enter the time worked.",
  "worklog.entry.logged_by": "Logged by {name}",
  "worklog.entry.logged_relative": "logged {relative}",
  "worklog.column_title": "Time logged",
  "worklog.table.person": "Person",
  "worklog.table.hours": "Hours",
  "worklog.table.note": "Note",
  "worklog.table.date": "Date",
  "worklog.cell.log_action_aria": "Log time on this work item",
  "worklog.cell.log_with_total_aria": "Log time — {total} logged on this work item",
  "worklog.summary.aria_label": "{total} logged on this issue",

  // Work item detail panel (NG-2582)
  "issue.properties_overview": "Overview",
  "issue.properties_actual_hours": "Actual Hours",
  "issue.activity_no_match": "No matching activity",
  "issue.attachments_download": "Download",
  "issue.attachments_drop_hint": "Drop files here or click to upload",
  "worklog.filter_aria": "Filter time logs",
  "worklog.total_logged": "Logged this task",
  "worklog.estimate_remaining": "Estimate remaining",
  "worklog.logged_by": "Logged by",

  // Project settings
  "project_settings.tabs.general": "General",
  "project_settings.tabs.members": "Members",
  "project_settings.tabs.states": "States",
  "project_settings.tabs.workflow": "Workflow",

  // Project delivery: what an LMS delivers this project (NG-770)
  "project_settings.delivery.title": "Delivery",
  "project_settings.delivery.description":
    "Point this project at the course or learning path that delivers it, so results come back attributed to the work that produced them.",
  "project_delivery.title": "Delivered by",
  "project_delivery.description":
    "One course or learning path per connected LMS. Results pulled from it are attributed to this project.",
  "project_delivery.empty_title": "Nothing linked yet",
  "project_delivery.empty_description":
    "Link a {vendor} course or learning path and Cognota can attribute its results to this project.",
  "project_delivery.empty_description_any_lms":
    "Link a course or learning path from your LMS and Cognota can attribute its results to this project.",
  "project_delivery.empty_description_read_only":
    "Nobody has linked a course or learning path to this project yet. Someone who can edit the project can add one.",
  "project_delivery.kind.course": "Course",
  "project_delivery.kind.path": "Learning path",
  "project_delivery.kind.course_plural": "Courses",
  "project_delivery.kind.path_plural": "Learning paths",
  "project_delivery.withdrawn_from_catalog": "No longer in the LMS catalog",
  "project_delivery.inert.title": "This link is not syncing",
  "project_delivery.inert.description":
    "Its connection is paused or disconnected, so no new results will arrive until a workspace admin resumes it in the integration library. The link and everything already synced are kept.",
  "project_delivery.link.action": "Link content",
  "project_delivery.link.success": "Linked.",
  "project_delivery.link.error": "Could not link this content. Nothing was changed.",
  "project_delivery.preview.action": "Preview",
  "project_delivery.synced_at": "Synced {when}",
  "project_delivery.unlink.action": "Unlink",
  "project_delivery.unlink.confirm_title": "Unlink this content?",
  "project_delivery.unlink.confirm_description":
    "This project stops being attributed to it. Nothing already synced is removed, and you can link it again later.",
  "project_delivery.unlink.success": "Unlinked.",
  "project_delivery.unlink.error": "Could not unlink. Nothing was changed.",
  "project_delivery.picker.title": "Link LMS content",
  "project_delivery.picker.description":
    "Courses and learning paths Cognota has pulled from your connected LMS. A project links one of them per connection.",
  "project_delivery.picker.description_has_link":
    "This project already links content on a connection, so entries from it are unavailable below. Unlink the existing one first to replace it.",
  "project_delivery.picker.search_label": "Search the catalog",
  "project_delivery.picker.search_placeholder": "Search by name or ID...",
  "project_delivery.picker.clear_search": "Clear search",
  "project_delivery.picker.kind_label": "Filter by type",
  "project_delivery.picker.kind_all": "All types",
  "project_delivery.picker.linked_elsewhere": "Another project also links this",
  "project_delivery.picker.blocked_by_existing_link":
    "This project already links content on this connection",
  "project_delivery.picker.more_results": "Showing the first results of {count}. Search to narrow.",
  "project_delivery.picker.empty_title": "Nothing matched",
  "project_delivery.picker.empty_description":
    "Try a different name or ID, or clear the type filter. The catalog is refreshed every few hours, so a course created in the LMS just now may not be listed yet.",
  "project_delivery.picker.not_connected_title": "No LMS is connected",
  "project_delivery.picker.not_connected_description":
    "A workspace admin connects {vendor} in the workspace integration library. Until then there is no catalog to pick from.",
  "project_delivery.picker.not_connected_description_any_lms":
    "A workspace admin connects your LMS in the workspace integration library. Until then there is no catalog to pick from.",
  // NG-1951: a row links only through its Link button, so a stray click on a row
  // cannot link it. Both names carry the entry's name so every row's controls are
  // told apart, and neither contains the Delivered-by row's "Preview {name}".
  "project_delivery.picker.link_action": "Link",
  "project_delivery.picker.link_label": "Link {name}",
  "project_delivery.picker.preview_action": "Preview",
  "project_delivery.picker.preview_label": "Preview in {vendor}: {name}",

  // Project results: what the linked LMS content achieved (NG-775)
  "project_settings.results.title": "Results",
  "project_settings.results.description":
    "What the linked course or learning path achieved, built from the learner activity the LMS has already sent us.",
  "lms_preview.eyebrow": "LMS content",
  "lms_preview.suggestion_eyebrow": "Recommended content",
  "lms_preview.position": "{position} of {total}",
  "lms_preview.previous": "Previous suggestion",
  "lms_preview.next": "Next suggestion",
  "lms_preview.why_suggested": "Why it was suggested",
  "lms_preview.shares": "Shares",
  "lms_preview.with_request_title": "with the request title",
  "lms_preview.fact.type": "Type",
  "lms_preview.fact.duration": "Duration",
  "lms_preview.fact.duration_minutes": "{count} min",
  "lms_preview.fact.languages": "Languages",
  "lms_preview.fact.author": "Author",
  "lms_preview.fact.modified": "Last modified",
  "lms_preview.fact.version": "Version",
  "lms_preview.fact.steps": "Steps",
  "lms_preview.content_type.native": "Native course",
  "lms_preview.content_type.scorm": "SCORM package",
  "lms_preview.step_not_cached": "Not in the synced catalog ({id})",
  "lms_preview.results.title": "Learner results",
  "lms_preview.results.completion": "Completion",
  "lms_preview.results.learners": "Learners",
  "lms_preview.results.avg_score": "Avg score",
  "lms_preview.results.avg_time": "Avg time",
  "lms_preview.results.provenance":
    "From the link on {project} · as of {time}. Course-level counts.",
  "lms_preview.catalog.title": "In the catalog",
  "lms_preview.catalog.status": "Status in {vendor}",
  "lms_preview.catalog.last_synced": "Last synced",
  "lms_preview.catalog.delivers": "Delivers",
  "lms_preview.link.eyebrow": "Delivered by",
  "lms_preview.link.title": "This link",
  "lms_preview.link.linked_by": "Linked by",
  "lms_preview.link.linked_on": "Linked on",
  "lms_preview.link.from_request": "From request",
  "lms_preview.view_results": "View results",
  "lms_preview.status.published": "Published",
  "lms_preview.status.draft": "Draft",
  "lms_preview.limit_course":
    "360Learning does not expose a course's activities over its API, so the module list is only in 360Learning itself.",
  "lms_preview.limit_path":
    "A path's steps are listed above. The activities inside each step stay in 360Learning, which does not expose them over its API.",
  // NG-2242: Cognota reads no course activities from Cornerstone either, and nobody
  // has checked what its API exposes, so this says what Cognota does, not the vendor.
  "lms_preview.cornerstone.limit_course":
    "Cognota does not read a course's activities from Cornerstone, so the module list is only in Cornerstone itself.",
  "lms_preview.cornerstone.limit_path":
    "A path's steps are listed above. The activities inside each step stay in Cornerstone, and Cognota does not read them.",
  "lms_preview.open_label": "Preview {name}",
  "lms_preview.open_in_vendor": "Open in {vendor}",

  // The shared LMS content row vocabulary (NG-1158): a type, a length, and whatever the
  // caller adds. Its own block rather than `project_delivery.kind.*`, because these are
  // read on the request's suggestion rows too and a request surface reaching into the
  // delivery namespace for the word "Course" is how one of them ends up renamed alone.
  "lms_content.type.course": "Course",
  "lms_content.type.path": "Learning path",
  "lms_content.steps_one": "1 step",
  "lms_content.steps_many": "{count} steps",
  "lms_content.duration_minutes": "{count} min",
  "lms_content.duration_hours": "{hours} h",
  "lms_content.duration_hours_minutes": "{hours} h {minutes} min",

  // Kept on the request: what the requests behind a project already decided (NG-1157)
  "project_kept_content.title": "Kept on the request",
  "project_kept_content.description_one_request":
    "Content the request owner kept on {request}. Pick the one that delivers this project; the rest stay here as reference.",
  "project_kept_content.description_many_requests":
    "Content kept on the {count} requests behind this project. Pick the one that delivers it; the rest stay here as reference.",
  "project_kept_content.kept_by": "kept by {name}",
  "project_kept_content.is_delivered_by": "Delivered by",
  "project_kept_content.conflict":
    "Unavailable while this project is delivered by other content. Unlink that under Delivered by first, then use this instead.",
  "project_kept_content.use.action": "Use as Delivered by",
  "project_kept_content.preview_label": "Preview kept content {name}",
  "project_kept_content.dismiss.label": "Dismiss {name} from this project",
  "project_kept_content.dismiss.success":
    "Dismissed from this project. The request still keeps it.",
  "project_kept_content.dismiss.error": "Could not dismiss that. It is back on the list.",
  "project_results.title": "Learner results",
  "project_results.description":
    "Counts come from the learner rows the LMS has already sent. Recalculating rebuilds them from that stored activity, it does not fetch anything new from the LMS.",
  "project_results.refresh.action": "Recalculate",
  "project_results.refresh.in_progress": "Recalculating…",
  "project_results.refresh.success": "Recalculated from the learner activity already stored.",
  "project_results.refresh.error": "Could not recalculate. The numbers on screen are unchanged.",
  "project_results.empty_title": "Nothing linked yet",
  "project_results.empty_description":
    "Link a course or learning path on the Delivery tab, and what it achieves shows up here.",
  "project_results.empty_description_read_only":
    "Nobody has linked a course or learning path to this project yet, so there are no results to show. Someone who can edit the project can add one.",
  "project_results.cost_per_completion.title": "Cost per completion",
  "project_results.cost_per_completion.description":
    "Labour cost from time logs on this project, divided by completions. Vendor content and platform costs are not included yet.",
  "project_results.cost_per_completion.suffix": "per completion · labour only",
  "project_results.cost_per_completion.labour_only": "Labour only",
  "project_results.cost_per_completion.labour_cost": "Labour cost to date",
  "project_results.cost_per_completion.completions": "Completions",
  "project_results.cost_per_completion.hours_logged": "Hours logged",
  "project_results.cost_per_completion.hours_value":
    "{hours, number} h across {people, plural, one {# person} other {# people}}",
  "project_results.cost_per_completion.footnote":
    "Visible to people with financial data access. Falls as completions grow; a vendor content line changes it materially once costing carries one.",
  "project_results.cost_per_completion.no_completions":
    "Nobody has completed this content yet, so there is nothing to divide the logged time by. This is not a cost of zero.",
  "project_results.cost_per_completion.load_error":
    "Could not load the logged time for this project, so cost per completion is unavailable. The results above are unaffected.",
  "project_results.load_error.title": "Could not load results",
  "project_results.load_error.description":
    "Nothing was lost. These numbers are stored in Cognota, so trying again is safe.",
  "project_results.load_error.retry": "Try again",
  "project_results.load_error.retry_failed": "Still could not load the results.",
  "project_results.no_data.title": "No learner data yet",
  "project_results.no_data.description_never":
    "Nothing has been pulled from the LMS for this content yet. Learner activity is checked every hour, so a newly linked course shows its data within about an hour.",
  "project_results.no_data.description_empty":
    "The LMS holds no learner activity for this content yet. It records a learner only once they start, so this stays empty until somebody does.",
  "project_results.stat.completion_rate": "Completion",
  "project_results.stat.completion_rate_sub": "{completed} of {total} learners with activity",
  "project_results.stat.learners": "Learners",
  "project_results.stat.learners_sub": "with activity · enrolment is not synced",
  "project_results.stat.started": "Started",
  "project_results.stat.started_sub": "{rate} of learners",
  "project_results.stat.completed": "Completed",
  "project_results.stat.completed_sub": "{count} in progress",
  "project_results.stat.avg_time_to_complete": "Avg time to complete",
  "project_results.stat.avg_time_to_complete_sub": "completed learners only",
  "project_results.stat.avg_time_all_attempts": "Avg time, all attempts",
  "project_results.stat.avg_time_all_attempts_sub": "every attempt, finished or not",
  "project_results.stat.cornerstone_no_time_spent": "Cornerstone doesn't report time spent",
  "project_results.stat.avg_score": "Avg score",
  "project_results.stat.avg_score_sub": "out of 100",
  "project_results.stat.attempts": "Attempts",
  "project_results.stat.attempts_sub":
    "{count, plural, one {# repeat attempt} other {# repeat attempts}}",
  "project_results.shared_link.note":
    "These counts are course level: {count} projects link this content, so they cover every learner on it, not this project's alone.",
  "project_results.course_level.cornerstone_note":
    "These counts are course level: they cover every learner Cornerstone shows on this content, not only this project's audience.",
  "project_results.timestamps.recalculated": "Recalculated {when}",
  "project_results.timestamps.just_now": "just now",
  "project_results.timestamps.never_computed": "Not recalculated yet",
  "project_results.timestamps.lms_data_as_of": "LMS data as of {date}",
  "project_results.timestamps.never_pulled": "no LMS data pulled yet",
  "project_results.breakdown.title": "Completion by job role",
  "project_results.breakdown.column.job_role": "Job role",
  "project_results.breakdown.column.learners": "Learners",
  "project_results.breakdown.column.completed": "Completed",
  "project_results.breakdown.column.completion": "Completion",
  "project_results.breakdown.no_job_role": "No job role",
  "project_results.breakdown.empty":
    "No job-role breakdown yet. A learner appears here once their LMS account is matched to a Cognota user.",
  "project_results.breakdown.empty_all_removed":
    "No job-role breakdown. Every learner with activity here was removed in the LMS, so none of them can be matched to a Cognota user. What they completed still counts in the totals above.",
  "project_results.coverage.note":
    "Based on {attributed} of {total} learners whose LMS account is matched to a Cognota user, {coverage} of everyone with activity.",
  "project_results.coverage.none":
    "None of the learners with activity are matched to a Cognota user, so there is nothing to break down by job role.",
  "project_results.coverage.unmatched":
    "{count, plural, one {# is} other {# are}} in the totals above but in no row here, because their LMS account is not matched to a Cognota user yet.",
  "project_results.coverage.removed_in_lms":
    "{count, plural, one {# was} other {# were}} removed in the LMS, so no match is coming for them. What they completed still counts in the totals above.",
  "project_settings.not_authorized.title": "Access denied",
  "project_settings.not_authorized.description":
    "You do not have permission to view this project settings section.",
  "project_settings.general.title": "General",
  "project_settings.general.description":
    "Update the project identity, lead, default assignee, and lifecycle controls.",
  "project_settings.general.name_label": "Project name",
  "project_settings.general.identifier_label": "Identifier",
  "project_settings.general.identifier_checking": "Checking availability…",
  "project_settings.general.identifier_taken": "This identifier is already in use.",
  "project_settings.general.identifier_hint":
    "Up to 10 letters or numbers. Used as a prefix on work item IDs.",
  "project_settings.general.description_label": "Description",
  "project_settings.general.privacy_label": "Privacy",
  "project_settings.general.privacy_public": "Public",
  "project_settings.general.privacy_private": "Private",
  "project_settings.general.timezone_label": "Timezone",
  "project_settings.general.project_type_label": "Project type",
  "project_settings.general.start_date_label": "Start date",
  "project_settings.general.target_completion_date_label": "Target completion date",
  "project_settings.general.target_launch_date_label": "Target launch date",
  "project_settings.general.actual_completion_date_label": "Actual completion date",
  "project_settings.general.client_teams_label": "Client teams",
  "project_settings.general.delivery_teams_label": "Delivery teams",
  "project_settings.general.programs_label": "Programs",
  "project_settings.general.goals_label": "Goals",
  "project_settings.general.save": "Save changes",
  "project_settings.general.saving": "Saving…",
  "project_settings.general.save_success": "Project updated.",
  "project_settings.general.save_error": "Failed to update project.",
  "project_settings.custom_fields.title": "Custom Fields",
  "project_settings.custom_fields.save": "Save",
  "project_settings.custom_fields.saving": "Saving…",
  "project_settings.custom_fields.archived": "archived",
  "project_settings.custom_fields.toast.saved": "Custom fields saved.",
  "project_settings.custom_fields.toast.error_save": "Couldn't save custom fields.",
  "project_settings.general.defaults_title": "Member defaults",
  "project_settings.general.defaults_description":
    "Choose who leads the project and who receives new work items by default.",
  "project_settings.general.defaults_save_success": "Defaults updated.",
  "project_settings.general.defaults_save_error": "Failed to update defaults.",
  "project_settings.general.project_lead_label": "Project lead",
  "project_settings.general.project_lead_placeholder": "Select a project lead",
  "project_settings.general.project_lead_none": "No lead",
  "project_settings.general.default_assignee_label": "Default assignee",
  "project_settings.general.default_assignee_placeholder": "Select a default assignee",
  "project_settings.general.default_assignee_none": "Unassigned",
  "project_settings.general.guest_view_all_label": "Guests can view all features",
  "project_settings.general.guest_view_all_description":
    "Allow guest members to view cycles, modules, pages, and views in this project.",
  "project_settings.general.member_select_placeholder": "Select a member",
  "project_settings.general.member_select_none": "None",
  "project_settings.general.archive": "Archive project",
  "project_settings.general.archive_title": "Archive project",
  "project_settings.general.archive_description":
    "Archiving {name} will hide it from navigation. You can restore it at any time.",
  "project_settings.general.archive_section_description":
    "Archiving removes this project from sidebars and dashboards. You can restore it later.",
  "project_settings.general.archive_success": "Project archived.",
  "project_settings.general.archive_error": "Failed to archive project.",
  "project_settings.general.restore": "Restore project",
  "project_settings.general.restore_title": "Restore project",
  "project_settings.general.restore_description":
    "Restore {name} so it appears in navigation again.",
  "project_settings.general.restore_section_description":
    "This project is archived. Restore it to bring it back into active navigation.",
  "project_settings.general.restore_success": "Project restored.",
  "project_settings.general.restore_error": "Failed to restore project.",
  "project_settings.general.duplicate": "Duplicate project",
  "project_settings.general.duplicate_section_description":
    "Create a copy of this project with the same fields and work items. The new project starts in default status.",
  "project_settings.general.duplicate_success": "Project duplicated.",
  "project_settings.general.duplicate_error": "Failed to duplicate project.",
  "project_settings.general.delete": "Delete project",
  "project_settings.general.delete_title": "Delete project",
  "project_settings.general.delete_description":
    "Deleting {name} permanently removes all work items, members, and data. This cannot be undone.",
  "project_settings.general.delete_section_description":
    "All data within this project will be permanently removed. This cannot be undone.",
  "project_settings.general.delete_name_prompt": "Type the project name ({name}) to continue.",
  "project_settings.general.delete_confirm_prompt": "Type {phrase} below to confirm.",
  "project_settings.general.delete_confirm_phrase": "delete my project",
  "project_settings.general.delete_success": "Project deleted.",
  "project_settings.general.delete_error": "Failed to delete project.",
  "project_settings.general.deleting": "Deleting…",
  "project_settings.members.title": "Members",
  "project_settings.members.description":
    "Manage project members, invite collaborators, and update member roles.",
  "project_settings.members.invite_action": "Invite members",
  "project_settings.members.section_title": "Active members",
  "project_settings.members.section_description":
    "Project members can access this project's work items, cycles, and modules.",
  "project_settings.members.no_members": "No members match these filters.",
  "project_settings.members.no_email": "No email",
  "project_settings.members.unknown_user": "Unknown user",
  "project_settings.members.role_label": "Role",
  "project_settings.members.role.admin": "Admin",
  "project_settings.members.role.member": "Member",
  "project_settings.members.role.guest": "Guest",
  "project_settings.members.role_updated": "Role updated.",
  "project_settings.members.role_update_error": "Failed to update role.",
  "project_settings.members.remove": "Remove",
  "project_settings.members.remove_confirm_title": "Remove member",
  "project_settings.members.remove_confirm_description":
    "Remove {name} from this project? They will lose access immediately.",
  "project_settings.members.remove_success": "Member removed.",
  "project_settings.members.remove_error": "Failed to remove member.",
  "project_settings.members.filters_search_placeholder": "Search by name or email",
  "project_settings.members.filters_role_all": "All roles",
  "project_settings.members.filters_role_admin": "Admins",
  "project_settings.members.filters_role_member": "Members",
  "project_settings.members.filters_role_guest": "Guests",
  "project_settings.members.invitations_title": "Pending invitations",
  "project_settings.members.invitations_description":
    "Invitations that have not yet been accepted.",
  "project_settings.members.invitations_empty": "No pending invitations.",
  "project_settings.members.invitation_revoke": "Revoke",
  "project_settings.members.invitation_revoking": "Revoking…",
  "project_settings.members.invitation_revoke_success": "Invitation revoked.",
  "project_settings.members.invitation_revoke_error": "Failed to revoke invitation.",
  "project_settings.members.invite_dialog_title": "Invite to project",
  "project_settings.members.invite_dialog_description":
    "Invite new collaborators by email, or add existing workspace members.",
  "project_settings.members.invite_tab_email": "By email",
  "project_settings.members.invite_tab_existing": "Existing members",
  "project_settings.members.invite_emails_label": "Email addresses",
  "project_settings.members.invite_emails_placeholder": "Separate emails with a comma or newline",
  "project_settings.members.invite_emails_required": "Enter at least one email address.",
  "project_settings.members.invite_email_invalid": "{email} is not a valid email.",
  "project_settings.members.invite_send": "Send invitations",
  "project_settings.members.invite_sending": "Sending…",
  "project_settings.members.invite_success": "Invitations sent.",
  "project_settings.members.invite_error": "Failed to send invitations.",
  "project_settings.members.add_existing_submit": "Add to project",
  "project_settings.members.add_existing_required": "Select at least one member.",
  "project_settings.members.add_existing_success": "Members added.",
  "project_settings.members.add_existing_error": "Failed to add members.",
  "project_settings.members.add_existing_empty":
    "Every workspace member is already on this project.",
  "project_settings.states.title": "States",
  "project_settings.states.description":
    "Configure the workflow states used by work items in this project.",
  "project_settings.states.group.backlog": "Backlog",
  "project_settings.states.group.unstarted": "Unstarted",
  "project_settings.states.group.started": "Started",
  "project_settings.states.group.completed": "Completed",
  "project_settings.states.group.cancelled": "Cancelled",
  "project_settings.states.add_state": "Add state",
  "project_settings.states.create": "Create state",
  "project_settings.states.save": "Save",
  "project_settings.states.edit": "Edit state",
  "project_settings.states.delete": "Delete state",
  "project_settings.states.deleting": "Deleting…",
  "project_settings.states.mark_default": "Mark as default",
  "project_settings.states.default_badge": "Default",
  "project_settings.states.empty_group": "No states in this group.",
  "project_settings.states.drag_handle": "Drag to reorder",
  "project_settings.states.cannot_delete_default":
    "The default state cannot be deleted. Mark another state as default first.",
  "project_settings.states.create_success": "State created.",
  "project_settings.states.create_error": "Failed to create state.",
  "project_settings.states.update_success": "State updated.",
  "project_settings.states.update_error": "Failed to update state.",
  "project_settings.states.delete_success": "State deleted.",
  "project_settings.states.delete_error": "Failed to delete state.",
  "project_settings.states.mark_default_success": "Default state updated.",
  "project_settings.states.mark_default_error": "Failed to mark state as default.",
  "project_settings.states.reorder_error": "Failed to reorder states.",
  "project_settings.states.delete_title": "Delete state",
  "project_settings.states.delete_description":
    'Delete the state "{name}"? Work items assigned to it may need to be reassigned.',
  "project_settings.states.form.name_label": "Name",
  "project_settings.states.form.name_placeholder": "e.g. In review",
  "project_settings.states.form.group_label": "Group",
  "project_settings.states.form.color_label": "Color",

  // Project settings — workflow
  "project_settings.workflow.title": "Workflow",
  "project_settings.workflow.description":
    "Define which status transitions are allowed and who can perform them. Statuses with no rules allow all transitions.",
  "project_settings.workflow.reset_to_defaults": "Reset to defaults",
  "project_settings.workflow.reset_confirm_title": "Reset workflow to defaults?",
  "project_settings.workflow.reset_confirm_description":
    "This will remove all custom transition rules and approvers. All status transitions will become unrestricted.",
  "project_settings.workflow.reset_success": "Workflow reset to defaults.",
  "project_settings.workflow.reset_error": "Failed to reset workflow.",
  "project_settings.workflow.fetch_error": "Failed to load workflow configuration.",
  "project_settings.workflow.create_allowed": "Allowed",
  "project_settings.workflow.create_blocked": "Blocked",
  "project_settings.workflow.create_label": "Create:",
  "project_settings.workflow.no_rules": "No rules configured — all transitions allowed",
  "project_settings.workflow.movers_label": "Moved by:",
  "project_settings.workflow.movers_everyone": "Everyone",
  "project_settings.workflow.mover_search_placeholder": "Search members…",
  "project_settings.workflow.require_approval_label": "Require approval",
  "project_settings.workflow.approvers_label": "Approvers:",
  "project_settings.workflow.approvers_none": "None (anyone)",
  "project_settings.workflow.approvers_count": "{count} users configured",
  "project_settings.workflow.required_approvals": "Requires: {count} of {total}",
  "project_settings.workflow.rejection_state": "Rejection:",
  "project_settings.workflow.configure": "Configure",
  "project_settings.workflow.configure_title": "Configure: {state}",
  "project_settings.workflow.configure_description":
    "Set which statuses this status can transition to and configure approval requirements.",
  "project_settings.workflow.allow_creation": "Allow project creation in this status",
  "project_settings.workflow.allow_creation_hint":
    "When enabled, new projects can be created directly in this status.",
  "project_settings.workflow.allowed_transitions": "Allowed Transitions",
  "project_settings.workflow.transition_added": "Transition added.",
  "project_settings.workflow.transition_removed": "Transition removed.",
  "project_settings.workflow.transition_update_error": "Failed to update transition.",
  "project_settings.workflow.update_success": "Workflow updated.",
  "project_settings.workflow.update_error": "Failed to update workflow.",
  "project_settings.workflow.required_approvals_label": "Required approvals:",
  "project_settings.workflow.approvals_all": "All",
  "project_settings.workflow.rejection_state_label": "On rejection:",
  "project_settings.workflow.rejection_none": "None",
  "project_settings.workflow.approver_search_placeholder": "Search members…",
  "project_settings.workflow.approver_no_members": "No members found",
  "project_settings.workflow.approver_unknown": "Unknown user",

  // Project workflow — approval request dialog
  "project.workflow.approval_required_title": "Approval Required",
  "project.workflow.approval_required_description":
    'Moving this project from "{from}" to "{to}" requires approval.',
  "project.workflow.approval_request_to": "This will send an approval request to:",
  "project.workflow.required_approvals_count": "Required approvals: {count} of {total}",
  "project.workflow.required_approvals_all": "Required approvals: All of {total}",
  "project.workflow.request_approval_button": "Request Approval",
  "project.workflow.approval_request_sent": "Approval request sent",
  "project.workflow.approval_request_error": "Couldn't send the approval request",
  "project.workflow.pending_approval": "Pending Approval",
  "project.workflow.review_required": "Review Required",
  "project.workflow.pending_approval_title": "Pending Approval",
  "project.workflow.approval_requested_title": "Approval Requested",
  "project.workflow.current_approvals": "Current approvals: {current} of {required} required",
  "project.workflow.approver_status_pending": "Pending",
  "project.workflow.approver_status_approved": "Approved",
  "project.workflow.approver_status_rejected": "Rejected",
  "project.workflow.already_voted": "You have already {action} this transition.",
  "project.workflow.comment_optional": "Comment (optional)",
  "project.workflow.approve_button": "Approve",
  "project.workflow.reject_button": "Reject",
  "project.workflow.approval_success": "Approval submitted",
  "project.workflow.rejection_success": "Rejection submitted",
  "project.workflow.approval_error": "Failed to submit approval",
  "project.workflow.rejection_error": "Failed to submit rejection",
  "project.workflow.dismiss": "Dismiss",
  "project.workflow.you": "you",

  // Project workflow — activity log
  "project_settings.workflow.activity_title": "Workflow Activity",
  "project_settings.workflow.activity_empty": "No activity yet",
  "project_settings.workflow.activity_created_transition": '{actor} added transition to "{state}"',
  "project_settings.workflow.activity_removed_transition":
    '{actor} removed transition to "{state}"',
  "project_settings.workflow.activity_updated_creation": "{actor} {action} project creation",
  "project_settings.workflow.activity_updated_approvers": "{actor} updated approvers",
  "project_settings.workflow.activity_reset": "{actor} reset workflow to defaults",
  "project_settings.workflow.activity_enabled": "enabled",
  "project_settings.workflow.activity_disabled": "disabled",
  "project_settings.workflow.activity_unknown_actor": "Unknown user",
  "project_settings.workflow.activity_updated_transition": "{actor} updated transition",
  "project_settings.workflow.activity_generic": "{actor} {verb} {field}",
  "project_settings.workflow.unknown_state": "Unknown status",

  // Teams
  "teams.page_title": "Teams",
  "teams.my_teams_title": "My Teams",
  "teams.manage_teams": "Manage teams",
  "teams.count_noun": "teams",
  "teams.count_noun_one": "team",
  "teams.count_noun_found": "teams found",
  "teams.count_noun_found_one": "team found",
  "teams.create": "Create team",
  "teams.search_placeholder": "Search teams",
  "teams.expand_all": "Expand all",
  "teams.collapse_all": "Collapse all",
  "teams.tabs.rates": "Rates",
  "teams.tabs.members": "Members",
  "teams.members.col_member": "Member",
  "teams.members.col_email": "Email",
  "teams.members.col_location": "Location",
  "teams.members.col_status": "Account status",
  "teams.members.col_actions": "Actions",
  "teams.members.status_active": "Active",
  "teams.members.status_inactive": "Inactive",
  "teams.members.edit_profile": "Edit user profile",
  "teams.members.add_time_off": "Add time off",
  "teams.members.remove_from_team": "Remove from team",
  "teams.members.actions_menu_aria": "Actions for {name}",
  "teams.detail.goal_alignment": "{percent}% of projects aligned to goals ({aligned} of {total})",
  "teams.detail.edit_team": "Edit team",
  "teams.detail.delete_team": "Delete team",
  "teams.rate.title": "Team hourly rate",
  "teams.rate.total_label": "Combined rate",
  "teams.rate.per_hour": "{amount}/hr",
  "teams.rate.member_col": "Member",
  "teams.rate.rate_col": "Rate",
  "teams.rate.no_members": "No members with a rate yet.",
  "teams.rate.untitled": "Unnamed",
  "teams.rate.load_error": "Unavailable",
  "teams.rate.hint": "The sum of team members' current hourly rates. Visible to admins only.",
  "teams.column_name": "Name",
  "teams.column_members": "Members",
  "teams.column_lead": "Lead",
  "teams.column_projects": "Projects",
  "teams.column_actions": "Actions",
  "teams.rename_hint": "Rename team",
  "teams.lead_edit_aria": "Edit team leads",
  "teams.lead_more": "+{count}",
  "teams.empty_title": "No teams yet",
  "teams.empty_description": "Organize your team into virtual groups.",
  "teams.empty_cta": "Create your first team",
  "teams.empty_filtered": "No teams match your search.",
  "teams.empty_mine_title": "You're not part of any teams yet",
  "teams.empty_mine_cta": "Create a team",
  "teams.empty_mine_description":
    "Teams you're a member or a lead of show up here, along with their sub-teams.",
  "teams.dialog.create_title": "Create team",
  "teams.dialog.create_description": "Group members and projects under a shared space.",
  "teams.fields.name": "Name",
  "teams.fields.description": "Description",
  "teams.fields.logo": "Logo",
  "teams.fields.lead": "Lead",
  "teams.fields.lead_search": "Search members",
  "teams.fields.lead_empty": "No members found",
  "teams.fields.members": "Members",
  "teams.fields.members_placeholder": "Add members",
  "teams.fields.members_count": "{count} selected",
  "teams.fields.members_search": "Search members",
  "teams.fields.members_empty": "No members found",
  "teams.fields.members_remove": "Remove {name}",
  "teams.fields.projects_search": "Search projects",
  "teams.fields.projects_remove": "Remove {name}",
  "teams.actions.create": "Create",
  "teams.actions.cancel": "Cancel",
  "teams.actions.delete": "Delete",
  "teams.delete.title": "Delete team",
  "teams.delete.description": "Are you sure you want to delete {name}? This cannot be undone.",
  "teams.delete.home.title": "Delete {name}?",
  "teams.delete.home.root_title": "Delete {name}",
  "teams.delete.home.confirm": "Delete team",
  "teams.delete.home.loading": "Checking what {name} is the Home team for…",
  "teams.delete.home.preflight_failed":
    "Couldn't check what {name} is the Home team for, so it can't be deleted yet. Close this and try again.",
  "teams.delete.home.plain_body":
    "{name} isn't the Home team for any project, program or event. Nothing needs to move.",
  "teams.delete.home.plain_children": "Its sub-teams ({children}) become top-level teams.",
  "teams.delete.home.sub_body_one":
    "{name} is the Home team for 1 record. When you delete it, it moves to its parent team, {parent}, automatically.",
  "teams.delete.home.sub_body_many":
    "{name} is the Home team for {count} records. When you delete it, they move to its parent team, {parent}, automatically.",
  "teams.delete.home.sub_children_one":
    "Its sub-team {children} becomes a top-level team. It keeps its own projects, programs and events.",
  "teams.delete.home.sub_children_many":
    "Its sub-teams ({children}) become top-level teams. They keep their own projects, programs and events.",
  "teams.delete.home.root_body_one":
    "{name} is the Home team for 1 record. It's a top-level team, so there's no parent to move it to.",
  "teams.delete.home.root_body_many":
    "{name} is the Home team for {count} records. It's a top-level team, so there's no parent to move them to.",
  "teams.delete.home.root_hint": "Reassign them to another team before deleting.",
  "teams.delete.home.meta_project": "{identifier} · Project",
  "teams.delete.home.meta_project_on_roster":
    "{identifier} · Project · parent already on Delivery teams",
  "teams.delete.home.meta_program": "Program",
  "teams.delete.home.meta_event": "{identifier} · Event",
  "teams.delete.home.meta_event_no_identifier": "Event",
  "teams.delete.home.meta_program_on_roster": "Program · parent already on Team Access",
  "teams.delete.home.added_delivery": "Added to Delivery teams",
  "teams.delete.home.added_access": "Added to Team Access",
  "teams.delete.home.moves_to": "Moves to {parent}",
  "teams.delete.home.more": "And {count} more",
  "teams.reassign_home.title": "Delete {name}",
  "teams.reassign_home.intro":
    "{name} is the Home team for {records}. It's a top-level team, so there's no parent to move them to. Give every record a new Home team, then delete. Your choices are saved as you go, so you can leave and come back.",
  "teams.reassign_home.intro_records_one": "{count} record",
  "teams.reassign_home.intro_records_many": "{count} records",
  "teams.reassign_home.lock_note":
    "Only a workspace admin can delete a top-level team that is a Home team. Ask an admin to delete it and reassign its records.",
  "teams.reassign_home.load_failed":
    "Couldn't load the records {name} is the Home team for. Reload the page to try again.",
  "teams.reassign_home.job_title_one": "Reassigning {total} record…",
  "teams.reassign_home.job_title_many": "Reassigning {total} records…",
  "teams.reassign_home.job_done": "{done} of {total} done",
  "teams.reassign_home.job_body":
    "This runs in the background. You can leave this page. We'll notify you when {name} is deleted. If any record fails, the team isn't deleted and the failed records stay listed here.",
  "teams.reassign_home.job_back": "Back to Teams",
  "teams.reassign_home.bulk_label": "Reassign all to",
  "teams.reassign_home.bulk_aria": "Replacement team for all records",
  "teams.reassign_home.bulk_placeholder": "Choose a team…",
  "teams.reassign_home.apply_all": "Apply to all {total}",
  "teams.reassign_home.bulk_hint": "then change any exceptions below",
  "teams.reassign_home.download": "Export list (CSV)",
  "teams.reassign_home.download_failed": "Couldn't download the list. Try again.",
  "teams.reassign_home.tabs_aria": "Record type",
  "teams.reassign_home.tab_all": "All",
  "teams.reassign_home.tab_projects": "Projects",
  "teams.reassign_home.tab_programs": "Programs",
  "teams.reassign_home.tab_events": "Events",
  "teams.reassign_home.search": "Search records",
  "teams.reassign_home.needs_team": "Needs a team ({remaining})",
  "teams.reassign_home.selected": "{count} selected",
  "teams.reassign_home.selected_aria": "Team for selected records",
  "teams.reassign_home.selected_placeholder": "Assign selected to…",
  "teams.reassign_home.assign": "Assign",
  "teams.reassign_home.clear": "Clear",
  "teams.reassign_home.select_page": "Select all on this page",
  "teams.reassign_home.select_row": "Select {name}",
  "teams.reassign_home.col_record": "Record",
  "teams.reassign_home.col_type": "Type",
  "teams.reassign_home.col_roster": "Current roster",
  "teams.reassign_home.col_new_home": "New Home team",
  "teams.reassign_home.type_project": "Project",
  "teams.reassign_home.type_program": "Program",
  "teams.reassign_home.type_event": "Event",
  "teams.reassign_home.empty": "No records match.",
  "teams.reassign_home.row_aria": "New Home team for {name}",
  "teams.reassign_home.row_placeholder": "Choose new Home team…",
  "teams.reassign_home.page_of": "Page {page} of {count}",
  "teams.reassign_home.footnote":
    "The new Home team is added to each project's Delivery teams or program's Team Access if it isn't there already. Events linked to a project follow that project's Home team, so they aren't listed.",
  "teams.reassign_home.progress_pending":
    "{done} of {total} reassigned — delete unlocks when every record has a new Home team",
  "teams.reassign_home.progress_ready": "{done} of {total} reassigned — ready to delete",
  "teams.reassign_home.progress_admins": "Workspace admins only",
  "teams.reassign_home.cancel": "Cancel",
  "teams.reassign_home.confirm": "Reassign & delete",
  "teams.reassign_home.deleted_toast_one": "{name} deleted · {total} record has a new Home team",
  "teams.reassign_home.deleted_toast_many": "{name} deleted · {total} records have a new Home team",
  "teams.reassign_home.partial_failed_one":
    "{failed} record couldn't be reassigned, so {name} wasn't deleted. It's still listed below.",
  "teams.reassign_home.partial_failed_many":
    "{failed} records couldn't be reassigned, so {name} wasn't deleted. They're still listed below.",
  "teams.reassign_home.save_failed": "Couldn't save the new Home team. Try again.",
  "teams.reassign_home.reassign_failed": "Couldn't reassign the records. Try again.",
  "teams.reassign_home.picker_search": "Search teams",
  "teams.reassign_home.picker_empty": "No teams match.",
  "teams.reassign_home.clear_search": "Clear search",
  "teams.reassign_home.job_not_deleted":
    "{name} wasn't deleted: some records couldn't be reassigned. They're still listed below.",
  "teams.reassign_home.csv_filename": "{name} home records.csv",
  "teams.reassign_home.row_done": "Has a new Home team",
  "teams.reassign_home.row_todo": "Needs a new Home team",
  "teams.list_item.lead_label": "Lead: {name}",
  "teams.list_item.member_count": "{count} members",
  "teams.list_item.project_count": "{count} projects",
  "teams.list_item.menu_view": "View",
  "teams.list_item.menu_edit": "Edit",
  "teams.list_item.menu_delete": "Delete",
  "teams.list_item.row_actions_aria": "Actions for {name}",
  "teams.toast.created": "Team created",
  "teams.toast.create_failed": "Failed to create team",
  "teams.toast.deleted": "Team deleted",
  "teams.toast.delete_failed": "Failed to delete team",
  "teams.toast.updated": "Team updated",
  "teams.toast.update_failed": "Failed to update team",
  "teams.toast.member_added": "Member added",
  "teams.toast.member_removed": "Member removed",
  "teams.toast.member_failed": "Failed to update members",
  "teams.toast.project_added": "Project added",
  "teams.toast.project_removed": "Project removed",
  "teams.toast.project_failed": "Failed to update projects",
  "teams.detail.capacity": "Capacity",
  "teams.detail.members_heading": "Members",
  "teams.detail.members_empty": "No members yet",
  "teams.detail.members_add": "Add member",
  "teams.detail.projects_heading": "Projects",
  "teams.detail.projects_empty": "No projects linked",
  "teams.detail.projects_add": "Add project",
  "teams.detail.description_placeholder": "Add a description",
  "teams.detail.lead_placeholder": "Choose a lead",
  "teams.detail.name_edit_hint": "Press Enter to save, Esc to cancel",
  "teams.detail.not_found": "Team not found",
  "teams.detail.load_failed": "Could not load team. Please try again.",
  "teams.detail.last_member_cannot_remove": "A team must have at least one member.",
  "teams.detail.lead_cannot_remove": "Cannot remove the last team lead.",
  "teams.detail.add_members_title": "Add members",
  "teams.detail.add_projects_title": "Add projects",
  "teams.detail.add_projects_empty": "All projects are linked",
  "teams.detail.no_subteams": "No sub-teams yet",
  "teams.tabs.subteams": "Sub-Teams",
  "teams.tabs.team_capacity": "Team Capacity",
  "teams.tabs.aria_label": "Team sections",
  "teams.actions.create_subteam": "Create Sub-Team",
  "teams.dialog.create_subteam_title": "Create sub-team",
  "teams.fields.parent_team": "Parent team",
  "teams.fields.parent_placeholder": "Select a parent team",
  "teams.fields.parent_search": "Search teams",
  "teams.fields.parent_empty": "No teams found",
  "teams.fields.no_parent": "None (root team)",
  // The settings picker's own root row (NG-1147). Deliberately NOT `no_parent`
  // above: that one is the create dialog's, which is ungated, and rewording it
  // would put an ungated copy change inside a `team-hierarchy-editing` commit.
  // Fold the two together when the flag goes away, not before.
  "teams.fields.parent_root_option": "No parent (root team)",
  "teams.fields.parent_admin_only": "Only admins can change a team's parent.",
  // Fragments, not sentences: these render ON the locked row beside the team's
  // name, so they share its width — see `CommandSelectOption.disabledNote`.
  "teams.fields.parent_note_self": "this team",
  "teams.fields.parent_note_descendant": "sub-team",
  "teams.fields.parent_confirm": "Confirm change",
  "teams.fields.parent_change_title": "Change {team}'s parent to {parent}?",
  "teams.fields.parent_change_description":
    "{team} will now inherit any Workflows, Templates, SLAs, and other resources assigned to {parent} (if any), and {parent} will be able to see {team}'s data in reporting. Team membership and reporting roll-ups update immediately.",
  "teams.fields.parent_remove_title": "Remove {team}'s parent?",
  "teams.fields.parent_remove_description":
    "{team} will become a root team. It will no longer inherit resources from {parent}, and {parent} will no longer see {team}'s data in reporting.",
  "teams.fields.parent_remove_description_unnamed":
    "{team} will become a root team. It will no longer inherit resources from its current parent, and that team will no longer see {team}'s data in reporting.",
  // Appended to either dialog, and only when the team actually has sub-teams —
  // a team with none needs no reassurance about them. States what the write
  // already does (the subtree travels with the team), which is the behaviour
  // product confirmed on NG-1148 for Limited Availability rather than the
  // stay-behind rule that ticket originally asked for.
  "teams.fields.parent_children_note": "{team}'s sub-teams move with it.",

  // Team settings
  "team_settings.tabs.general": "General",
  "team_settings.tabs.members": "Members",
  "team_settings.tabs.templates": "Project Templates",
  "team_settings.tabs.intake_workflows": "Intake Workflows",
  "team_settings.tabs.calendars": "Calendars",
  "team_settings.tabs.sla": "SLAs",
  "team_settings.templates.list_description":
    "Project templates this team has access to. Templates scoped to a parent team, or to the whole workspace, are view only.",
  "team_settings.templates.empty_state": "No project templates are available to this team yet.",
  "team_settings.intake_workflows.list_description":
    "Intake workflows this team manages. Workflows managed by a parent team are view only.",
  "team_settings.intake_workflows.empty_state": "This team manages no intake workflows yet.",
  "team_settings.sla.title": "SLAs",
  "team_settings.sla.description": "The service level agreements that govern this team's requests.",
  "team_settings.sla.group_manages_title": "Managed by this team",
  "team_settings.sla.group_manages_description":
    "Scoped to workflows this team owns. Editable unless the SLA also covers a workflow belonging to a team you don't manage.",
  "team_settings.sla.group_inherited_title": "Inherited from another team",
  "team_settings.sla.group_inherited_description":
    "Managed elsewhere in the team hierarchy, and applied to this team's requests. View only.",
  "team_settings.sla.group_residual_title": "Workspace catch-all",
  "team_settings.sla.group_residual_description":
    "Applies to this team's requests from workflows that have no SLA of their own. Managed in workspace settings.",
  "team_settings.sla.row_editable": "Edit",
  "team_settings.sla.row_view_only": "View only",
  "team_settings.sla.empty_title": "No SLAs apply to this team yet",
  "team_settings.sla.empty_description":
    "Once an SLA covers one of this team's workflows, it appears here.",
  "team_settings.sla.load_error_title": "Couldn't load this team's SLAs",
  "team_settings.sla.load_error_description": "Something went wrong fetching the list.",
  "team_settings.sla.view_only_banner_unnamed_team": "another team",
  "team_settings.sla.view_only_banner_other_team_title": "Managed by a different team",
  "team_settings.sla.view_only_banner_other_team_description":
    "This SLA also covers workflows owned by {teams}, so it can only be changed by someone who manages all of them.",
  "team_settings.sla.view_only_banner_inherited_title": "Managed by {teams}",
  "team_settings.sla.view_only_banner_inherited_description":
    "Another team owns this SLA. It applies to your team's requests, but only that team can change it.",
  "team_settings.sla.view_only_banner_residual_title": "This is the workspace catch-all SLA",
  "team_settings.sla.view_only_banner_residual_description":
    "It covers requests from workflows that have no SLA of their own, and is managed in workspace settings.",
  "team_settings.general.title": "General",
  "team_settings.general.description": "Manage your team's basic information.",
  "team_settings.general.logo_label": "Logo",
  "team_settings.general.name_label": "Team name",
  "team_settings.general.description_label": "Description",
  "team_settings.general.name_required": "Team name is required.",
  "team_settings.general.save": "Save changes",
  "team_settings.general.privacy_label": "Private team",
  "team_settings.general.privacy_description":
    "Only this team, its related teams, and admins can see its programs and projects.",
  "team_settings.members.title": "Members",
  "team_settings.members.description": "Manage who belongs to this team and their roles.",
  "team_settings.members.add_action": "Add members",
  "team_settings.members.add_dialog_title": "Add members",
  "team_settings.members.add_dialog_description": "Select workspace members to add to this team.",
  "team_settings.members.child_access_lead":
    "These members will also get access to {count, plural, one {# child team} other {# child teams}}:",
  "team_settings.members.add_placeholder": "Select users...",
  "team_settings.members.search": "Search members...",
  "team_settings.members.no_results": "No users found.",
  "team_settings.members.current_heading": "Current members",
  "team_settings.members.current_description": "Change roles or remove members from this team.",
  "team_settings.members.no_members": "No members yet.",
  "team_settings.members.role_label": "Role",
  "team_settings.members.role.lead": "Lead",
  "team_settings.members.role.member": "Member",
  "team_settings.members.remove": "Remove",
  "team_settings.members.remove_confirm_title": "Remove member",
  "team_settings.members.remove_confirm_description":
    "Are you sure you want to remove {name} from this team?",

  // Team permission graph (experimental spike)
  "team_settings.permission_graph.open": "Team access",
  "team_settings.permission_graph.title": "Team access",
  "team_settings.permission_graph.description":
    "Effective access per team, including inherited access. Workspace roles: {roles}",
  "team_settings.permission_graph.select_placeholder": "Select a member…",
  "team_settings.permission_graph.empty":
    "Pick a member to see their effective access across the team hierarchy.",
  "team_settings.permission_graph.error": "Couldn't load the permission graph.",
  "team_settings.permission_graph.inherited": "Inherited",
  "team_settings.permission_graph.axis.team": "Team",
  "team_settings.permission_graph.axis.resources": "Resources",
  "team_settings.permission_graph.level.edit": "Edit",
  "team_settings.permission_graph.level.read": "Read",
  "team_settings.permission_graph.level.none": "No access",
  "team_settings.permission_graph.resource_level.lead": "Lead",
  "team_settings.permission_graph.resource_level.editor": "Editor",
  "team_settings.permission_graph.resource_level.viewer": "Viewer",
  "team_settings.permission_graph.resource_level.none": "No access",

  // Connected apps: the rotate-secret and revoke modals and the one-time secret banner (NG-2581)
  "oauth_apps.rotate.title": "Rotate client secret",
  "oauth_apps.rotate.description_before": "A new secret will be generated for",
  "oauth_apps.rotate.description_after":
    ". The current secret stops working immediately — any deployed integration using the old secret will fail until updated.",
  "oauth_apps.rotate.rotating": "Rotating…",
  "oauth_apps.rotate.confirm": "Rotate secret",
  "oauth_apps.rotate.error": "Failed to rotate the secret.",
  "oauth_apps.secret.title": "Client secret for {name}",
  "oauth_apps.secret.note":
    "This is shown once. Save it now — you can't see it again. If you lose it, rotate the secret to issue a new one.",
  "oauth_apps.secret.copied": "Copied",
  "oauth_apps.secret.copy": "Copy",
  "oauth_apps.secret.copy_first": "Copy the secret first",
  "oauth_apps.secret.dismiss": "I've copied it",
  "oauth_apps.revoke.title": "Revoke access?",
  "oauth_apps.revoke.description_after":
    "will no longer be able to act on your behalf. To use this app again, you'll have to grant access from scratch.",
  "oauth_apps.revoke.revoking": "Revoking…",
  "oauth_apps.revoke.confirm": "Revoke access",

  // Logo picker
  "logo_picker.emoji_tab": "Emoji",
  "logo_picker.icon_tab": "Icon",
  "logo_picker.search_emoji": "Search emoji",
  "logo_picker.search_icon": "Search icons",
  "logo_picker.remove": "Remove logo",

  // Shared activity, picker and label components (NG-2585)
  "activity.filter.all": "All activity",
  "activity.filter.comments": "Comments",
  "activity.filter.changes": "Changes",
  "activity.filter.system": "System",
  "activity.filter.aria": "Filter activity",
  "activity.time.just_now": "Just now",
  "activity.time.minutes_ago": "{count} minutes ago",
  "activity.time.hours_ago": "{count, plural, one {# hour ago} other {# hours ago}}",
  "activity.time.yesterday": "Yesterday",
  "activity.time.yesterday_at": "Yesterday · {time}",
  "activity.sheet.title": "Activity",
  "activity.sheet.close_aria": "Close activity",
  "activity.timeline.hide_earlier": "Hide earlier events",
  "activity.timeline.show_earlier":
    "{count, plural, one {Show {countText} earlier event} other {Show {countText} earlier events}}",
  "custom_field_input.archived_option": "{value} (archived option)",
  "logo_picker.skin_tone": "Skin tone",
  "logo_picker.loading": "Loading…",
  "logo_picker.no_emoji": "No emoji found",
  "project.state_label.aria": "{name} status",

  // Schedule (Gantt)
  "schedule.title": "Schedule",
  "schedule.subtitle_count": "{count} work items",
  "schedule.time_off_conflict": "Conflicts with time off",
  "schedule.today": "Today",
  "schedule.fullscreen_enter": "Fullscreen",
  "schedule.fullscreen_exit": "Exit fullscreen",
  "schedule.peek_title": "Work item",
  "schedule.peek_description": "View and edit details for this work item.",

  // Quick view panel — lightweight read-only summary opened from a name click.
  "quick_view.title": "Quick view",
  "quick_view.description": "A summary of this work item.",
  "quick_view.open_full_view": "Open full view",
  "quick_view.tabs_label": "Quick view sections",
  "quick_view.tab_details": "Details",
  "quick_view.tab_comments": "Comments",
  "quick_view.group.overview": "Overview",
  "quick_view.group.sub_tasks": "Sub-tasks",
  "quick_view.group.dependencies": "Dependencies",
  "quick_view.group.time_logs": "Time logs",
  "quick_view.group.attachments": "Attachments",
  "quick_view.stage": "Stage",
  "quick_view.skills": "Qualifications",
  "quick_view.skills_search": "Search qualifications…",
  "quick_view.skills_empty": "No qualifications found",
  "quick_view.skills_saving": "Saving…",
  "quick_view.actual_hours": "Actual hours",
  "quick_view.not_completed": "Not completed",
  "quick_view.all_day_yes": "Yes",
  "quick_view.all_day_no_timezone": "No timezone. Same dates for everyone.",
  "quick_view.unassigned": "Unassigned",
  "quick_view.col.name": "Name",
  "quick_view.col.assignee": "Assignee",
  "quick_view.col.status": "Status",
  "quick_view.col.relation": "Relation",
  "quick_view.col.id": "ID",
  "quick_view.col.title": "Title",
  "quick_view.col.file": "File",
  "quick_view.col.size": "Size",
  "quick_view.add_sub_task": "Add sub-task",
  "quick_view.add_dependency": "Add dependency",
  "quick_view.add_time_log": "Log time",
  "quick_view.add_attachment": "Add attachment",
  "schedule.block_reschedule_failed": "Failed to reschedule. Please try again.",
  "schedule.empty_title": "Nothing scheduled",
  "schedule.empty_description": "Set start and target dates on work items to see them here.",
  "schedule.quick_add_tooltip": "Double-click an empty row to add a work item",
  "schedule.missing_dates_hint": "Not scheduled",
  "schedule.column.name": "Name",
  "schedule.column.duration": "Duration",
  "schedule.column.dates": "Dates",
  "schedule.cycle_rejected": "That would create a dependency cycle.",
  "schedule.delete_dependency_title": "Delete dependency",
  "schedule.delete_dependency_description":
    "Remove this relationship between the two work items? This can be re-created later.",
  "schedule.delete_dependency_confirm": "Delete dependency",
  "schedule.delete_dependency_failed": "Failed to delete dependency. Please try again.",
  "schedule.create_dependency_failed": "Failed to create dependency. Please try again.",
  "schedule.clear_filters": "Clear filters",
  "schedule.empty_filtered_title": "No matches",
  "schedule.empty_filtered_description": "No work items match the current filters.",
  "schedule.loading": "Loading schedule",
  "schedule.aria_label": "{name}, scheduled {start} to {end}",
  "schedule.aria_label_unscheduled": "{name}, not scheduled",

  // Intake
  "intake.nav.forms": "Forms",
  "intake.nav.intakeWorkflows": "Intake Workflows",
  "intake.nav.submissions": "Requests",
  "intake.nav.requests": "Requests",
  "intake.nav.reviews": "My reviews",
  "intake.noAccess": "You don't have access to the intake surface.",
  "intake.project.disabled.title": "Intake is disabled",
  "intake.project.disabled.description":
    "Enable intake for this project in project settings before requesters can use it.",
  "intake.project.disabled.enable": "Enable intake",
  "intake.project.enabled.title": "Intake is managed at the workspace level",
  "intake.project.enabled.description":
    "Create, edit, and review request forms from the workspace intake area.",
  "intake.project.enabled.goToWorkspace": "Go to workspace intake",
  "intake.builder.title": "V3 intake demo",
  "intake.builder.fileMultiple": "Allow multiple files",
  "intake.builder.fileMaxSizeMb": "Max size per file (MB)",
  "intake.builder.fileMaxSizeMbHint": "Leave blank to use the workspace cap.",
  "intake.builder.fileAcceptedMimeTypes": "Accepted file types",
  "intake.builder.fileAcceptedMimeTypesHint":
    "Comma-separated MIME types. Use globs like image/* or leave blank to accept anything.",
  "intake.builder.badge": "Vendor-agnostic backend",
  "intake.builder.description":
    "A demo Content Request form rendered from Cognota-owned schema, with EN/FR labels, conditional pages, and internal-field filtering.",
  "intake.builder.localeLabel": "Demo form language",
  "intake.builder.english": "English",
  "intake.builder.french": "Français",
  "intake.builder.submit": "Submit request",
  "intake.builder.submitting": "Submitting…",
  "intake.builder.submitSuccess": "Demo request submitted.",
  "intake.builder.submitError": "Failed to submit demo request.",
  "intake.builder.loadError": "Failed to load the form.",
  "intake.builder.errorTitle": "Demo unavailable",
  "intake.builder.errorRetry": "Try again",
  "intake.builder.debugTitle": "Schema boundary",
  "intake.builder.debugClose": "Close schema boundary",
  "intake.builder.debugDescription":
    "The backend stores Cognota JSON. SurveyJS JSON is generated at the frontend boundary.",
  "intake.builder.agnosticJson": "Cognota agnostic definition",
  "intake.builder.surveyJson": "Generated SurveyJS JSON",
  "intake.builder.tabs.requester": "Requester",
  "intake.builder.tabs.author": "Author",
  "intake.builder.formDetails": "Form details",
  "intake.builder.workflowDetails": "Workflow details",
  "intake.builder.formBuilder": "Form builder",
  "intake.builder.formWorkflow": "Workflow",
  "intake.builder.active": "Active",
  "intake.builder.inactive": "Deactivated",
  "intake.builder.activeToggle": "Active toggle",
  "intake.builder.formName": "Workflow name",
  "intake.builder.formDescription": "Description",
  // The workflow's AUDIENCE axis (#3144) — renamed from the bare "Teams" now
  // that ownership is its own field (Managing Team).
  "intake.builder.teams": "Submitting Teams",
  "intake.builder.teamsAllTeams": "All teams",
  // The workflow's OWNERSHIP axis (#3144) — single-select, required at publish.
  "intake.builder.managingTeam": "Managing Team",
  "intake.builder.managingTeamPlaceholder": "Select a team",
  "intake.builder.managingTeamSearchPlaceholder": "Search teams",
  "intake.builder.managingTeamHint":
    "Selected team and every team beneath them in the hierarchy will be able to see requests submitted using this workflow.",
  "intake.builder.managingTeamLockedHint": "Set to the team this workflow is being created from.",
  "intake.builder.viewOnlyBanner.title": "This workflow is view only",
  "intake.builder.viewOnlyBanner.description":
    "It's managed by another team, so only that team and the teams above it can change it.",
  // A view-only reader opened a workflow whose graph was never built (NG-409): an
  // author's GET mints the first Page, a reader's GET answers an empty graph.
  "intake.builder.emptyReadOnly.title": "This workflow hasn't been built yet",
  "intake.builder.emptyReadOnly.description":
    "It's managed by another team and has no steps to view. Steps will show here once that team adds them.",
  "intake.builder.canEditError":
    "We couldn't check whether you can edit this workflow, so it's open read-only. Reload to try again.",
  // The builder's draft GET failed. Distinct from ``canEditError``, which is the
  // permissions probe failing — here the probe answered and the workflow itself
  // would not load (NG-409 makes a 403 here reachable: the Workflow tab is earned
  // by the form read, which an audience-axis caller holds without the workflow verb).
  "intake.builder.loadErrorBanner.title": "We couldn't load this workflow",
  "intake.builder.loadErrorBanner.description":
    "Something went wrong fetching it. Reload the page to try again.",
  "intake.builder.loadErrorBanner.descriptionWithStatus":
    "The server responded with {status}. Reload the page to try again — if it keeps happening, you may not have access to this workflow.",
  "intake.builder.requestType": "Default Request Type",
  "intake.builder.requestCategory": "Default Request Category",
  "intake.builder.defaultRequestOwner": "Default Request Owner",
  "intake.builder.teamsPlaceholder": "Select teams",
  "intake.builder.requestTypePlaceholder": "Select request type",
  "intake.builder.requestCategoryPlaceholder": "Select request category",
  "intake.builder.defaultRequestOwnerPlaceholder": "Select default owner",
  "intake.builder.teamsHint":
    "Selected teams and every team beneath them in the hierarchy will be able to use this workflow when creating requests.",
  "intake.builder.defaultRequestOwnerSearchPlaceholder": "Search default owners",
  "intake.builder.noTeams": "No active teams available.",
  "intake.builder.noOwners": "No active members available.",
  "intake.builder.slas": "SLAs",
  "intake.builder.slasPlaceholder": "Select SLAs",
  "intake.builder.slasSearchPlaceholder": "Search SLAs",
  "intake.builder.noSlas": "No SLAs available.",
  "intake.builder.slasHint":
    "Requests from this workflow are measured against these SLAs. Leave empty to fall back to the workspace catch-all SLA.",
  "intake.builder.slasCreate": "Create SLA",
  "intake.builder.slasError": "Couldn’t update this workflow's SLAs.",
  "intake.builder.slasCatchAllDisabled":
    "The catch-all SLA already covers every workflow that has no SLA of its own, so it can't be assigned to one directly.",
  "intake.builder.slasOnePerWorkflow":
    "A workflow belongs to one SLA. Remove the current one to choose another.",
  "intake.builder.slasInactiveHeld": "{name} (inactive)",
  "intake.builder.save": "Save",
  "intake.builder.saving": "Saving…",
  "intake.builder.discard": "Discard changes",
  // Shared autosave status pill — used by the intake form builder and the
  // project template detail tabs.
  "autosave.saving": "Saving…",
  "autosave.saved_at": "Saved {time}",
  "autosave.saved": "Saved",
  "autosave.idle": "Not saved yet",
  "autosave.up_to_date": "Up to date",
  "autosave.pending": "Unpublished changes",
  "autosave.pending_changes": "Pending changes",
  "autosave.save_failed_pill": "Save failed",
  // Intake-only: label for the changed-fields tooltip on a pending form.
  "intake.builder.autosavePendingFormContent": "Form content",
  "intake.builder.formDetails.fieldName": "Form name",
  "intake.builder.formDetails.fieldDescription": "Description",
  "intake.builder.formDetails.fieldRequestType": "Request type",
  "intake.builder.formDetails.fieldRequestCategory": "Request category",
  "intake.builder.formDetails.fieldTeams": "Submitting Teams",
  "intake.builder.formDetails.fieldManagingTeam": "Managing Team",
  "intake.builder.publish": "Publish changes",
  "intake.builder.publishing": "Publishing…",
  "intake.builder.publishSuccess": "Changes published. Requesters will see them now.",
  "intake.builder.publishError": "Couldn't publish changes. Try again.",
  "intake.builder.publishBlocked": "Can't publish yet:",
  "intake.builder.publishDisabledNoChanges": "No unpublished changes to publish.",
  "intake.builder.publishDisabledInactive":
    "The form must be Active before changes can be published to requesters.",
  "intake.builder.publishMissingFields":
    "Can't publish yet — required before publishing: {fields}. Set them in Form Details.",
  "intake.builder.publishInvalidDraft":
    "Can't publish — {count} builder field(s) have validation errors. Fix the highlighted fields and try again.",
  "intake.builder.requiredField.name": "form name",
  "intake.builder.requiredField.requestType": "request type",
  "intake.builder.requiredField.requestCategory": "request category",
  "intake.builder.autosaveError":
    "Your latest changes couldn't be saved. Fix any highlighted errors — editing again retries the save.",
  "intake.builder.moreActions": "More actions",
  "intake.builder.discardConfirmTitle": "Discard unpublished changes?",
  "intake.builder.discardConfirmBody":
    "Your form will revert to the version last published {publishedAt}. Any name, description, request type, team, or builder edits you've autosaved since then will be lost. This can't be undone.",
  "intake.builder.discardConfirmNeverPublished": "(never published)",
  "intake.builder.discardCancel": "Keep changes",
  "intake.builder.discardConfirm": "Discard changes",
  "intake.builder.discardSuccess": "Changes discarded.",
  "intake.builder.discardError": "Couldn't discard changes. Try again.",
  "intake.builder.discardNeverPublished":
    "This workflow has never been published, so there's no earlier version to go back to.",
  "intake.builder.reset": "Reset workflow",
  "intake.builder.resetConfirmTitle": "Reset this workflow?",
  "intake.builder.resetConfirmBody":
    "Every step, page and field you've built will be deleted and the workflow will start again from an empty first page. This workflow has never been published, so there's no earlier version to fall back on. This can't be undone.",
  "intake.builder.resetCancel": "Keep building",
  "intake.builder.resetConfirm": "Reset workflow",
  "intake.builder.resetSuccess": "Workflow reset.",
  "intake.builder.resetError": "Couldn't reset the workflow. Try again.",
  "intake.builder.discardFormOnlyNeverPublished":
    "Form details reverted. The workflow itself was left as it is — it has never been published, so there's no earlier version to restore.",
  "intake.builder.saveSuccess": "Form saved.",
  "intake.builder.saveError": "Failed to save form.",
  "intake.builder.saveDisabledInvalid":
    "Complete all required field labels and option values before saving.",
  "intake.builder.errorBanner": "Some changes could not be saved",
  "intake.builder.collapseProperties": "Collapse properties panel",
  "intake.builder.expandProperties": "Expand properties panel",
  "intake.builder.backToList": "Request forms",
  "intake.builder.untitled": "Untitled form",
  "intake.builder.statusActive": "Active",
  "intake.builder.statusInactive": "Deactivated",
  "intake.builder.lastSaved": "Last saved {time}",
  "intake.builder.preview": "Preview form",
  "intake.builder.delete": "Delete form",
  "intake.builder.previewDialogDescription": "This is how requesters will see the form.",
  "intake.builder.previewPartial":
    "Starting from this page — earlier Branch and Review decisions haven't been evaluated.",
  "intake.builder.previewLoading": "Loading preview…",
  "intake.builder.previewEmpty": "Add fields to the first page to preview the requester form.",
  "intake.builder.previewError": "Couldn't load the preview. Try again.",
  "intake.builder.previewContinue": "Continue",
  "intake.builder.previewReviewChoicePrompt":
    "Preview can't tell how this review would be decided — pick an outcome to keep walking through it.",
  "intake.builder.previewReachedEnd": "You've reached the end of this workflow.",
  "intake.builder.previewIncomplete":
    "This path hasn't been finished being built yet, so Preview can't continue through it.",
  "intake.builder.previewRestart": "Restart preview",
  "intake.builder.previewAdvanceError": "Couldn't continue the preview. Try again.",
  "intake.builder.previewPartialRecourse":
    "Restart the preview from the first page to walk them for real.",
  "intake.builder.previewEndAuto":
    "This workflow completes the request automatically here, creating its project.",
  "intake.builder.previewEndManual": "The request waits here to be completed manually.",
  "intake.builder.previewIncompleteRecourse":
    "Wire the rest of this path on the canvas, then preview it again.",
  "intake.builder.previewOwnerHeading": "Request owner",
  "intake.builder.previewOwnerNone": "No owner — nothing on this path assigned one.",
  "intake.builder.previewTrail.heading": "Path taken",
  "intake.builder.previewTrail.description":
    "What this walk went through to reach the step above. Requesters don\u2019t see this.",
  "intake.builder.previewTrail.current": "You are here",
  "intake.builder.previewTrail.branchTrue": "Condition met — took the yes path",
  "intake.builder.previewTrail.branchFalse": "Condition not met — took the no path",
  "intake.builder.previewTrail.decidedAutomatically": "Decided automatically: {option}",
  "intake.builder.previewTrail.decidedByYou": "You picked: {option}",
  "intake.builder.previewTrail.pageSkipped": "Skipped — hidden by its display rule",
  "intake.builder.previewTrail.ownerAdded": "Owner added: {names}",
  "intake.builder.previewTrail.ownerReplaced": "Owner changed to {names}",
  "intake.builder.previewTrail.ownerNoRule":
    "No rule matched this answer — the owner would stay as it is",
  "intake.builder.previewTrail.ownerNobody":
    "Matched a rule that names nobody — the owner would stay as it is",
  "intake.builder.previewTrail.awaitingDecision": "Waiting on a review decision",
  "intake.builder.previewTrail.laneUnwired": "This path isn\u2019t finished yet",
  "intake.builder.previewTrail.unnamedPage": "Page",
  "intake.builder.previewTrail.unnamedReview": "Review",
  "intake.builder.previewTrail.unnamedEnd": "End",
  "intake.builder.previewTrail.unnamedStep": "Step",
  "intake.builder.deleteConfirmTitle": 'Delete "{name}"?',
  "intake.builder.deleteConfirmBody":
    "This form and its submission history will be archived. New submissions will be blocked.",
  "intake.builder.deleteCancel": "Cancel",
  "intake.builder.deleteConfirm": "Delete",
  "intake.builder.deleteSuccess": "Form deleted.",
  "intake.builder.deleteError": "Failed to delete form.",
  "intake.builder.discardDialogTitle": "Discard unsaved changes?",
  "intake.builder.discardDialogDescription":
    "You have unsaved edits. Leaving now will discard them.",
  "intake.builder.discardDialogConfirm": "Discard changes",
  "intake.builder.discardDialogCancel": "Keep editing",
  "intake.builder.pages": "Pages",
  "intake.builder.addPage": "Add page",
  "intake.builder.removePage": "Remove page",
  "intake.builder.pageName": "Page name",
  "intake.builder.localeTab.label": "Editing language",
  "intake.builder.localeTab.en": "English",
  "intake.builder.localeTab.fr": "Français",
  "intake.builder.pageInternal": "Internal page (hidden from requesters)",
  "intake.builder.elements": "Elements",
  "intake.builder.addElement": "Add element",
  "intake.builder.addRadio": "Radio",
  "intake.builder.addNumber": "Number",
  "intake.builder.removeElement": "Remove element",
  // Intake builder: field preview placeholders, info text and the URL input (NG-2580)
  "intake.builder.fieldPreview.addOptions": "Add options in the Data tab",
  "intake.builder.fieldPreview.dropFiles": "Drag & drop files here",
  "intake.builder.fieldPreview.calculated": "Calculated · {name}",
  "intake.builder.infoTextPlaceholder": "Informational text",
  "intake.builder.urlSchemeAria": "URL scheme",
  "intake.builder.elementName": "Field name",
  "intake.builder.elementLabel": "Label",
  "intake.builder.tooltip": "Tooltip",
  "intake.builder.fieldDescription": "Description",
  "intake.builder.placeholder": "Placeholder",
  "intake.builder.showCharCounter": "Show character counter",
  "intake.builder.prefix": "Prefix",
  "intake.builder.suffix": "Suffix",
  "intake.builder.inlineLayout": "Inline layout",
  "intake.builder.inputMask": "Input mask",
  "intake.builder.dateFormat": "Format",
  "intake.builder.defaultValue": "Default value",
  "intake.builder.defaultChecked": "Default checked",
  "intake.builder.thousandsSeparator": "Use thousands separator",
  "intake.builder.decimalPlaces": "Decimal places",
  "intake.builder.requireDecimal": "Require decimal",
  "intake.builder.enableTime": "Include time picker",
  "intake.builder.enableDate": "Enable date input",
  "intake.builder.disableSpecificDates": "Disable specific dates",
  "intake.builder.disableWeekends": "Disable weekends",
  "intake.builder.disableWeekdays": "Disable weekdays",
  "intake.builder.twelveHour": "12-hour time (AM/PM)",
  "intake.builder.currencyCode": "Currency code",
  "intake.builder.allowMultipleUrls": "Allow multiple URLs",
  "intake.builder.multiselect": "Allow multiple selections",
  "intake.builder.defaultValues": "Default values",
  "intake.builder.defaultValuesPlaceholder": "Select default options",
  "intake.builder.defaultValuesSearch": "Search options",
  "intake.builder.defaultValuesEmpty": "No options found",
  "intake.builder.defaultValuePlaceholder": "Select a default option",
  "intake.builder.defaultValueNone": "No default",
  "intake.builder.minLength": "Min length",
  "intake.builder.maxLength": "Max length",
  "intake.builder.minValue": "Min value",
  "intake.builder.maxValue": "Max value",
  "intake.builder.minChecked": "Min checked",
  "intake.builder.maxChecked": "Max checked",
  "intake.builder.required": "Required",
  "intake.builder.internal": "Internal",
  "intake.builder.replaceCurrentOwner": "Replace current owner",
  "intake.builder.replaceCurrentOwnerTooltip":
    "On: replaces all existing owners with the selected user. Off: adds them as an additional owner.",
  "intake.builder.options": "Options",
  "intake.builder.addOption": "Add option",
  "intake.builder.removeOption": "Remove option",
  "intake.builder.optionLabelEn": "Label (EN)",
  "intake.builder.optionLabelFr": "Label (FR)",
  "intake.builder.optionLabelsShowBoth": "Show both languages",
  "intake.builder.visibleIfTitle": "Visibility",
  "intake.builder.visibleIfField": "When field",
  "intake.builder.visibleIfValue": "Equals",
  "intake.builder.visibleIfClear": "Clear condition",
  "intake.builder.visibleIfAdd": "Show when…",
  "intake.builder.visibleIfStale": "Field ''{field}'' is no longer defined.",
  "intake.builder.booleanTrue": "True",
  "intake.builder.booleanFalse": "False",
  "intake.builder.optionPlaceholder": "Select an option…",
  "intake.builder.visibleIfNone": "Always visible",
  "intake.builder.systemFieldNotice":
    "System field — you can rename its label; the field itself cannot be removed.",
  "intake.builder.requestTitleNotice":
    "Request Title — always required and on the first page. You can edit its label, placeholder, and help text, but it cannot be removed, made internal, or shown conditionally.",
  "intake.builder.kindLabel": "Kind",
  "intake.builder.dragHandle": "Drag to reorder",
  "intake.builder.invalidNamePattern":
    "Use lowercase letters, numbers, and underscores; must start with a letter.",
  "intake.builder.paletteTitle": "Components",
  "intake.builder.paletteGroupRecommended": "Recommended",
  "intake.builder.paletteRecommendedHint":
    "Global fields stay in sync across the workspace and carry into the project.",
  "intake.builder.paletteGroupFormOnly": "Form-only components",
  "intake.builder.paletteHint": "Drag a component into a page. These stay on this form.",
  "intake.builder.paletteGroupBasic": "Basic",
  "intake.builder.paletteGroupChoice": "Choice",
  "intake.builder.paletteGroupAdvanced": "Advanced",
  "intake.builder.paletteGroupLayout": "Layout",
  "intake.builder.addGlobalField": "Add global field",
  "intake.builder.addGlobalFieldTo": "Add global field to {destination}",
  "intake.builder.globalFieldModal.type_program": "Program",
  "intake.builder.globalFieldModal.type_goal": "Goal",
  "intake.builder.globalFieldModal.type_request_category": "Request category",
  "intake.builder.globalFieldModal.type_date": "Date",
  "intake.builder.palette.empty_no_fields":
    "No global custom fields yet. Ask an admin to create one in the",
  "intake.builder.palette.empty_no_fields_link": "custom fields library",
  "intake.builder.bank_linked_banner": 'Linked to workspace bank field "{name}".',
  "intake.builder.bank_linked_manage": "Manage in settings →",
  "intake.builder.bank_linked_options_note": "Options are managed in workspace settings.",
  "intake.builder.global_marker_label": "Global",
  "intake.builder.global_marker_tooltip": "Managed in Global Custom Fields",
  "intake.builder.system_marker_label": "System",
  "intake.builder.system_marker_tooltip": "System field — its value is managed by Cognota",
  "intake.builder.conditional_marker_label": "Show when",
  "intake.builder.internal_marker_tooltip": "Internal — hidden from requesters",
  "intake.builder.conditional_marker_tooltip": "Conditional — shown only when its rule matches",
  "intake.builder.hide_option_from_requesters": "Hide from requesters",
  "intake.builder.show_option_to_requesters": "Show to requesters",
  "intake.builder.duplicate_action_label": "Duplicate as form-specific",
  "intake.builder.duplicate_confirm_title": "Duplicate as form-specific field?",
  "intake.builder.duplicate_confirm_body":
    "This creates a form-specific copy that won't sync with the global library. Future changes to the global field won't affect this copy.",
  "intake.builder.duplicate_confirm_cta": "Duplicate",
  "intake.builder.copy_suffix": "(Copy)",
  "intake.builder.addText": "Single line",
  "intake.builder.addTextarea": "Paragraph",
  "intake.builder.addCheckbox": "True / False",
  "intake.builder.addCheckboxes": "Checkboxes",
  "intake.builder.addSelect": "Dropdown",
  "intake.builder.addEmail": "Email",
  "intake.builder.addUrl": "URL",
  "intake.builder.addPhone": "Phone",
  "intake.builder.addDatetime": "Date / Time",
  "intake.builder.addCurrency": "Currency",
  "intake.builder.addFile": "File upload",
  "intake.builder.dropZoneEmpty": "Drag and drop a form component",
  "intake.builder.dropZoneActive": "Release to add",
  "intake.builder.dropZoneTail": "Drop here to add to the end",
  "intake.builder.matchAll": "Match all",
  "intake.builder.matchAny": "Match any",
  "intake.builder.removeClause": "Remove condition",
  "intake.builder.addClause": "Add condition",
  "intake.builder.opEq": "equals",
  "intake.builder.opNeq": "does not equal",
  "intake.builder.opGt": "is greater than",
  "intake.builder.opGte": "is at least",
  "intake.builder.opLt": "is less than",
  "intake.builder.opLte": "is at most",
  "intake.builder.opContains": "contains",
  "intake.builder.opIsEmpty": "is empty",
  "intake.builder.opIsNotEmpty": "is not empty",
  "intake.builder.addExpression": "Calculation",
  "intake.builder.addInfoText": "Informational text",
  "intake.builder.addRichText": "Rich text",
  "intake.builder.addDivider": "Divider",
  "intake.builder.addFieldGroup": "Group",
  "intake.builder.addColumns": "Columns",
  "intake.builder.addProgram": "Program",
  "intake.builder.addGoal": "Goal",
  "intake.builder.addRequestCategory": "Request Category",
  "intake.builder.addTargetLaunchDate": "Target launch date",
  "intake.builder.addRequestOwner": "Request Owner",
  "intake.builder.addUserList": "User List",
  "intake.builder.container.untitled": "Untitled group",
  "intake.builder.container.expand": "Expand group",
  "intake.builder.container.collapse": "Collapse group",
  "intake.builder.container.columnsTitle": "Columns ({count})",
  "intake.builder.container.dropHere": "Drop here",
  "intake.builder.container.dropHereColumn": "Drop here",
  "intake.builder.container.dropTail": "Drop here to add to the end",
  "intake.builder.container.columnLabel": "column {index}",
  "intake.builder.pane.container.group.title": "Group",
  "intake.builder.pane.container.group.collapsed": "Collapsed by default",
  "intake.builder.pane.container.group.internal": "Internal group (hidden from requesters)",
  "intake.builder.pane.container.columns.title": "Columns",
  "intake.builder.pane.container.columns.count": "Column count",
  "intake.builder.pane.container.columns.hint":
    "Shrinking the count merges trailing column contents into the previous column.",
  "intake.builder.pane.container.info.title": "Informational text",
  "intake.builder.pane.container.info.content": "Content",
  "intake.builder.pane.container.richText.title": "Rich text",
  "intake.builder.pane.container.richText.content": "Content",
  "intake.builder.richText.bold": "Bold",
  "intake.builder.richText.italic": "Italic",
  "intake.builder.richText.bulletList": "Bullet list",
  "intake.builder.richText.orderedList": "Numbered list",
  "intake.builder.richText.link": "Link",
  "intake.builder.richText.linkTextLabel": "Text to display",
  "intake.builder.richText.linkUrlLabel": "Link URL",
  "intake.builder.richText.linkUrlPlaceholder": "https://example.com",
  "intake.builder.richText.linkApply": "Apply",
  "intake.builder.richText.linkRemove": "Remove",
  "intake.builder.richText.addImage": "Add image",
  "intake.builder.richText.embedMedia": "Embed media",
  "intake.builder.richText.embedUrlLabel": "Video URL",
  "intake.builder.richText.embedUrlPlaceholder": "https://youtube.com/watch?v=...",
  "intake.builder.richText.embedUrlHint":
    "YouTube, Vimeo, Loom, Wistia, Microsoft Stream, or Google Drive.",
  "intake.builder.richText.embedUrlError": "Enter a valid video URL from a supported provider.",
  "intake.builder.richText.embedApply": "Embed",
  "intake.builder.pane.container.divider.title": "Divider",
  "intake.builder.pane.container.divider.hint": "Dividers have no editable properties.",
  "intake.builder.expressionTitle": "Expression",
  "intake.builder.expressionHelp":
    "Calculates a value from the form's other numeric fields. Choose an aggregate (sum, min, max, average) and pick the fields to combine, or build a custom formula with math operations.",
  "intake.builder.expressionHelpAria": "About calculation fields",
  "intake.builder.exprNodeLiteral": "Number",
  "intake.builder.exprNodeRef": "Field",
  "intake.builder.exprNodeBinary": "Math",
  "intake.builder.exprNodeAggregate": "Aggregate",
  "intake.builder.exprBinaryAdd": "+",
  "intake.builder.exprBinarySub": "−",
  "intake.builder.exprBinaryMul": "×",
  "intake.builder.exprBinaryDiv": "÷",
  "intake.builder.exprBinaryLeft": "Left",
  "intake.builder.exprBinaryRight": "Right",
  "intake.builder.exprAggSum": "Sum",
  "intake.builder.exprAggMin": "Min",
  "intake.builder.exprAggMax": "Max",
  "intake.builder.exprAggAvg": "Average",
  "intake.builder.exprAddArg": "Add operand",
  "intake.builder.exprNoNumericFields": "Define a Number field first",
  "intake.builder.formsTitle": "Forms",
  "intake.builder.formsCreate": "New form",
  "intake.builder.formsEmpty": "No forms yet.",
  "intake.builder.formsCreatePlaceholder": "Untitled form",
  "intake.builder.formsCreateError": "Failed to create form.",
  "intake.builder.pane.empty.title": "No field selected",
  "intake.builder.pane.empty.description": "Click a field on the canvas to edit its properties.",
  "intake.builder.pane.pageSettings.title": "Page settings",
  // #3311: the Edit Page settings pane groups its controls under two section
  // headings — Overview (page name + status) and Access (internal + who fills +
  // notify) — to match the Workflow Polish 2 mock-up.
  "intake.builder.pane.pageSettings.overview": "Overview",
  "intake.builder.pane.pageSettings.access": "Access",
  "intake.builder.pane.systemField.title": "System field",
  "intake.builder.pane.fieldProperties.title": "Field properties",
  "intake.builder.pane.tab.display": "Display",
  "intake.builder.pane.tab.data": "Data",
  "intake.builder.pane.section.date": "Date",
  "intake.builder.pane.section.time": "Time",
  "intake.builder.pane.tab.validation": "Validation",
  "intake.builder.pane.tab.conditional": "Conditional",
  "intake.builder.pane.data.empty":
    "No data properties yet — placeholder, default value, and similar options land in a future slice.",
  "intake.builder.pane.validation.empty":
    "More validation options land in a future slice. Required is in the header above.",
  "intake.builder.row.duplicate": "Duplicate field",
  "intake.builder.row.requiredAria": "Toggle required",
  "intake.builder.row.moreActions": "More actions",
  "intake.builder.moveToPage": "Move to page…",
  "intake.builder.duplicateToPage": "Duplicate to page…",
  "intake.builder.targetPage": "Target page",

  "intake.builder.pageFallback": "Page {index}",
  "intake.builder.kindLabel.text": "Single line",
  "intake.builder.kindLabel.textarea": "Paragraph",
  "intake.builder.kindLabel.number": "Number",
  "intake.builder.kindLabel.checkbox": "True / False",
  "intake.builder.kindLabel.checkboxes": "Checkboxes",
  "intake.builder.kindLabel.program": "Program",
  "intake.builder.kindLabel.goal": "Goal",
  "intake.builder.kindLabel.request_category": "Request Category",
  "intake.builder.kindLabel.select": "Dropdown",
  "intake.builder.kindLabel.radio": "Radio buttons",
  "intake.builder.kindLabel.email": "Email",
  "intake.builder.kindLabel.url": "URL",
  "intake.builder.kindLabel.phone": "Phone",
  "intake.builder.kindLabel.datetime": "Date / Time",
  "intake.builder.kindLabel.currency": "Currency",
  "intake.builder.kindLabel.user_list": "User List",
  "intake.builder.kindLabel.file": "File upload",
  "intake.builder.kindLabel.expression": "Calculation",

  "intake.formCrumbs.new": "New form",
  "intake.formCrumbs.untitled": "Untitled form",

  "intake.formsList.title": "Intake forms",
  "intake.formsList.description": "Manage the intake forms available in this workspace.",
  "intake.formsList.newForm": "Create Form",
  "intake.formsList.colName": "Name",
  "intake.formsList.colManagingTeams": "Managing Teams",
  "intake.formsList.colSubmittingTeams": "Submitting Teams",
  "intake.formsList.colUpdatedAt": "Last updated",
  "intake.formsList.managingTeamWorkspace": "Workspace",
  "intake.formsList.colActive": "Active",
  "intake.formsList.colActions": "Actions",
  // Per-row read-only marker on the team-settings tab (#3144 D4): the workflow is
  // managed by an ancestor team, so this team can see it but not change it.
  "intake.formsList.viewOnlyBadge": "View only",
  "intake.formsList.deleteAction": "Delete form",
  "intake.formsList.duplicateAction": "Duplicate form",
  "intake.formsList.duplicateSuccess": 'Duplicated "{name}".',
  "intake.formsList.duplicateError": "Failed to duplicate form.",
  "intake.formsList.toggleError": "Couldn't update form status.",
  "intake.formsList.activatedToast": "Form activated.",
  "intake.formsList.deactivatedToast": "Form deactivated.",
  "intake.formsList.deleteTitle": "Delete form",
  "intake.formsList.deleteConfirm":
    'Delete "{name}"? This permanently removes the form and any submissions.',
  "intake.formsList.delete": "Delete",
  "intake.formsList.deleting": "Deleting…",
  "intake.formsList.cancel": "Cancel",
  "intake.formsList.deleteSuccess": "Form deleted.",
  "intake.formsList.deleteError": "Failed to delete form.",
  "intake.formsList.emptyTitle": "No forms yet",
  "intake.formsList.emptyDescription": "Create your first intake form to get started.",
  "intake.formsList.searchPlaceholder": "Search forms…",
  "intake.formsList.selectAll": "Select all forms",
  "intake.formsList.selectOne": "Select {name}",
  "intake.formsList.noMatches": "No forms match this search.",
  "intake.formsList.rangeSummary": "Showing {from}–{to} of {total}",
  "intake.formsList.paginationPrev": "Previous page",
  "intake.formsList.paginationNext": "Next page",

  // Admin wording for the settings list page (the Form Workflow Builder is
  // admin-only). Parallel to intake.formsList.* — selected at the render site so
  // a non-admin keeps the "Forms" naming above.
  "intake.intakeWorkflowsList.title": "Intake Workflows",
  "intake.intakeWorkflowsList.description":
    "Manage the intake workflows available in this workspace.",
  "intake.intakeWorkflowsList.newForm": "Create Intake Workflow",
  "intake.intakeWorkflowsList.deleteAction": "Delete intake workflow",
  "intake.intakeWorkflowsList.duplicateAction": "Duplicate intake workflow",
  "intake.intakeWorkflowsList.duplicateSuccess": 'Duplicated "{name}".',
  "intake.intakeWorkflowsList.duplicateError": "Failed to duplicate intake workflow.",
  "intake.intakeWorkflowsList.toggleError": "Couldn't update intake workflow status.",
  "intake.intakeWorkflowsList.activatedToast": "Intake workflow activated.",
  "intake.intakeWorkflowsList.deactivatedToast": "Intake workflow deactivated.",
  "intake.intakeWorkflowsList.deleteTitle": "Delete intake workflow",
  "intake.intakeWorkflowsList.deleteConfirm":
    'Delete "{name}"? This permanently removes the intake workflow and any submissions.',
  "intake.intakeWorkflowsList.deleteSuccess": "Intake workflow deleted.",
  "intake.intakeWorkflowsList.deleteError": "Failed to delete intake workflow.",
  "intake.intakeWorkflowsList.emptyTitle": "No intake workflows yet",
  "intake.intakeWorkflowsList.emptyDescription":
    "Create your first intake workflow to get started.",
  "intake.intakeWorkflowsList.searchPlaceholder": "Search intake workflows…",
  "intake.intakeWorkflowsList.selectAll": "Select all intake workflows",
  "intake.intakeWorkflowsList.noMatches": "No intake workflows match this search.",

  "intake.reactivation.blocked_title": "Can't activate this form",
  "intake.reactivation.blocked_body":
    "Activation is blocked because the form references global custom fields that have been archived, or includes visibility rules that no longer resolve. Open the form to fix each issue listed below, then try activating again.",
  "intake.reactivation.issue.archived_global_field":
    "{element} is bound to a global field that has been archived.",
  "intake.reactivation.issue.archived_global_field_dep":
    '{element} has a visibility rule depending on "{reference}", whose global field has been archived.',
  "intake.reactivation.issue.missing_dep":
    '{element} has a visibility rule referencing "{reference}", which no longer exists in this form.',
  "intake.reactivation.open_form_cta": "Open form to fix",
  "intake.reactivation.close_cta": "Close",

  "intake.convert.title": "Convert request to project",
  "intake.convert.loading": "Loading carryover plan…",
  "intake.convert.template_label": "Project template",
  "intake.convert.template_placeholder": "Pick a template to start",
  "intake.convert.template_hint": "We use this to decide which request answers carry over.",
  "intake.convert.shared_heading": "Carried automatically",
  "intake.convert.shared_empty":
    "Nothing on this template matches the request answers — pick what you want to carry below.",
  "intake.convert.programs_heading": "Programs",
  "intake.convert.goals_heading": "Goals",
  "intake.convert.programs_hint":
    "Programs linked on the request are pre-filled. Add or remove before creating the project.",
  "intake.convert.goals_hint":
    "Goals linked on the request are pre-filled. Add or remove before creating the project.",
  "intake.convert.extras_heading": "Additional fields",
  "intake.convert.extras_hint":
    "These were answered on the request but aren't part of the template. Tick any you want to add to the new project.",
  "intake.convert.extras_empty": "No additional fields to carry.",
  "intake.convert.template_only_heading": "Template fields",
  "intake.convert.template_only_hint":
    "Add values for fields on the project template that weren't on the request.",
  "intake.convert.archived_heading": "Cannot be carried — archived in your global library",
  "intake.convert.archived_field": "{field} is archived and won't be carried.",
  "intake.convert.archived_option":
    "{field}: the option “{option}” is archived and will be dropped.",
  "intake.convert.value_empty": "No answer",
  "intake.convert.confirm_cta": "Create",
  "intake.convert.confirm_open_cta": "Create & Open",
  "intake.convert.cancel_cta": "Cancel",
  "intake.convert.process_from_template": "Use the template's process",
  "intake.convert.created": "Project created.",
  "intake.convert.error": "Couldn't load the carryover plan. Try again.",
  "intake.convert.project_details_heading": "Project details",
  "intake.convert.project_details_hint":
    "Override the template's defaults for the new project. Leave a field blank to use the template value.",
  "intake.convert.project_name_label": "Project name",
  "intake.convert.project_description_label": "Description",
  "intake.convert.project_lead_label": "Project lead",
  "intake.convert.project_start_date_label": "Start date",
  "intake.convert.project_end_date_label": "Target completion date",
  "intake.convert.subtitle":
    "Project details have been pre-filled from the request. Review and edit before creating.",

  // Form Workflow runtime "awaiting my review" queue (epic #2166, issue #2176).
  "intake.reviewerQueue.title": "My reviews",
  "intake.reviewerQueue.countNoun": "awaiting your review",
  "intake.reviewerQueue.awaitingYourReview": "Awaiting your review",
  "intake.reviewerQueue.submittedBy": "Submitted by {name}",
  "intake.reviewerQueue.emptyTitle": "You're all caught up",
  "intake.reviewerQueue.emptyDescription": "No requests are awaiting your review right now.",
  "intake.reviewerQueue.errorTitle": "Couldn't load your reviews",
  "intake.reviewerQueue.retry": "Try again",

  // Form Workflow runtime reviewing & decision surface (issue #2255; BE #2254).
  "intake.reviewDecision.submittedBy": "Submitted by {name}",
  "intake.pendingPage.title": "Next step",
  "intake.pendingPage.prompt": "This request is waiting on a page you can fill.",
  "intake.pendingPage.promptNamed": "This request is waiting on ''{page}''.",
  "intake.pendingPage.submit": "Submit page",
  "intake.pendingPage.submitting": "Submitting…",
  "intake.pendingPage.submitError": "Could not submit this page. Please try again.",
  "intake.pendingPage.complete": "Complete this step",
  "intake.pendingPage.completing": "Completing…",
  "intake.pendingPage.completeBlocked":
    "Fill in these required fields before completing this step:",
  "intake.pendingPage.completeError": "Could not complete this step. Please try again.",
  "intake.pendingPage.completeNeedsSubmit":
    "This page has required fields. Fill them in, then use “Submit page”.",
  "intake.reviewInline.title": "New Review",
  "intake.reviewInline.defaultPrompt":
    "Please review this request and determine if it should be added to the project backlog.",
  "intake.reviewInline.selectDecision": "Select Decision",
  "intake.reviewInline.submit": "Submit",
  "intake.reviewDecision.instructionsLabel": "Reviewer instructions",
  "intake.reviewDecision.decidePrompt": "Choose an outcome",
  "intake.reviewDecision.declineIntro":
    "You're about to {option} this request. Tell the requester why — they'll see this.",
  "intake.reviewDecision.declineReasonLabel": "Reason",
  "intake.reviewDecision.declineReasonPlaceholder": "Choose a reason",
  "intake.reviewDecision.declineReasonsUnavailable":
    "No decline reasons are set up for this workspace yet. An admin can add them in Settings.",
  "intake.reviewDecision.declineNoteLabel": "Note (optional)",
  "intake.reviewDecision.declineNotePlaceholder": "Add any detail that would help the requester",
  "intake.reviewDecision.declineNoteHelp":
    "The reason groups this decline for reporting; the note explains it in your own words.",
  "intake.reviewDecision.declineBack": "Back",
  "intake.reviewDecision.declineConfirm": "{option} request",
  "intake.reviewDecision.reasonIntro.declined":
    "You're about to {option} this request. Tell the requester why; they'll see this.",
  "intake.reviewDecision.reasonIntro.waitlisted":
    "You're about to {option} this request. Choose at least one waitlist reason; the requester will see this.",
  "intake.reviewDecision.reasonIntro.cancelled":
    "You're about to {option} this request. Tell the requester why it was cancelled; they'll see this.",
  "intake.reviewDecision.reasonBack": "Back",
  "intake.reviewDecision.reasonConfirm.declined": "{option} request",
  "intake.reviewDecision.reasonConfirm.waitlisted": "{option} request",
  "intake.reviewDecision.reasonConfirm.cancelled": "{option} request",
  "intake.statusReason.declined.reasonLabel": "Reason",
  "intake.statusReason.declined.reasonPlaceholder": "Choose a reason",
  "intake.statusReason.declined.reasonsUnavailable":
    "No decline reasons are set up for this workspace yet. An admin can add them in Settings.",
  "intake.statusReason.declined.noteLabel": "Note (optional)",
  "intake.statusReason.declined.notePlaceholder": "Add any detail that would help the requester",
  "intake.statusReason.declined.noteHelp":
    "The reason groups this decline for reporting; the note explains it in your own words.",
  "intake.statusReason.waitlisted.reasonLabel": "Reasons",
  "intake.statusReason.waitlisted.reasonPlaceholder": "Choose reasons",
  "intake.statusReason.waitlisted.selectedCount": "{count} selected",
  "intake.statusReason.waitlisted.reasonsUnavailable":
    "No waitlist reasons are set up for this workspace yet. An admin can add them in Settings.",
  "intake.statusReason.waitlisted.noteLabel": "Note (optional)",
  "intake.statusReason.waitlisted.notePlaceholder": "Add any detail that would help the requester",
  "intake.statusReason.waitlisted.noteHelp":
    "The reasons group this waitlist decision for reporting; the note explains it in your own words.",
  "intake.statusReason.cancelled.reasonLabel": "Reason",
  "intake.statusReason.cancelled.reasonPlaceholder": "Choose a reason",
  "intake.statusReason.cancelled.reasonsUnavailable":
    "No cancel reasons are set up for this workspace yet. An admin can add them in Settings.",
  "intake.statusReason.cancelled.noteLabel": "Note (optional)",
  "intake.statusReason.cancelled.notePlaceholder": "Add any detail that would help the requester",
  "intake.statusReason.cancelled.noteHelp":
    "The reason groups this cancellation for reporting; the note explains it in your own words.",
  "intake.reviewDecision.retry": "Try again",
  "intake.reviewDecision.loadError": "Couldn't load this review. Please try again.",
  "intake.reviewDecision.alreadyResolved":
    "This request has already moved on — it's no longer awaiting your decision.",
  "intake.reviewDecision.cannotDecide":
    "You're not an assigned reviewer of this request's current step.",
  "intake.reviewDecision.decisionSuccess": "Decision recorded.",
  "intake.reviewDecision.decisionError": "Couldn't record your decision. Please try again.",
  "intake.reviewDecision.raceResolved": "Another reviewer already resolved this request.",

  "intake.submissionsList.title": "Requests",
  "intake.submissionsList.selectAll": "Select all rows",
  "intake.submissionsList.countNoun": "requests",
  "intake.submissionsList.createRequest": "Create Request",
  "intake.submissionsList.searchPlaceholder": "Search requests…",
  "intake.submissionsList.layoutTabsAria": "Request views",
  "intake.submissionsList.showArchived": "Show archived",
  "intake.submissionsList.filter.label": "Filter",
  "intake.submissionsList.filter.anyPlaceholder": "Any",
  "intake.submissionsList.sort.label": "Sort",
  "intake.submissionsList.sort.default": "Default",
  "intake.submissionsList.sort.created": "Created",
  "intake.submissionsList.sort.lastUpdated": "Last updated",
  "intake.submissionsList.sort.status": "Status",
  "intake.submissionsList.sort.requestedBy": "Requested by",
  "intake.submissionsList.sort.form": "Form",
  "intake.submissionsList.sort.sla": "SLA",
  "intake.submissionsList.sort.priority": "Priority",
  "intake.submissionsList.sort.requestCategory": "Request Category",
  "intake.submissionsList.groupBy.label": "Group by",
  "intake.submissionsList.groupBy.none": "None",
  "intake.submissionsList.groupBy.status": "Status",
  "intake.submissionsList.groupBy.form": "Form",
  "intake.submissionsList.groupBy.category": "Request Category",
  "intake.submissionsList.groupBy.noneStatus": "No status",
  "intake.submissionsList.groupBy.noneCategory": "No category",
  "intake.submissionsList.colRequestName": "Request Name",
  "intake.submissionsList.colStatus": "Status",
  "intake.submissionsList.colRequestedBy": "Requested by",
  "intake.submissionsList.colOwner": "Owner",
  "intake.submissionsList.colTeams": "Teams",
  "intake.submissionsList.colLinkedProjects": "Linked Projects",
  "intake.submissionsList.colPrograms": "Programs",
  "intake.submissionsList.colFormUsed": "Form Used",
  "intake.submissionsList.colRequestCategory": "Request Category",
  "intake.submissionsList.colCreated": "Created",
  "intake.submissionsList.colSubmitted": "Submitted",
  "intake.submissionsList.colLastUpdated": "Last Updated",
  "intake.submissionsList.colSla": "SLA",
  "intake.submissionsList.slaStateWithin": "Within",
  "intake.submissionsList.slaStatePaused": "Paused",
  "intake.submissionsList.slaStateBreached": "Breached",
  "intake.submissionsList.slaStateMet": "Met",
  "intake.submissionsList.ownerUnassigned": "Unassigned",
  "intake.submissionsList.slaDaysLeft": "{count}d left",
  "intake.submissionsList.slaDaysOverdue": "{count}d overdue",
  "intake.submissionsList.slaDueToday": "Due today",
  "intake.submissionsList.slaDueYesterday": "Due yesterday",
  "intake.submissionsList.slaDaysElapsed": "{count}d",
  "intake.submissionsList.colPriority": "Priority",
  "intake.submissionsList.priorityTierUrgent": "Urgent",
  "intake.submissionsList.priorityTierHigh": "High",
  "intake.submissionsList.priorityTierMedium": "Medium",
  "intake.submissionsList.priorityTierLow": "Low",
  "intake.submissionsList.editPriorityAria": "Edit priority",
  "intake.submissionsList.editStatusAria": "Edit status",
  "intake.submissionsList.priorityPinnedAria": "Priority manually pinned",
  "intake.submissionsList.priorityLockedAria": "Priority locked",
  "intake.submissionsList.priorityLockedTooltip":
    "Priority is locked while the request is completed or cancelled.",
  "intake.submissionsList.priorityRevertToAuto": "Revert to auto",
  "intake.submissionsList.priorityOverrideSuccess": "Priority pinned.",
  "intake.submissionsList.priorityRevertSuccess": "Priority reverted to auto.",
  "intake.submissionsList.priorityOverrideError": "Failed to update priority.",
  "intake.submissionsList.colForm": "Form",
  "intake.submissionsList.colSubmitter": "Submitter",
  "intake.submissionsList.colSubmittedAt": "Submitted at",
  "intake.submissionsList.filterFormPlaceholder": "Filter by form",
  "intake.submissionsList.filterFormAll": "All forms",
  "intake.submissionsList.colState": "State",
  "intake.submissionsList.stateNone": "No state",
  "intake.submissionsList.editNameAria": "Edit request name",
  "intake.submissionsList.rename": "Rename",
  "intake.submissionsList.editOwnerAria": "Edit request owners",
  "intake.submissionsList.editOwnerSearch": "Search people…",
  "intake.submissionsList.editNoMembers": "No members",
  "intake.submissionsList.loadingStates": "Loading options…",
  "intake.submissionsList.statesLoadError": "Couldn't load options",
  "intake.submissionsList.statesLoadRetry": "Try again",
  "intake.submissionsList.loadingMore": "Loading more requests…",
  "intake.submissionsList.statusUpdateSuccess": "Request state updated.",
  "intake.submissionsList.statusUpdateError": "Failed to update request state.",
  "intake.submissionsList.declineStatusTitle": "Decline request",
  "intake.submissionsList.declineStatusDescription":
    "Choose a reason before moving this request to {status}.",
  "intake.submissionsList.declineStatusConfirm": "Decline request",
  "intake.submissionsList.statusReasonTitle.declined": "Decline request",
  "intake.submissionsList.statusReasonTitle.waitlisted": "Waitlist request",
  "intake.submissionsList.statusReasonTitle.cancelled": "Cancel request",
  "intake.submissionsList.statusReasonDescription.declined":
    "Choose a reason before moving this request to {status}.",
  "intake.submissionsList.statusReasonDescription.waitlisted":
    "Choose at least one reason before moving this request to {status}.",
  "intake.submissionsList.statusReasonDescription.cancelled":
    "Choose a reason before moving this request to {status}.",
  "intake.submissionsList.statusReasonConfirm.declined": "Decline request",
  "intake.submissionsList.statusReasonConfirm.waitlisted": "Waitlist request",
  "intake.submissionsList.statusReasonConfirm.cancelled": "Cancel request",
  "intake.submissionsList.empty": "No requests match these filters.",
  "intake.submissionsList.emptyUnfiltered": "You don't have any requests yet.",
  "intake.submissionsList.accessDeniedTitle": "You don't have access to Requests",
  "intake.submissionsList.accessDeniedDescription":
    "Your role doesn't include viewing requests. Ask a workspace admin if you need access.",
  "intake.submissionsList.actionsMenu": "Actions for {name}",
  "intake.submissionsList.delete": "Delete",
  "intake.submissionsList.confirmDeleteTitle": "Delete request?",
  "intake.submissionsList.confirmDeleteDescription":
    "“{name}” will be permanently removed. This cannot be undone.",
  "intake.submissionsList.deleteSuccess": "Request deleted.",
  "intake.submissionsList.bulkDeleteTitle": "Delete {count} requests?",
  "intake.submissionsList.bulkDeleteDescription":
    "{count} requests will be permanently removed. This cannot be undone.",
  "intake.submissionsList.bulkDeleteSuccess": "{count} requests deleted.",
  "intake.submissionsList.layout.list": "Table",
  "intake.submissionsList.layout.board": "Board",

  // Intake — board view
  "intake.board.moveFailed": "Couldn't move the request",
  "intake.board.loadingMore": "Loading more…",
  "intake.board.statusReasonTitle.declined": "Decline request",
  "intake.board.statusReasonTitle.waitlisted": "Waitlist request",
  "intake.board.statusReasonTitle.cancelled": "Cancel request",
  "intake.board.statusReasonDescription.declined":
    "Choose a reason before moving this card to {status}.",
  "intake.board.statusReasonDescription.waitlisted":
    "Choose at least one reason before moving this card to {status}.",
  "intake.board.statusReasonDescription.cancelled":
    "Choose a reason before moving this card to {status}.",
  "intake.board.statusReasonConfirm.declined": "Move to Declined",
  "intake.board.statusReasonConfirm.waitlisted": "Move to Waitlisted",
  "intake.board.statusReasonConfirm.cancelled": "Move to Cancelled",

  // Intake — workflow editor
  "intake.workflow.tab": "Workflow",
  "intake.workflow.builder.stepNavLabel": "Workflow stages",
  "intake.workflow.builder.node.page": "Page",
  "intake.workflow.builder.node.review": "Review",
  "intake.workflow.builder.node.branch": "Branch",
  "intake.workflow.builder.node.changeOwner": "Change Owner",
  "intake.workflow.builder.node.end": "End",
  "intake.workflow.builder.node.selectReviewers": "Select reviewers",
  "intake.workflow.builder.node.selectStatus": "Select status",
  // Placeholder in the Branch card body when no condition is authored yet (#2419).
  "intake.workflow.builder.node.setCondition": "Set condition",
  // One row of a Branch card's condition summary (NG-1056), derived from the
  // persisted conditions. Names the FIELD the branch turns on, not the operator
  // or the answer: the panel shows those in full, and the wire cannot even name
  // the answer for a Request Category / Program / Goal condition, which stores an
  // entity id. The first row is labelled; each later one carries the Branch's own
  // combinator. Whole-row templates, so a locale can reorder the parts.
  "intake.workflow.builder.node.branchClause.first": "Condition on {field}",
  "intake.workflow.builder.node.branchClause.and": "and {field}",
  "intake.workflow.builder.node.branchClause.or": "or {field}",
  // A condition whose source field cannot be named: a legacy bank-field condition
  // references no Page element, so the row still says a condition is set.
  "intake.workflow.builder.node.branchClause.unnamedField": "a field",
  "intake.workflow.builder.node.setOwnerRouting": "Set owner",
  "intake.workflow.builder.node.changeOwnerSummary.rules_one": "{count} rule",
  "intake.workflow.builder.node.changeOwnerSummary.rules_other": "{count} rules",
  "intake.workflow.builder.node.changeOwnerSummary.owners_one": "{count} owner",
  "intake.workflow.builder.node.changeOwnerSummary.owners_other": "{count} owners",
  "intake.workflow.builder.node.renameLabel": "Stage name",
  // The rename affordance (NG-1142). Rename used to fire from a click on the name
  // itself, which had to stop propagation so it did not also open the card — and
  // that button is ``flex-1``, so on a short card it covered the middle and
  // swallowed the click that should have opened the editor. It is its own small
  // target now, named for its stage so a canvas of cards does not carry N buttons
  // with one label.
  "intake.workflow.builder.node.rename": "Rename {name}",
  "intake.workflow.builder.node.actions": "Stage actions",
  "intake.workflow.builder.node.previewFromHere": "Preview from here",
  "intake.workflow.builder.node.delete": "Delete stage",
  "intake.workflow.builder.node.dragHandle": "Drag to reorder step",
  "intake.workflow.builder.node.moveTo": "Move to",
  "intake.workflow.builder.node.moveToMainPath": "Main path",
  "intake.workflow.builder.node.newPage": "New Page",
  "intake.workflow.builder.node.newReview": "New Review",
  "intake.workflow.builder.node.newBranch": "New Branch",
  "intake.workflow.builder.node.newChangeOwner": "New Change Owner",
  "intake.workflow.builder.node.newEnd": "End",
  // Removing a Review decision option whose path has steps (NG-2649): move them to
  // another option's path, or delete them with the option.
  "intake.workflow.builder.removeOption.title": "Remove “{name}”?",
  "intake.workflow.builder.removeOption.body":
    "Requests that get this decision follow its path. Removing the option deletes the steps on that path.",
  "intake.workflow.builder.removeOption.intro":
    "Requests that get this decision follow its path, which has {count, plural, one {# step} other {# steps}} on it. You can move {count, plural, one {that step} other {those steps}} onto another path before the option goes.",
  "intake.workflow.builder.removeOption.choiceLegend":
    "What should happen to the steps on this path?",
  "intake.workflow.builder.removeOption.moveTo": "Move the steps to “{path}”",
  "intake.workflow.builder.removeOption.moveToHint":
    "{count, plural, one {# step moves} other {# steps move}} to the end of the “{path}” path. Requests that get that decision then go through {count, plural, one {it} other {them}}.",
  "intake.workflow.builder.removeOption.deleteAll": "Delete them with the option",
  "intake.workflow.builder.removeOption.deleteAllHint":
    "{count, plural, one {The # step on this path is deleted too.} other {All # steps on this path are deleted too.}}",
  "intake.workflow.builder.removeOption.choosePrompt":
    "Choose an option above to see where each step ends up.",
  "intake.workflow.builder.removeOption.stayingAfter": "Staying (after “{path}”)",
  "intake.workflow.builder.removeOption.thisOption": "The “{name}” decision",
  "intake.workflow.builder.removeOption.confirmMove":
    "Remove option, move {count, plural, one {# step} other {# steps}}",
  "intake.workflow.builder.removeOption.confirm":
    "Remove option and {count, plural, one {# step} other {# steps}}",
  // Deleting a step (NG-2511): keep one Review/Branch path, or confirm a linear step.
  "intake.workflow.builder.deleteStep.title": "Delete “{name}”?",
  "intake.workflow.builder.deleteStep.body.page":
    "This removes the page and everything on it. The steps before and after it stay and join up.",
  "intake.workflow.builder.deleteStep.body.changeOwner":
    "This removes the step and its owner settings. The steps before and after it stay and join up.",
  "intake.workflow.builder.deleteStep.body.empty":
    "There are no steps after it, so nothing else is removed.",
  "intake.workflow.builder.deleteStep.intro.review":
    "This review sends each request down one of {count} paths, depending on the decision. You can keep the steps on one path: they take the review’s place.",
  "intake.workflow.builder.deleteStep.intro.branch":
    "This branch checks a condition and sends each request down one of {count} paths. You can keep the steps on one path: they take the branch’s place.",
  "intake.workflow.builder.deleteStep.choiceLegend": "What should happen to the steps after it?",
  "intake.workflow.builder.deleteStep.keepLane": "Keep the steps after “{path}”",
  "intake.workflow.builder.deleteStep.keepLaneHint.review":
    "{count, plural, one {# step moves} other {# steps move}} up to where this review was. Requests no longer wait for an approval.",
  "intake.workflow.builder.deleteStep.keepLaneHint.branch":
    "{count, plural, one {# step moves} other {# steps move}} up to where this branch was. Every request goes through them, whether or not the condition is met.",
  "intake.workflow.builder.deleteStep.deleteAll": "Delete everything after it",
  "intake.workflow.builder.deleteStep.deleteAllHint":
    "{count, plural, one {The # step after it is deleted too.} other {All # steps after it are deleted too.}}",
  "intake.workflow.builder.deleteStep.choosePrompt":
    "Choose an option above to see what stays and what is removed.",
  "intake.workflow.builder.deleteStep.staying": "Staying",
  "intake.workflow.builder.deleteStep.removing": "Being removed",
  "intake.workflow.builder.deleteStep.nothing": "Nothing",
  "intake.workflow.builder.deleteStep.this.review": "{name} (this review)",
  "intake.workflow.builder.deleteStep.this.branch": "{name} (this branch)",
  "intake.workflow.builder.deleteStep.dragTip":
    "Need steps from more than one path? Cancel, drag them out of the path (or use Move to), then delete.",
  "intake.workflow.builder.deleteStep.discardHint":
    "Deleted the wrong thing? Discard changes in the form’s More actions menu brings back your last published workflow, until you publish again.",
  "intake.workflow.builder.deleteStep.confirmKeep.review":
    "Delete review, keep {count, plural, one {# step} other {# steps}}",
  "intake.workflow.builder.deleteStep.confirmKeep.branch":
    "Delete branch, keep {count, plural, one {# step} other {# steps}}",
  "intake.workflow.builder.deleteStep.confirmAll":
    "Delete {count, plural, one {# step} other {# steps}}",
  "intake.workflow.builder.deleteStep.confirmPending": "Delete",
  "intake.workflow.builder.deleteStep.confirmPage": "Delete page",
  "intake.workflow.builder.deleteStep.confirmStep": "Delete step",
  "intake.workflow.builder.addPicker.pageDesc": "Capture information",
  "intake.workflow.builder.addPicker.reviewDesc": "Require approval",
  "intake.workflow.builder.addPicker.branchDesc": "Condition split",
  "intake.workflow.builder.addPicker.changeOwnerDesc": "Route to an owner",
  "intake.workflow.builder.changeOwner.newWorkflowOnly":
    "Change Owner works only on newer workflows built with the new form builder.",
  "intake.workflow.builder.config.changeOwner.modeLabel": "Assign owner",
  "intake.workflow.builder.config.changeOwner.modeSearch": "Search",
  "intake.workflow.builder.config.changeOwner.modeEmpty": "No options",
  "intake.workflow.builder.config.changeOwner.modeStatic": "To specific owners",
  "intake.workflow.builder.config.changeOwner.modeFromField": "From a field's answer",
  "intake.workflow.builder.config.changeOwner.sourceLabel": "Source field",
  "intake.workflow.builder.config.changeOwner.sourceHint":
    "The answer to this field decides who the request is assigned to.",
  "intake.workflow.builder.config.changeOwner.sourcePlaceholder": "Select a field",
  "intake.workflow.builder.config.changeOwner.sourceSearch": "Search fields",
  "intake.workflow.builder.config.changeOwner.sourceEmpty": "No fields on earlier pages",
  "intake.workflow.builder.config.changeOwner.rulesLabel": "Routing rules",
  // Pinned to the label's right edge: order is a fact about the whole list, and
  // three words there say what a sentence underneath it used to.
  "intake.workflow.builder.config.changeOwner.rulesHint": "First match wins",
  // A rule matches an answer to the source field, so with no source chosen there
  // is nothing to write a rule against yet — say that instead of showing rows
  // whose value control has no options to offer.
  "intake.workflow.builder.config.changeOwner.rulesEmpty":
    "Choose a source field to define routing rules",
  // The two halves of a rule, as row labels: what the answer must equal, and
  // who it goes to. The numbering states which rule is checked first.
  "intake.workflow.builder.config.changeOwner.equalsLabel": "Equals",
  // A multi-value answer is a set, and the runtime matches when the rule's value
  // is among the options chosen — containment, not equality.
  "intake.workflow.builder.config.changeOwner.containsLabel": "Contains",
  "intake.workflow.builder.config.changeOwner.assignLabel": "Assign",
  "intake.workflow.builder.config.changeOwner.ownersLabel": "Owners",
  "intake.workflow.builder.config.changeOwner.valueLabel": "Answer value",
  "intake.workflow.builder.config.changeOwner.valuePlaceholder": "Answer value",
  "intake.workflow.builder.config.changeOwner.addRule": "Add rule",
  "intake.workflow.builder.config.changeOwner.removeRule": "Remove rule",
  "intake.workflow.builder.config.changeOwner.usersLabel": "People",
  "intake.workflow.builder.config.changeOwner.usersPlaceholder": "Select people",
  "intake.workflow.builder.config.changeOwner.usersSearch": "Search people",
  "intake.workflow.builder.config.changeOwner.usersEmpty": "No people found",
  "intake.workflow.builder.config.changeOwner.replaceLabel": "Existing owners",
  "intake.workflow.builder.config.changeOwner.replaceHint":
    "Replace the current owners instead of adding to them",
  "intake.workflow.builder.addPicker.endDesc": "Finish workflow",
  "intake.workflow.builder.page.noTeam": "No team assigned",
  // Placeholder for the "who fills this page" row when no assignee is set yet
  // (#2393 "Validation Errors"). The card shows the resolved assignee once set;
  // the role labels below name the fixed owner/requester/reviewer enum (#2418).
  "intake.workflow.builder.page.selectFormUsers": "Select form users",
  // The fixed first Page's who-fills row (NG-1056). It STATES the rule rather
  // than prompting, because that Page has no assignee pickers to satisfy a
  // prompt with — the long form is ``config.page.firstPageAssigneeHelper``,
  // which this shortens to fit a 320px card row.
  "intake.workflow.builder.page.firstPageFormUsers": "Set by the workflow's teams",
  // An internal Page 2+ naming nobody states the default-filler rule instead of
  // prompting "Select form users" (NG-2617): ``_default_page_fillers(...,
  // internal=True)`` lets the request owners, each owner's managers and every
  // Administrator-tier role holder fill it, and keeps the requester out. A
  // stated rule like ``firstPageFormUsers``, not a prompt — leaving the page
  // unset is a valid choice. Short form to fit a 320px card row.
  "intake.workflow.builder.page.internalPageFormUsers": "Request owners, their managers & admins",
  "intake.workflow.builder.page.assigneeRole.owner": "Request owner",
  "intake.workflow.builder.page.assigneeRole.requester": "Requester",
  "intake.workflow.builder.page.assigneeRole.reviewer": "Reviewer",
  "intake.workflow.builder.page.editTitle": "Edit page",
  "intake.workflow.builder.page.editModalDescription":
    "Edit the fields and configuration for this workflow page.",
  "intake.workflow.builder.page.editModalLoading": "Loading page…",
  "intake.workflow.builder.page.editModalSaving": "Saving…",
  "intake.workflow.builder.page.editModalMissing": "This page is no longer available.",
  // Blank-option-label block in the Edit Page modal — surfaces why close is prevented (#2404/#2412).
  "intake.workflow.builder.page.blankOptionLabel.bannerLabel": "Blank option label",
  "intake.workflow.builder.page.blankOptionLabel.bannerTitle":
    "Add a label to every option before closing this page.",
  "intake.workflow.builder.page.blankOptionLabel.locate": "Go to option {number}",
  // Delete Page — the trash button in the Edit Page modal's Page settings panel (#2395).
  "intake.workflow.builder.page.settings.deletePage": "Delete page",
  "intake.workflow.builder.page.saveBlockedToast":
    "Some components couldn't be saved yet: {names}. Remove or adjust them and try again.",
  "intake.workflow.builder.canvas.label": "Workflow canvas",
  "intake.workflow.builder.canvas.zoomIn": "Zoom in",
  "intake.workflow.builder.canvas.zoomOut": "Zoom out",
  "intake.workflow.builder.canvas.fitView": "Fit to screen",
  "intake.workflow.builder.canvas.addStage": "Add stage",
  "intake.workflow.builder.canvas.addStepAtEnd": "Add step at end",
  "intake.workflow.builder.canvas.ifTrue": "If true",
  "intake.workflow.builder.canvas.ifFalse": "If false",
  "intake.workflow.builder.seed.status.underReview": "Under review",
  "intake.workflow.builder.seed.requestDetails": "Request Details",
  "intake.workflow.builder.seed.ldReview": "L&D Review",
  "intake.workflow.builder.seed.end": "End",
  "intake.workflow.builder.seed.field.requestName": "Request name",
  "intake.workflow.builder.seed.field.requester": "Requester",
  "intake.workflow.builder.seed.field.requestOwner": "Request owner",
  "intake.workflow.builder.seed.field.lineOfBusiness": "Line of Business",
  "intake.workflow.builder.seed.decision.accept": "Accept",
  "intake.workflow.builder.seed.decision.changes": "Changes requested",
  "intake.workflow.builder.seed.decision.deny": "Deny",
  "intake.workflow.builder.condition.logicLabel": "Match",
  "intake.workflow.builder.condition.logicAnd": "All",
  "intake.workflow.builder.condition.logicOr": "Any",
  "intake.workflow.builder.condition.fieldPlaceholder": "Select field",
  "intake.workflow.builder.condition.valuePlaceholder": "Value",
  "intake.workflow.builder.condition.boolPlaceholder": "Select…",
  "intake.workflow.builder.condition.boolTrue": "True",
  "intake.workflow.builder.condition.boolFalse": "False",
  "intake.workflow.builder.condition.invalidOptionValue":
    "This value is not one of the field's options. Pick an option again.",
  "intake.workflow.builder.condition.add": "Add condition",
  "intake.workflow.builder.condition.remove": "Remove condition",
  "intake.workflow.builder.condition.noFields":
    "Add fields to a preceding Page to build conditions.",
  "intake.workflow.builder.condition.op.equals": "equals",
  "intake.workflow.builder.condition.op.notEquals": "does not equal",
  "intake.workflow.builder.condition.op.contains": "contains",
  "intake.workflow.builder.condition.op.gt": "greater than",
  "intake.workflow.builder.condition.op.lt": "less than",
  "intake.workflow.builder.condition.op.gte": "greater than or equal to",
  "intake.workflow.builder.condition.op.lte": "less than or equal to",
  // Publish validation (#2204): surfaced by the route-header "Publish changes"
  // control when the workflow publish returns a 422 incomplete-node report.
  "intake.workflow.builder.publish.error": "Couldn’t publish the workflow. Please try again.",
  "intake.workflow.builder.publish.blocked":
    "This workflow can’t be published yet — some paths are incomplete.",
  "intake.workflow.builder.publish.banner.label": "Publish validation errors",
  "intake.workflow.builder.publish.banner.title":
    "This workflow has validation errors and cannot be published. Fix the items below and try again.",
  // NG-859: the report is a snapshot of the last publish attempt, so once the
  // graph changes under it the banner says so in the past tense instead of
  // continuing to assert blockers it can no longer vouch for. Title carries the
  // fact, hint carries the recourse.
  "intake.workflow.builder.publish.banner.titleStale":
    "These validation errors are from your last publish attempt.",
  "intake.workflow.builder.publish.banner.staleHint":
    "You\u2019ve changed the workflow since \u2014 publish again to re-check.",
  "intake.workflow.builder.publish.banner.dismiss": "Dismiss",
  "intake.workflow.builder.publish.banner.itemWithPath": "{name} — {path} path",
  // NG-859: one message per required-field blocker code, each naming the field
  // rather than only the step and lane. Why these exist, and why the field
  // labels are copied here instead of composed from the config panel's own keys,
  // is in the ISSUE_MESSAGES table in workflow-validation-banner.tsx.
  "intake.workflow.builder.publish.banner.itemIncompletePathMain":
    "{name} \u2014 the main path needs a next step to finish",
  "intake.workflow.builder.publish.banner.itemIncompletePathTrue":
    "{name} \u2014 the True path needs a next step to finish",
  "intake.workflow.builder.publish.banner.itemIncompletePathFalse":
    "{name} \u2014 the False path needs a next step to finish",
  "intake.workflow.builder.publish.banner.itemIncompletePathLane":
    "{name} \u2014 the \u201c{path}\u201d path needs a next step to finish",
  "intake.workflow.builder.publish.banner.itemReviewNoDecisionOptions":
    "{name} \u2014 add at least one decision option",
  "intake.workflow.builder.publish.banner.itemMissingReviewer":
    "{name} — \u201cReviewers\u201d needs at least one person",
  "intake.workflow.builder.publish.banner.itemDecisionOptionMissingStatus":
    "{name} — set \u201cChanges status to\u201d for the {path} decision",
  "intake.workflow.builder.publish.banner.itemMissingTrueStatus":
    "{name} — choose \u201cStatus if true\u201d",
  "intake.workflow.builder.publish.banner.itemMissingFalseStatus":
    "{name} — choose \u201cStatus if false\u201d",
  "intake.workflow.builder.publish.banner.itemMissingPathStatusNoPath":
    "{name} — a branch path is missing its status",
  "intake.workflow.builder.publish.banner.itemChangeOwnerMissingSource":
    "{name} — choose a \u201cSource field\u201d",
  "intake.workflow.builder.publish.banner.itemChangeOwnerInvalidReference":
    "{name} — its \u201cSource field\u201d must come before this step",
  "intake.workflow.builder.publish.banner.itemChangeOwnerNoRules":
    "{name} — add at least one routing rule",
  "intake.workflow.builder.publish.banner.itemChangeOwnerRuleNoTargets":
    "{name} — the \u201c{path}\u201d rule needs at least one owner",
  "intake.workflow.builder.publish.banner.itemChangeOwnerRuleNoTargetsUnnamed":
    "{name} — a routing rule needs at least one owner",
  "intake.workflow.builder.publish.banner.itemBranchNoConditions":
    "{name} \u2014 add at least one condition, or it always takes the False path",
  "intake.workflow.builder.publish.banner.itemBranchConditionBlankValue":
    "{name} \u2014 {path} needs a value to compare against",
  "intake.workflow.builder.publish.banner.itemInvalidReopenDest":
    "{name} — \u201cIf re-opened, return to\u201d can\u2019t point at another End step",
  // #4154. Not a path problem, so it states the actual fix rather than falling
  // through to the bare node name under the incomplete-paths headline.
  "intake.workflow.builder.publish.banner.itemMissingProjectTemplate":
    "{name} — choose a project template for auto-create",
  "intake.workflow.builder.publish.banner.itemChangeOwnerRuleBlankValue":
    "{name} — {path} needs an answer value",
  "intake.workflow.builder.publish.banner.itemChangeOwnerRuleInvalidOption":
    "{name} — {path} uses {value}, which is not one of the source field's options",
  // ``visible_if`` blockers: ``path`` is the dependent field's (or page's)
  // label, not a lane, so the generic "{path} path" phrasing read as a branch
  // that doesn't exist on the canvas.
  "intake.workflow.builder.publish.banner.itemVisibleIfMissingController":
    "{name} — “{path}” has a visibility condition that points to a field that no longer exists or isn’t filled in yet",
  "intake.workflow.builder.publish.banner.itemVisibleIfInvalidReference":
    "{name} — “{path}” is shown based on “{controller}”, which must come before it",
  "intake.workflow.builder.publish.banner.locate": "Show {name} on the canvas",
  // Config panel (M3 #2091).
  "intake.workflow.builder.config.paneLabel": "Step settings",
  "intake.workflow.builder.config.openLabel": "Open settings for {name}",
  "intake.workflow.builder.config.close": "Close",
  "intake.workflow.builder.config.delete": "Delete step",
  // Clear row on the optional Status pickers (Page, End) — NG-1017.
  "intake.workflow.builder.config.statusClear": "No status",
  "intake.workflow.builder.config.stepName": "Step name",
  "intake.workflow.builder.config.stubBody": "Settings for this step are coming soon.",
  "intake.workflow.builder.config.page.whoFills": "Who fills this page",
  "intake.workflow.builder.config.page.assigneeTypeLabel": "Assignee type",
  "intake.workflow.builder.config.page.assigneeTypePlaceholder": "Select who fills this page",
  "intake.workflow.builder.config.page.assigneeType.team": "Team",
  "intake.workflow.builder.config.page.assigneeType.role": "Role",
  "intake.workflow.builder.config.page.assigneeType.user": "User",
  "intake.workflow.builder.config.page.assignment": "Assignment",
  "intake.workflow.builder.config.page.assigneeRoleAria": "Assignee role",
  "intake.workflow.builder.config.page.assigneeRolePlaceholder": "Select a role",
  "intake.workflow.builder.config.page.teamPlaceholder": "Select a team",
  "intake.workflow.builder.config.page.teamSearch": "Search teams",
  "intake.workflow.builder.config.page.teamEmpty": "No teams found",
  "intake.workflow.builder.config.page.teamAria": "Assign to team",
  "intake.workflow.builder.config.page.userPlaceholder": "Select a user",
  "intake.workflow.builder.config.page.userSearch": "Search users",
  "intake.workflow.builder.config.page.userEmpty": "No users found",
  "intake.workflow.builder.config.page.userAria": "Assign to user",
  // #2696: "Who fills this page" is two independent multi-selects (available
  // teams + available users), shown together on Pages 2+. The fixed first Page
  // shows no pickers — access there is driven by the workflow's teams.
  // #3311: labelled "Teams" / "Users" (the surrounding "Access" section already
  // says these are who fills the page), matching the mock-up.
  "intake.workflow.builder.config.page.availableTeamsLabel": "Teams",
  "intake.workflow.builder.config.page.availableUsersLabel": "Users",
  "intake.workflow.builder.config.page.availableTeamsPlaceholder": "Select teams",
  "intake.workflow.builder.config.page.availableUsersPlaceholder": "Select users",
  "intake.workflow.builder.config.page.availableTeamsHelper":
    "Selecting a team limits editing to that team's members, and notifies the team lead when a request reaches this page.",
  "intake.workflow.builder.config.page.availableUsersHelper":
    "Selecting specific users limits editing to just those users, who are notified when a request reaches this page.",
  "intake.workflow.builder.config.page.firstPageAssigneeHelper":
    "Access to the first page is driven by the workflow's teams.",
  "intake.workflow.builder.config.page.statusSection": "Status",
  // #2395 renamed this from "Status when complete" to "Status" for the Page
  // settings panel in the Edit Page modal.
  "intake.workflow.builder.config.page.statusLabel": "Status",
  "intake.workflow.builder.config.page.statusPlaceholder": "Select a status",
  "intake.workflow.builder.config.page.statusSearch": "Search statuses",
  "intake.workflow.builder.config.page.statusEmpty": "No statuses found",
  "intake.workflow.builder.config.page.statusAria": "Status",
  // #3311: the always-on helper under the Status control (a first / derived Page
  // swaps in its own helper below).
  "intake.workflow.builder.config.page.statusHelper":
    "This is the status that will be assigned to the request during this stage.",
  "intake.workflow.builder.config.page.firstPageStatusHelper":
    "Status is applied when the next stage is reached.",
  // #2406: a Page opening a Review decision-option lane inherits that option's
  // status, so the control is pre-filled + read-only.
  "intake.workflow.builder.config.page.reviewBranchStatusHelper":
    "Status is set by the Review decision “{option}” and can't be changed here.",
  // #2699 S2: author opt-in for the per-step notification. When on, the runtime
  // (S3 #2719) notifies this page's audience as the request lands on it.
  "intake.workflow.builder.config.page.notifyLabel": "Notify users",
  "intake.workflow.builder.config.page.notifyHelper":
    "Notify the people responsible for this page when a request reaches it.",
  "intake.workflow.builder.config.page.notifyRecipientsOwners": "Notifies the request owners.",
  "intake.workflow.builder.config.page.notifyRecipientsUsers": "Notifies {names}.",
  "intake.workflow.builder.config.page.notifyRecipientsTeams":
    "Notifies the {count, plural, one {lead} other {leads}} of {names}.",
  "intake.workflow.builder.config.page.notifyRecipientsBoth":
    "Notifies {users}, and the {count, plural, one {lead} other {leads}} of {teams}.",
  "intake.workflow.builder.config.page.notifyRecipientsNone":
    "The people or teams selected above are no longer available, so nobody will be notified.",
  "intake.workflow.builder.config.page.notificationLabel": "Notification message",
  "intake.workflow.builder.config.page.notificationPlaceholder":
    "Set a custom message for notified users",
  "intake.workflow.builder.config.page.fieldsSection": "Fields on this page",
  "intake.workflow.builder.config.page.noFields": "No fields yet",
  "intake.workflow.builder.config.page.noFieldsHint": "Open Edit page to add fields to this page.",
  "intake.workflow.builder.config.page.editPage": "Edit page",
  "intake.workflow.builder.config.page.removeField": "Remove {name}",
  "intake.workflow.builder.config.page.unknownField": "Unknown field",
  // Review config panel (M3 #2091, PRD v2 §6.4.3).
  "intake.workflow.builder.config.review.statusLabel": "Status (optional)",
  "intake.workflow.builder.config.review.statusPlaceholder": "Leave unchanged",
  "intake.workflow.builder.config.review.statusSearch": "Search statuses…",
  "intake.workflow.builder.config.review.statusEmpty": "No statuses",
  "intake.workflow.builder.config.review.statusAria": "Review status",
  // #2956: a Review that opens a decision-option lane inherits that option's
  // status, so the control is pre-filled + read-only (mirrors the Page helper).
  "intake.workflow.builder.config.review.reviewBranchStatusHelper":
    "Status is set by the Review decision “{option}” and can't be changed here.",
  "intake.workflow.builder.config.review.reviewersLabel": "Reviewers",
  "intake.workflow.builder.config.review.reviewersPlaceholder": "Select reviewers",
  "intake.workflow.builder.config.review.reviewersSearch": "Search people…",
  "intake.workflow.builder.config.review.reviewersEmpty": "No people",
  "intake.workflow.builder.config.review.reviewersRequired": "Select at least one reviewer.",
  "intake.workflow.builder.config.review.decisionsLabel": "Decision options",
  "intake.workflow.builder.config.review.decisionsHelper":
    "Each decision becomes a branch below this step.",
  "intake.workflow.builder.config.review.decisionNameAria": "Decision option name",
  "intake.workflow.builder.config.review.decisionStatusLabel": "Changes status to",
  "intake.workflow.builder.config.review.decisionStatusAria": "Changes status to ({name})",
  "intake.workflow.builder.config.review.decisionStatusPlaceholder": "Choose a status",
  "intake.workflow.builder.config.review.decisionStatusRequired": "Select a status.",
  "intake.workflow.builder.config.review.removeDecision": "Remove decision option ({name})",
  "intake.workflow.builder.config.review.addDecision": "Add decision option",
  "intake.workflow.builder.config.review.notificationLabel": "Notification message",
  "intake.workflow.builder.config.review.notificationPlaceholder":
    "Message sent to reviewers when this step is reached.",
  // End config panel (M3 #2091, §6.4.5).
  "intake.workflow.builder.config.end.statusTitle": "Status",
  "intake.workflow.builder.config.end.statusLabel": "Status on completion",
  "intake.workflow.builder.config.end.statusHelper":
    "The status a request is set to when it terminates at this step.",
  // #2956: an End that opens a decision-option lane inherits that option's
  // status, so the control is pre-filled + read-only (mirrors the Page helper).
  "intake.workflow.builder.config.end.reviewBranchStatusHelper":
    "Status is set by the Review decision “{option}” and can't be changed here.",
  "intake.workflow.builder.config.end.statusPlaceholder": "Select status",
  "intake.workflow.builder.config.end.statusSearch": "Search statuses…",
  "intake.workflow.builder.config.end.statusEmpty": "No statuses",
  "intake.workflow.builder.config.end.completionTitle": "On completion",
  "intake.workflow.builder.config.end.completionLabel": "When this step is reached",
  "intake.workflow.builder.config.end.completionHelper":
    "Choose whether a project is created automatically or an admin creates it.",
  "intake.workflow.builder.config.end.completionAuto": "Auto-create project",
  "intake.workflow.builder.config.end.completionManual": "Manual — admin creates",
  // Auto-create project template (#4154). The "required" helper replaces the
  // guidance one while unset, because publish is blocked until it is chosen.
  "intake.workflow.builder.config.end.projectTemplateLabel": "Project template",
  "intake.workflow.builder.config.end.projectTemplateHelper":
    "The created project is built from this template, with the request's answers carried into it.",
  "intake.workflow.builder.config.end.projectTemplateRequired":
    "Choose a project template. This workflow can't be published until you do.",
  "intake.workflow.builder.config.end.projectTemplatePlaceholder": "Select a project template",
  "intake.workflow.builder.config.end.projectTemplateSearch": "Search project templates…",
  "intake.workflow.builder.config.end.projectTemplateEmpty": "No project templates found.",
  "intake.workflow.builder.config.end.reopenTitle": "Re-opening",
  "intake.workflow.builder.config.end.reopenLabel": "If re-opened, return to",
  "intake.workflow.builder.config.end.reopenHelper":
    "The step a re-opened request returns to. Leave empty for no destination.",
  "intake.workflow.builder.config.end.reopenPlaceholder": "Select a step",
  "intake.workflow.builder.config.end.reopenSearchPlaceholder": "Search steps...",
  "intake.workflow.builder.config.end.reopenEmpty": "No steps found.",
  "intake.workflow.builder.config.branch.trueStatus": "Status if true",
  "intake.workflow.builder.config.branch.falseStatus": "Status if false",
  "intake.workflow.builder.config.branch.statusPlaceholder": "Select a status",
  "intake.workflow.builder.config.branch.statusSearch": "Search statuses",
  "intake.workflow.builder.config.branch.statusEmpty": "No statuses found",
  "intake.workflow.builder.config.branch.statusRequired": "A status is required.",
  "intake.workflow.builder.config.branch.conditionsTitle": "Conditions",
  "intake.workflow.title": "Workflow",
  "intake.workflow.description":
    "Define which status transitions are allowed and who can perform them. Statuses with no rules allow all transitions.",
  "intake.workflow.reset_to_defaults": "Reset to defaults",
  "intake.workflow.reset_confirm_title": "Reset workflow to defaults?",
  "intake.workflow.reset_confirm_description":
    "This will remove all custom transition rules and approvers. All status transitions will become unrestricted.",
  "intake.workflow.reset_success": "Workflow reset to defaults.",
  "intake.workflow.reset_error": "Failed to reset workflow.",
  "intake.workflow.fetch_error": "Failed to load workflow configuration.",
  "intake.workflow.statuses_fetch_error": "Failed to load workflow statuses.",
  "intake.workflow.create_allowed": "Allowed",
  "intake.workflow.create_blocked": "Blocked",
  "intake.workflow.create_label": "Create:",
  "intake.workflow.no_rules": "No rules configured — all transitions allowed",
  "intake.workflow.movers_label": "Moved by:",
  "intake.workflow.movers_everyone": "Everyone",
  "intake.workflow.mover_search_placeholder": "Search members…",
  "intake.workflow.require_approval_label": "Require approval",
  "intake.workflow.approvers_label": "Approvers:",
  "intake.workflow.approvers_none": "None (anyone)",
  "intake.workflow.approvers_count": "{count} users configured",
  "intake.workflow.required_approvals": "Requires: {count} of {total}",
  "intake.workflow.rejection_state": "Rejection:",
  "intake.workflow.configure": "Configure",
  "intake.workflow.configure_title": "Configure: {state}",
  "intake.workflow.configure_description":
    "Set which statuses this status can transition to and configure approval requirements.",
  "intake.workflow.allow_creation": "Allow project creation in this status",
  "intake.workflow.allow_creation_hint":
    "When enabled, new projects can be created directly in this status.",
  "intake.workflow.transition_added": "Transition added.",
  "intake.workflow.transition_removed": "Transition removed.",
  "intake.workflow.transition_update_error": "Failed to update transition.",
  "intake.workflow.update_success": "Workflow updated.",
  "intake.workflow.update_error": "Failed to update workflow.",
  "intake.workflow.required_approvals_label": "Required approvals:",
  "intake.workflow.approvals_all": "All",
  "intake.workflow.approval_state_label": "On approval:",
  "intake.workflow.approval_none": "Transition target",
  "intake.workflow.rejection_state_label": "On rejection:",
  "intake.workflow.rejection_none": "None",
  "intake.workflow.approver_search_placeholder": "Search members…",
  "intake.workflow.approver_no_members": "No members found",
  "intake.workflow.approver_unknown": "Unknown user",
  "intake.workflow.unknown_state": "Unknown status",
  "intake.workflow.activity_title": "Workflow Activity",
  "intake.workflow.activity_empty": "No activity yet",
  "intake.workflow.activity_created_transition": '{actor} added transition to "{state}"',
  "intake.workflow.activity_removed_transition": '{actor} removed transition to "{state}"',
  "intake.workflow.activity_updated_creation": "{actor} {action} project creation",
  "intake.workflow.activity_updated_approvers": "{actor} updated approvers",
  "intake.workflow.activity_reset": "{actor} reset workflow to defaults",
  "intake.workflow.activity_enabled": "enabled",
  "intake.workflow.activity_disabled": "disabled",
  "intake.workflow.activity_unknown_actor": "Unknown user",
  "intake.workflow.activity_updated_transition": "{actor} updated transition",
  "intake.workflow.activity_generic": "{actor} {verb} {field}",

  // Intake — workflow runtime (requester-facing)
  "intake.workflow.transition_blocked_title": "Transition Not Allowed",
  "intake.workflow.transition_blocked_description":
    "The transition from {from} to {to} is not allowed by the current workflow rules.",
  "intake.workflow.allowed_transitions": "Allowed transitions:",
  "intake.workflow.contact_admin": "Contact a workspace admin to update workflow rules.",
  "intake.workflow.approval_required_title": "Approval Required",
  "intake.workflow.approval_required_description":
    "Moving from {from} to {to} requires approval before the transition can proceed.",
  "intake.workflow.approval_request_to": "This will send an approval request to:",
  "intake.workflow.required_approvals_count": "Required approvals: {count} of {total}",
  "intake.workflow.required_approvals_all": "Required approvals: All of {total}",
  "intake.workflow.request_approval_button": "Request Approval",
  "intake.workflow.approval_request_sent": "Approval request sent",
  "intake.workflow.approval_request_error": "Failed to send approval request",
  "intake.workflow.pending_approval": "Pending Approval",
  "intake.workflow.review_required": "Approval required",
  "intake.reviewPending.pending": "Review pending",
  "intake.reviewPending.awaitingYou": "Awaiting your review",
  "intake.reviewPending.reviewersTitle": "Awaiting review",
  "intake.reviewPending.reviewersDescription":
    "Any one of these reviewers can decide this request.",
  "intake.reviewPending.reviewStep": "Review step: {name}",
  "intake.reviewPending.noReviewers": "No reviewers are assigned to this review.",
  "intake.reviewPending.reviewersLabel": "Reviewers",
  "intake.workflow.pending_approval_title": "Pending Approval",
  "intake.workflow.approval_requested_title": "Approval Requested",
  "intake.workflow.current_approvals": "Current approvals: {current} of {required} required",
  "intake.workflow.approver_status_pending": "Pending",
  "intake.workflow.approver_status_approved": "Approved",
  "intake.workflow.approver_status_rejected": "Rejected",
  "intake.workflow.already_voted": "You have already {action} this transition.",
  "intake.workflow.comment_optional": "Comment (optional)",
  "intake.workflow.approve_button": "Approve",
  "intake.workflow.reject_button": "Reject",
  "intake.workflow.approval_success": "Approval submitted",
  "intake.workflow.rejection_success": "Rejection submitted",
  "intake.workflow.approval_error": "Failed to submit approval",
  "intake.workflow.rejection_error": "Failed to submit rejection",
  "intake.workflow.dismiss": "Dismiss",
  "intake.workflow.you": "you",

  // Plan work: field editors and the cost breakdown sheet (NG-2580)
  "intake.planWork.editors.select_template": "Select a template",
  "intake.planWork.editors.switch_template": "Switch template",
  "intake.planWork.editors.no_templates": "No templates available",
  "intake.planWork.editors.no_estimate": "No estimate",
  "intake.planWork.editors.avg_hours": "Avg {hours} hrs",
  "intake.planWork.editors.unknown_requester": "Unknown",
  "intake.planWork.editors.change_requester": "Change requester",
  "intake.planWork.editors.loading_members": "Loading members…",
  "intake.planWork.editors.remove_qualification": "Remove {name}",
  "intake.planWork.editors.add_qualification": "+ Add qualification",
  "intake.planWork.editors.search_qualifications": "Search qualifications…",
  "intake.planWork.editors.date_not_set": "Not set",
  "intake.planWork.editors.edit_target_date": "Edit target launch date",
  "intake.planWork.editors.edit_end_date": "Edit end date",
  "intake.planWork.editors.edit_start_date": "Edit start date",
  "intake.planWork.costBreakdown.title": "Estimated cost breakdown",
  "intake.planWork.costBreakdown.description":
    "Each role's planned hours priced at the rate that applies to it — a staffed member's own rate where someone is assigned, otherwise the role's rate.",
  "intake.planWork.costBreakdown.by_role": "By role",
  "intake.planWork.costBreakdown.column_role": "Role",
  "intake.planWork.costBreakdown.column_hours": "Hours",
  "intake.planWork.costBreakdown.column_rate": "Rate",
  "intake.planWork.costBreakdown.column_cost": "Cost",
  "intake.planWork.costBreakdown.empty": "No priced work in this template yet.",
  "intake.planWork.costBreakdown.no_role": "No role set",
  "intake.planWork.costBreakdown.per_hour": "{rate}/h",
  "intake.planWork.costBreakdown.total": "Total ({hours} hrs)",
  "intake.planWork.costBreakdown.basis_member_average": "Average of {count} staffed members' rates",
  "intake.planWork.costBreakdown.basis_member": "Staffed member's rate",
  "intake.planWork.costBreakdown.basis_role": "Role rate — nobody staffed yet",
  "intake.planWork.costBreakdown.basis_unpriced": "No job role set on these items",
  "intake.planWork.costBreakdown.hours_scaled":
    "Hours are your planned effort spread across the roles the selected template uses, keeping their relative split.",
  "intake.planWork.costBreakdown.hours_from_template":
    "Hours come from the selected project template's work items.",

  "intake.planWork.gantt.search_placeholder": "Search projects…",
  "intake.planWork.gantt.this_project": "This project",
  "intake.planWork.gantt.launch_marker": "Launch {date}",
  "intake.planWork.gantt.this_over_duration": "Longer than the template's {days}-day duration",
  "intake.planWork.gantt.priority_tooltip": "{tier} · {points} pts",
  "intake.planWork.gantt.priority_unranked": "Unranked",
  "intake.planWork.gantt.priority_loading": "Loading the score breakdown…",
  "intake.planWork.gantt.open_project": "Open project",
  "intake.planWork.gantt.empty": "No active projects are scheduled yet.",
  "intake.planWork.gantt.empty_search": "No projects match your search.",
  "intake.planWork.gantt.private_project": "Private project",
  // Plan work: the page's loading, error and create states, and the accept dialog (NG-2580)
  "intake.planWork.page.loading": "Loading planning data…",
  "intake.planWork.page.loading_aria": "Loading planning data",
  "intake.planWork.page.denied_title": "You don't have permission to plan work on this request.",
  "intake.planWork.page.error_title": "Could not load planning data",
  "intake.planWork.page.denied_description": "Contact your workspace admin if you need access.",
  "intake.planWork.page.error_description": "Please try again.",
  "intake.planWork.page.retry": "Retry",
  "intake.planWork.page.accept_and_create": "Accept & create project",
  "intake.planWork.page.create": "Create project",
  "intake.planWork.page.create_blocked_rejected":
    "A project can't be created from a rejected request.",
  "intake.planWork.page.create_blocked_unaccepted":
    "A project can't be created until the request is accepted.",
  "intake.planWork.page.create_blocked_unreachable":
    "This request's workflow doesn't allow a move to an accepted status from its current status.",
  "intake.planWork.page.accept_error":
    "Couldn't accept the request — check the workflow allows this status.",
  "intake.planWork.acceptDialog.title": "Accept request",
  "intake.planWork.acceptDialog.description":
    "Pick the status the request moves to — the project is created right after.",
  "intake.planWork.acceptDialog.status_aria": "Accepted status",
  "intake.planWork.acceptDialog.accepting": "Accepting…",
  "intake.planWork.acceptDialog.confirm": "Accept & create project",
  "intake.planWork.acceptDialog.needs_approval": "Needs approval",
  "intake.planWork.acceptDialog.request_approval": "Request approval",
  "intake.planWork.gantt.empty_filtered": "No projects match your filters.",
  "intake.planWork.gantt.filters_heading": "Filters",
  "intake.planWork.gantt.filter_team": "Team",
  "intake.planWork.gantt.filter_project_lead": "Project lead",
  "intake.planWork.gantt.filter_project_type": "Project type",
  "intake.planWork.gantt.filter_project_status": "Project status",
  "intake.planWork.gantt.filter_no_matches": "No matches.",
  "intake.planWork.gantt.group_by": "Group",
  "intake.planWork.gantt.group_none": "No grouping",
  "intake.planWork.gantt.group_project_type": "Project type",
  // Plan work: the "Plan your project" card (NG-2580)
  "intake.planWork.card.title": "Plan your project",
  "intake.planWork.card.status_on_track": "On track",
  "intake.planWork.card.status_no_end_date": "No end date",
  "intake.planWork.card.status_no_target_date": "No target launch date",
  "intake.planWork.card.status_will_miss": "Will miss deadline",
  "intake.planWork.card.status_at_risk": "At risk",
  "intake.planWork.card.project_template": "Project template",
  "intake.planWork.card.required_roles": "Required roles",
  "intake.planWork.card.roles_need_template": "Select a template to see required roles",
  "intake.planWork.card.roles_none": "This template defines no required roles",
  "intake.planWork.card.role_at_risk_aria": "{role}: no available capacity in the planned dates",
  "intake.planWork.card.role_at_risk_teams":
    "No one with this role on the selected delivery {count, plural, one {team} other {teams}} has capacity between the planned start and end dates.",
  "intake.planWork.card.role_at_risk":
    "No one with this role has capacity between the planned start and end dates.",
  "intake.planWork.card.role_hours_aria": "Edit {role} hours",
  "intake.planWork.card.delivery_team": "Delivery team",
  "intake.planWork.card.delivery_team_placeholder": "Select a team",
  "intake.planWork.card.project_lead": "Project lead",
  "intake.planWork.card.project_lead_placeholder": "Not set",
  "intake.planWork.card.estimated_duration": "Estimated duration",
  "intake.planWork.card.duration_need_template": "Select a template",
  "intake.planWork.card.duration_unscheduled": "No scheduled work items on this template",
  "intake.planWork.card.duration_days": "days",
  "intake.planWork.card.duration_template": "{name} template",
  "intake.planWork.card.target_launch_date": "Target launch date",
  "intake.planWork.card.project_dates": "Project dates",
  "intake.planWork.card.weeks_total":
    "{weeks, plural, one {{weeksText} week} other {{weeksText} weeks}} total",
  "intake.planWork.card.requested_by": "Requested by",
  "intake.planWork.card.estimated_effort": "Estimated effort",
  "intake.planWork.card.edit_effort_aria": "Edit estimated effort",
  "intake.planWork.card.estimated_cost": "Estimated cost",
  "intake.planWork.card.cost_basis_member": "Based on the selected members' rates",
  "intake.planWork.card.cost_basis_role": "Based on role rates — no members selected yet",
  "intake.planWork.card.required_qualifications": "Required qualifications",
  "intake.planWork.card.project_window": "Project window",
  "intake.planWork.card.required_marker": "Required",

  "intake.submissionDetail.errorTitle": "Submission unavailable",
  "intake.submissionDetail.loadError": "Failed to load submission.",
  "intake.submissionDetail.definitionUnavailableTitle": "This request's form can't be shown",
  "intake.submissionDetail.definitionUnavailableDescription":
    "We couldn't read the form this request was submitted with, so its answers can't be displayed. Try reloading; if it keeps happening, contact support.",
  "intake.submissionDetail.priorityScoreHeading": "Priority score",
  "intake.submissionDetail.priorityScorePoints": "{count} pts",
  "intake.submissionDetail.priorityScoreLinePoints": "+{count} pts",
  "intake.submissionDetail.priorityScoreTotal": "Total",
  "intake.submissionDetail.tabRequestDetails": "Details",
  "intake.submissionDetail.tabPlanWork": "Plan work",
  "intake.submissionDetail.tabLinkedProjects": "Linked Projects",
  "intake.submissionDetail.tabsAria": "Request sections",
  "intake.submissionDetail.pageNav.pillsLabel": "Form pages",
  "intake.submissionDetail.pageNav.pageFallback": "Page {index}",
  "intake.submissionDetail.pageNav.back": "Back",
  "intake.submissionDetail.pageNav.next": "Next",
  "intake.submissionDetail.pageNav.noPages": "This request has no pages to show.",
  "intake.submissionDetail.actions.menu": "More actions",
  "intake.submissionDetail.delete.action": "Delete request",
  "intake.submissionDetail.delete.title": "Delete this request?",
  "intake.submissionDetail.delete.description":
    "This will remove the request and its data. Linked projects are not deleted.",
  "intake.submissionDetail.delete.confirm": "Delete request",
  "intake.submissionDetail.delete.success": "Request deleted.",
  "intake.submissionDetail.delete.error": "Couldn't delete the request. Try again.",
  "intake.submissionDetail.archive.archiveAction": "Archive request",
  "intake.submissionDetail.archive.unarchiveAction": "Unarchive request",
  "intake.submissionDetail.archive.archived": "Request archived.",
  "intake.submissionDetail.archive.unarchived": "Request unarchived.",
  "intake.submissionDetail.archive.error": "Couldn't update the request. Try again.",
  "intake.submissionDetail.headerMeta": "Form used: {form} · Submitted by {name} on {date}",
  "intake.submissionDetail.people.requester": "Requester",
  "intake.submissionDetail.people.requesterPlaceholder": "Select requester",
  "intake.submissionDetail.people.owners": "Request owners",
  "intake.submissionDetail.people.ownersPlaceholder": "Select owners",
  "intake.submissionDetail.people.ownersMinOne": "A request needs at least one owner.",
  "intake.submissionDetail.people.searchMembers": "Search members…",
  "intake.submissionDetail.people.noMembers": "No members found.",
  "intake.submissionDetail.people.unassigned": "Unassigned",
  "intake.submissionDetail.archive.badge": "Archived",
  "intake.submissionDetail.reviewLock.title": "This request is waiting on a review",
  "intake.submissionDetail.reviewLock.description":
    "Only its status can be changed until the reviewer decides. Comments stay open.",
  // The request's SLA state. The date is the SLA's own due date, so the badge
  // says which side of the target the request is on AND when the target is —
  // `days_remaining` is ceil'd and cannot be turned back into a date (#3629).
  "intake.submissionDetail.slaWithinDue": "Within SLA · Due {date}",
  "intake.submissionDetail.slaWithin": "Within SLA",
  "intake.submissionDetail.slaBreachedDue": "SLA breached · Due {date}",
  "intake.submissionDetail.slaBreached": "SLA breached",
  // No date while paused: `due_at` is stale until the clock resumes.
  "intake.submissionDetail.slaPaused": "SLA on hold",
  "intake.submissionDetail.slaNameTooltip": "SLA: {name}",
  "intake.submissionDetail.saveError": "Could not save change. Please try again.",
  "intake.submissionDetail.saveSuccess": "Saved",
  "intake.submissionDetail.comments.title": "Comments",
  "intake.submissionDetail.comments.button": "Comments",
  "intake.submissionDetail.comments.empty": "No comments yet.",
  "intake.submissionDetail.comments.placeholder": "Add a comment",
  "intake.submissionDetail.comments.submit": "Post comment",
  "intake.submissionDetail.activity.title": "Activity",
  "intake.submissionDetail.activity.button": "Activity",
  "intake.submissionDetail.activity.empty": "No activity yet.",
  "intake.submissionDetail.activity.declinedDetails": "Declined: {details}",
  "intake.submissionDetail.activity.waitlistedDetails": "Waitlisted: {details}",
  "intake.submissionDetail.activity.cancelledDetails": "Cancelled: {details}",

  // Why a request was declined, shown back to the requester (#4197). The reason
  // comes from the workspace's curated list; the note is the reviewer's own
  // words. `titleNoReason` covers a reason an admin has since deleted (the FK is
  // SET_NULL), where the note survives without it.
  "intake.submissionDetail.decline.title": "Declined — {reason}",
  "intake.submissionDetail.decline.titleNoReason": "Declined",
  "intake.submissionDetail.outcome.declined.title": "Declined: {reason}",
  "intake.submissionDetail.outcome.declined.titleNoReason": "Declined",
  "intake.submissionDetail.outcome.waitlisted.title": "Waitlisted: {reasons}",
  "intake.submissionDetail.outcome.waitlisted.titleNoReason": "Waitlisted",
  "intake.submissionDetail.outcome.cancelled.title": "Cancelled: {reason}",
  "intake.submissionDetail.outcome.cancelled.titleNoReason": "Cancelled",

  "intake.requesterFlow.formsTitle": "Forms",
  "intake.requesterFlow.submitDeniedTitle": "You can't submit requests",
  "intake.requesterFlow.submitDeniedDescription":
    "Ask a workspace admin for permission to submit requests.",
  "intake.requesterFlow.selectTypeTitle": "Start a request",
  "intake.requesterFlow.selectTypeDescription":
    "Pick the form that matches what you need. Your request is only created when you submit it.",
  "intake.requesterFlow.startRequest": "Start Request",
  "intake.requesterFlow.submitRequest": "Submit Request",
  "intake.requesterFlow.formsAvailable": "{count} forms available",
  "intake.requesterFlow.formsEmpty": "No forms available.",
  "intake.requesterFlow.formsEmptyForQuery": "No forms match this search.",
  "intake.requesterFlow.formsSearchPlaceholder": "Search forms",
  "intake.requesterFlow.formsSearchClear": "Clear form search",
  "intake.requesterFlow.formsLoadMore": "Load more",
  "intake.requesterFlow.formsLoadingMore": "Loading…",
  "intake.requesterFlow.selectFormTitle": "Select a form",
  "intake.requesterFlow.selectFormDescription": "Pick a form from the sidebar to submit a request.",
  "intake.requesterFlow.submit": "Submit request",
  "intake.requesterFlow.submitting": "Submitting…",
  "intake.requesterFlow.submitSuccess": "Request submitted.",
  "intake.requesterFlow.submitError": "Failed to submit request.",
  "intake.requesterFlow.noFieldsTitle": "This form has no fields to fill in",
  "intake.requesterFlow.noFieldsDescription":
    "There are no questions to answer, so this form can't be submitted. Pick another form, or contact your workspace admin to finish setting this one up.",
  "intake.validation.url": "Enter a valid URL. We'll add https:// if you leave it off.",
  "intake.requesterFlow.submittedTitle": "Request submitted",
  "intake.requesterFlow.submittedDescription": "Your request is pending review.",
  "intake.requesterFlow.submitAnother": "Submit another request",
  "intake.requesterFlow.backToRequests": "Back to Requests",
  "intake.requesterFlow.backToForms": "Back to forms",
  "intake.requesterFlow.requiredQuestionsLeft":
    "{count, plural, one {# required question left} other {# required questions left}}",
  "intake.requesterFlow.reviewersTitle":
    "{count, plural, one {Reviewed by # request owner} other {Reviewed by # request owners}}",
  "intake.requesterFlow.exit": "Exit",
  "intake.requesterFlow.submitNow": "Submit now",
  "intake.requesterFlow.stepProgress":
    "Step {current} of {total} · {answered} of {questions} answered",
  "intake.requesterFlow.answeredProgress": "{answered} of {questions} answered",
  "intake.requesterFlow.submitPartialTitle":
    "{count, plural, one {Submit with # question unanswered?} other {Submit with # questions unanswered?}}",
  "intake.requesterFlow.submitPartialBody":
    "We can start with this. Anything you add now is one less thing we come back to ask you.",
  "intake.requesterFlow.submitPartialCancel": "Keep filling it in",
  "intake.requesterFlow.submitPartialConfirm": "Submit anyway",
  "intake.requesterFlow.more": "More",
  "intake.requesterFlow.less": "Less",
  "intake.requesterFlow.reviewersHeading": "What happens next",
  "intake.requesterFlow.reviewersDescription": "{names} will review this request.",
  "intake.requesterFlow.navBack": "Back",
  "intake.requesterFlow.navContinue": "Continue",
  "intake.requesterFlow.navPrevious": "Previous",
  "intake.requesterFlow.navNext": "Next",
  "intake.requesterFlow.navEnd": "You've reached the end of this form.",
  "intake.requesterFlow.navNoPages": "This form has no pages to fill in.",
  "intake.requesterFlow.pageIndicator": "Page {current} of {total}",
  "intake.requesterFlow.validationTitle":
    "Complete the required fields on earlier pages before finishing.",
  "intake.requesterFlow.validationFieldRequired": "{field} is required",
  "intake.requesterFlow.validationGoToPage": "Go to page {page}",
  "intake.requesterFlow.saveDraft": "Save draft",
  "intake.requesterFlow.savingDraft": "Saving…",
  "intake.requesterFlow.draftSaved": "Draft saved. Only you can see it.",
  "intake.requesterFlow.draftSaveError": "Failed to save draft.",
  "intake.requesterFlow.draftLoadError": "Failed to load this draft.",
  "intake.requesterFlow.draftDeleteError": "Failed to delete draft.",
  "intake.requesterFlow.draftsHeading": "Your drafts",
  "intake.requesterFlow.draftUntitled": "Untitled draft",
  "intake.requesterFlow.draftMeta": "{form} · saved {updated}",
  "intake.requesterFlow.draftResume": "Resume",
  "intake.requesterFlow.draftDelete": "Delete draft {name}",

  // Intake → project bridge: drafts list on submission detail
  "intake.drafts.title": "Linked projects",
  "intake.drafts.createButton": "Convert to project",
  "intake.drafts.empty": "No projects yet.",
  "intake.drafts.loading": "Loading projects…",
  "intake.drafts.gate.permission": "You don't have permission to create draft projects.",
  "intake.drafts.gate.notCompleted":
    "Submission must be in a completed state before it can be converted.",
  "intake.drafts.status.draft": "Draft",
  "intake.drafts.status.published": "Published",
  "intake.drafts.rowActions.edit": "Edit",
  "intake.drafts.rowActions.delete": "Delete",
  "intake.drafts.delete.title": "Delete draft project?",
  "intake.drafts.delete.description": 'This will permanently delete the draft project "{name}".',
  "intake.drafts.delete.confirm": "Delete",
  "intake.drafts.delete.success": "Draft project deleted.",
  "intake.drafts.delete.error": "Failed to delete draft project.",

  // Intake → project bridge: the owner-only "Create project" button on the
  // Linked Projects tab (NG-701)
  "intake.linkedProjects.createButton": "Create project",
  "intake.linkedProjects.createDisabledUnaccepted":
    "Accept the request first — a project can only be created from an accepted request.",
  // Intake → project bridge: "Link projects" picker modal (plan-work-rework PR5)
  "intake.linkProjects.openButton": "Link projects",
  "intake.linkProjects.title": "Link existing projects",
  "intake.linkProjects.description":
    "Select one or more workspace projects to link to this request.",
  "intake.linkProjects.searchPlaceholder": "Search by name or identifier…",
  "intake.linkProjects.empty": "No projects available to link.",
  "intake.linkProjects.loading": "Loading projects…",
  "intake.linkProjects.loadMore": "Load more",
  "intake.linkProjects.cancel": "Cancel",
  "intake.linkProjects.linkN": "Link {count} projects",
  "intake.linkProjects.linking": "Linking…",
  "intake.linkProjects.success": "Linked {count} projects.",
  "intake.linkProjects.loadError": "Couldn't load projects. Try again.",
  "intake.linkProjects.submitError": "Couldn't link the selected projects. Try again.",
  "intake.linkProjects.unlink.action": "Unlink project",
  "intake.linkProjects.unlink.title": "Unlink project?",
  "intake.linkProjects.unlink.description":
    'Remove the link between "{name}" and this request? The project itself isn\'t deleted.',
  "intake.linkProjects.unlink.confirm": "Unlink",
  "intake.linkProjects.unlink.success": "Project unlinked.",
  "intake.linkProjects.unlink.error": "Couldn't unlink the project. Try again.",

  // Linked Projects table — columns on the request → Linked Projects tab.
  "intake.linkedProjects.col.identifier": "ID",
  "intake.linkedProjects.col.name": "Project Name",
  "intake.linkedProjects.col.status": "Status",
  "intake.linkedProjects.col.lead": "Lead",
  "intake.linkedProjects.col.progress": "Progress",
  "intake.linkedProjects.col.startDate": "Start Date",
  "intake.linkedProjects.col.targetCompletion": "Target Completion Date",
  "intake.linkedProjects.col.actualCompletion": "Actual Completion Date",
  "intake.linkedProjects.rowActions.open": "Open project",
  "intake.linkedProjects.restricted.tooltip":
    "This project is linked to the request but has not been shared with you.",

  // Intake → project bridge: schedule (postdraft) page

  // Intake → project bridge: team-capacity sidebar on schedule page

  // Project settings — features
  "project_settings.features.intake.title": "Intake",
  "project_settings.features.intake.description":
    "Allow this project to be referenced from intake requests.",
  "project_settings.features.intake.toggle": "Enable intake for this project",
  "project_settings.features.intake.save_error": "Couldn't save the intake setting. Try again.",

  // RBAC eligibility
  "rbac.ineligible_assignment": "This user's workspace role is not eligible for this assignment",
  "rbac.role_display.team.lead": "Team Lead",
  "rbac.role_display.team.member": "Team Member",
  "rbac.role_display.project.lead": "Project Lead",
  "rbac.role_display.project.member": "Project Member",
  "rbac.role_display.workitem.assignee": "Task Assignee",
  "rbac.role_display.request.owner": "Request Owner",
  "rbac.role_display.request.reviewer": "Request Reviewer",
  "rbac.role_display.request.client": "Request Client",
  "rbac.role_display.program.lead": "Program Lead",
  "rbac.role_display.goal.lead": "Goal Lead",

  // Team lead badge
  "teams.detail.lead_badge": "Lead",

  // Capacity v2 (redesigned workspace capacity page)
  "capacity.v2.subline.live_snapshot": "Live snapshot",
  "capacity.v2.subline.team_view": "Team view",
  "capacity.v2.action.export": "Export",
  "capacity.v2.action.alerts": "Alerts",
  "capacity.v2.action.new_allocation": "New allocation",
  "capacity.v2.period.label": "Period",
  "capacity.v2.period.week": "This week",
  "capacity.v2.period.month": "Month",
  "capacity.v2.scope.label": "Team scope",
  "capacity.v2.scope.my_team": "My team",
  "capacity.v2.scope.all_teams": "All teams",
  "capacity.v2.team.label": "Team",
  "capacity.v2.team.all": "All",
  "capacity.v2.team.pick": "Filter by team",
  "capacity.v2.team.search": "Search teams...",
  "capacity.v2.team.not_found": "No team found",
  "capacity.v2.project.label": "Project",
  "capacity.v2.project.all": "All",
  "capacity.v2.project.pick": "Filter by project",
  "capacity.v2.project.search": "Search projects...",
  "capacity.v2.project.not_found": "No project found",
  "capacity.v2.filter.exclude_deactivated": "Exclude deactivated users",
  "capacity.v2.kpi.over_allocated": "Overallocated",
  "capacity.v2.kpi.over_allocated.caption": ">100% utilization · need to rebalance",
  "capacity.v2.kpi.people": "people",
  "capacity.v2.kpi.free_capacity": "Free capacity",
  "capacity.v2.kpi.free_capacity.caption": "{count} people have room to take on work",
  "capacity.v2.kpi.hours": "hours",
  "capacity.v2.kpi.team_utilization": "Team utilization",
  "capacity.v2.kpi.project_utilization": "Project utilization",
  "capacity.v2.kpi.pts_vs_target": "pts vs {target}% target",
  "capacity.v2.kpi.trend": "8-week trend",
  "capacity.v2.kpi.period_trend": "Period trend",
  "capacity.v2.kpi.trend_tooltip_week": "Week of {date}",
  "capacity.v2.kpi.trend_tooltip_hours": "{hours}h / {capacity}h",
  "capacity.v2.wow_short": "WoW",
  "capacity.v2.team_panel.title": "My team",
  "capacity.v2.team_panel.member_summary": "{total} members · {over} over · {under} under",
  "capacity.v2.team_panel.pts_vs_target": "pts vs target",
  "capacity.v2.team_panel.see_all": "See all teams",
  "capacity.v2.list.title": "Who is over / under allocated",
  "capacity.v2.list.of": "of",
  "capacity.v2.list.empty": "No members match the current filter.",
  "capacity.v2.list.loading_more": "Loading more members…",
  "capacity.v2.list.scroll_for_more": "Scroll for more",
  "capacity.v2.chip.all": "All",
  "capacity.v2.chip.over": "Over capacity",
  "capacity.v2.chip.near_cap": "Nearing cap",
  "capacity.v2.chip.on_target": "On target",
  "capacity.v2.chip.under": "Under target",
  "capacity.v2.chip.people": "{count} {count, plural, one {person} other {people}}",
  "capacity.v2.status.over": "Over",
  "capacity.v2.status.near_cap": "Near cap",
  "capacity.v2.status.on_target": "On target",
  "capacity.v2.status.under": "Under",
  "capacity.v2.sort.label": "Sort",
  "capacity.v2.sort.utilization_desc": "Utilization (high → low)",
  "capacity.v2.sort.utilization_asc": "Utilization (low → high)",
  "capacity.v2.sort.name_asc": "Name (A → Z)",
  "capacity.v2.col.member": "Member",
  "capacity.v2.col.utilization": "Utilization vs {target}% target",
  "capacity.v2.col.assigned_avail": "Assigned / Avail",
  "capacity.v2.col.hours_by_category": "Hours by category",
  "capacity.v2.row.over_by": "+{hours}h over",
  "capacity.v2.row.free": "{hours}h free",
  "capacity.v2.row.on_target_short": "on target",
  "capacity.v2.category.project": "Project",
  "capacity.v2.category.event": "Event",
  "capacity.v2.category.other": "Other",
  "capacity.v2.category.project_description": "Hours allocated to tasks on a project.",
  "capacity.v2.category.event_description": "Hours allocated to events tied to a project.",
  "capacity.v2.category.other_description":
    "Calendar overhead — meetings, app events, and external calendar entries that aren't planned project work. Time off and holidays are excluded; they reduce capacity instead.",

  // OAuth consent, unavailable and denied pages, and the sign-in brand panel (NG-2581)
  "oauth.consent.loading": "Loading…",
  "oauth.consent.load_error": "Failed to load consent context",
  "oauth.consent.no_redirect": "Server did not return a redirect URL.",
  "oauth.consent.network_error": "Network error",
  "oauth.consent.verified": "Cognota-verified app",
  "oauth.consent.from_workspace": "From workspace {workspace}",
  "oauth.consent.requesting": "{app} is requesting access to your Cognota account.",
  "oauth.consent.workspace": "Workspace",
  "oauth.consent.choose_workspace": "Choose a workspace",
  "oauth.consent.able_to": "It will be able to:",
  "oauth.consent.denying": "Denying…",
  "oauth.consent.deny": "Deny",
  "oauth.consent.approving": "Approving…",
  "oauth.consent.approve": "Approve",
  "oauth.unavailable.loading": "Loading…",
  "oauth.unavailable.invalid_title": "This link is no longer valid",
  "oauth.unavailable.invalid_description": "Try signing in to the app again from its main page.",
  "oauth.unavailable.this_app": "this app",
  "oauth.unavailable.title": "You can't use {app} here",
  "oauth.unavailable.members_only_before": "{app} is only available to members of the ",
  "oauth.unavailable.members_only_after":
    " workspace. You don't have access to that workspace. If you think this is a mistake, ask the workspace admin to add you.",
  "oauth.unavailable.no_workspace":
    "{app} isn't available to your account in any workspace you're a member of.",
  "oauth.unavailable.back": "Back to {app}",
  "oauth.denied.title": "Access denied",
  "oauth.denied.description":
    "You declined to share access with this app. You can close this tab and return to where you were.",
  "auth.brand.tagline_line1": "Your All-in-One",
  "auth.brand.tagline_line2": "LearnOps® Platform",
  "auth.brand.description":
    "Evaluate demand, prioritize the right work and unlock capacity to execute more efficiently.",
  "auth.continue_with_google": "Continue with Google",

  // Breadcrumbs
  "breadcrumb.teams": "Teams",
  "breadcrumb.team_settings": "Settings",
  "breadcrumb.team_projects": "Projects",
  "breadcrumb.projects": "Projects",
  "breadcrumb.workspace_settings": "Settings",
  "breadcrumb.project_settings": "Settings",
  "breadcrumb.archived_work_items": "Archived work items",
  "breadcrumb.programs": "Programs",
  "breadcrumb.program_settings": "Settings",
  "breadcrumb.dashboards": "Dashboards",
  "breadcrumb.reports": "Reports",
  "breadcrumb.reports.teammate": "Reporting Teammate",
  "breadcrumb.dashboards.capacity_report": "Capacity report",
  "breadcrumb.dashboards.capacity_trajectory": "Capacity trajectory",
  "breadcrumb.custom_fields": "Global fields",
  "breadcrumb.templates": "Project templates",
  "breadcrumb.processes": "Processes",
  "breadcrumb.label": "Breadcrumb",
  "breadcrumb.webhooks": "Webhooks",
  "breadcrumb.events": "Events",
  "breadcrumb.event_new": "New event",
  "breadcrumb.event_schedule": "Schedule",
  "breadcrumb.browse": "Tasks",
  "breadcrumb.notifications": "Notifications",
  "breadcrumb.my_work": "My Work",
  "breadcrumb.availability": "Availability",
  "breadcrumb.capacity": "Capacity",
  "breadcrumb.project_intake": "Intake",
  "timeline.title": "Timeline",
  "timeline.action.filter": "Filter",
  "timeline.action.export": "Export",
  "timeline.action.new_event": "New event",
  "timeline.filter.heading": "Filters",
  "timeline.filter.task_category": "Task category",
  "timeline.filter.event_category": "Event category",
  "timeline.filter.project": "Project",
  "timeline.filter.all": "All",
  "timeline.filter.search_placeholder": "Search…",
  "timeline.filter.empty": "No matches",
  "timeline.filter.clear": "Clear",
  "timeline.search_placeholder": "Search events & tasks",
  "timeline.all_day": "All day",
  "timeline.timezone_label": "Your time zone: {timezone}",
  "timeline.midnight": "Midnight",
  "timeline.archived": "Archived",
  "timeline.hours_stretched_full_day": "Hours stretched to the full day",
  // NG-2020. The chip a day column shows when the displayed hours cannot hold
  // every event it has. Deliberately no time: the count is the whole message,
  // and the real times appear on the grid the moment the chip is clicked.
  "timeline.outside.before": "{count} before",
  "timeline.outside.after": "{count} later",
  "timeline.outside.before_hint": "Events earlier than the hours shown. Show them.",
  "timeline.outside.after_hint": "Events later than the hours shown. Show them.",
  // The marker above the chart while a chip has stretched the range past the
  // viewer's own setting. Temporary by design (NG-2020 AC 7), so it says so and
  // offers the way back rather than letting the chart read as permanently
  // different.
  "timeline.hours_stretched": "Hours stretched to {range}",
  "timeline.hours_reset": "Reset hours",
  "timeline.week_ordinal": "Week {number}",
  "timeline.group_by": "Group by",
  "timeline.group_by.none": "No grouping",
  "timeline.group_by.user": "Assignee",
  "timeline.group_by.team": "Team",
  "timeline.group_by.project": "Project",
  "timeline.group_by.type": "Type",
  "timeline.group_by.stage": "Process stage",
  "timeline.group_by.category": "Category",
  "timeline.bucket.no_category": "No category",
  "timeline.type.events": "Events",
  "timeline.type.tasks": "Tasks",
  "timeline.type.milestones": "Milestones",
  "timeline.bucket.no_assignee": "No assignee",
  "timeline.bucket.no_team": "No team",
  "timeline.bucket.no_project": "No project",
  "timeline.granularity.day": "Day",
  "timeline.granularity.week": "Week",
  "timeline.granularity.month": "Month",
  "timeline.today": "Today",
  "timeline.jump_to_date": "Jump to date",
  "timeline.scroll_back": "Scroll back",
  "timeline.scroll_forward": "Scroll forward",
  "timeline.view_settings": "View settings",
  "timeline.settings.weekends": "Show weekends",
  "timeline.settings.weekends_sub": "Display Saturday & Sunday columns",
  "timeline.settings.timeoff": "Show time off",
  "timeline.settings.timeoff_sub": "Mark approved PTO over the timeline",
  "timeline.settings.holidays": "Show regional holidays",
  "timeline.settings.holidays_sub": "Highlight observed holidays",
  "timeline.empty.title": "No events or tasks scheduled in this period.",
  "timeline.empty.sub": "Try clearing your search or adjusting the time range.",
  "timeline.error": "Something went wrong loading your timeline. Try refreshing.",
  "timeline.retry": "Retry",
  "timeline.panel.event": "Event",
  "timeline.panel.task": "Task",
  "timeline.panel.milestone": "Milestone",
  "timeline.panel.close": "Close",
  "timeline.panel.status": "Status",
  "timeline.panel.status_scheduled": "Scheduled",
  "timeline.panel.assignee": "Assignee",
  "timeline.panel.assignees": "Assignees",
  "timeline.panel.project": "Project",
  "timeline.panel.no_project": "No project",
  "timeline.panel.start": "Start",
  "timeline.panel.end": "End",
  "timeline.panel.description": "Description",
  "timeline.panel.open_event": "Open event",
  "timeline.panel.open_task": "Open task",
  "timeline.panel.open_milestone": "Open milestone",
  "timeline.panel.edit": "Edit",
  "timeline.conflict_banner": "Scheduling conflict — overlaps approved time off.",
  "timeline.no_permission_tooltip": "You don't have permission to edit this item.",
  "timeline.conflict_dialog.title": "Overlaps time off",
  "timeline.conflict_dialog.body":
    "This item overlaps with {name}'s time off on {dates}. Do you want to continue?",
  "timeline.conflict_dialog.continue": "Continue",
  "timeline.conflict_dialog.cancel": "Cancel",
  "timeline.persist_error": "Couldn't save the new dates. Please try again.",
  "breadcrumb.new": "New",
  "breadcrumb.untitled": "Untitled",

  // App version update
  "version_update.title": "New version available",
  "version_update.description":
    "Refresh to load the latest version. Your current session will keep working until you do.",
  "version_update.refresh": "Refresh",

  // Project template — Workflow tab
  "project_templates.workflow.title": "Workflow",
  "project_templates.workflow.description":
    "Statuses come from your workspace Project statuses and are created when a project is started from this template. Use “Configure” to set which statuses each one can transition to.",
  "project_templates.workflow.reset_transitions": "Reset transitions",
  "project_templates.workflow.default_badge": "Default",
  "project_templates.workflow.empty":
    "No Project statuses configured yet. Add them under Settings → Project statuses, and they will appear here.",
  "project_templates.workflow.reset_confirm_title": "Reset transitions?",
  "project_templates.workflow.reset_confirm_description":
    "This will remove all custom transition rules. All status transitions will become unrestricted.",
  "project_templates.workflow.reset_success": "Transitions reset.",
  "project_templates.workflow.save_error": "Failed to save workflow.",
  "project_templates.workflow.load_error": "Failed to load project statuses.",
  "project_templates.workflow.fetch_error": "Failed to load project statuses.",

  // Capacity Planning (People view)
  "capacity_planning.zoom.day": "Day",
  "capacity_planning.zoom.week": "Week",
  "capacity_planning.zoom.month": "Month",
  "capacity_planning.nav.today": "Today",
  "capacity_planning.nav.earlier": "Earlier",
  "capacity_planning.nav.earlier_shortcut": "Earlier (Alt+←)",
  "capacity_planning.nav.later": "Later",
  "capacity_planning.nav.later_shortcut": "Later (Alt+→)",
  "capacity_planning.people.column_title": "People",
  "capacity_planning.people.search_placeholder": "Search people…",
  "capacity_planning.people.group_count": "{count} people",
  "capacity_planning.filter.label": "Filter",
  "capacity_planning.filter.team": "Team",
  "capacity_planning.filter.skill": "Qualification",
  "capacity_planning.filter.program": "Program",
  "capacity_planning.filter.capacity": "Capacity",
  "capacity_planning.filter.bucket_over": "Over-allocated",
  "capacity_planning.filter.bucket_near": "Near capacity",
  "capacity_planning.filter.bucket_target": "On target",
  "capacity_planning.filter.bucket_under": "Under-utilized",
  "capacity_planning.filter.job_role": "Job role",
  "capacity_planning.filter.project_status": "Project status",
  "capacity_planning.filter.archived_projects": "Archived projects",
  "capacity_planning.filter.include_archived": "Include archived projects",
  "capacity_planning.filter.archived_included": "Archived included",
  "capacity_planning.filter.clear_all": "Clear all",
  "capacity_planning.filter.remove": "Remove {label} filter",
  "capacity_planning.sort.label": "Sort by",
  "capacity_planning.sort.name_asc": "Name (A–Z)",
  "capacity_planning.sort.name_desc": "Name (Z–A)",
  "capacity_planning.sort.utilization_desc": "Utilization (highest)",
  "capacity_planning.sort.room_desc": "Most room first",
  "capacity_planning.group.label": "Group by",
  "capacity_planning.group.member": "None",
  "capacity_planning.group.team": "Team",
  "capacity_planning.group.project": "Project",
  "capacity_planning.people.project_meta": "{tasks} tasks · {hours}",
  "capacity_planning.people.over_allocated": "Over-allocated",
  "capacity_planning.people.toggle_member": "Toggle {name}'s work",
  "capacity_planning.people.open_task": "Open {name}",
  "capacity_planning.people.utilization_label": "{pct}% utilized",
  "capacity_planning.people.loading": "Loading…",
  "capacity_planning.people.empty": "No people match your filters.",
  "capacity_planning.people.empty_no_groups":
    "No teams to group by yet \u2014 this workspace\u2019s capacity is still here.",
  "capacity_planning.people.group_by_person": "Group by person",
  "capacity_planning.people.load_failed_title": "Couldn't load capacity",
  "capacity_planning.people.load_failed_description":
    "The read failed or took too long, so no numbers are shown. Try again.",
  "capacity_planning.people.load_more": "Load more people",
  "capacity_planning.people.legend_hint":
    "Hours committed per period. Click a person to see contributing work.",
  "capacity_planning.legend.unconfigured": "Not configured",
  "capacity_planning.cell.not_applicable": "N/A",
  "capacity_planning.legend.booked": "Estimated hours",
  "capacity_planning.legend.actual": "Logged hours",
  "capacity_planning.legend.target": "Target — past the line reads on target",
  "capacity_planning.legend.free": "Free, up to the week's hours",
  "capacity_planning.legend.over": "Over — capped, the number says how far",
  "capacity_planning.legend.off": "Time off",
  "capacity_planning.insights.utilization": "Utilization",
  "capacity_planning.insights.utilization_caption": "{planned}h of {capacity}h capacity",
  "capacity_planning.insights.no_capacity": "No configured capacity in this window",
  "capacity_planning.insights.trend": "Utilization trend",
  "capacity_planning.insights.balance": "Workload balance",
  "capacity_planning.insights.balance_even": "Well distributed",
  "capacity_planning.insights.balance_uneven": "Some unevenness",
  "capacity_planning.insights.balance_poor": "Uneven distribution",
  "capacity_planning.insights.balance_empty": "Needs at least two people with planned work",
  "capacity_planning.insights.accuracy": "Estimate accuracy",
  "capacity_planning.insights.accuracy_caption": "Logged vs estimated · past 30 days",
  "capacity_planning.insights.accuracy_empty": "No hours logged in the past 30 days",
  "capacity_planning.member_panel.title": "Team member",
  "capacity_planning.member_panel.weekly_cap": "Weekly cap",
  "capacity_planning.member_panel.active_items": "Active items",
  "capacity_planning.member_panel.issues_label":
    "{count, plural, one {# overallocation issue} other {# overallocation issues}} in this window",
  "capacity_planning.member_panel.over_by": "{hours} over capacity per period (peak)",
  "capacity_planning.member_panel.replace_with": "Replace with",
  "capacity_planning.member_panel.pick_person": "Pick a person…",
  "capacity_planning.member_panel.search_people": "Search people…",
  "capacity_planning.member_panel.no_candidates": "No matching people.",
  "capacity_planning.member_panel.candidate_select_label": "Pick who takes over {task}",
  "capacity_planning.member_panel.candidate_free": "{hours} free in the task window",
  "capacity_planning.member_panel.assign": "Assign",
  "capacity_planning.member_panel.reassigned_to": "Reassigned to {name}",
  "capacity_planning.member_panel.reassigned_toast": "Assignment updated.",
  "capacity_planning.member_panel.reassign_error": "Couldn't update the assignment.",
  "capacity_planning.member_panel.assigned_label": "Assigned work",
  "capacity_planning.member_panel.assigned_empty": "No work assigned in this window.",
  "capacity_planning.member_panel.col_item": "Item",
  "capacity_planning.member_panel.col_dates": "Dates",
  "capacity_planning.member_panel.col_estimated": "Est",
  "capacity_planning.member_panel.col_logged": "Logged",
  "capacity_planning.member_panel.total_label": "Total",
  "capacity_planning.member_panel.daily_label": "Daily",
  "capacity_planning.member_panel.per_day": "{hours}/day",
  "capacity_planning.actuals.toggle": "Hours shown",
  "capacity_planning.actuals.estimated": "Estimated",
  "capacity_planning.actuals.actual": "Actual",
  "capacity_planning.actuals.node_title": "Estimated {estimated} · Logged {logged}",
  "capacity_planning.bar_drag.rescheduled": "Dates updated.",
  "capacity_planning.bar_drag.undo": "Undo",
  "capacity_planning.bar_drag.reschedule_error": "Couldn't update the dates.",
  "capacity_planning.bar_menu.open_details": "Open details",
  "capacity_planning.bar_menu.unassign": "Unassign from this person",
  "capacity_planning.bar_menu.unassigned": "Assignee removed.",
  "capacity_planning.bar_menu.unassign_error": "Couldn't remove the assignee.",
  "capacity_planning.bar_drag.reassigned": "Assignment updated.",
  "capacity_planning.bar_drag.reassign_error": "Couldn't update the assignment.",
  "capacity_planning.risks.badge_label": "{count} risk",
  "capacity_planning.risks.badge_label_plural": "{count} risks",
  "capacity_planning.risks.panel_title": "Capacity risks",
  "capacity_planning.risks.severity_high": "High",
  "capacity_planning.risks.severity_medium": "Medium",
  "capacity_planning.risks.type_pto_conflict": "PTO conflict",
  "capacity_planning.risks.type_over_allocation": "Over-allocation",
  "capacity_planning.risks.type_capacity_gap": "Capacity gap",
  "capacity_planning.risks.badge_aria":
    "{count, plural, one {# capacity risk on this project} other {# capacity risks on this project}}",
  "capacity_planning.risks.strip_title":
    "{count, plural, one {# capacity risk in this window} other {# capacity risks in this window}}",
  "capacity_planning.risks.new": "new",
  "capacity_planning.risks.more": "+{count} more",
  "capacity_planning.risks.capped": "Risks cover the 25 largest projects in view",
  "capacity_planning.risks.chip_over_allocation": "{name} · {days} days over · peak {pct}",
  "capacity_planning.risks.chip_over_allocation_consecutive":
    "{name} · {days} straight days over · peak {pct}",
  // Both figures describe the window on screen, so the chip says so; `date` is the
  // project's own due date, which used to read as the period the figures covered.
  "capacity_planning.risks.chip_capacity_gap":
    "{project} · {left} left vs {free} free this window · due {date}",
  "capacity_planning.risks.chip_pto_conflict":
    "{name} · {blocked} of {total} days blocked ({pct}%)",

  // Scan-first People view (2026-09-03)
  "capacity_planning.scan.legend_room": "{count} have room · {hours}h free",
  "capacity_planning.scan.legend_at_capacity": "{count} on target",
  "capacity_planning.scan.legend_over": "{count} over-allocated · {hours}h over",
  "capacity_planning.scan.legend_not_configured": "{count} not set up",
  "capacity_planning.scan.header_person_role_eyebrow": "Person / role",
  "capacity_planning.scan.person_external_assignee": "External assignee",
  "capacity_planning.scan.person_weekly_hours": "{hours} h/wk",
  "capacity_planning.scan.action_set_hours": "Set hours",
  "capacity_planning.scan.set_hours_member_error":
    "This person isn't a member of this workspace yet, so their hours can't be set here.",
  "capacity_planning.scan.set_hours_lookup_error":
    "We couldn't load this workspace's members, so their hours can't be opened. Try again.",
  // Unstaffed rows only. The person-row and week-popover buttons that shared
  // this label were removed — they opened the read-only member panel and
  // assigned nothing (Eric, 2026-09-14). This one opens the work item itself,
  // where the assignee field is.
  "capacity_planning.scan.action_assign": "Assign",
  "capacity_planning.scan.action_rebalance": "Reassign work",
  "capacity_planning.scan.team_collapse": "Collapse {name}",
  "capacity_planning.scan.team_expand": "Expand {name}",
  "capacity_planning.scan.member_collapse": "Collapse {name}",
  "capacity_planning.scan.member_expand": "Expand {name}",
  "capacity_planning.scan.team_over_count": "{count} over",
  "capacity_planning.scan.team_shown_count": "{shown} of {total}",
  "capacity_planning.scan.team_member_total": "{count}",
  "capacity_planning.scan.team_band_placeholder": "Expand to see people by state",
  "capacity_planning.scan.show_more_people": "Show {count} more people",
  // Groups are teams OR projects, so the pager cannot name one of them.
  "capacity_planning.scan.show_more_groups": "View more",
  "capacity_planning.scan.show_more_tasks": "Show more tasks",
  "capacity_planning.scan.cell_not_applicable": "Not applicable",
  "capacity_planning.scan.cell_off": "Off",
  "capacity_planning.scan.cell_blocked": "{hours}h blocked",
  "capacity_planning.scan.cell_booked_off": "booked while off",
  "capacity_planning.scan.cell_on_target": "on target",
  "capacity_planning.scan.cell_to_target": "{hours}h to target",

  // Unstaffed demand — role-held work nobody owns yet (NG-1100)
  "capacity_planning.unstaffed.row_label": "Unstaffed",
  "capacity_planning.unstaffed.row_subtitle":
    "{count, plural, one {# item} other {# items}} · {hours}h · {roles, plural, one {# role} other {# roles}}",
  "capacity_planning.unstaffed.expand": "Expand unstaffed work",
  "capacity_planning.unstaffed.collapse": "Collapse unstaffed work",
  "capacity_planning.unstaffed.short": "{hours}h short",
  "capacity_planning.unstaffed.fits": "Fits",
  "capacity_planning.unstaffed.detail": "needs {demand}h · {free}h free in the right roles",
  "capacity_planning.unstaffed.cell_short": "{hours}h with no one to take it",
  "capacity_planning.unstaffed.cell_fits": "fits the free hours in the right roles",
  "capacity_planning.unstaffed.needs_role": "needs {role}",
  "capacity_planning.unstaffed.no_holders": "No one holds this role",
  "capacity_planning.unstaffed.team_short": "{hours}h unstaffed short",
  "capacity_planning.unstaffed.strip_short": "{hours}h unstaffed · {short}h with no one to take it",
  "capacity_planning.unstaffed.strip_fits": "{hours}h unstaffed · all could be assigned today",
  "capacity_planning.scan.no_allocations": "No allocations in this window.",
  "capacity_planning.scan.other_project": "Other",
  "capacity_planning.scan.project_summary":
    "{count, plural, one {# task} other {# tasks}} · {hours}",
  "capacity_planning.headroom.header_estimated": "Estimated hours",
  "capacity_planning.headroom.header_actual": "Actual hours",
  "capacity_planning.headroom.over": "{hours}h over",
  "capacity_planning.headroom.free": "{hours}h free",
  "capacity_planning.headroom.of_total": "{used}h of {total}h",
  "capacity_planning.headroom.target_line": "target {target}%",
  "capacity_planning.headroom.not_set": "Not set",
  "capacity_planning.headroom.group_over": "{over} over · {ok} have capacity",
  "capacity_planning.headroom.group_ok":
    "{count, plural, one {# has capacity} other {# have capacity}}",
  "capacity_planning.headroom.group_not_set": "{count} not set",
  "capacity_planning.rebalance.apply_caveat":
    "Apply writes each assignment as its own change and can be undone per task.",
  "capacity_planning.rebalance.title": "Reassign work from {name} · {range}",
  "capacity_planning.rebalance.title_loading": "Reassign work",
  "capacity_planning.rebalance.description":
    "Move at least {hours} off this person in the window. Only their team and this window are considered; nothing else changes.",
  "capacity_planning.rebalance.description_loading": "Working out what could move.",
  "capacity_planning.rebalance.loading": "Finding plans…",
  "capacity_planning.rebalance.strategy_fewest_moves": "Fewest moves",
  "capacity_planning.rebalance.strategy_skill_fit": "Best qualification fit",
  "capacity_planning.rebalance.strategy_most_even": "Most even",
  "capacity_planning.rebalance.strategy_summary":
    "{count, plural, one {# task} other {# tasks}} · {hours} moved",
  "capacity_planning.rebalance.strategy_same": "Same plan as another option",
  "capacity_planning.rebalance.moves_heading": "Moves",
  "capacity_planning.rebalance.receiver_detail":
    "{free} free · {matched} of {required} qualifications",
  "capacity_planning.rebalance.before_after_heading": "{range}, before and after",
  "capacity_planning.rebalance.of_max": "of {max} · target {target}",
  "capacity_planning.rebalance.cancel": "Cancel",
  "capacity_planning.rebalance.apply": "Reassign {count, plural, one {# task} other {# tasks}}",
  "capacity_planning.rebalance.applied": "Reassigned {count, plural, one {# task} other {# tasks}}",
  "capacity_planning.rebalance.apply_error": "Some moves could not be written",
  "capacity_planning.rebalance.best_effort_title":
    "Some of this work doesn't fit anyone's calendar",
  "capacity_planning.rebalance.best_effort_body":
    "These moves use every hour the team has free. The last {remaining} needs a later date or another team.",
  "capacity_planning.rebalance.infeasible_title":
    "No one on the team can take {hours} in this window",
  "capacity_planning.rebalance.infeasible_body":
    "Every move that fits the team leaves someone over. The closest plan still leaves {remaining} over.",
  "capacity_planning.rebalance.infeasible_no_tasks":
    "Nothing here can move: {shared, plural, =0 {no single-assignee tasks in the window} one {the only task is shared with someone else} other {the # tasks are shared with other people}}.",
  "capacity_planning.rebalance.free_heading": "Free in {range}, in the roles this work needs",
  "capacity_planning.rebalance.free_hours": "{hours} free",
  "capacity_planning.rebalance.infeasible_next":
    "What would work: move a target date, borrow from another team, or leave it over on purpose so the red cell and the risk chip keep saying so.",
  "capacity_planning.rebalance.unmovable_title": "No plan can move {hours} of this",
  "capacity_planning.rebalance.unmovable_body":
    "It's work that has started, is shared or belongs to another team, or it's time off. To clear it, reschedule or reassign that work yourself.",
  "capacity_planning.cell.aria": "{bucket}: {booked}h booked of {capacity}h, {delta}",
  "capacity_planning.week_popover.over": "{hours}h over",
  "capacity_planning.week_popover.on_target": "on target",
  "capacity_planning.week_popover.to_target": "{hours}h to target",
  "capacity_planning.week_popover.figure_booked": "Booked",
  "capacity_planning.week_popover.figure_available": "Available",
  "capacity_planning.week_popover.figure_target": "Target",
  "capacity_planning.week_popover.figure_target_value": "{hours}h · {pct}%",
  "capacity_planning.week_popover.figure_blocked": "Blocked",
  "capacity_planning.week_popover.figure_blocked_reason": "{hours} · {reason}",
  "capacity_planning.week_popover.peak_day": "Peak day {day} · {hours}h of {capacity}h ({pct})",
  "capacity_planning.week_popover.open_week": "Open {firstName}'s week",
  "capacity_planning.week_popover.estimated_split":
    "Estimated split of the work item's hours across its dates",
  "capacity_planning.week_popover.private_item": "Private work item",
  "capacity_planning.week_popover.time_off": "Time off",
  "capacity_planning.week_popover.holiday": "Holiday",
  "capacity_planning.week_popover.calendar_blocked": "Calendar blocked",
  "capacity_planning.week_popover.non_working_day": "Time off",
  "capacity_planning.week_popover.available": "Available",
  "capacity_planning.week_popover.fully_utilized": "On target",
  "capacity_planning.week_popover.over_capacity": "Over capacity",
  "capacity_planning.week_popover.no_configured_capacity": "Not set",
  "capacity_planning.week_popover.not_in_scope": "Not in scope",

  // CSV importer (NG-645)
  // The optional first step (NG-1478). Only rendered by a consumer that passes
  // `instructions`, so Qualifications and Requests never show it.
  "csv_importer.step.instructions": "Instructions",
  "csv_importer.issue.transform_error": "Could not read “{field}”.",
  "csv_importer.step.upload": "Upload",
  "csv_importer.step.mapping": "Map columns",
  "csv_importer.step.values": "Map values",
  // Sub-label on the Map values step when the file has nothing to map, so the
  // step stays in the strip (fixed numbering) but reads as already handled (NG-2343).
  "csv_importer.step.values_skipped": "Nothing to map",
  "csv_importer.step.review": "Review",
  "csv_importer.cancel": "Cancel",
  "csv_importer.back": "Back",
  "csv_importer.next": "Next",
  // Shown when closing the import modal would throw away an uploaded file and its
  // column mapping (NG-2339). Nothing has been written yet, which is the point of
  // the reassurance in the body.
  "csv_importer.discard.title": "Discard this import?",
  "csv_importer.discard.body":
    "You'll lose your uploaded file and any column mapping. Nothing has been imported yet.",
  "csv_importer.discard.keep": "Keep editing",
  "csv_importer.discard.confirm": "Discard and close",
  "csv_importer.upload.title": "Upload a CSV file",
  "csv_importer.upload.subtitle":
    "Up to {maxMb} MB. The file is read in your browser and never uploaded.",
  "csv_importer.upload.choose_file": "Choose CSV file",
  "csv_importer.upload.parsing": "Reading file…",
  // The staged-file card, after Back to the upload step (NG-2344).
  "csv_importer.upload.file_rows": "{count, plural, one {# row} other {# rows}}",
  "csv_importer.upload.file_columns": "{count, plural, one {# column} other {# columns}}",
  "csv_importer.upload.replace": "Replace file",
  "csv_importer.upload.remove": "Remove",
  "csv_importer.upload.replace_confirm_title": "Replace file?",
  "csv_importer.upload.replace_confirm_body": "Your column mappings will be reset.",
  "csv_importer.upload.remove_confirm_title": "Remove file?",
  "csv_importer.upload.remove_confirm_body": "Your column mappings will be reset.",
  "csv_importer.upload.template_hint": "Not sure of the format?",
  "csv_importer.upload.download_template": "Download template",
  "csv_importer.upload.error_too_large":
    "That file is larger than {maxMb} MB. Choose a smaller file.",
  "csv_importer.upload.error_no_headers":
    "That file has no header row. Add a header row and try again.",
  "csv_importer.upload.error_empty": "That file has no data rows.",
  "csv_importer.upload.error_parse_failed":
    "We couldn't read that file. Check that it's a valid CSV and try again.",
  "csv_importer.mapping.instructions":
    "Match each column in your file to a field. Unmatched columns are ignored.",
  "csv_importer.mapping.ignore": "Ignore this column",
  "csv_importer.mapping.map_aria": "Map column “{header}” to a field",
  // Summary bar + status count chips (NG-2347).
  "csv_importer.mapping.summary": "{count, plural, one {# column} other {# columns}}",
  "csv_importer.mapping.summary_file":
    "{count, plural, one {# column} other {# columns}} in {file}",
  "csv_importer.mapping.count_matched": "{count} matched",
  "csv_importer.mapping.count_ignored": "{count} ignored",
  "csv_importer.mapping.count_attention": "{count} need attention",
  // Required-fields check.
  "csv_importer.mapping.required_check_title": "Required fields · {mapped} of {total} mapped",
  "csv_importer.mapping.required_unmapped": "{label} — no column mapped",
  // Filter tabs + search.
  "csv_importer.mapping.filter_aria": "Filter columns by status",
  "csv_importer.mapping.filter_all": "All",
  "csv_importer.mapping.filter_attention": "Needs attention",
  "csv_importer.mapping.filter_ignored": "Ignored",
  "csv_importer.mapping.filter_matched": "Matched",
  "csv_importer.mapping.search_placeholder": "Search columns or fields",
  "csv_importer.mapping.no_matches": "No columns match.",
  // Group headers + status pills.
  "csv_importer.mapping.group_attention": "Needs attention",
  "csv_importer.mapping.group_ignored": "Ignored",
  "csv_importer.mapping.group_matched": "Matched",
  "csv_importer.mapping.status_matched": "Matched",
  "csv_importer.mapping.status_ignored": "Ignored",
  "csv_importer.mapping.status_attention": "Needs attention",
  // Row hints.
  "csv_importer.mapping.hint_duplicate": "{field} is also mapped from “{other}”. Choose one.",
  "csv_importer.mapping.hint_suggestion": "These values look like {field}.",
  "csv_importer.mapping.suggestion_action": "Map to {field}",
  "csv_importer.mapping.hint_ignored_by_user": "Ignored. Won't be imported.",
  "csv_importer.mapping.hint_no_match": "No matching field. Won't be imported.",
  "csv_importer.mapping.sample": "e.g. {value}",
  "csv_importer.mapping.sample_empty": "No sample values",
  "csv_importer.mapping.custom_field_tag": "· custom field",
  // Field picker.
  "csv_importer.mapping.meta_required": "Required",
  "csv_importer.mapping.meta_custom": "Custom field",
  "csv_importer.mapping.meta_used_by": "Used by “{header}”",
  "csv_importer.mapping.picker_search": "Search fields",
  "csv_importer.mapping.picker_empty": "No fields match.",
  // Footer reason when Next is blocked (NG-2347).
  "csv_importer.mapping.fix_to_continue":
    "Fix {count, plural, one {# column} other {# columns}} to continue",
  "csv_importer.mapping.map_required_to_continue": "Map all required fields to continue",
  "csv_importer.values.instructions":
    "Some values in your file don't match a form option. Map each one to an option, or leave it unmapped to have that cell flagged.",
  "csv_importer.values.unmapped":
    "{count, plural, one {# value is not mapped to an option} other {# values are not mapped to an option}}",
  // How many rows the still-unmapped values touch, so the admin can weigh the work
  // (NG-2342). Shown alongside the unmapped count and updates as values are mapped.
  "csv_importer.values.affected": "They affect {count, plural, one {# row} other {# rows}}.",
  "csv_importer.values.unmapped_hint":
    "Unmapped values are imported as-is and flagged per row on the next step.",
  // Per-value count beside each unmatched value, e.g. "Learning Design · 8 rows".
  "csv_importer.values.value_rows": "{count, plural, one {# row} other {# rows}}",
  "csv_importer.values.leave_unmapped": "Leave unmapped",
  "csv_importer.values.no_match": "No matching option",
  "csv_importer.values.map_aria": "Map value “{value}” for {field} to an option",
  "csv_importer.review.summary": "{total, plural, one {# row} other {# rows}}",
  // The same count badge once the row cap has cut into the file, so it names what
  // the file held rather than only what survived (NG-1474).
  "csv_importer.review.summary_truncated":
    "{kept, number} of {total, plural, one {# row} other {# rows}}",
  "csv_importer.review.valid": "{count, plural, one {# valid} other {# valid}}",
  "csv_importer.review.invalid": "{count, plural, one {# invalid} other {# invalid}}",
  "csv_importer.review.with_warnings":
    "{count, plural, one {# with warnings} other {# with warnings}}",
  "csv_importer.review.import": "Import {count, plural, one {# row} other {# rows}}",
  // The same button while a consumer's pre-commit check is still deciding how
  // many rows there are to import (NG-1563). Countless on purpose: the browser's
  // own number is the one that ticket exists to stop showing, so the button
  // names the action and waits rather than promising a figure it is about to
  // correct. Disabled throughout, so it is never an offer.
  "csv_importer.review.import_pending": "Import",
  "csv_importer.review.col_row": "Row",
  // The Review step's own report frame (NG-1389). Deliberately future tense and
  // deliberately NOT the `csv_importer.result.*` headings: this screen is a
  // decision an admin has yet to take, the result is a report of one already
  // taken, and the two are on screen in the same dialog — the wizard stays
  // mounted behind the Users preview, so a shared string would be two matches.
  "csv_importer.review.title": "Review your file",
  // `{total, number}`, not a bare `{total}`: this now carries the FILE's row count
  // (NG-1474), so it reaches four digits routinely, and unformatted it read "of
  // 1050" right beside a badge saying "1,000 of 1,050".
  "csv_importer.review.outcome":
    "{valid, plural, one {# row} other {# rows}} of {total, number} will be imported.",
  // Not scoped to `.review.`, like `row_line` above and for the same reason: the
  // mapping and values steps say this too, so that an admin learns the file was
  // cut BEFORE mapping it rather than after (NG-1474).
  //
  // The dropped count leads because it is the half an admin can act on — "some
  // rows were left out" is not something anyone can split a file by — and the
  // sentence ends on the recourse rather than the loss. Pluralised on `dropped`
  // because the boundary case is the LIKELY one: a 1,001-row file drops exactly
  // one row, and the flat form read "The last 1 will not be imported ... put
  // them in a second file".
  "csv_importer.over_limit":
    "Your file has {total, number} rows, which is over the {max, number}-row limit. {dropped, plural, one {The last row will not be imported — put it in a second file and import that too.} other {The last # rows will not be imported — put them in a second file and import that too.}}",
  // The banner title the mapping and values steps carry it under. Review needs no
  // such title: there it rides inside the report banner, which has its own.
  "csv_importer.over_limit_title": "Your file is over the row limit",
  "csv_importer.review.errors_heading": "Rows that will not be imported",
  "csv_importer.review.warnings_heading": "Rows that will be imported with warnings",
  "csv_importer.review.dataset_heading": "Issues across the file",
  // Not scoped to `.review.`: the Requests importer's finished report says the
  // same thing about the same rows, so the two read one key rather than two that
  // get translated twice and drift.
  "csv_importer.row_line": "Row {row}: {message}",
  // The accessible name of the Review table's per-row marker. The icon beside a
  // row number is `aria-hidden`, so this is the only thing a screen reader hears.
  "csv_importer.review.row_status_error": "Has errors",
  "csv_importer.review.row_status_warning": "Has warnings",
  "csv_importer.dialog.title": "Import from CSV",
  // The report frame every importer shows (NG-1307). These four read identically
  // in Qualifications, Requests and Users, so they live with the shared component rather
  // than three times over; a heading a single importer owns (e.g. Users' skipped
  // rows) stays in that importer's namespace.
  "csv_importer.result.title": "Import complete",
  "csv_importer.result.errors_heading": "Rows that could not be imported",
  "csv_importer.result.import_another": "Import another file",
  "csv_importer.result.done": "Done",
  // The read-only banner on a record the viewer can open but not edit (NG-1872).
  // The title is the fact; the two reason lines sit behind "Why?". The
  // role-less reason is for a viewer whose System Role has no name to show
  // (the workspace payload predates `role_name`, or the role row is gone).
  "read_only_banner.why": "Why?",
  "read_only_banner.request.title": "You can read this request but not change it.",
  "read_only_banner.request.reason_with_role":
    "Your role, {role}, can view requests but not edit them.",
  "read_only_banner.request.reason": "Your role can view requests but not edit them.",
  "read_only_banner.request.recourse":
    "To change something, ask a request owner to make the edit, or ask a workspace admin to update your role.",
  "read_only_banner.project.title": "You can read this project but not change it.",
  "read_only_banner.project.reason_with_role":
    "Your role, {role}, can view projects but not edit them.",
  "read_only_banner.project.reason": "Your role can view projects but not edit them.",
  "read_only_banner.project.recourse":
    "To change something, ask a project lead to make the edit, or ask a workspace admin to update your role.",
  "read_only_banner.program.title": "You can read this program but not change it.",
  "read_only_banner.program.reason_with_role":
    "Your role, {role}, can view programs but not edit them.",
  "read_only_banner.program.reason": "Your role can view programs but not edit them.",
  "read_only_banner.program.recourse":
    "To change something, ask a program lead to make the edit, or ask a workspace admin to update your role.",
  "read_only_banner.work_item.title": "You can read this work item but not change it.",
  "read_only_banner.work_item.reason_with_role":
    "Your role, {role}, can view work items but not edit them.",
  "read_only_banner.work_item.reason": "Your role can view work items but not edit them.",
  "read_only_banner.work_item.recourse":
    "To change something, ask an assignee to make the edit, or ask a workspace admin to update your role.",

  // Team roster field (NG-2161): a team picker whose selection lists one Home team.
  "team_roster.home_lock_reason": "Home team can't be removed. Set another home team first.",
  "team_roster.footer_note":
    "The Home team owns this project and can't be removed. Set another team as Home first.",
  "team_roster.footer_note_program":
    "The Home team owns this program and can't be removed. Set another team as Home first.",
  "team_roster.program_access_tooltip":
    "Members and leads of the selected teams and their parent teams can see this program, and so can members and leads of every team under its Home team. Program leads and workspace administrators also have access. Programs with no Team Access are visible to everyone in the workspace.",
  "team_roster.program_access_help":
    "Choose which teams can access this program. Members and leads of the selected teams and their parent teams can see it, and so can members and leads of every team under its Home team. Program leads and workspace administrators also have access. Programs with no Team Access are visible to everyone in the workspace. You can only pick teams you have access to.",
  "team_roster.save_error": "Failed to update the project's teams",
  "team_roster.home_badge": "Home",
  "team_roster.set_home": "Set home",
  "team_roster.set_home_aria": "Set home: {name}",
};

export default en;
